import {
  Type,
  GenerateContentResponse,
  FunctionDeclaration,
} from "@google/genai";
import { callGeminiViaProxy, callServerAI, getAiInstance, getEnvKey, buildGeminiConfig, recordUsage, createTokenLedger } from "./geminiClient";
import type { TokenLedger, GeminiTelemetry } from "./geminiClient";
import { MODELS } from "../config/models";
import type { ModelEntry } from "../config/models";
import {
  fetchFMP,
  fetchAvOverview,
  fetchUsdConversionRate,
  getProxyAuthHeaders,
} from "./dataFetchers";
import { withRetry, cleanJson, extractText, type RetryContext } from "./aiUtilities";
import {
  getDeterministicData,
  trimInstitutionalData,
  buildIrrBridge,
  computeScenarioReturns,
  computeType4TerminalValue,
  computeType4ResidualIncomeValue,
} from "./financialEngine";
import {
  runScenarios,
  runSensitivities,
  validateScenarioConstraints,
  DEFAULT_SCENARIO_CONSTRAINTS,
} from "../lib/dcfEngine";
import { fetchEdgarContext } from "../lib/edgarFetcher";
import {
  HistoricalFactSheet,
  IntelligenceBrief,
  MathematicalEngine,
  CompanyAnalysis,
  AnalysisStageCallbacks,
  EdgarContext,
  OracleCategoryDetail,
  PulseOutput,
} from "../types/geminiTypes";

let failoverCallback: ((info: { agent: string, modelTried: string, fallbackModel: string, statusCode: number | string, ticker?: string }) => void) | undefined;

// ─── [QUOTE-SNAPSHOT-LOCK] ──────────────────────────────────────────────────
// The deterministic aggregation writes its stable/quote result here; the
// get_stock_quote agent tool serves it for the same symbol instead of
// re-fetching FMP at a later timestamp. One price per job — header, math,
// and agent prose all see the same snapshot. TTL bounds staleness to one
// job's duration; entries for other symbols (peer lookups) are never cached.
const quoteSnapshotCache = new Map<string, { data: any; fetchedAt: number }>();
const QUOTE_SNAPSHOT_TTL_MS = 15 * 60 * 1000; // ~ one analysis job duration

function setQuoteSnapshot(symbol: string, data: any) {
  // Opportunistic prune so the map never grows unbounded
  const now = Date.now();
  for (const [k, v] of quoteSnapshotCache) {
    if (now - v.fetchedAt > QUOTE_SNAPSHOT_TTL_MS) quoteSnapshotCache.delete(k);
  }
  quoteSnapshotCache.set(symbol, { data, fetchedAt: now });
}

function getQuoteSnapshot(symbol: string): any | null {
  const entry = quoteSnapshotCache.get(symbol);
  if (!entry) return null;
  if (Date.now() - entry.fetchedAt > QUOTE_SNAPSHOT_TTL_MS) {
    quoteSnapshotCache.delete(symbol);
    return null;
  }
  return entry;
}

export function setFailoverCallback(cb: typeof failoverCallback) {
  failoverCallback = cb;
}

function recordModelFailover({ agent, modelTried, fallbackModel, statusCode, ticker }: { agent: string, modelTried: string, fallbackModel: string, statusCode: number | string, ticker?: string }) {
  if (failoverCallback) {
    failoverCallback({ agent, modelTried, fallbackModel, statusCode, ticker });
  }
}

// [S6] Per-job Gemini JSON parse-retry accounting. Follows the same
// module-scoped idiom as failoverCallback above: reset at the start of each
// analysis run (before the agents launch) and read into observability at the
// end. A JSON parse failure that is silently retried is a model-reliability
// signal (flash parse-fails more than pro) and feeds the held Oracle model-tier
// decision. jsonRetryCtx() is passed to withRetry at every JSON-parsing agent
// call site so the wrapper can both name the offender in the log and count it.
let __geminiJsonRetryCount = 0;
let __geminiJsonRetryAgent: string | null = null;
function resetGeminiJsonRetryStats() {
  __geminiJsonRetryCount = 0;
  __geminiJsonRetryAgent = null;
}
function jsonRetryCtx(agent: string, model: string): RetryContext {
  return {
    agent,
    model,
    onJsonParseRetry: () => {
      __geminiJsonRetryCount += 1;
      __geminiJsonRetryAgent = agent;
    },
  };
}
// [S6] withRetry wrapper that binds the JSON-retry context. Keeps agent/model
// out of every closing paren: only the opening call changes, and maxRetries
// stays in its usual trailing position.
function withRetryJson<T>(
  agent: string,
  model: string,
  fn: () => Promise<T>,
  maxRetries = 4,
): Promise<T> {
  return withRetry(fn, maxRetries, jsonRetryCtx(agent, model));
}

const getIncomeStatementDeclaration: FunctionDeclaration = {
  name: "get_income_statement",
  description: "Get the income statement for a company.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      symbol: {
        type: Type.STRING,
        description:
          "Ticker symbol to fetch data for. Defaults to the primary company being analyzed if omitted. Pass a peer ticker (e.g., 'MSFT', 'GOOGL', 'META') to retrieve data for that company instead.",
      },
      limit: {
        type: Type.NUMBER,
        description: "Number of statements to return (default 10).",
      },
      period: {
        type: Type.STRING,
        enum: ["annual", "quarter"],
        description: "The period for the statement.",
      },
    },
    required: ["symbol"],
  },
};
const getBalanceSheetStatementDeclaration: FunctionDeclaration = {
  name: "get_balance_sheet_statement",
  description: "Get the balance sheet statement for a company.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      symbol: {
        type: Type.STRING,
        description:
          "Ticker symbol to fetch data for. Defaults to the primary company being analyzed if omitted. Pass a peer ticker (e.g., 'MSFT', 'GOOGL', 'META') to retrieve data for that company instead.",
      },
      limit: {
        type: Type.NUMBER,
        description: "Number of statements to return (default 10).",
      },
      period: {
        type: Type.STRING,
        enum: ["annual", "quarter"],
        description: "The period for the statement.",
      },
    },
    required: ["symbol"],
  },
};
const getCashFlowStatementDeclaration: FunctionDeclaration = {
  name: "get_cash_flow_statement",
  description: "Get the cash flow statement for a company.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      symbol: {
        type: Type.STRING,
        description:
          "Ticker symbol to fetch data for. Defaults to the primary company being analyzed if omitted. Pass a peer ticker (e.g., 'MSFT', 'GOOGL', 'META') to retrieve data for that company instead.",
      },
      limit: {
        type: Type.NUMBER,
        description: "Number of statements to return (default 10).",
      },
      period: {
        type: Type.STRING,
        enum: ["annual", "quarter"],
        description: "The period for the statement.",
      },
    },
    required: ["symbol"],
  },
};
const getKeyMetricsDeclaration: FunctionDeclaration = {
  name: "get_key_metrics",
  description: "Get key metrics for a company (PE ratio, ROE, etc.).",
  parameters: {
    type: Type.OBJECT,
    properties: {
      symbol: {
        type: Type.STRING,
        description:
          "Ticker symbol to fetch data for. Defaults to the primary company being analyzed if omitted. Pass a peer ticker (e.g., 'MSFT', 'GOOGL', 'META') to retrieve data for that company instead.",
      },
      limit: {
        type: Type.NUMBER,
        description: "Number of years to return (default 10).",
      },
      period: {
        type: Type.STRING,
        enum: ["annual", "quarter"],
        description: "The period.",
      },
    },
    required: ["symbol"],
  },
};
const getFinancialRatiosDeclaration: FunctionDeclaration = {
  name: "get_financial_ratios",
  description: "Get comprehensive financial ratios for a company.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      symbol: {
        type: Type.STRING,
        description:
          "Ticker symbol to fetch data for. Defaults to the primary company being analyzed if omitted. Pass a peer ticker (e.g., 'MSFT', 'GOOGL', 'META') to retrieve data for that company instead.",
      },
      limit: {
        type: Type.NUMBER,
        description: "Number of years to return (default 10).",
      },
      period: {
        type: Type.STRING,
        enum: ["annual", "quarter"],
        description: "The period.",
      },
    },
    required: ["symbol"],
  },
};
const getStockQuoteDeclaration: FunctionDeclaration = {
  name: "get_stock_quote",
  description: "Get real-time stock price and quote for a company.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      symbol: {
        type: Type.STRING,
        description:
          "Ticker symbol to fetch data for. Defaults to the primary company being analyzed if omitted. Pass a peer ticker (e.g., 'MSFT', 'GOOGL', 'META') to retrieve data for that company instead.",
      },
    },
    required: ["symbol"],
  },
};
const getInsiderTradingDeclaration: FunctionDeclaration = {
  name: "get_insider_trading",
  description: "Get recent insider trading events for a company.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      symbol: {
        type: Type.STRING,
        description:
          "Ticker symbol to fetch data for. Defaults to the primary company being analyzed if omitted. Pass a peer ticker (e.g., 'MSFT', 'GOOGL', 'META') to retrieve data for that company instead.",
      },
      limit: { type: Type.NUMBER, description: "Number of events to return." },
    },
    required: ["symbol"],
  },
};
const getStockNewsDeclaration: FunctionDeclaration = {
  name: "get_stock_news",
  description: "Get the latest news articles for a company.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      symbol: {
        type: Type.STRING,
        description:
          "Ticker symbol to fetch data for. Defaults to the primary company being analyzed if omitted. Pass a peer ticker (e.g., 'MSFT', 'GOOGL', 'META') to retrieve data for that company instead.",
      },
      limit: {
        type: Type.NUMBER,
        description: "Number of news articles to return.",
      },
    },
    required: ["symbol"],
  },
};
const getRevenueProductSegmentationDeclaration: FunctionDeclaration = {
  name: "get_revenue_product_segmentation",
  description: "Get the revenue breakdown by product segment for a company.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      symbol: {
        type: Type.STRING,
        description:
          "Ticker symbol to fetch data for. Defaults to the primary company being analyzed if omitted. Pass a peer ticker (e.g., 'MSFT', 'GOOGL', 'META') to retrieve data for that company instead.",
      },
      period: {
        type: Type.STRING,
        enum: ["annual", "quarter"],
        description: "The period (annual or quarter).",
      },
    },
    required: ["symbol"],
  },
};
const getRevenueGeographicSegmentationDeclaration: FunctionDeclaration = {
  name: "get_revenue_geographic_segmentation",
  description: "Get the revenue breakdown by geographic region for a company.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      symbol: {
        type: Type.STRING,
        description:
          "Ticker symbol to fetch data for. Defaults to the primary company being analyzed if omitted. Pass a peer ticker (e.g., 'MSFT', 'GOOGL', 'META') to retrieve data for that company instead.",
      },
      period: {
        type: Type.STRING,
        enum: ["annual", "quarter"],
        description: "The period (annual or quarter).",
      },
    },
    required: ["symbol"],
  },
};
const getCompanyProfileDeclaration: FunctionDeclaration = {
  name: "get_company_profile",
  description: "Get comprehensive profile information for a company.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      symbol: {
        type: Type.STRING,
        description:
          "Ticker symbol to fetch data for. Defaults to the primary company being analyzed if omitted. Pass a peer ticker (e.g., 'MSFT', 'GOOGL', 'META') to retrieve data for that company instead.",
      },
    },
    required: ["symbol"],
  },
};
const getStockPeersDeclaration: FunctionDeclaration = {
  name: "get_stock_peers",
  description: "Get a list of peer companies for a given stock.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      symbol: {
        type: Type.STRING,
        description:
          "Ticker symbol to fetch data for. Defaults to the primary company being analyzed if omitted. Pass a peer ticker (e.g., 'MSFT', 'GOOGL', 'META') to retrieve data for that company instead.",
      },
    },
    required: ["symbol"],
  },
};
const getOwnerEarningsDeclaration: FunctionDeclaration = {
  name: "get_owner_earnings",
  description:
    "Fetches FMP pre-calculated owner earnings for a company. Owner earnings = net income + D&A - capex - working capital changes. Use this as a primary FCF quality check and Buffett-style valuation input. Always call this for any company where FCF sustainability or capital intensity is relevant to the thesis.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      symbol: { type: Type.STRING, description: "Stock ticker symbol" },
    },
    required: ["symbol"],
  },
};
const getAnalystEstimatesDeclaration: FunctionDeclaration = {
  name: "get_analyst_estimates",
  description:
    "Fetches Wall Street consensus revenue and EPS estimates for the next 1-2 fiscal years. Use as a sanity check against Oracle's own growth assumptions — significant divergence from consensus (>20%) should be explicitly acknowledged in the memo.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      symbol: { type: Type.STRING, description: "Stock ticker symbol" },
      period: {
        type: Type.STRING,
        description: "Period of estimates (annual or quarter). Default: annual.",
      },
      limit: {
        type: Type.STRING,
        description: "Number of periods to return, default 2",
      },
    },
    required: ["symbol"],
  },
};
const getPriceTargetDeclaration: FunctionDeclaration = {
  name: "get_price_target",
  description:
    "Fetches Wall Street analyst consensus price target and range (high/low/average). Use as a secondary reference point alongside Oracle's intrinsic value calculation. Do NOT anchor Oracle's IV to analyst targets — they are a data point, not the methodology.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      symbol: { type: Type.STRING, description: "Stock ticker symbol" },
    },
    required: ["symbol"],
  },
};
const fmpTools = [
  {
    functionDeclarations: [
      getIncomeStatementDeclaration,
      getBalanceSheetStatementDeclaration,
      getCashFlowStatementDeclaration,
      getKeyMetricsDeclaration,
      getFinancialRatiosDeclaration,
      getStockQuoteDeclaration,
      getInsiderTradingDeclaration,
      getStockNewsDeclaration,
      getCompanyProfileDeclaration,
      getStockPeersDeclaration,
      getRevenueProductSegmentationDeclaration,
      getRevenueGeographicSegmentationDeclaration,
      getOwnerEarningsDeclaration,
    ],
  },
];
const COMPANY_ANALYSIS_SCHEMA = {
  type: Type.OBJECT,
  properties: {
    entityType: {
      type: Type.STRING,
      enum: [
        "SPAC",
        "Pre-Revenue",
        "REIT",
        "Financial",
        "Spin-off",
        "Standard",
      ],
      description:
        "Entity classification that determines valuation methodology. SPAC=1, Pre-Revenue=2, REIT=3, Financial=4, Spin-off=5, Standard=6.",
    },
    description: { type: Type.STRING },
    currentPrice: {
      type: Type.NUMBER,
      description: "Latest market price in USD.",
    },
    marketCap: {
      type: Type.NUMBER,
      description: "Market capitalization in millions of USD.",
    },
    sharesOutstanding: {
      type: Type.NUMBER,
      description:
        "Total shares outstanding in millions. CRITICAL: Use millions (e.g., Apple is ~15,000, NOT 15).",
    },
    netDebt: {
      type: Type.NUMBER,
      description: "Total debt minus cash in millions of USD.",
    },
    lastYearRevenue: {
      type: Type.NUMBER,
      description:
        "Revenue from the most recent fiscal year in millions of USD. (e.g., Apple is ~390,000, NOT 390).",
    },
    lastYearFcf: {
      type: Type.NUMBER,
      description:
        "Free Cash Flow from the most recent fiscal year in millions of USD. (e.g., Apple is ~100,000, NOT 100).",
    },
    dividend: {
      type: Type.NUMBER,
      description: "Dividend yield in percentage (e.g., 0.5 for 0.5%).",
    },
    oracleMetrics: {
      type: Type.OBJECT,
      properties: {
        roe: { type: Type.NUMBER },
        fcf: { type: Type.NUMBER },
        eps: { type: Type.NUMBER },
        ownerEarnings: { type: Type.NUMBER },
        debtToEquity: { type: Type.NUMBER },
        peRatio: { type: Type.NUMBER },
        pegRatio: { type: Type.NUMBER },
        pbRatio: { type: Type.NUMBER },
        debtToEbitda: { type: Type.NUMBER },
        roic: { type: Type.NUMBER },
        epsForward: { type: Type.NUMBER },
        peForward: { type: Type.NUMBER },
        fcfYield: { type: Type.NUMBER },
        forwardFcfYield: { type: Type.NUMBER },
      },
      required: [
        "roe",
        "fcf",
        "eps",
        "ownerEarnings",
        "debtToEquity",
        "peRatio",
        "pegRatio",
        "pbRatio",
        "debtToEbitda",
        "roic",
        "epsForward",
        "peForward",
        "fcfYield",
        "forwardFcfYield",
      ],
    },
    aftermarketPrice: { type: Type.NUMBER },
    aftermarketChange: { type: Type.NUMBER },
    normalizedEarnings: {
      type: Type.STRING,
      description:
        "Detailed explanation of estimated steady-state earnings power, excluding one-time items and cyclical peaks. Do not leave empty.",
    },
    throughCycleAssumptions: {
      type: Type.STRING,
      description:
        "Detailed explanation of how the business performs across full economic cycles. Do not leave empty.",
    },
    longTermOutlook: {
      type: Type.STRING,
      description:
        "Detailed 10-year business and industry outlook, including terminal state expectations. Do not leave empty.",
    },
    oracleDetails: {
      type: Type.OBJECT,
      properties: {
        competence: {
          type: Type.OBJECT,
          properties: {
            type: { type: Type.STRING },
            strength: {
              type: Type.STRING,
              description:
                "Short categorical rating (e.g., 'Exceptional', 'High', 'Moderate', 'Complex'). DO NOT write sentences.",
            },
            duration: { type: Type.STRING },
            vulnerabilities: { type: Type.STRING },
            competitiveThreats: { type: Type.STRING },
            description: { type: Type.STRING },
          },
          required: [
            "type",
            "strength",
            "duration",
            "vulnerabilities",
            "competitiveThreats",
            "description",
          ],
        },
        management: {
          type: Type.OBJECT,
          properties: {
            type: { type: Type.STRING },
            strength: {
              type: Type.STRING,
              description:
                "Short categorical rating (e.g., 'Exceptional', 'Good', 'Questionable', 'Poor'). DO NOT write sentences.",
            },
            duration: { type: Type.STRING },
            vulnerabilities: { type: Type.STRING },
            competitiveThreats: { type: Type.STRING },
            description: { type: Type.STRING },
          },
          required: [
            "type",
            "strength",
            "duration",
            "vulnerabilities",
            "competitiveThreats",
            "description",
          ],
        },
        predictability: {
          type: Type.OBJECT,
          properties: {
            type: { type: Type.STRING },
            strength: {
              type: Type.STRING,
              description:
                "Short categorical rating (e.g., 'High', 'Moderate', 'Low'). DO NOT write sentences.",
            },
            duration: { type: Type.STRING },
            vulnerabilities: { type: Type.STRING },
            competitiveThreats: { type: Type.STRING },
            description: { type: Type.STRING },
          },
          required: [
            "type",
            "strength",
            "duration",
            "vulnerabilities",
            "competitiveThreats",
            "description",
          ],
        },
        marginOfSafety: {
          type: Type.OBJECT,
          properties: {
            type: { type: Type.STRING },
            strength: {
              type: Type.STRING,
              description:
                "Short categorical rating (e.g., 'Excellent', 'Adequate', 'Insufficient', 'None'). DO NOT write sentences.",
            },
            duration: { type: Type.STRING },
            vulnerabilities: { type: Type.STRING },
            competitiveThreats: { type: Type.STRING },
            description: { type: Type.STRING },
          },
          required: [
            "type",
            "strength",
            "duration",
            "vulnerabilities",
            "competitiveThreats",
            "description",
          ],
        },
        stability: {
          type: Type.OBJECT,
          properties: {
            type: { type: Type.STRING },
            strength: {
              type: Type.STRING,
              description:
                "Short categorical rating (e.g., 'Highly Stable', 'Moderate', 'Cyclical'). DO NOT write sentences.",
            },
            duration: { type: Type.STRING },
            vulnerabilities: { type: Type.STRING },
            competitiveThreats: { type: Type.STRING },
            description: { type: Type.STRING },
          },
          required: [
            "type",
            "strength",
            "duration",
            "vulnerabilities",
            "competitiveThreats",
            "description",
          ],
        },
      },
      required: [
        "competence",
        "management",
        "predictability",
        "marginOfSafety",
        "stability",
      ],
    },
    buyZoneJustification: {
      type: Type.STRING,
      description:
        "If your buy triggers differ from the calculated 'Buy Under' price (Intrinsic Value - Margin of Safety), justify why. Otherwise, leave empty.",
    },
    dataTimestamp: { type: Type.STRING },
    keyDrivers: {
      type: Type.ARRAY,
      items: {
        type: Type.OBJECT,
        properties: {
          title: { type: Type.STRING },
          description: { type: Type.STRING },
        },
        required: ["title", "description"],
      },
    },
    keyRisks: {
      type: Type.ARRAY,
      items: {
        type: Type.OBJECT,
        properties: {
          title: { type: Type.STRING },
          description: { type: Type.STRING },
          severity: { type: Type.STRING, enum: ["low", "medium", "high"] },
        },
        required: ["title", "description", "severity"],
      },
    },
    riskModeling: {
      type: Type.ARRAY,
      description:
        "Quantitative risk modeling table. Provide 3-5 rows. Each row must quantify a specific risk as a dollar impact on intrinsic value per share.",
      items: {
        type: Type.OBJECT,
        properties: {
          risk: {
            type: Type.STRING,
            description:
              "Name of the specific risk factor. Be concrete — name the mechanism, not a category. Max 8 words.",
          },
          probability: {
            type: Type.STRING,
            description:
              "Probability of this risk materializing over 3 years. Format: '25%' or '40%'. Must be a percentage string.",
          },
          impact: {
            type: Type.STRING,
            description:
              "Severity label. Must be one of: 'HIGH', 'MEDIUM', 'LOW'.",
          },
          ivChange: {
            type: Type.STRING,
            description:
              "Estimated percentage change to intrinsic value if this risk materializes. Format: '-12%' or '-8%'. Must be negative. No positive values.",
          },
        },
        required: [
          "risk",
          "probability",
          "impact",
          "ivChange",
        ],
      },
    },
    moatAnalysis: {
      type: Type.OBJECT,
      properties: {
        type: { type: Type.STRING },
        strength: {
          type: Type.STRING,
          description:
            "Short categorical rating (e.g., 'Wide', 'Narrow', 'None', 'Exceptional'). DO NOT write sentences.",
        },
        description: { type: Type.STRING },
        score: { type: Type.NUMBER },
        duration: { type: Type.STRING },
        vulnerabilities: { type: Type.STRING },
        competitiveThreats: { type: Type.STRING },
        hasBrandRelationshipComponent: {
          type: Type.BOOLEAN,
          description:
            "Set true if consumer brand recognition, membership loyalty, customer relationship stickiness, emotional brand attachment, or repeat-purchase behavior is a MATERIAL component of the moat — even if the primary moat type is cost, scale, or switching costs. Set false if the moat is purely cost, regulatory, technical lock-in, patent, or B2B network effects with no meaningful consumer relationship component.",
        },
      },
      required: [
        "type",
        "strength",
        "description",
        "score",
        "duration",
        "vulnerabilities",
        "competitiveThreats",
        "hasBrandRelationshipComponent",
      ],
    },
    valuationInsights: { type: Type.STRING },
    isObviouslyAttractive: { type: Type.BOOLEAN },
    isGrowthStructurallyInevitable: { type: Type.BOOLEAN },
    isInevitableCompounderOverride: { type: Type.BOOLEAN },
    businessClassification: {
      type: Type.STRING,
      enum: [
        "High Quality, Low Return at Current Price",
        "Too Hard Pile",
        "Speculative / Option-Like",
        "Obviously Attractive (Act Now)",
      ],
    },
    shouldActDecisively: { type: Type.BOOLEAN },
    valuationRationale: { type: Type.STRING },
    marginOfSafetyDetails: {
      type: Type.OBJECT,
      properties: {
        valuation: {
          type: Type.STRING,
          description: "e.g., 'NONE' if price > IV, or '20%' if price < IV.",
        },
        businessQuality: {
          type: Type.STRING,
          description:
            "e.g., 'EXTREME' for elite businesses, 'MODERATE' for others.",
        },
        rationale: {
          type: Type.STRING,
          description: "Justification for the MOS assessment.",
        },
      },
      required: ["valuation", "businessQuality", "rationale"],
    },
    entryStrategyMode: {
      type: Type.STRING,
      enum: [
        "Accumulation Mode",
        "Measured Conviction (MOS-backed)",
        "Wait Mode",
      ],
    },
    optionalityMultiplier: {
      type: Type.OBJECT,
      properties: {
        hasHighOptionality: { type: Type.BOOLEAN },
        description: { type: Type.STRING },
      },
      required: ["hasHighOptionality", "description"],
    },
    capitalAllocationDecision: {
      type: Type.STRING,
      description:
        "Specific capital allocation decision. MUST match the 'Universal Decision Label' (e.g., 'PASS', 'AVOID', 'BUY', '7-10%'). If the decision is PASS or AVOID, this field MUST be exactly 'PASS' or 'AVOID'.",
    },
    passConfidenceScore: {
      type: Type.NUMBER,
      description:
        "0-100 score of how confident the model is in the PASS decision.",
    },
    buyZoneFramework: {
      type: Type.ARRAY,
      description:
        "ENGINE-LOCKED SECTION — do not invest effort here. The buy-zone tiers (thresholds, labels, AND rationale) are entirely engine-computed post-parse from intrinsic value; any array you return for this field is discarded. Put company-specific valuation narrative in `buyZoneJustification` instead, not here. Return an empty array [].",
      items: {
        type: Type.OBJECT,
        properties: {
          trigger: {
            type: Type.STRING,
            description:
              "Short label for this zone (e.g., 'Core Buy Zone', 'Aggressive Accumulation', 'Current Market Price').",
          },
          threshold: {
            type: Type.STRING,
            description:
              "Dollar price threshold ONLY. Format as '$NNN.NN' or '< $NNN.NN'. NEVER a ratio (e.g., '1.4x P/TBV' is a violation — convert to dollar price first).",
          },
          rationale: { type: Type.STRING },
        },
        required: ["trigger", "threshold", "rationale"],
      },
    },
    upsideCase: {
      type: Type.OBJECT,
      properties: {
        scenario: { type: Type.STRING },
        impact: { type: Type.STRING },
        probability: { type: Type.STRING, enum: ["Low", "Medium", "High"] },
      },
      required: ["scenario", "impact", "probability"],
    },
    oracleScore: {
      type: Type.NUMBER,
      description: "Overall quality/value score from 0-100.",
    },
    allocationGuidance: {
      type: Type.STRING,
      description:
        "Specific portfolio allocation guidance (e.g., '2% position').",
    },
    convictionLevel: {
      type: Type.STRING,
      description: "Conviction level (e.g., 'High', 'Medium', 'Low').",
    },
    unitEconomics: {
      type: Type.ARRAY,
      items: {
        type: Type.OBJECT,
        properties: {
          label: { type: Type.STRING },
          value: { type: Type.STRING },
          benchmark: { type: Type.STRING },
        },
        required: ["label", "value"],
      },
    },
    peerBenchmarking: {
      type: Type.ARRAY,
      description:
        "Must contain one row per peer per metric. For elite tech (AAPL, MSFT, GOOGL, META, AMZN), MUST produce 9 rows minimum: ROIC, FCF Yield, and EV/FCF for each of MSFT, GOOGL, and META. Each row is a single peer-metric pair. Three peers × three metrics = nine rows. Do not combine metrics into one row. For non-elite companies, produce 3 peers × 3 metrics = 9 rows minimum using get_stock_peers to identify rivals.",
      items: {
        type: Type.OBJECT,
        properties: {
          peer: { type: Type.STRING },
          metric: { type: Type.STRING },
          value: { type: Type.STRING },
          comparison: {
            type: Type.STRING,
            description:
              "A short 1-2 word comparison (e.g. 'HIGHER', 'LOWER', 'SIMILAR', 'BETTER', 'WORSE').",
          },
          context: {
            type: Type.STRING,
            description:
              "A concise one-liner context explaining the difference (e.g. 'Lower reported ROIC due to higher physical asset base').",
          },
        },
        required: ["peer", "metric", "value", "comparison", "context"],
      },
    },
    historicalFailuresContext: {
      type: Type.STRING,
      description:
        "Historical perspective on why similar products or business models failed in the past. Be specific. DO NOT output 'N/A'.",
    },
    technicalDifferentiation: {
      type: Type.STRING,
      description:
        "Deep technical or operational reasons why this business wins vs peers. DO NOT output 'N/A'.",
    },
    expectedReturnMath: {
      type: Type.OBJECT,
      properties: {
        impliedGrowth: {
          type: Type.STRING,
          description:
            "Engine-injected post-parse. Output any non-empty string here; it will be overridden.",
        },
        requiredIrr: {
          type: Type.STRING,
          description:
            "Engine-injected post-parse. Output any non-empty string here; it will be overridden.",
        },
        realisticIrr: {
          type: Type.STRING,
          description:
            "Your assessed realistic IRR from current market price, e.g. '10.5%'.",
        },
        rationale: {
          type: Type.STRING,
          description:
            "IRR explanation. VERDICT-PROSE-LOCK: do NOT name a verdict label word (BUY / ACCUMULATE / FAT PITCH / HOLD / CORE HOLD / WATCH / PASS / AVOID) in this rationale — the engine computes and displays the final verdict; reference the IRR, discount/premium to IV, and margin of safety instead (e.g. 'the current price offers a wide discount to base-case IV'), not a verdict label. When referencing the current price in prose, do not invent a figure — the engine supplies analysis.currentPrice; state discount/premium qualitatively or use the exact provided price. If price > IV by more than 20%, include the C-14a reconciliation sentence. Do not compute or state a current or fair Price-to-Tangible-Book (P/TBV) multiple in prose — the engine supplies __type4CurrentPtbv (current) and __type4RiImpliedPtbv (RI-implied fair) and displays both; reference those values or omit the ratio.",
        },
      },
      required: ["impliedGrowth", "requiredIrr", "realisticIrr", "rationale"],
    },
    killerInsight: {
      type: Type.STRING,
      description:
        "The single most important 'lynchpin' fact about this business.",
    },
    isDecelerating: { type: Type.BOOLEAN },
    isReInflection: { type: Type.BOOLEAN },
    newRevenueContribution: { type: Type.NUMBER },
    volatilityScore: { type: Type.NUMBER },
    lollapaloozaEffects: {
      type: Type.STRING,
      description:
        "Charlie Munger's concept: where multiple forces move in the same direction to produce non-linear results. DO NOT output 'N/A'.",
    },
    tenYearHistoricalContext: {
      type: Type.STRING,
      description:
        "What has been the defining trend of this business over the last decade? DO NOT output 'N/A'.",
    },
    whyNotPerfect: {
      type: Type.STRING,
      description:
        "Be an adversarial thinker: what prevents this from being a 10/10 business? DO NOT output 'N/A'.",
    },
    summary: {
      type: Type.STRING,
      description:
        "Comprehensive Executive Summary (4-5 sentences). Synthesize business quality (e.g., margins, ROIC), the investment thesis, the valuation gap (intrinsic value vs current price), and the capital-allocation posture. VERDICT-PROSE-LOCK: do NOT state, assign, or name a verdict label word in this prose — never write 'BUY', 'ACCUMULATE', 'FAT PITCH', 'HOLD', 'CORE HOLD', 'WATCH', 'PASS', or 'AVOID' as the call (e.g. 'we assign a BUY verdict', 'this is an ACCUMULATE'). The final verdict is computed deterministically by the engine and shown in the verdict pill; any label you write here will contradict it. Describe the conclusion qualitatively instead (e.g. 'the valuation offers a meaningful margin of safety' or 'the price sits above intrinsic value, so discipline is warranted'). When referencing the current price in prose, do not invent a figure — the engine supplies analysis.currentPrice; state discount/premium qualitatively or use the exact provided price. BAN narrative bias (e.g., 'rare discount'). Use objective language. If growth gap is material, include: 'Current pricing implies X growth. Our base case supports Y growth.' Note that 'growth' in this summary refers to the scenario growth input; do not conflate it with the residual-income tangible-book compounding rate, which the engine reports separately. Do not compute or state a current or fair Price-to-Tangible-Book (P/TBV) multiple in prose — the engine supplies __type4CurrentPtbv (current) and __type4RiImpliedPtbv (RI-implied fair) and displays both; reference those values or omit the ratio.",
    },
    scorecard: {
      type: Type.OBJECT,
      properties: {
        moat: {
          type: Type.STRING,
          enum: ["Wide", "Narrow", "None", "Eroding", "Exceptional"],
          description: "Buffett style moat classification.",
        },
        management: {
          type: Type.STRING,
          description:
            "Buffett style (e.g., 'Exceptional', 'Good', 'Questionable', 'Poor').",
        },
        understandable: {
          type: Type.STRING,
          description:
            "Buffett style (e.g., 'Exceptional', 'High', 'Moderate', 'Complex').",
        },
        longTerm: {
          type: Type.STRING,
          enum: ["High", "Medium", "Low", "None", "Exceptional"],
          description: "Buffett style long-term predictability rating.",
        },
        marginOfSafety: {
          type: Type.STRING,
          description:
            "Buffett style (e.g., 'Adequate', 'Insufficient', 'None').",
        },
        stability: {
          type: Type.STRING,
          description:
            "Buffett style business stability rating (e.g., 'Highly Stable', 'Moderate', 'Cyclical').",
        },
      },
      required: [
        "moat",
        "management",
        "understandable",
        "longTerm",
        "marginOfSafety",
        "stability",
      ],
    },
    archetype: {
      type: Type.OBJECT,
      properties: {
        category: {
          type: Type.STRING,
          enum: [
            "Dominant Leader",
            "Challenger / Share Gainer",
            "Turnaround",
            "Compounder",
            "Speculative Growth",
            "Value Trap Risk",
          ],
        },
        rationale: { type: Type.STRING },
      },
      required: ["category", "rationale"],
    },
    reinvestmentRunway: {
      type: Type.OBJECT,
      properties: {
        score: { type: Type.STRING, enum: ["HIGH", "MEDIUM", "LOW"] },
        tamVsRevenue: { type: Type.STRING },
        incrementalRoicTrend: {
          type: Type.STRING,
          enum: ["expanding", "stable", "compressing"],
        },
        acquisitionDependence: {
          type: Type.STRING,
          enum: ["low", "medium", "high"],
        },
        rationale: { type: Type.STRING },
      },
      required: [
        "score",
        "tamVsRevenue",
        "incrementalRoicTrend",
        "acquisitionDependence",
        "rationale",
      ],
    },
    oracleFramework: {
      type: Type.OBJECT,
      properties: {
        understandable: {
          type: Type.OBJECT,
          properties: {
            value: {
              type: Type.STRING,
              description: "Rating (e.g. Exceptional, High, Moderate, Complex)",
            },
            rationale: {
              type: Type.STRING,
              description:
                "Detailed explanation of the rating. Do not leave empty.",
            },
          },
          required: ["value", "rationale"],
        },
        moat: {
          type: Type.OBJECT,
          properties: {
            value: {
              type: Type.STRING,
              description: "Rating (e.g. Exceptional, Wide, Narrow, None)",
            },
            rationale: {
              type: Type.STRING,
              description:
                "Detailed explanation of the moat rating. Do not leave empty.",
            },
          },
          required: ["value", "rationale"],
        },
        management: {
          type: Type.OBJECT,
          properties: {
            value: {
              type: Type.STRING,
              description:
                "Rating (e.g. Exceptional, Good, Questionable, Poor)",
            },
            rationale: {
              type: Type.STRING,
              description:
                "Detailed explanation of the management rating. Do not leave empty.",
            },
          },
          required: ["value", "rationale"],
        },
        marginOfSafety: {
          type: Type.OBJECT,
          properties: {
            value: {
              type: Type.STRING,
              description: "Rating (e.g. Excellent, Adequate, Insufficient)",
            },
            rationale: {
              type: Type.STRING,
              description:
                "Detailed explanation of the margin of safety rating. Do not leave empty.",
            },
          },
          required: ["value", "rationale"],
        },
        longTerm: {
          type: Type.OBJECT,
          properties: {
            value: {
              type: Type.STRING,
              description: "Rating (e.g. High, Moderate, Low)",
            },
            rationale: {
              type: Type.STRING,
              description:
                "Detailed explanation of the long-term rating. Do not leave empty.",
            },
          },
          required: ["value", "rationale"],
        },
        stability: {
          type: Type.OBJECT,
          properties: {
            value: {
              type: Type.STRING,
              description: "Rating (e.g. Highly Stable, Moderate, Cyclical)",
            },
            rationale: {
              type: Type.STRING,
              description:
                "Detailed explanation of the stability rating. Do not leave empty.",
            },
          },
          required: ["value", "rationale"],
        },
        decision: {
          type: Type.OBJECT,
          properties: {
            value: {
              type: Type.STRING,
              enum: [
                "BUY",
                "CORE HOLD",
                "ACCUMULATE",
                "FAT PITCH",
                "PASS",
                "AVOID",
              ],
              description: "The final oracle decision. HOLD is retired — use CORE HOLD.",
            },
            rationale: {
              type: Type.STRING,
              description:
                "Detailed explanation of the decision. Do not leave empty.",
            },
          },
          required: ["value", "rationale"],
        },
        riskTier: {
          type: Type.STRING,
          enum: ["STABLE", "CYCLICAL", "LEVERED", "DISTRESSED"],
        },
        riskTierRationale: {
          type: Type.STRING,
        },
        oracleIntrinsicValue: {
          type: Type.NUMBER,
          description:
            "Your definitive Oracle-style estimate of fair value per share. This MUST match the deterministic DCF result exactly per Rule 3a — do not substitute your own estimate.",
        },
      },
      required: [
        "understandable",
        "moat",
        "management",
        "longTerm",
        "stability",
        "marginOfSafety",
        "decision",
        "riskTier",
        "riskTierRationale",
        "oracleIntrinsicValue",
      ],
    },
    confidenceScore: {
      type: Type.OBJECT,
      properties: {
        score: { type: Type.NUMBER },
        label: { type: Type.STRING },
        rationale: { type: Type.STRING },
      },
      required: ["score", "label", "rationale"],
    },
    assumptionQuality: {
      type: Type.OBJECT,
      description:
        "Code-computed score reflecting reliability of IV input assumptions. Not AI-generated.",
      properties: {
        score: { type: Type.NUMBER },
        label: { type: Type.STRING },
        rationale: { type: Type.STRING },
      },
      required: ["score", "label", "rationale"],
    },
    suggestedAssumptions: {
      type: Type.OBJECT,
      properties: {
        revenueGrowth: {
          type: Type.NUMBER,
          description:
            "Expected annual revenue growth over next 5 years (%). For elite businesses like AAPL, be realistic (7-12% including buyback impact on EPS). Ensure this growth rate is CONSISTENT with any price targets or entry triggers you mention in your text.",
        },
        marginExpansion: {
          type: Type.NUMBER,
          description: "Expected annual margin expansion/contraction (%).",
        },
        discountRate: {
          type: Type.NUMBER,
          description:
            "Suggested discount rate (%). For elite businesses with wide moats, 8-9% is standard.",
        },
        terminalGrowth: {
          type: Type.NUMBER,
          description:
            "Expected terminal growth rate (%). Capped at 4.0% for elite.",
        },
      },
      required: [
        "revenueGrowth",
        "marginExpansion",
        "discountRate",
        "terminalGrowth",
      ],
    },
    valuationScenarios: {
      type: Type.ARRAY,
      items: {
        type: Type.OBJECT,
        properties: {
          scenario: { type: Type.STRING, enum: ["Bear", "Base", "Bull"] },
          growth: { type: Type.STRING },
          irr: { type: Type.STRING },
          probability: { type: Type.STRING, enum: ["Low", "Medium", "High"] },
        },
        required: ["scenario", "growth", "irr", "probability"],
      },
    },
    whatWouldBreakThisFast: {
      type: Type.ARRAY,
      description:
        "MAXIMUM 2 ITEMS. Rule 99: List only the 1-2 thesis killers — events that would force a permanent re-evaluation of the investment thesis. These must be existential or near-existential. Do NOT include cyclical headwinds, sentiment shifts, or management style concerns. If you can only identify 1 genuine thesis killer, output 1 item. Never output 3 or more.",
      minItems: 1,
      maxItems: 2,
      items: { type: Type.STRING },
    },
    cycleSensitivityPenalty: {
      type: Type.STRING,
      description:
        "Explain any confidence or position size reduction due to stability or uncertainty.",
    },
    oracleJudgment: {
      type: Type.STRING,
      description:
        "Your final comprehensive judgment. Include the SCENARIO CAVEAT here if required, and discuss governance, transitions, or violations.",
    },
  },
  required: [
    "entityType",
    "description",
    "currentPrice",
    "marketCap",
    "sharesOutstanding",
    "netDebt",
    "lastYearRevenue",
    "lastYearFcf",
    "dividend",
    "oracleMetrics",
    "aftermarketPrice",
    "aftermarketChange",
    "normalizedEarnings",
    "throughCycleAssumptions",
    "longTermOutlook",
    "oracleDetails",
    "buyZoneJustification",
    "dataTimestamp",
    "keyDrivers",
    "keyRisks",
    "riskModeling",
    "moatAnalysis",
    "valuationInsights",
    "isObviouslyAttractive",
    "isGrowthStructurallyInevitable",
    "isInevitableCompounderOverride",
    "businessClassification",
    "shouldActDecisively",
    "valuationRationale",
    "marginOfSafetyDetails",
    "entryStrategyMode",
    "optionalityMultiplier",
    "capitalAllocationDecision",
    "passConfidenceScore",
    "buyZoneFramework",
    "upsideCase",
    "oracleScore",
    "allocationGuidance",
    "convictionLevel",
    "unitEconomics",
    "peerBenchmarking",
    "historicalFailuresContext",
    "technicalDifferentiation",
    "expectedReturnMath",
    "killerInsight",
    "isDecelerating",
    "isReInflection",
    "newRevenueContribution",
    "volatilityScore",
    "lollapaloozaEffects",
    "tenYearHistoricalContext",
    "whyNotPerfect",
    "summary",
    "scorecard",
    "archetype",
    "reinvestmentRunway",
    "oracleFramework",
    "confidenceScore",
    "assumptionQuality",
    "suggestedAssumptions",
    "valuationScenarios",
    "whatWouldBreakThisFast",
    "cycleSensitivityPenalty",
    "oracleJudgment",
  ],
};
async function executeFunctionCall(
  name: string,
  args: any,
  defaultSymbol: string,
) {
  const symbol = args.symbol || defaultSymbol;
  const limit = args.limit ? String(args.limit) : "10";
  const period = args.period || "annual";

  switch (name) {
    case "get_income_statement": {
      const data = await fetchFMP(`stable/income-statement`, {
        symbol,
        limit,
        period,
      });
      if (Array.isArray(data)) {
        return data.slice(0, 5).map((item: any) => {
          const essentialKeys = [
            "date",
            "revenue",
            "netIncome",
            "eps",
            "ebitda",
            "operatingIncome",
            "grossProfit",
          ];
          const pruned: any = {};
          essentialKeys.forEach((k) => {
            if (k in item) pruned[k] = item[k];
          });
          return pruned;
        });
      }
      return data;
    }
    case "get_balance_sheet_statement": {
      const data = await fetchFMP(`stable/balance-sheet-statement`, {
        symbol,
        limit,
        period,
      });
      if (Array.isArray(data)) {
        return data.slice(0, 5).map((item: any) => {
          const essentialKeys = [
            "date",
            "totalAssets",
            "totalLiabilities",
            "totalEquity",
            "cashAndShortTermInvestments",
            "longTermDebt",
          ];
          const pruned: any = {};
          essentialKeys.forEach((k) => {
            if (k in item) pruned[k] = item[k];
          });
          return pruned;
        });
      }
      return data;
    }
    case "get_cash_flow_statement": {
      const data = await fetchFMP(`stable/cash-flow-statement`, {
        symbol,
        limit,
        period,
      });
      if (Array.isArray(data)) {
        return data.slice(0, 5).map((item: any) => {
          const essentialKeys = [
            "date",
            "operatingCashFlow",
            "capitalExpenditure",
            "freeCashFlow",
            "dividendsPaid",
            "stockRepurchase",
          ];
          const pruned: any = {};
          essentialKeys.forEach((k) => {
            if (k in item) pruned[k] = item[k];
          });
          return pruned;
        });
      }
      return data;
    }
    case "get_key_metrics":
      return await fetchFMP(`stable/key-metrics`, { symbol, limit, period });
    case "get_financial_ratios":
      return await fetchFMP(`stable/ratios`, { symbol, limit, period });
    case "get_stock_quote": {
      const snap = getQuoteSnapshot(symbol);
      if (snap) {
        console.log(`[QUOTE-CACHE-HIT] ${symbol}: served deterministic snapshot age=${Math.round((Date.now() - snap.fetchedAt) / 1000)}s (FMP fetch avoided)`);
        return snap.data;
      }
      return await fetchFMP(`stable/quote`, { symbol });
    }
    case "get_insider_trading": {
      // Insider trading is not currently available in stable for new accounts
      // or has a different name not yet discoverable.
      // Returning empty array to prevent legacy errors.
      return [];
    }
    case "get_stock_news": {
      const data = await fetchFMP(`stable/fmp-articles`, { symbol, limit });
      if (Array.isArray(data)) {
        return data.map((item: any) => ({
          symbol: symbol,
          publishedDate: item.date,
          title: item.title,
          image: item.image,
          site: item.site,
          text: item.content,
          url: item.link,
        }));
      }
      return data;
    }
    case "get_company_profile":
      return await fetchFMP(`stable/profile`, { symbol });
    case "get_stock_peers":
      return await fetchFMP(`stable/stock-peers`, { symbol });
    case "get_owner_earnings":
      return await fetchFMP(`stable/owner-earnings`, { symbol });
    case "get_analyst_estimates":
      return await fetchFMP(`stable/analyst-estimates`, {
        symbol,
        period: args.period ?? "annual",
        limit: args.limit ?? "2",
      });
    case "get_price_target":
      return await fetchFMP(`stable/price-target`, { symbol });
    case "get_revenue_product_segmentation":
      return await fetchFMP(`stable/revenue-product-segmentation`, {
        symbol,
        period,
      });
    case "get_revenue_geographic_segmentation":
      return await fetchFMP(`stable/revenue-geographic-segmentation`, {
        symbol,
        period,
      });
    default:
      return null;
  }
}
export async function callGeminiWithTools(params: {
  model?: string;
  contents: any[];
  config?: any;
  tools?: any[];
  symbol: string;
  telemetry?: GeminiTelemetry;
  entry?: ModelEntry;
}): Promise<GenerateContentResponse> {
  let currentContents = [...params.contents];
  const modelToUse = params.model || MODELS.ORACLE.primary;
  const tools = [...(params.tools || [])];

  // [MODEL-1] All registry models support tool-call context circulation, so the
  // old 2.x-prefix gate is gone — tool config is set unconditionally.
  let config: any = {
    ...params.config,
  };
  config.tools = tools;
  config.toolConfig = {
    ...params.config?.toolConfig,
    includeServerSideToolInvocations: true,
  };

  // VERY IMPORTANT: googleSearch is currently incompatible with responseMimeType: "application/json"
  // in many Gemini regions/versions. We must disable JSON mode if search is present to avoid INVALID_ARGUMENT errors.
  const hasSearch = tools.some((t) => t.googleSearch);
  const hasFunctionDeclarations = tools.some(
    (t) => t.functionDeclarations && t.functionDeclarations.length > 0,
  );

  if (hasSearch || hasFunctionDeclarations) {
    if (config.responseMimeType) delete config.responseMimeType;
    if (config.responseSchema) {
      // Since we are stripping the schema, we MUST inject it into the prompt or the model will guess.
      const schemaStr = JSON.stringify(config.responseSchema, null, 2);
      const lastIdx = currentContents.length - 1;
      if (lastIdx >= 0) {
        currentContents[lastIdx] = {
          ...currentContents[lastIdx],
          parts: [...currentContents[lastIdx].parts],
        };
        currentContents[lastIdx].parts.push({
          text: `\n\nCRITICAL INSTRUCTION: You MUST format your entire response as a valid JSON object matching this schema exactly. DO NOT wrap the output in markdown code blocks. ENSURE ALL INNER DOUBLE QUOTES ARE PROPERLY ESCAPED (\\") TO PREVENT JSON PARSE ERRORS:\n${schemaStr}`,
        });
      }
      delete config.responseSchema;
    }
  }

  let iterations = 0;
  const MAX_ITERATIONS = 15;

  const sanitizedConfig = buildGeminiConfig(modelToUse, config, params.entry);
  const _hasLocalKey = !!(getEnvKey() || "").trim();
  while (iterations < MAX_ITERATIONS) {
    const response = _hasLocalKey
      ? await getAiInstance().models.generateContent({
          model: modelToUse,
          contents: currentContents,
          config: sanitizedConfig,
        })
      : await callGeminiViaProxy({
          model: modelToUse,
          contents: currentContents,
          config: sanitizedConfig,
        });

    recordUsage(params.telemetry?.ledger, params.telemetry?.agent, response);

    let functionCalls = response.functionCalls;
    if (
      !functionCalls &&
      response.candidates &&
      response.candidates.length > 0
    ) {
      const parts = response.candidates[0].content?.parts || [];
      const calls = parts
        .filter((p) => !!p.functionCall)
        .map((p) => p.functionCall);
      if (calls.length > 0) functionCalls = calls;
    }

    if (!functionCalls || functionCalls.length === 0) {
      return response;
    }

    console.log(
      `[Gemini Tool] Model ${modelToUse} requested ${functionCalls.length} tools:`,
      functionCalls.map((c) => c.name),
    );

    // Add model's turn to history
    if (response && response.candidates && response.candidates.length > 0) {
      currentContents.push(response.candidates[0].content);
    } else {
      // If we have no candidates but functionCalls exist, we might be in a weird state
      // or the model just provided a function call without a content block.
      // Usually functionCalls are on a candidate.
      if (functionCalls && functionCalls.length > 0) {
        // Create a dummy content block for the tool execution turns
        currentContents.push({ role: "model", parts: [{ text: "" }] });
      } else {
        return response;
      }
    }

    // Execute all calls in parallel to reduce latency
    const toolResponses = await Promise.all(
      functionCalls.map(async (call) => {
        const result = await executeFunctionCall(
          call.name,
          call.args,
          params.symbol,
        );
        return {
          functionResponse: {
            name: call.name,
            response: { result },
            id: (call as any).id,
          },
        };
      }),
    );

    // Add tool responses to history
    currentContents.push({ role: "tool", parts: toolResponses } as any);
    iterations++;
  }

  throw new Error("Gemini tool-call loop exceeded maximum iterations.");
}

async function fetchPeerBenchmarks(
  ticker: string,
  entityType: string,
  deterministicData: any,
  registryParentTicker?: string | null,
): Promise<any[]> {
  let peerTickers: string[] = [];
  const trio = ["AAPL", "MSFT", "GOOGL", "META", "AMZN"];
  if (trio.includes(ticker)) {
    peerTickers = trio.filter((t) => t !== ticker).slice(0, 3);
  } else if (deterministicData?.isType4Financial) {
    if (deterministicData.type4SubType === "BANK") {
      if (ticker === "JPM") peerTickers = ["BAC", "WFC", "C"];
      else if (ticker === "BAC") peerTickers = ["JPM", "WFC", "C"];
      else peerTickers = ["JPM", "BAC", "WFC"];
    } else {
      peerTickers = ["BRK-B", "PGR", "MKL"];
    }
  } else {
    try {
      const stockPeers = await fetchFMP(`stable/stock-peers`, {
        symbol: ticker,
      });
      if (Array.isArray(stockPeers) && stockPeers.length > 0) {
        if (Array.isArray(stockPeers[0]?.peersList)) {
          // Legacy FMP shape: [{ peersList: ["SKX", "LULU", ...] }]
          peerTickers = stockPeers[0].peersList
            .filter((p: string) => p && p !== ticker)
            .slice(0, 4);
        } else if (stockPeers[0]?.symbol) {
          // New FMP shape: [{ symbol: "SKX", companyName: "...", price: N }, ...]
          peerTickers = stockPeers
            .map((p: any) => p.symbol)
            .filter((s: string) => s && s !== ticker)
            .slice(0, 4);
          console.log(`[PEER-BENCHMARKS] ${ticker}: resolved ${peerTickers.length} peers from new FMP shape: ${peerTickers.join(', ')}`);
        }
      }
    } catch (err) {
      console.warn(`Failed to fetch FMP peers for ${ticker}`, err);
    }

    // ── PEER-SECTOR-GATE ────────────────────────────────────────────────────
    // FMP's stock-peers endpoint matches on market cap + broad classification,
    // not sub-industry. This gate rejects cross-sector peers (e.g., packaging
    // companies benchmarked against footwear) and attempts to source replacements
    // when too few valid peers survive.
    //
    // Skipped for: FAANG hardcoded list (already handled above), Type 4 entities
    // (their peer sets are hardcoded), and when subject sector is unknown.
    if (deterministicData?.sector && peerTickers.length > 0) {
      const subjectSector = (deterministicData.sector as string).toLowerCase().trim();
      const subjectIndustry = ((deterministicData.industry as string) || '').toLowerCase().trim();
      const subjectEmployees = Number(deterministicData.fullTimeEmployees) || 0;
      // [PEER-SECTOR-GATE] (Tuning 10.3) Type 5 = recent spin-off/IPO. FMP backfills the
      // parent's profile, so profile.sector is unreliable (SUNB → "financial services" for an
      // equipment-rental industrial; HONA → "Technology" for an aerospace company). profile.industry,
      // however, IS reliable for spin-offs (SOLS → "Chemicals - Specialty", HONA → "Aerospace & Defense").
      // So for Type 5 we SKIP the sector test but ENFORCE the industry test (see passesClassification),
      // plus the scale gate. This rejects cross-industry peers FMP returns by market-cap proximity.
      const subjectIsType5 = deterministicData?.entityType === "Type 5";
      if (subjectIsType5) {
        console.log(
          `[PEER-SECTOR-GATE] ${ticker}: Type 5 — sector test skipped (FMP sector "${subjectSector}" unreliable for a fresh spin-off); INDUSTRY test "${subjectIndustry}" enforced; scale gate retained.`,
        );
      }
      // PEER-SCALE-GATE threshold: reject a same-industry peer whose headcount diverges
      // from the subject by more than this factor in either direction. Tunable. Basis:
      // legitimate CRM peers (SAP/ADBE/NOW/INTU) are all ≤4.2× CRM; AppLovin is 87×.
      const SCALE_MAX_RATIO = 15;

      // Helper: fetch a ticker's sector and industry from FMP profile, fail open on error
      const getPeerClassification = async (pt: string): Promise<{sector: string; industry: string; employees: number; isActive: boolean | null; companyName: string; cik: string}> => {
        try {
          const profileArr = await fetchFMP(`stable/profile`, { symbol: pt });
          const profile = Array.isArray(profileArr) ? profileArr[0] : profileArr;
          return {
            sector:  ((profile?.sector  as string) || '').toLowerCase().trim(),
            industry:((profile?.industry as string) || '').toLowerCase().trim(),
            employees: parseInt((profile?.fullTimeEmployees as string) || '0', 10) || 0,
            // [PEER-ACTIVE-GATE] (Tuning v2.94 F-3): stable/profile already carries listing
            // status + identity — zero new FMP calls. null = field absent → fail open.
            isActive: typeof profile?.isActivelyTrading === 'boolean' ? profile.isActivelyTrading : null,
            companyName: ((profile?.companyName as string) || '').trim(),
            cik: String(profile?.cik ?? '').trim(),
          };
        } catch {
          return { sector: '', industry: '', employees: 0, isActive: null, companyName: '', cik: '' }; // unknown — fail open
        }
      };

      // [PEER-DEDUP-GATE] (Tuning v2.94 F-3): FMP peer lists can carry the SAME company
      // under multiple listings (HENKY: BDRFF ordinary + BDRFY ADR = Beiersdorf twice,
      // consuming 2 of 3 peer slots). Dedup on CIK when present, else on a normalized
      // company name (suffix-stripped). Also seeds the subject so a subject share-class
      // variant can never occupy a peer slot (§46.4 self-inclusion family).
      const normalizeCompanyIdentity = (name: string): string =>
        name
          .toLowerCase()
          .replace(/[.,'()&]/g, ' ')
          .replace(/\b(incorporated|inc|corporation|corp|company|co|plc|ltd|limited|holdings?|group|ag|se|sa|nv|kgaa|aktiengesellschaft|adr|sponsored|class\s+[a-c])\b/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
      const __acceptedPeerCiks = new Set<string>();
      const __acceptedPeerNames = new Set<string>();
      const __subjectNameNorm = normalizeCompanyIdentity((deterministicData?.companyName as string) || '');
      if (__subjectNameNorm) __acceptedPeerNames.add(__subjectNameNorm);
      const __subjectTickerNorm = ticker.toUpperCase().replace(/[-.]/g, '');
      let __peerInactiveDropped = 0;
      let __peerDuplicateDropped = 0;
      // Returns true (and logs) when the candidate is a duplicate of an already-accepted
      // peer / the subject; otherwise registers its identity and returns false.
      const isDuplicatePeer = (c: {companyName: string; cik: string}, pt: string): boolean => {
        const nameNorm = normalizeCompanyIdentity(c.companyName);
        const cikKey = c.cik && c.cik !== '0' ? c.cik : '';
        if ((cikKey && __acceptedPeerCiks.has(cikKey)) || (nameNorm && __acceptedPeerNames.has(nameNorm))) {
          __peerDuplicateDropped++;
          console.warn(
            `[PEER-DEDUP-GATE] ${ticker}: Rejected ${pt} — duplicate listing of an already-included company ` +
            `("${c.companyName || 'unknown'}"${cikKey ? `, CIK ${cikKey}` : ''}).`,
          );
          return true;
        }
        if (cikKey) __acceptedPeerCiks.add(cikKey);
        if (nameNorm) __acceptedPeerNames.add(nameNorm);
        return false;
      };

      // [PEER-SCALE-GATE] (Tuning v2.94 F-4): FMP headcount is garbage for holding /
      // externally-managed structures (NMM run rejected DAC at "employees 4" — Danaos
      // crews via managers). Below this floor the employee test is unreliable → bypass
      // it (fail open to the industry gate) rather than reject a legitimate comp.
      const EMPLOYEE_FLOOR = 50;

      const passesClassification = (c: {sector:string; industry:string; employees:number; isActive: boolean | null; companyName: string; cik: string}, pt: string): 'sector' | 'industry' | 'scale' | 'inactive' | null => {
        // [PEER-ACTIVE-GATE] (Tuning v2.94 F-3): delisted / dead instruments (SNY run
        // rendered CELG-RI — a delisted Celgene CVR — as a peer with metrics). Explicit
        // false rejects; null/undefined (field absent) fails open.
        if (c.isActive === false) {
          __peerInactiveDropped++;
          console.warn(
            `[PEER-ACTIVE-GATE] ${ticker}: Rejected ${pt} — isActivelyTrading=false (delisted or non-trading instrument).`,
          );
          return 'inactive';
        }
        // Type 5: sector is parent-contaminated → skip; industry is reliable → enforce for everyone.
        if (!subjectIsType5 && c.sector && c.sector !== subjectSector) return 'sector';
        if (subjectIndustry && c.industry && c.industry !== subjectIndustry) return 'industry';
        // PEER-SCALE-GATE: within the same industry, reject gross headcount outliers
        // the sector+industry tiers cannot separate (FMP "Software - Application" lumps
        // enterprise SaaS with mobile adtech, etc.). Fail open if either count is 0
        // or below the reliability floor (F-4).
        if (subjectEmployees > 0 && c.employees > 0) {
          if (subjectEmployees < EMPLOYEE_FLOOR || c.employees < EMPLOYEE_FLOOR) {
            console.log(
              `[PEER-SCALE-GATE] ${ticker}: ${pt} headcount test bypassed — reported employees ` +
              `${Math.min(subjectEmployees, c.employees).toLocaleString()} below reliability floor (${EMPLOYEE_FLOOR}); ` +
              `FMP headcount unreliable at this scale (holding/managed structures).`,
            );
          } else {
            const ratio = Math.max(subjectEmployees / c.employees, c.employees / subjectEmployees);
            if (ratio > SCALE_MAX_RATIO) {
              console.warn(
                `[PEER-SCALE-GATE] ${ticker}: Rejected ${pt} — employees ${c.employees.toLocaleString()} ` +
                `vs subject ${subjectEmployees.toLocaleString()} (${ratio.toFixed(0)}× > ${SCALE_MAX_RATIO}×)`,
              );
              return 'scale';
            }
          }
        }
        return null;
      };

      // Helper: parse a stock-peers FMP response into a flat ticker array
      const parsePeerList = (raw: any[]): string[] => {
        if (!Array.isArray(raw) || raw.length === 0) return [];
        if (Array.isArray(raw[0]?.peersList))
          return raw[0].peersList.filter(Boolean);
        if (raw[0]?.symbol)
          return raw.map((p: any) => p.symbol).filter(Boolean);
        return [];
      };

      // Step 1: validate the FMP-returned candidates
      const validatedTickers: string[] = [];
      for (const pt of peerTickers) {
        // [PEER-SELF-GATE] (Tuning v2.94, §46.4): the subject (or a share-class variant,
        // BRK-B vs BRK.B) must never occupy its own peer slot — also saves the profile fetch.
        if (pt.toUpperCase().replace(/[-.]/g, '') === __subjectTickerNorm) {
          console.warn(`[PEER-SELF-GATE] ${ticker}: Rejected ${pt} — subject cannot be its own peer.`);
          continue;
        }
        const c = await getPeerClassification(pt);
        const reason = passesClassification(c, pt);
        if (reason !== null) {
          if (reason === 'sector') {
            console.warn(
              `[PEER-SECTOR-GATE] ${ticker}: Rejected ${pt} — ` +
              `sector "${c.sector}" ≠ subject "${subjectSector}"`,
            );
          } else if (reason === 'industry') {
            console.warn(
              `[PEER-INDUSTRY-GATE] ${ticker}: Rejected ${pt} — ` +
              `industry "${c.industry}" ≠ subject "${subjectIndustry}"`,
            );
          }
        } else if (!isDuplicatePeer(c, pt)) {
          validatedTickers.push(pt); // fail open → keep
        }
      }

      // Step 2: if fewer than 2 valid peers survived, attempt fallback sourcing.
      // Fetch peers-of-peers starting from any surviving valid peer (or the subject
      // itself as last resort). Each candidate is sector-validated before inclusion.
      if (validatedTickers.length < 2) {
        const seedTicker = validatedTickers[0] ?? ticker;
        const alreadySeen = new Set<string>([ticker, ...peerTickers]);

        try {
          const fallbackRaw = await fetchFMP(`stable/stock-peers`, { symbol: seedTicker });
          const fallbackCandidates = parsePeerList(fallbackRaw)
            .filter((ft: string) => !alreadySeen.has(ft));

          for (const ft of fallbackCandidates) {
            if (validatedTickers.length >= 4) break;
            if (ft.toUpperCase().replace(/[-.]/g, '') === __subjectTickerNorm) continue; // [PEER-SELF-GATE]
            const c = await getPeerClassification(ft);
            if (passesClassification(c, ft) === null && !isDuplicatePeer(c, ft)) {
              console.log(
                `[PEER-SECTOR-GATE] ${ticker}: Added fallback peer ${ft} ` +
                `(sector: "${c.sector || 'unknown'}", industry: "${c.industry || 'unknown'}")`,
              );
              validatedTickers.push(ft);
              alreadySeen.add(ft);
            }
          }
        } catch {
          console.warn(`[PEER-SECTOR-GATE] ${ticker}: Fallback peer fetch failed.`);
        }

        // If second fallback pass still comes up short, try peers-of-subject again
        // with a wider slice in case FMP returned more candidates than we sliced
        if (validatedTickers.length < 2) {
          try {
            const widerRaw = await fetchFMP(`stable/stock-peers`, { symbol: ticker });
            const widerCandidates = parsePeerList(widerRaw)
              .filter((ft: string) => !alreadySeen.has(ft))
              .slice(0, 8); // wider window than the initial slice(0,4)
            for (const ft of widerCandidates) {
              if (validatedTickers.length >= 4) break;
              if (ft.toUpperCase().replace(/[-.]/g, '') === __subjectTickerNorm) continue; // [PEER-SELF-GATE]
              const c = await getPeerClassification(ft);
              if (passesClassification(c, ft) === null && !isDuplicatePeer(c, ft)) {
                console.log(`[PEER-SECTOR-GATE] ${ticker}: Added wider-window peer ${ft}`);
                validatedTickers.push(ft);
                alreadySeen.add(ft);
              }
            }
          } catch { /* ignore */ }
        }
      }

      // Step 3: apply result — only replace if validation produced at least 1 peer
      if (validatedTickers.length >= 1) {
        peerTickers = validatedTickers;
        console.log(
          `[PEER-SECTOR-GATE] ${ticker}: Final peer set: [${peerTickers.join(', ')}]`,
        );
      } else {
        // [PEER-EMPTY-ABSTAIN] (Tuning v2.92): reinstating rejected peers contradicted the
        // gate's own rejections (CON: benchmarked vs biotech). An empty, honest peer set is
        // better than an invalid one — downstream already handles peerBenchmarks length 0
        // ("NOT PROVIDED — treat peer comparisons as unverified").
        peerTickers = [];
        // [PEER-EMPTY-ABSTAIN] (Tuning v2.94 F-1): mark the abstention as DELIBERATE so the
        // downstream PEER-CARD-PROVENANCE-LOCK fails CLOSED (suppresses Oracle-authored peer
        // cards) instead of failing open into LLM-recalled metrics (CAAP: ASR/PAC/OMAB).
        (deterministicData as any).__peerEmptyAbstain = true;
        console.warn(
          `[PEER-EMPTY-ABSTAIN] ${ticker}: All candidates failed sector validation ` +
          `and fallback found nothing. Abstaining from peer benchmarking (empty set) ` +
          `instead of keeping the rejected FMP list.`,
        );
      }
      // (Tuning v2.94 F-3) measurement stash — sizes the new gates corpus-wide.
      (deterministicData as any).__peerInactiveDropped = __peerInactiveDropped;
      (deterministicData as any).__peerDuplicateDropped = __peerDuplicateDropped;
    }
    // ── END PEER-SECTOR-GATE ────────────────────────────────────────────────
  }

  if (entityType === "Type 5" && peerTickers.length === 0 && registryParentTicker) {
    try {
      const stockPeers = await fetchFMP(`stable/stock-peers`, {
        symbol: registryParentTicker,
      });
      let seeded: string[] = [];
      if (Array.isArray(stockPeers) && stockPeers.length > 0) {
        if (Array.isArray(stockPeers[0]?.peersList)) {
          // Legacy FMP shape
          seeded = stockPeers[0].peersList
            .filter((p: string) => p && p !== ticker && p !== registryParentTicker)
            .slice(0, 8);
        } else if (stockPeers[0]?.symbol) {
          // New FMP shape
          seeded = stockPeers
            .map((p: any) => p.symbol)
            .filter((s: string) => s && s !== ticker && s !== registryParentTicker)
            .slice(0, 8);
        }
      }
      // [PEER-INDUSTRY-GATE] Parent-seeded peers are the PARENT's diversified peer set
      // (e.g. HON → ADP/DE/ETN/GD). Industry-validate them against the spinco's own (reliable)
      // industry so cross-industry names (ADP "Staffing & Employment Services") are dropped and
      // only true comps (GD "Aerospace & Defense") survive. Fail open on profile-fetch error.
      const seedSubjectIndustry = ((deterministicData?.industry as string) || '').toLowerCase().trim();
      if (seedSubjectIndustry && seeded.length > 0) {
        const validatedSeed: string[] = [];
        for (const pt of seeded) {
          if (validatedSeed.length >= 4) break;
          try {
            const profArr = await fetchFMP(`stable/profile`, { symbol: pt });
            const prof = Array.isArray(profArr) ? profArr[0] : profArr;
            const ind = ((prof?.industry as string) || '').toLowerCase().trim();
            if (!ind || ind === seedSubjectIndustry) {
              validatedSeed.push(pt);
            } else {
              console.warn(
                `[PEER-INDUSTRY-GATE] ${ticker}: Rejected parent-seed ${pt} — ` +
                `industry "${ind}" ≠ subject "${seedSubjectIndustry}"`,
              );
            }
          } catch {
            validatedSeed.push(pt); // unknown — fail open
          }
        }
        peerTickers = validatedSeed;
      } else {
        peerTickers = seeded.slice(0, 4);
      }
      console.log(`[TYPE5-PARENT-PEERS] ${ticker}: stock-peers empty; seeded ${peerTickers.length} industry-validated peers from parent ${registryParentTicker}: ${peerTickers.join(', ')}`);
    } catch (err) {
      console.warn(`[TYPE5-PARENT-PEERS-FAIL] ${ticker}: failed to fetch parent peers for ${registryParentTicker}`, err);
    }
  }

  const peerBenchmarks = [];

  // ─── BUG-6 FIX: canonicalize + exclude the subject from its own peer set ─────
  // Share-class separators vary (BRK.B / BRK-B / BRK/B); normalize before compare
  // so the subject is never benchmarked against itself (BRK-B appeared as its own
  // peer with a different FCF-yield than the subject row).
  const canonTicker = (t: string) =>
    String(t || "").toUpperCase().replace(/[.\-\/ ]/g, "");
  const subjectCanon = canonTicker(ticker);

  for (const peerTicker of peerTickers) {
    if (canonTicker(peerTicker) === subjectCanon) {
      console.log(`[PEER-BENCHMARKS] ${ticker}: skipping self as peer (${peerTicker}).`);
      continue;
    }
    try {
      const metricsArr = await fetchFMP(`stable/key-metrics`, {
        symbol: peerTicker,
        limit: "1",
        period: "annual",
      });
      const ratiosArr = await fetchFMP(`stable/ratios`, {
        symbol: peerTicker,
        limit: "1",
        period: "annual",
      });
      const quoteArr = await fetchFMP(`stable/quote`, { symbol: peerTicker });

      const metrics = Array.isArray(metricsArr) ? metricsArr[0] : metricsArr;
      const ratios = Array.isArray(ratiosArr) ? ratiosArr[0] : ratiosArr;
      const quote = Array.isArray(quoteArr) ? quoteArr[0] : quoteArr;

      if (!metrics || !ratios || !quote) continue;

      if (deterministicData?.isType4Financial) {
        // ── [SPEC-5] Type-4 peer metric sourcing ────────────────────────────────
        // Every field below is read from the payload that ACTUALLY carries it.
        // Verified against the live FMP API (2026-07-15, MKL + PGR annual):
        //   • key-metrics carries NO ptbRatio, NO priceToTangibleBookRatio and NO
        //     tangibleBookValuePerShare — the old chain evaluated
        //     `quote.price / undefined` = NaN (and `??` does not catch NaN), which
        //     is why peer P/TBV rendered "UNVERIFIED" in the memo.
        //   • key-metrics carries NO return-on-tangible-EQUITY field at all. Its
        //     returnOnTangibleAssets is return on tangible ASSETS — MKL 0.0331,
        //     PGR 0.0935: the exact figures the memo printed as "ROTCE". For a
        //     leveraged insurer RoTA understates ROTCE by ~4-5x, which inverted the
        //     memo's claim (MKL's real ROTCE ~15.2% is at/above BRK's ~14.5%, not below).
        //   • ratios carries NO returnOnEquity, so the old `?? ratios.returnOnEquity`
        //     fallback was dead code and RoTA always won.
        // tangibleBookValuePerShare and netIncomePerShare BOTH live on `ratios`, which
        // is already fetched for every peer, so ROTCE is computed deterministically in
        // TypeScript with ZERO new FMP calls. Units match the Historian's FIELD
        // REPURPOSING NOTE (ptbv as a multiple, rotce/efficiency as decimals).
        const numOrNull = (v: any) =>
          typeof v === "number" && isFinite(v) ? v : null;
        const peerTbvPs = numOrNull(ratios.tangibleBookValuePerShare);
        const peerPx = numOrNull(quote.price);
        const peerNips = numOrNull(ratios.netIncomePerShare);
        const ptbv =
          peerTbvPs != null && peerTbvPs > 0 && peerPx != null && peerPx > 0
            ? peerPx / peerTbvPs
            : null;
        const rotce =
          peerTbvPs != null && peerTbvPs > 0 && peerNips != null
            ? peerNips / peerTbvPs
            : null;
        const efficiency = numOrNull(ratios.netProfitMargin);
        console.log(
          `[TYPE4-PEER-SOURCE] ${ticker} peer ${peerTicker}: ` +
            `ptbv=${ptbv != null ? ptbv.toFixed(2) + "x" : "n/a"}, ` +
            `rotce=${rotce != null ? (rotce * 100).toFixed(1) + "%" : "n/a"} ` +
            `(netIncomePerShare/tangibleBookValuePerShare), ` +
            `netMargin=${efficiency != null ? (efficiency * 100).toFixed(1) + "%" : "n/a"} ` +
            `— returnOnTangibleAssets is NOT used (it is return on tangible ASSETS).`,
        );
        peerBenchmarks.push({
          ticker: peerTicker,
          // [SPEC-3] Explicitly named Type-4 fields. The memo renders from THESE so no
          // value is surfaced under a label it does not belong to (the peer table was
          // showing ROTCE under an "FCF YIELD" header because fcfYield carried rotce).
          // Each is populated from its own source and is null when absent — never
          // mirrored from another field.
          ptbv,
          rotce,
          // [SPEC-6] Net profit margin, named for what it is. This is NOT a combined
          // ratio or a NIM: no combined ratio is sourceable per-peer from FMP (it needs
          // loss + expense ratios off insurance-specific statement lines FMP does not
          // reliably expose), so nothing here may be rendered under that label.
          netMarginPct: efficiency,
          // Legacy overloaded keys RETAINED (same values): the Historian prompt's
          // FIELD REPURPOSING NOTE consumes roic/fcfYield/pFcf for Type-4 rows, and
          // dropping them here would require a prompt edit — out of scope for this
          // pure-TS spec. Deterministic consumers must prefer the explicit keys above.
          roic: ptbv,
          fcfYield: rotce,
          pFcf: efficiency,
        });
      } else {
        peerBenchmarks.push({
          ticker: peerTicker,
          roic: metrics.returnOnInvestedCapital ?? metrics.roic ?? null,
          fcfYield: metrics.freeCashFlowYield ?? null,
          pFcf: metrics.evToFreeCashFlow ?? null,
          operatingMargin: ratios.operatingProfitMargin ?? null,
          interestCoverage: ratios.interestCoverageRatio ?? null,
        });
      }
    } catch (err) {
      console.warn(
        `Failed to fetch benchmark data for peer ${peerTicker}`,
        err,
      );
    }
  }
  return peerBenchmarks;
}
// [TYPE4-PEER-GATE] (Tuning 10.2) Shared by the primary AND fallback Historian branches.
// The Historian inconsistently returns FCF Yield / P/FCF (as N/A) for Type 4 peers rather than
// P/TBV / ROTCE / NIM; this gate detects the malformed state and flags it so the Oracle can
// disclose the gap rather than rendering N/A cells as data failures. We do NOT fabricate peer
// data. Keys off the RESOLVED classification (deterministicData.isType4Financial), which folds in
// SIC + sector + balance-sheet shape + known-ticker + Type-5 suppression, so it won't mis-fire on
// FMP-misclassified non-financials (e.g. SUNB "financial services").
function applyType4PeerGate(
  historianOutput: any,
  deterministicData: any,
  institutionalData: any,
  ticker: string,
): void {
  if (!(institutionalData && deterministicData?.isType4Financial === true)) return;
  const peers = historianOutput.peerBenchmarks || [];
  const hasMeaningfulData =
    peers.length > 0 &&
    peers.some((p: any) => p.roic > 0 || p.fcfYield > 0 || p.pFcf > 0);
  const hasNAPattern = peers.some(
    (p: any) => !p.roic && !p.fcfYield && !p.pFcf,
  );
  if (!hasMeaningfulData || hasNAPattern) {
    console.warn(
      `[TYPE4-PEER-GATE] ${ticker}: Historian returned malformed Type 4 peer ` +
        `benchmarks (${peers.length} peers, meaningful data: ${hasMeaningfulData}). ` +
        `Attaching flag for Oracle disclosure. Peer table will show available data only.`,
    );
    historianOutput.type4PeerDataIncomplete = true;
    historianOutput.type4PeerNote =
      "TYPE 4 PEER DATA INCOMPLETE: Historian did not return P/TBV, ROTCE, or NIM/Combined Ratio " +
      "figures for peer comparison. Oracle must disclose this gap in confidenceScore " +
      "rationale and peerBenchmarking comparison fields.";
  } else {
    console.log(
      `[TYPE4-PEER-GATE] ${ticker}: Historian returned ${peers.length} Type 4 peers ` +
        `with meaningful data. Gate passed.`,
    );
    historianOutput.type4PeerDataIncomplete = false;
  }

  // [SPEC-3] Per-field ROTCE guard. The row-level gate above only trips on the
  // all-null pattern, so a PRESENT-but-mis-sourced ROTCE passes and the memo
  // editorializes on it ("Markel's ROTCE is lower than Berkshire's…"). A peer's
  // rotce is usable only if it is present, finite, and does not mirror a sibling
  // field on the same row — a duplicated value is the signature of one field
  // surfaced twice. When unusable, null BOTH the explicit and the legacy key so the
  // Oracle sees the metric as missing and applies the same "UNVERIFIED — MISSING
  // HISTORICAL INPUT" treatment it already gives P/TBV and combined ratio. The model
  // may not compare on a field the gate marked unverified.
  let rotceUnverified = 0;
  let duplicateFieldSuppressed = 0;
  for (const p of peers) {
    const rawR = p?.rotce ?? p?.fcfYield;
    const r = typeof rawR === "number" ? rawR : NaN;
    const sibs = [p?.ptbv ?? p?.roic, p?.netMarginPct ?? p?.pFcf]
      .map((s: any) => (typeof s === "number" ? s : NaN))
      .filter((s: number) => isFinite(s));
    const present = isFinite(r);
    const duplicated = present && sibs.some((s: number) => s === r);
    if (!present || duplicated) {
      if (duplicated) duplicateFieldSuppressed += 1;
      rotceUnverified += 1;
      p.rotceUnverified = true;
      p.rotce = null;
      p.fcfYield = null;
    } else {
      p.rotceUnverified = false;
    }
  }
  if (rotceUnverified > 0 || duplicateFieldSuppressed > 0) {
    console.warn(
      `[TYPE4-PEER-GATE] ${ticker}: rotceUnverified=${rotceUnverified}/${peers.length} ` +
        `(duplicateFieldSuppressed=${duplicateFieldSuppressed}) — ROTCE nulled on those ` +
        `rows; memo must render UNVERIFIED and must not compare on them.`,
    );
  }
  (deterministicData as any).__type4PeerRotceUnverified = rotceUnverified;
  (deterministicData as any).__type4PeerDuplicateFieldSuppressed = duplicateFieldSuppressed;
}
async function runHistorian(
  ticker: string,
  institutionalData: any,
  deterministicData: any,
  peerBenchmarks: any[],
  ledger?: TokenLedger,
): Promise<HistoricalFactSheet> {
  const isIpoOrigin = deterministicData?.type5Origin === 'ipo';

  const prompt = `You are "The Historian". Your job is to perform a deep 10-15 year audit of ${ticker}'s performance.
  
  GROUNDING REQUIREMENT FOR THE HISTORIAN:
  Your output feeds directly into the Oracle's reasoning. Every 
  peer benchmark you report must use the exact figures provided in 
  the AUTHORITATIVE PEER BENCHMARKS block below.
  Do not estimate peer metrics. The Oracle will treat your output as ground truth.

  For every peer in your benchmarking section:
  - State the metric (ROIC, FCF Yield, EV/FCF, or Type 4 equivalents)
  - State the figure (e.g., "7.1%")
  - State the source (e.g., "FMP deterministic pipeline")
  - If unavailable: "Could not retrieve — tool returned null"
  
  Where you observe qualitative advantages in your research that are not captured in ratios — a CEO's track record, a structural industry shift, a behavioral pattern — document them explicitly in your output with the specific evidence. The Oracle cannot use what you do not report.
  
  PRE-COMPUTED DETERMINISTIC METRICS (calculated from raw financial data — treat as authoritative, do NOT re-estimate):

  Revenue 5-Year CAGR: ${deterministicData?.historicalMetrics?.revenueGrowth5Yr != null ? (deterministicData.historicalMetrics.revenueGrowth5Yr * 100).toFixed(1) + "%" : "INSUFFICIENT DATA"}
  FCF 3-Year Avg Growth: ${deterministicData?.historicalMetrics?.avgFcfGrowth3Yr != null ? (deterministicData.historicalMetrics.avgFcfGrowth3Yr * 100).toFixed(1) + "%" : "INSUFFICIENT DATA"}
  Positive FCF Streak (5yr): ${deterministicData?.historicalMetrics?.hasPositiveFcfStreak5Yr ?? "UNKNOWN"}
  ROIC 5-Year Average: ${deterministicData?.historicalMetrics?.avgRoic5Yr != null ? (deterministicData.historicalMetrics.avgRoic5Yr * 100).toFixed(1) + "%" : "INSUFFICIENT DATA"}
  Gross Margin Change (5yr): ${deterministicData?.historicalMetrics?.grossMarginTrend5Yr != null ? deterministicData.historicalMetrics.grossMarginTrend5Yr + " bps" : "INSUFFICIENT DATA"}
  Operating Margin Change (5yr): ${deterministicData?.historicalMetrics?.operatingMarginTrend5Yr != null ? deterministicData.historicalMetrics.operatingMarginTrend5Yr + " bps" : "INSUFFICIENT DATA"}
  Interest Coverage Ratio: ${deterministicData?.historicalMetrics?.interestCoverageRatio != null ? deterministicData.historicalMetrics.interestCoverageRatio + "x" : "N/A (Type 4 or debt-free)"}

  STRUCTURAL DECLINE PRE-ASSESSMENT (based on deterministic data only):
  - Revenue trigger (5yr CAGR < 0%): ${deterministicData?.entityType === "Type 5" ? (isIpoOrigin ? "N/A — Type 5 recent IPO: annual history is genuine (S-1 restated) but too short for 5yr triggers; assess from TTM/recent-quarter performance and Agent D intel" : "N/A — Type 5 spin-off: 5yr annual history is parent-company data (see [TYPE5-OE-ANCHOR]); do NOT infer structural decline from it — assess from standalone TTM/recent-quarter performance and Agent D intel only") : (deterministicData?.historicalMetrics?.revenueGrowth5Yr != null ? (deterministicData.historicalMetrics.revenueGrowth5Yr < 0 ? "FIRED" : "clear") : "INSUFFICIENT DATA")}
  - Gross margin trigger (compression > 300bps): ${deterministicData?.entityType === "Type 5" ? (isIpoOrigin ? "N/A — Type 5 recent IPO: annual history is genuine (S-1 restated) but too short for 5yr triggers; assess from TTM/recent-quarter performance and Agent D intel" : "N/A — Type 5 spin-off: parent-contaminated annual history; assess from standalone TTM + Agent D intel only") : (deterministicData?.historicalMetrics?.grossMarginTrend5Yr != null ? (deterministicData.historicalMetrics.grossMarginTrend5Yr < -300 ? "FIRED (" + deterministicData.historicalMetrics.grossMarginTrend5Yr + " bps)" : "clear (" + deterministicData.historicalMetrics.grossMarginTrend5Yr + " bps)") : "INSUFFICIENT DATA")}
  - Operating margin trigger (compression > 300bps): ${deterministicData?.entityType === "Type 5" ? (isIpoOrigin ? "N/A — Type 5 recent IPO: annual history is genuine (S-1 restated) but too short for 5yr triggers; assess from TTM/recent-quarter performance and Agent D intel" : "N/A — Type 5 spin-off: parent-contaminated annual history; assess from standalone TTM + Agent D intel only") : (deterministicData?.historicalMetrics?.operatingMarginTrend5Yr != null ? (deterministicData.historicalMetrics.operatingMarginTrend5Yr < -300 ? "FIRED (" + deterministicData.historicalMetrics.operatingMarginTrend5Yr + " bps)" : "clear (" + deterministicData.historicalMetrics.operatingMarginTrend5Yr + " bps)") : "INSUFFICIENT DATA")}
  - Interest coverage trigger (< 3x = elevated default risk): ${deterministicData?.historicalMetrics?.interestCoverageRatio != null ? (deterministicData.historicalMetrics.interestCoverageRatio < 3 ? "FIRED (" + deterministicData.historicalMetrics.interestCoverageRatio + "x)" : "clear (" + deterministicData.historicalMetrics.interestCoverageRatio + "x)") : "N/A"}
  - Replacement threat trigger: YOU MUST ASSESS THIS — it cannot be pre-computed.

  When outputting avgRoic5Yr, avgFcfGrowth3Yr, revenueGrowth5Yr, and hasHistoricalFcfGrowth in your JSON, use the deterministic figures above. When outputting interestCoverage in historicalRatios, use the deterministic figure above. Do NOT re-calculate from tool data. Only the replacement threat trigger requires your judgment.

  AUTHORITATIVE PEER BENCHMARKS:
  ${JSON.stringify(peerBenchmarks, null, 2)}
  
  CORE IDENTITY DATA:
  ${JSON.stringify(institutionalData)}
  
  Note: The keyMetricsTtm object within the CORE IDENTITY DATA contains trailing twelve month versions of pe, fcfYield, roic, debtToEquity, and similar metrics. Use these TTM figures alongside the annual figures when assessing current valuation and momentum. The ratiosTtm object contains trailing-twelve-month profitability ratios including the current operating margin (operatingProfitMarginTTM).

  TYPE 5 SPIN-OFF QUALITY METRICS: When entityType is "Type 5", the single most-recent ANNUAL ratios/key-metrics ${isIpoOrigin ? "reflect a partial/transition fiscal year around the IPO, which may not represent run-rate performance" : "reflect the parent-contaminated separation year, NOT the standalone company's current performance"}. For Type 5 entities, your unitEconomics "current quality" rows for operating margin and ROIC MUST use the TTM figures (ratiosTtm.operatingProfitMarginTTM and the TTM ROIC from keyMetricsTtm) and label them "(TTM)". Do NOT present the stale fiscal-year annual operating margin or ROIC as the company's current quality. You may include the prior annual figure only as explicitly-labeled historical/cyclical context, never as the headline metric.
  
  ON-DEMAND DATA ACCESS (NEW PROTOCOL):
  You have access to FMP MCP tools. Use them to retrieval deep historical data, segmentations, and insider stats.
  
  CRITICAL AUDIT RULES:
  1. SEGMENT DISAGGREGATION: For elite tech (like AAPL), you MUST disaggregate the business. Identify Hardware vs Services revenue, growth rates, and gross margins. Do not blend them.
  2. PEER BENCHMARKING: Peer benchmark figures are provided in the AUTHORITATIVE PEER BENCHMARKS block above. Do NOT fetch or estimate peer numbers via tools. Copy the provided peerBenchmarks array verbatim into your output schema.

  PEER VALIDATION GATE (run before interpreting any peer):
  The pipeline attempts to source same-sector peers, but sub-industry mismatches can still occur. Before interpreting any peer, verify it operates in the same sub-industry as ${ticker}. Ask: would a professional analyst use this company as a benchmark for ${ticker}?

  If a peer is in a materially different industry (e.g., a metal packaging company benchmarked against a footwear brand, a chemical company against a software firm, a utility against a consumer brand):
  - Still include it in your peerBenchmarks array output (required for system integrity)
  - In your qualitative peerBenchmarking commentary, flag it explicitly: "NOTE: [TICKER] ([company name]) is a [actual industry] company and is not a valid benchmark for ${ticker}. Its metrics are not analytically comparable and should be disregarded. Suggested replacement peers: [name 1-2 same-industry alternatives]."
  - Call get_stock_peers to identify the correct peer set and name appropriate replacements. You cannot fetch their metrics deterministically in this run, but naming them allows the system to correct the peer set on the next analysis.

  Cross-industry peers are only acceptable when explicitly justified — for example, using a tobacco company to benchmark pricing power for a consumer staples brand, or using a REIT to anchor leverage analysis for a retailer. State the analytical justification explicitly if using a cross-industry peer by design.
  3. BUYBACK INTEGRITY: Audit the share count reduction over 10 years. Is value being created or destroyed at current price/IV levels?
  4. PRICING POWER: Find empirical evidence for the "Buffett 5% Test".
  5. OWNER EARNINGS: The get_owner_earnings tool is available and should be called for every analysis. Compare owner earnings to reported FCF — significant divergence (>15%) is a quality flag worth noting in the factSheet. If owner earnings consistently exceed reported FCF, this signals conservative accounting (positive). If they consistently lag, this signals aggressive accruals (negative).

  6. BRAND RELATIONSHIP GATE (moatAnalysis.hasBrandRelationshipComponent): This field is read by Agent F (The Pulse) to determine whether a cultural/brand signal scan is warranted. Set it with care.

  Set TRUE if consumer brand recognition, membership loyalty, emotional brand attachment, or repeat-purchase relationship is a MATERIAL component of the moat — even when not the primary moat type:
  - COST: true (membership loyalty is co-primary alongside scale economies)
  - KO: true (brand is the primary moat)
  - MCD: true (brand + franchisee trust)
  - MSFT: true (Office/Xbox/consumer products have a meaningful consumer relationship component — the enterprise side alone would be false, but the mix warrants true)
  - SBUX: true (customer ritual and loyalty program are structurally important)

  Set FALSE if the moat is purely cost, regulatory, technical lock-in, patent, or B2B network effects with no meaningful end-consumer relationship:
  - ASML: false (technical monopoly, B2B only)
  - BNSF: false (regulatory/infrastructure moat)
  - VRTX: false (patent moat, no consumer brand relationship)
  - BRK-B: false (Type 4 — handled separately by the scope gate)
  - WM: false (regulatory/cost moat, no consumer brand attachment)

  For conglomerates with both consumer and non-consumer segments: set true if any material consumer-facing segment exists.

  NEW PROTOCOLS:
  - TYPE 4 FINANCIAL PROTOCOL (activated when institutionalData contains a company in the insurance or banking sector — SIC codes 6020-6399):
  Pre-classified sub-type: ${deterministicData?.type4SubType || "FINANCIAL_OTHER"}
  Use this sub-type to determine which metrics to collect.
  Do NOT re-derive the sub-type from narrative. The classification is deterministic.
  INSURANCE_CONGLOMERATE and INSURER: collect floatSize, combinedRatioHistory, investmentPortfolioSize.
  BANK: collect NIM from income statement.
  ASSET_MANAGER: collect AUM and fee revenue margin.
  FINANCIAL_OTHER: apply BANK logic as default.
    Standard FCF, ROIC, and EV/FCF metrics are SUPPRESSED for Type 4 entities.
    They are not meaningful for businesses whose primary economic engine is
    float deployment, underwriting, or deposit intermediation.

    INSTEAD, you MUST fetch and report the following via available FMP tools:

    (a) INSURANCE FLOAT: Fetch balance sheet data. Identify total insurance
        float as: policyholder reserves + loss reserves + unearned premiums.
        For conglomerates like BRK, use total insurance liabilities as the
        float proxy. Report as floatSize (e.g. "$170B").

    (b) COST OF FLOAT: Assess whether the combined ratio over the last 5 years
        is above or below 100. Combined ratio < 100 = profitable underwriting
        = negative cost of float (the company is paid to hold the money).
        Report combinedRatioHistory as a 5-year average.

    (c) ROTCE: Return on Tangible Common Equity is the primary capital
        efficiency metric for financial entities. Fetch via get_key_metrics
        for the subject company (not peers). Report rotce5Yr.

    (d) BOOK VALUE: Fetch current book value per share and 10-year P/B
        range from historical data. Report bookValuePerShare,
        currentPriceToBook, and priceToBookHistory.

    (e) INVESTMENT PORTFOLIO: For insurance conglomerates, identify the
        total equity + fixed income investment portfolio size. This is the
        float deployment vehicle and a major component of NAV.
        Report investmentPortfolioSize. Report only if disclosed in filings; do not estimate, sum, or relabel. The engine sources portfolio composition deterministically — this field is descriptive context, never the memo's stated figure.

    (f) PEER BENCHMARKING SUBSTITUTION — MANDATORY FOR TYPE 4:

    Standard metrics (FCF Yield, ROIC, EV/FCF) are structurally meaningless
    for banks and insurers. They MUST be replaced with the following three
    metrics for every peer in the table. The AUTHORITATIVE PEER BENCHMARKS 
    block above has ALREADY mapped these fields.

    FIELD REPURPOSING NOTE: The peerBenchmarks schema uses roic, fcfYield,
    and pFcf as field names. For Type 4 entities, the provided data uses them as:
      roic    → P/TBV multiple (e.g., 1.8 for 1.8x)
      fcfYield → ROTCE (e.g., 0.142 for 14.2%)
      pFcf    → NIM or Combined Ratio (e.g., 0.028 for 2.8% NIM)

    Copy the provided peerBenchmarks exactly as given into your output. Do NOT fetch custom peers or metrics.

    (g) METHODOLOGY NOTE: State explicitly why standard owner earnings DCF
        is insufficient for this entity and what the correct primary
        valuation anchor should be (float-adjusted NAV for insurers,
        ROTCE/TBV for banks). This populates methodologyNote.

    Populate ALL of these fields in the type4Data object of your output.
    For non-Type-4 entities, set type4Data to null.
  - SPIN-OFF PROTOCOL (activated for Entity Type 5): If recent spin-off, identify parent, reconstruct segment history (min 5 years pro-forma), label all pro-forma metrics, reconcile bridge. Disclosure required in header.
  ${
    deterministicData?.isType5DataContaminated
      ? deterministicData?.type5UnvaluableReason === 'no-earnings-anchor'
        ? `
  ⚠️ NO EARNINGS ANCHOR — RECENT IPO: This is a recent IPO where FMP's financial data shows genuine standalone losses.
  The company has not yet generated positive owner earnings. Both annual FCF and TTM FCF are negative.

  CRITICAL INSTRUCTIONS FOR THIS ANALYSIS:
  1. DO NOT anchor to the negative quantitative data — this company has not yet generated positive owner earnings.
  2. USE your web search capability to find this company's actual recent standalone quarterly
     earnings reports, revenue, and margins
  3. FRAME the entire qualitative analysis around the company's actual standalone business
     performance and path to profitability.
  4. The DCF model will show zero owner earnings because the company has not yet generated positive owner earnings — acknowledge this
     explicitly and note that a proper valuation requires positive standalone financial history
  5. Recommend PASS verdict with a clear disclosure that quantitative valuation is not possible
     until positive standalone audited financials are available
  `
        : `
  ⚠️ DATA CONTAMINATION WARNING: This is a recent spin-off where FMP's financial data reflects the
  PARENT COMPANY's consolidated historical operations, NOT this entity's standalone performance.
  Both annual FCF and TTM FCF are negative due to parent-company data contamination.

  CRITICAL INSTRUCTIONS FOR THIS ANALYSIS:
  1. DO NOT reference or rely on the quantitative financial data provided (owner earnings, FCF,
     margins) — these numbers reflect the parent company, not this entity
  2. USE your web search capability to find this company's actual recent standalone quarterly
     earnings reports, revenue, and margins
  3. FRAME the entire qualitative analysis around the company's actual standalone business
     performance, not the contaminated historical data
  4. The DCF model will show zero owner earnings due to data contamination — acknowledge this
     explicitly and note that a proper valuation requires clean standalone financial history
  5. Recommend PASS verdict with a clear disclosure that quantitative valuation is not possible
     until 2-3 years of standalone audited financials are available
  `
      : deterministicData?.entityType === "Type 5"
        ? `
  ⚠️ TYPE 5 SPIN-OFF — ANNUAL DATA WARNING: This entity recently separated from a parent company.
  ${isIpoOrigin ? "The multi-year annual history is genuine S-1-restated standalone data but is too short to establish durability; the TTM FCF remains the primary evidence of current earnings power." : "The multi-year annual financial history in the data provided (income statements, revenue CAGR,\\n  margin trends, ROIC) reflects the PARENT COMPANY'S consolidated operations prior to separation,\\n  NOT this standalone entity's actual performance. The TTM FCF IS genuine standalone data from\\n  post-separation quarterly filings and is the primary evidence of this entity's earnings power."}

  MANDATORY INSTRUCTIONS — override your standard analysis protocols where they conflict:

  1. STRUCTURAL DECLINE ASSESSMENT: Do NOT run this assessment against the multi-year annual
     history. That data belongs to the parent company. Instead, assess structural decline only
     from the standalone quarterly trajectory available in the TTM data and via web search.
     Annual revenue CAGR and margin compression signals from the historical data are noise, not
     evidence. Suppress the decline flag unless standalone quarterly data confirms it.

  2. MOMENTUM CHARACTERIZATION: Base this strictly on post-separation standalone quarters,
     not the annual data series. If standalone quarterly revenue is accelerating, label it
     ACCELERATING regardless of what the 5-year annual CAGR shows.

  3. HISTORICAL NARRATIVE: Frame the 10-year historical context as the parent company's
     history up to separation, then pivot cleanly to the standalone entity's post-separation
     performance. Do not conflate the two. The historical losses and margin compression belong
     to the parent; the standalone entity's trajectory begins at separation date.

  4. USE your web search capability to find the most recent post-separation standalone
     quarterly earnings results, forward guidance, and management commentary. Weight this
     heavily over the annual data provided.

  5. OWNER EARNINGS ANCHOR: The deterministic engine has anchored normalized owner earnings
     to the TTM FCF figure (post-separation standalone data). Treat this as the correct
     earnings power baseline, not the multi-year annual history.
  `
        : ""
  }
  - ADR AND CURRENCY VERIFICATION PROTOCOL (non-US): State reporting currency, ADR ratio (e.g. 1:5), conversion rate, and FMP data currency status. If uncertain, flag "CURRENCY VERIFICATION INCOMPLETE" and penalty applies.
  - MOMENTUM CHARACTERIZATION: Before passing data to the Mathematician, explicitly state the trailing business direction using the last 3 reported quarters: Is revenue accelerating, decelerating, or inflecting? Label it one of these three words. If the current trend is still negative or decelerating, flag it with: "TREND WARNING: Mathematician must explicitly justify any stabilization assumption — it cannot be assumed from current data."
  - STRUCTURAL DECLINE ASSESSMENT: Before passing data, assess whether this business shows signs of irreversible structural decline (not cyclical). Triggers that activate this flag: (a) Revenue trend: 5-year CAGR negative or < 0% in absolute terms (not just deceleration) (b) Margin trend: Gross or operating margin compressing >300bps over 5 years with no recovery (c) Replacement threat: Identify a specific technology, regulation, or substitute that is permanently shrinking the addressable market (not just competing for share). All three triggers must be assessed. The flag is binary: true only if at least two of the three triggers are confirmed. State which triggers fired and why.
  
  CRITICAL OUTPUT SCHEMA: You MUST output a strictly valid JSON object matching this exact structure. Escape all inner double quotes (\\"). Do NOT use markdown code blocks.

{
  "ticker": "string — the primary ticker symbol",
  "historicalRatios": [
    {
      "year": "string — fiscal year e.g. '2024'",
      "roic": "number — return on invested capital as decimal e.g. 0.28",
      "roe": "number — return on equity as decimal",
      "fcf": "number — free cash flow in millions",
      "revenue": "number — total revenue in millions",
      "operatingMargin": "number — operating margin as decimal"
    }
  ],
  "avgRoic5Yr": "number — 5-year average ROIC as decimal",
  "avgFcfGrowth3Yr": "number — 3-year average FCF growth rate as decimal",
  "capitalAllocationTrackRecord": "string — narrative assessment of capital allocation quality and consistency",
  "goodwillVsTangibleAnalysis": "string — assessment of goodwill as percent of total assets and acquisition quality",
  "hasHistoricalFcfGrowth": "boolean — true if FCF has grown consistently over 5+ years",
  "historicalFailuresContext": "string — specific historical failures of similar business models. Never output N/A.",
  "tenYearHistoricalContext": "string — defining trend of the business over the last decade. Never output N/A.",
  "isRecentSpinOff": "boolean — true if company was spun off within the last 3 years",
  "managementTenureYears": "number — years current CEO has been in role",
  "segmentData": [
    {
      "name": "string — segment name e.g. 'Services', 'Hardware'",
      "revenueEstimate": "string — revenue with units e.g. '$85.2B'",
      "grossMargin": "string — gross margin percentage e.g. '70-75%'",
      "growthRate": "string — trailing growth rate e.g. '14% YoY'"
    }
  ],
  "peerBenchmarks": [
    {
      "ticker": "string — peer ticker symbol",
      "roic": "number — peer ROIC as decimal fetched via get_key_metrics tool",
      "fcfYield": "number — peer FCF yield as decimal fetched via get_financial_ratios tool",
      "pFcf": "number — peer EV/FCF (Enterprise Value to Free Cash Flow) ratio fetched via get_key_metrics tool. MUST STORE EV/FCF HERE."
    }
  ],
  "buybackAnalysis": {
    "shareCountReduction10Yr": "string — percentage reduction in share count over 10 years e.g. '-38%'",
    "isValueCreating": "boolean — true if buybacks occurred below intrinsic value, false if at or above",
    "rationale": "string — specific explanation of buyback value creation or destruction"
  },
  "pricingPowerEvidence": "string — empirical evidence for the Buffett 5% Test. Name specific price increases and customer retention data. Never output N/A.",
  "momentumCharacterization": "string — MUST be exactly one of: 'accelerating', 'decelerating', or 'inflecting'",
  "hasTrendWarning": "boolean — true if momentumCharacterization is 'decelerating' and the Mathematician must justify any stabilization assumption",
  "structuralDeclineAssessment": {
    "isStructuralDecline": "boolean — true only if 2+ decline triggers confirmed",
    "triggersConfirmed": "string — which of the three triggers fired and specific evidence",
    "replacementThreatDescription": "string — named technology/regulation shrinking the TAM"
  },
  "type4Data": {
    "floatSize": "string — total insurance float e.g. $170B, or N/A for non-insurers",
    "floatCostOfCapital": "string — cost of float narrative e.g. ~0% (combined ratio 96.2)",
    "combinedRatioHistory": "string — 5yr average combined ratio e.g. 95.2 (2019-2024)",
    "rotce5Yr": "string — 5yr avg ROTCE e.g. 12.4%",
    "priceToBookHistory": "string — 10yr P/B range e.g. 1.1x - 1.6x",
    "currentPriceToBook": "string — current P/B e.g. 1.4x",
    "bookValuePerShare": "string — current BVPS e.g. $280.50",
    "investmentPortfolioSize": "string — total investment portfolio e.g. $550B. Report only if disclosed in filings; do not estimate, sum, or relabel. The engine sources portfolio composition deterministically — this field is descriptive context, never the memo's stated figure.",
    "methodologyNote": "string — why standard DCF is insufficient and what replaces it"
  },
  "insiderOwnershipContext": "string — insider ownership percentage and recent transaction direction. Never output N/A."
}

Required fields that must never be omitted or null: ticker, historicalRatios, avgRoic5Yr, avgFcfGrowth3Yr, capitalAllocationTrackRecord, hasHistoricalFcfGrowth, isRecentSpinOff, managementTenureYears, momentumCharacterization, hasTrendWarning, structuralDeclineAssessment.

For segmentData: required for any company with more than one material revenue stream. Omit only for pure single-segment businesses.

For peerBenchmarks: required for all analyses. Use the provided AUTHORITATIVE PEER BENCHMARKS array exactly as provided. DO NOT use tools to fetch peer data.

For buybackAnalysis: required for any company that has conducted share repurchases in the last 5 years.`;

  try {
    return await withRetryJson("Historian", MODELS.HISTORIAN.primary, async () => {
      const response = await callGeminiWithTools({
        model: MODELS.HISTORIAN.primary,
        contents: [
          {
            role: "user",
            parts: [
              {
                text:
                  prompt +
                  "\n\nRespond only with valid JSON. Do not wrap in markdown code blocks.",
              },
            ],
          },
        ],
        config: {},
        tools: [...fmpTools],
        symbol: ticker,
        telemetry: { ledger, agent: "historian" },
        entry: MODELS.HISTORIAN,
      });
      const cleaned = cleanJson(extractText(response));
      const historianOutput = JSON.parse(cleaned);
      historianOutput.peerBenchmarks = peerBenchmarks;

      applyType4PeerGate(historianOutput, deterministicData, institutionalData, ticker);

      return historianOutput;
    }, 3);
  } catch (err: any) {
    console.warn(
      `[Fallback] Historian Primary failed (Forbidden?), falling back to ${MODELS.HISTORIAN.fallback}...`,
    );
    recordModelFailover({
      agent: "Historian",
      modelTried: MODELS.HISTORIAN.primary,
      fallbackModel: MODELS.HISTORIAN.fallback,
      statusCode: err?.status || err?.error?.code || err?.code || "unknown",
      ticker
    });
    return await withRetryJson("Historian", MODELS.HISTORIAN.fallback, async () => {
      const response = await callGeminiWithTools({
        model: MODELS.HISTORIAN.fallback,
        contents: [
          {
            role: "user",
            parts: [
              {
                text:
                  prompt +
                  "\n\nRespond only with valid JSON. Do not wrap in markdown code blocks.",
              },
            ],
          },
        ],
        config: {},
        tools: [...fmpTools],
        symbol: ticker,
        telemetry: { ledger, agent: "historian" },
        entry: MODELS.HISTORIAN,
      });
      const cleaned = cleanJson(extractText(response));
      const historianOutput = JSON.parse(cleaned);
      historianOutput.peerBenchmarks = peerBenchmarks;

      applyType4PeerGate(historianOutput, deterministicData, institutionalData, ticker);

      return historianOutput;
    });
  }
}
// Agent F scope gate — deterministic, runs with zero API cost.
// Returns true only for businesses where brand identity / customer relationship
// is a material component of the moat. Brand-erosion risk is invisible on the
// income statement until it's too late (cf. Bud Light, Jaguar rebrand,
// Cracker Barrel identity drift) — those are the businesses The Pulse exists for.
//
// v1.1: gates on Historian-emitted hasBrandRelationshipComponent when available.
// Falls back to sector/industry heuristic when the field is absent (backward compat).
function isPulseInScope(
  deterministicData: any,
  entityType: string,
  hasBrandRelationshipComponent?: boolean,
): { inScope: boolean; rationale: string } {
  // Type 4 (financial/insurance) is never a brand-relationship moat in this sense.
  if ((entityType || "").includes("Type 4")) {
    return {
      inScope: false,
      rationale:
        "Financial/insurance entity — moat is balance-sheet/regulatory, not brand-relationship.",
    };
  }

  // v1.1 gate: use Historian's structured moat classification when available.
  if (hasBrandRelationshipComponent === true) {
    return {
      inScope: true,
      rationale:
        "Historian identified brand/consumer relationship as a material moat component.",
    };
  }
  if (hasBrandRelationshipComponent === false) {
    return {
      inScope: false,
      rationale:
        "Historian determined moat has no material brand/consumer relationship component.",
    };
  }

  // hasBrandRelationshipComponent === undefined → fall through to sector/industry heuristic.
  const sector = (deterministicData?.sector || "").toLowerCase();
  const industry = (deterministicData?.industry || "").toLowerCase();

  // Hard exclusions — infrastructure / regulatory / cost / technical-lock-in moats.
  const exclude = [
    "telecom",
    "wireless",
    "wireline",
    "pipeline",
    "reit",
    "bank",
    "insurance",
    "semiconductor",
    "utilities",
    "utility",
    "oil",
    "gas",
    "mining",
    "chemicals",
    "aerospace",
    "defense",
    "freight",
    "railroad",
    "shipping",
    "infrastructure",
  ];
  if (exclude.some((k) => industry.includes(k) || sector.includes(k))) {
    return {
      inScope: false,
      rationale: `Sector/industry ("${sector}"/"${industry}") indicates an infrastructure/regulatory/cost moat, not a brand-relationship moat.`,
    };
  }

  // Inclusions by sector (FMP taxonomy).
  const sectorIn = [
    "consumer cyclical",
    "consumer defensive",
    "consumer discretionary",
    "consumer staples",
  ].some((s) => sector.includes(s));

  // Inclusions by industry keyword (brand / customer-relationship driven).
  const industryIn = [
    "restaurant",
    "retail",
    "apparel",
    "footwear",
    "luxury",
    "beverage",
    "beverages",
    "packaged food",
    "confection",
    "household",
    "personal product",
    "leisure",
    "lodging",
    "hotel",
    "resort",
    "travel",
    "tobacco",
    "entertainment",
    "gaming",
    "media",
    "internet content",
    "specialty retail",
    "department store",
    "consumer electronics",
    "cosmetics",
    "auto manufacturer",
    "automobile",
  ].some((k) => industry.includes(k));

  if (sectorIn || industryIn) {
    return {
      inScope: true,
      rationale: `Consumer-facing business (sector "${sector}", industry "${industry}") — brand/customer relationship is a material moat component.`,
    };
  }

  return {
    inScope: false,
    rationale: `Sector/industry ("${sector}"/"${industry}") is not a recognized brand-relationship business; Pulse skipped.`,
  };
}

async function runAnalyst(
  ticker: string,
  companyName: string,
  entityType: string,
  edgarContext?: EdgarContext,
  type5Origin: string | null = null,
  ledger?: TokenLedger,
): Promise<IntelligenceBrief> {
  const isIpoOrigin = type5Origin === 'ipo';
  const prompt = `You are "The Analyst" (Agent D) for Oracle Insight, an institutional-grade investment analysis system.

Your job is to research ${ticker} (${companyName}) right now using real-time web search and return a structured intelligence brief that the Oracle will use to make a better investment judgment.

The Oracle already has 10 years of financial history (from the Historian) and a DCF valuation model (from the Mathematician). What it does NOT have is current business context — the live narrative that financial filings cannot capture. That is your job.

ENTITY CONTEXT: This company is classified as "${entityType}". Tailor your research accordingly:
${isIpoOrigin ? '- If a recent IPO: CRITICAL — research the IPO date, lockup expiry, use of proceeds, and whether reported growth is organic or IPO-window dressed.' : '- If "Spin-off": CRITICAL — research the spinoff date, parent company, and whether the post-separation revenue trajectory is genuinely organic growth or a base-effect artifact.'}
- If "Standard": Focus on industry cycle position, competitive developments, and analyst sentiment.
- If "Financial": Focus on credit environment, regulatory changes, and capital ratio trends.

${
  edgarContext
    ? `
EDGAR DOCUMENT CONTEXT (Source: SEC Filings — Management's Own Words)
---------------------------------------------------------------------
The following excerpts were extracted directly from this company's SEC 
filings. This is management's own language — treat it as primary source 
material, more reliable than news paraphrases.

Note: If filings are 20-F format (foreign private issuer), management 
language and risk factor structure may reflect non-US regulatory 
conventions. Weight qualitative signals accordingly and cross-reference 
with ADR ratio and currency context already confirmed by the Historian.

Use this context to:
1. Identify how management's stated priorities have shifted year-over-year
2. Flag any risk factors that have appeared, escalated, or disappeared 
   across the filing periods
3. Surface specific management language about margins, growth drivers, 
   or competitive position that the Oracle should reference in its thesis
4. Note any material differences between what management said in filings 
   vs. what appears in current news (narrative drift is a signal)

${edgarContext.filings
  .map(
    (f) => `
--- ${f.filingType} filed ${f.filingDate} ---
MD&A EXCERPT:
${f.mdaExcerpt}

RISK FACTORS EXCERPT:
${f.riskFactorsExcerpt}

${f.segmentExcerpt ? `SEGMENT DISCUSSION:\n${f.segmentExcerpt}` : ""}
`,
  )
  .join("\n")}

If EDGAR context is empty or unavailable, proceed with Google Search 
grounding only and note the absence in sourcesConsulted.
`
    : ""
}
YOU MUST SEARCH FOR AND REPORT ON THE FOLLOWING:

1. CORPORATE EVENTS (search: "${ticker} spinoff merger acquisition 2024 2025")
   - Has this company been spun off from a parent in the last 3 years? If so: when, from whom, and what does the standalone business actually look like vs the parent?
   
SPINOFF TROUGH DIFFERENTIATION (MANDATORY FOR TYPE 5 ENTITIES):
If isRecentSpinoff is true, you MUST explicitly answer all four of 
the following questions in keyContextSummary. These answers flow 
directly into the Mathematician's normalization method selection 
and the Oracle's framing of the investment thesis:

Q1 — SEPARATION COST DRAG: Are current negative or compressed 
margins attributable to one-time separation costs, stranded 
overhead from the parent, or new public-company infrastructure 
costs that did not exist in the segment P&L? If yes, estimate 
how many quarters until these normalize and what the clean 
run-rate margin might look like.

Q2 — CYCLE TIMING: Did the spinoff occur during an industry 
cyclical trough? If yes, are peers experiencing similar margin 
compression? This matters because a spinoff that separated at 
the bottom of a cycle will show historically depressed margins 
that are trough-driven, not structurally driven.

Q3 — STANDALONE DIFFERENTIATION: Does the standalone entity 
have a meaningfully different competitive positioning, product 
mix, or customer base than the parent division had? SanDisk as 
a standalone brand targeting enterprise SSDs is a different 
business than WDC's undifferentiated NAND commodity division. 
Surface this distinction explicitly if it exists.

Q4 — GROWTH AUTHENTICITY: Is the post-separation revenue growth 
rate genuine standalone organic growth, a base-effect artifact 
of being carved out from a larger entity, or both? Quantify 
the base-effect component if possible.

Output these answers as a structured block in keyContextSummary 
under the header "SPINOFF TROUGH ANALYSIS:" before the general 
context summary.

   - Is it a merger/acquisition target? Any activist investor activity?
   - Any CEO or CFO change in the last 12 months?

2. REVENUE AND GROWTH NARRATIVE (search: "${ticker} revenue growth earnings 2024 2025" and "${ticker} analyst forecast outlook")
   - What is the recent growth story? Is it organic or base-effect driven (common in spinoffs/restructurings)?
   For spinoff entities specifically, also search: 
   "[ticker] standalone growth organic vs base effect" and 
   "[ticker] separation costs stranded overhead normalization"
   These queries surface the base-effect vs. organic growth 
   distinction that financial databases cannot provide.
   - What is the current analyst consensus and price target range?
   - How has the company performed against earnings estimates in the last 4 quarters?

3. INDUSTRY AND COMPETITIVE CONTEXT (search: "${ticker} industry competitive landscape 2025" and "[industry name] market trends 2025")
   - Where is the industry in its current cycle? (Upcycle, downcycle, recovering, peaking)
   - What are the 2-3 most important current tailwinds specific to this business?
   - What are the 2-3 most important current headwinds?
   - What are peers doing? Are competitors gaining or losing ground? 
   - Are there any major competitor announcements (capacity cuts, new products, pricing moves) that materially affect the subject company's outlook?

4. PEER CONTEXT FOR HISTORIAN VALIDATION (search: "[peer ticker 1] [peer ticker 2] [peer ticker 3] financials 2025")
   - The Historian benchmarked this company against its peers on ROIC, FCF Yield, and EV/FCF.
   - Search for current context on 2-3 key peers to validate whether those benchmarks are still representative.
   - Note any peers that have had material changes (earnings misses, guidance cuts, restructurings) that would skew the comparison.

5. MATERIAL CURRENT RISKS (search: "${ticker} risk lawsuit regulatory 2025")
   - Any pending litigation, regulatory investigations, or geopolitical risks NOT visible in annual filings?
   - Any supply chain, customer concentration, or debt maturity risks that have emerged in the last 6 months?

   SUBJECT-ATTRIBUTION RULE (MANDATORY): Every item you place in
   currentMaterialRisks MUST be a risk for which ${ticker} (${companyName})
   is the DIRECT party — the named defendant, the investigated entity, the
   company whose revenue/margin/license is directly exposed. ${ticker} does
   business with many other companies as customer, vendor, partner, or
   service provider. Litigation, settlements, or regulatory actions that
   legally belong to those OTHER companies are NOT ${ticker}'s risks, even
   when ${ticker}'s name appears alongside them in search results.

   Before adding any risk, ask: "If this event fully materializes, does
   ${ticker} pay the settlement / lose the license / take the charge — or
   does some other company?" If the liability lands on another company,
   DO NOT include it. The only exception is a risk that flows THROUGH a
   relationship to directly hit ${ticker}'s own financials (e.g., the loss
   of a named top-5 customer that materially reduces ${ticker}'s revenue) —
   in that case, frame the risk as the revenue/relationship loss to
   ${ticker}, not as the other company's legal matter.

6. UPCOMING CATALYSTS (search: "${ticker} earnings date catalyst upcoming 2025")
   - Next earnings date and what analysts are watching
   - Any product launches, contract wins, or strategic announcements expected

CRITICAL RULES:
- You MUST use Google Search. Do NOT rely on your training knowledge alone — prices, ratings, and business conditions change. Your value is current intelligence, not stale data.
- Search at least 4-5 different queries across the topics above.
- When you find conflicting information across sources, note the conflict in keyContextSummary and weight the more recent source.
- Do NOT confabulate. If you cannot find reliable information on a topic, report searchConfidenceScore below 0.7 and note the gap.
${isIpoOrigin ? '- For recent IPOs specifically: research the lockup expiration date, post-lockup insider selling, dual-class voting structure, and whether the aftermarket price is underwriter-supported.' : '- For spinoffs specifically: the financial databases (FMP, Bloomberg) often backfill parent-company history onto the new ticker. Your job is to surface the actual standalone narrative that corrects this distortion.'}
- peerContextNotes must specifically call out whether the Historian's peer benchmarks appear reliable or whether any peer has had a material event that would skew the comparison.

Respond ONLY with a valid JSON object matching this exact structure. Do not wrap in markdown code blocks.

{
  "corporateEvents": {
    "isRecentSpinoff": boolean,
    "spinoffDate": "YYYY-MM-DD or null",
    "parentCompany": "string or null",
    "isMergerTarget": boolean,
    "recentAcquisitions": ["array of strings"],
    "nameChangeHistory": "string or null",
    "isUnderActivistPressure": boolean,
    "hasRecentCEOChange": boolean
  },
  "troughClassification": {
    "isCyclicalTrough": boolean,
    "isSpinoffTrough": boolean,
    "isStructuralDecline": boolean,
    "classificationConfidence": "HIGH | MEDIUM | LOW",
    "classificationRationale": "string",
    "estimatedTroughDuration": "string or null"
  },
  "revenueNarrative": {
    "recentGrowthContext": "string",
    "growthIsOffSmallBase": boolean,
    "analystConsensus": "STRONG BUY | BUY | HOLD | SELL | STRONG SELL | MIXED",
    "analystPriceTargetRange": "string or null",
    "recentEarningsSurprises": "string"
  },
  "industryContext": {
    "currentCyclePosition": "string",
    "keyIndustryTailwinds": ["string", "string", "string"],
    "keyIndustryHeadwinds": ["string", "string", "string"],
    "peerContextNotes": "string",
    "majorCompetitorDevelopments": "string"
  },
  "currentMaterialRisks": ["string", "string", "string"],
  "upcomingCatalysts": ["string", "string"],
  "keyContextSummary": "string — 3-4 sentences synthesizing the most critical things the Oracle needs to know that the financial data cannot reveal",
  "searchConfidenceScore": number between 0.0 and 1.0,
  "sourcesConsulted": ["domain1.com", "domain2.com"]
}`;

  const fallbackBrief: IntelligenceBrief = {
    corporateEvents: {
      isRecentSpinoff: false,
      isMergerTarget: false,
      recentAcquisitions: [],
      isUnderActivistPressure: false,
      hasRecentCEOChange: false,
    },
    troughClassification: {
      isCyclicalTrough: false,
      isSpinoffTrough: false,
      isStructuralDecline: false,
      classificationConfidence: "LOW",
      classificationRationale:
        "Trough classification unavailable — search did not return reliable results.",
      estimatedTroughDuration: undefined,
    },
    revenueNarrative: {
      recentGrowthContext:
        "Analyst data unavailable — search did not return reliable results.",
      growthIsOffSmallBase: false,
      analystConsensus: "UNKNOWN",
      recentEarningsSurprises: "Unavailable",
    },
    industryContext: {
      currentCyclePosition: "Unavailable",
      keyIndustryTailwinds: [],
      keyIndustryHeadwinds: [],
      peerContextNotes:
        "Peer validation unavailable — Historian benchmarks unverified.",
      majorCompetitorDevelopments: "Unavailable",
    },
    currentMaterialRisks: [],
    upcomingCatalysts: [],
    keyContextSummary:
      "The Analyst was unable to retrieve current intelligence. The Oracle should weight the financial data more heavily and apply an additional uncertainty discount to its confidence score.",
    searchConfidenceScore: 0.0,
    sourcesConsulted: edgarContext?.fetchSuccess ? ["sec.gov/edgar"] : [],
    edgarFilingsUsed: edgarContext?.fetchSuccess || false,
  };

  try {
    return await withRetryJson("Analyst", MODELS.ANALYST.primary, async () => {
      const response = await callGeminiWithTools({
        model: MODELS.ANALYST.primary,
        contents: [{ role: "user", parts: [{ text: prompt }] }],
        // [THINKING-EXPLICIT] Agent D thinking level now set explicitly from the
        // registry (previously implicit). See commit note — one of the B/D/E changes.
        config: { thinking_level: MODELS.ANALYST.thinkingLevel },
        tools: [{ googleSearch: {} }],
        symbol: ticker,
        telemetry: { ledger, agent: "analyst" },
        entry: MODELS.ANALYST,
      });
      const cleaned = cleanJson(extractText(response));
      const parsed = JSON.parse(cleaned) as IntelligenceBrief;

      // Defensive array coercions — same pattern as Pulse agent.
      // Gemini occasionally returns strings or objects for array fields.
      if (!Array.isArray(parsed.upcomingCatalysts)) parsed.upcomingCatalysts = [];
      if (!Array.isArray(parsed.currentMaterialRisks)) parsed.currentMaterialRisks = [];
      if (!Array.isArray(parsed.sourcesConsulted)) parsed.sourcesConsulted = [];
      if (parsed.industryContext) {
        if (!Array.isArray(parsed.industryContext.keyIndustryTailwinds)) parsed.industryContext.keyIndustryTailwinds = [];
        if (!Array.isArray(parsed.industryContext.keyIndustryHeadwinds)) parsed.industryContext.keyIndustryHeadwinds = [];
      }

      if (edgarContext?.fetchSuccess) {
        if (!parsed.sourcesConsulted) parsed.sourcesConsulted = [];
        parsed.sourcesConsulted.push("sec.gov/edgar");
        parsed.edgarFilingsUsed = true;
      } else {
        parsed.edgarFilingsUsed = false;
      }

      console.log(
        `[ANALYST] ${ticker}: Intelligence gathered. Confidence: ${parsed.searchConfidenceScore}. Spinoff detected: ${parsed.corporateEvents?.isRecentSpinoff}. Sources: ${parsed.sourcesConsulted?.join(", ")}`,
      );
      return parsed;
    });
  } catch (err) {
    console.warn(
      `[ANALYST] ${ticker}: Search failed, using fallback brief. Error: ${err}`,
    );
    return fallbackBrief;
  }
}
async function runPulse(
  ticker: string,
  companyName: string,
  deterministicData: any,
  entityType: string,
  hasBrandRelationshipComponent?: boolean,
  ledger?: TokenLedger,
): Promise<PulseOutput> {
  const gate = isPulseInScope(deterministicData, entityType, hasBrandRelationshipComponent);

  // Scope-gated out → return a zero-cost NOT_APPLICABLE result. No Gemini call.
  if (!gate.inScope) {
    console.log(`[PULSE] ${ticker}: NOT_APPLICABLE — ${gate.rationale}`);
    return {
      scopeStatus: "NOT_APPLICABLE",
      scopeRationale: gate.rationale,
      culturalSignal: "NOT_ASSESSED",
      trajectory: "NOT_ASSESSED",
      timeHorizon: "NOT_ASSESSED",
      managementAwareness: "NOT_ASSESSED",
      brandMoatImpact: "",
      keyEvidence: [],
      directionalConfidence: "NOT_ASSESSED",
      sourcesConsulted: [],
    };
  }

  const fallbackPulse: PulseOutput = {
    scopeStatus: "IN_SCOPE",
    scopeRationale: gate.rationale,
    culturalSignal: "NOT_ASSESSED",
    trajectory: "NOT_ASSESSED",
    timeHorizon: "NOT_ASSESSED",
    managementAwareness: "NOT_ASSESSED",
    brandMoatImpact:
      "Pulse search failed; no cultural signal could be gathered. Treat as no-signal, not a negative signal.",
    keyEvidence: [],
    directionalConfidence: "LOW",
    sourcesConsulted: [],
  };

  const prompt = `You are "The Pulse" (Agent F) for Oracle Insight, an institutional-grade investment analysis system.

You assess ONE question about ${ticker} (${companyName}) using real-time web search:
**Is this company's relationship with its customers getting stronger or weaker, and is management aware of the difference?**

This is NOT sentiment analysis and NOT a brand score. You are reading the *direction* of the customer relationship and brand identity for a consumer-facing business — the category of risk that is invisible in financial statements until it is too late. Examples of brand-relationship damage that no financial ratio flagged in advance: a beverage brand alienating its core customer through a marketing misstep; an iconic auto brand confusing its identity through a rebrand; a heritage restaurant chain drifting from the identity its customers valued.

RESEARCH (use web search): recent consumer sentiment and reception, brand identity coherence vs. drift, management/marketing decisions and how customers reacted, community reaction (review trends, social/forum signal, boycotts or surges in loyalty), and whether management commentary shows awareness of any shift.

STRICT ANTI-HEADLINE-CHASING RULE — you may classify culturalSignal/trajectory as anything OTHER than STRENGTHENING/STABLE/WEAKENING-with-evidence ONLY if ALL THREE are present:
  1. A clear DIRECTION (improving or deteriorating), not a one-off event;
  2. DURATION — the signal persists across at least TWO reporting periods / two distinct time windows; and
  3. A NAMED ROOT CAUSE (a specific decision, trend, or shift), not a vague vibe.
If any of the three is missing, you MUST return culturalSignal: "STABLE", trajectory: "FLAT", and explain in brandMoatImpact why the available signal does not meet the bar. A single viral headline is NOT a signal.

If media coverage is too thin to read a direction (common for low-profile or B2B-leaning consumer names), return culturalSignal "STABLE", directionalConfidence "LOW", and say so. Quiet is not negative.

Return ONLY a JSON object (no markdown, no commentary) with EXACTLY these fields:
{
  "scopeStatus": "IN_SCOPE",
  "scopeRationale": "${gate.rationale.replace(/"/g, "'")}",
  "culturalSignal": "STRENGTHENING" | "STABLE" | "WEAKENING",
  "trajectory": "IMPROVING" | "FLAT" | "DETERIORATING",
  "timeHorizon": "SHORT_TERM" | "12_18_MONTHS" | "18_MONTHS_PLUS",
  "managementAwareness": "AWARE_RESPONDING" | "AWARE_NOT_RESPONDING" | "UNAWARE",
  "brandMoatImpact": "2-3 sentences, qualitative only, NO price/IV numbers",
  "keyEvidence": ["dated, specific, sourced observation", "..."],
  "directionalConfidence": "HIGH" | "MEDIUM" | "LOW",
  "sourcesConsulted": ["domain.com", "..."]
}`;

  try {
    return await withRetryJson("Pulse", MODELS.PULSE.primary, async () => {
      const response = await callGeminiWithTools({
        model: MODELS.PULSE.primary,
        contents: [{ role: "user", parts: [{ text: prompt }] }],
        config: {},
        tools: [{ googleSearch: {} }],
        symbol: ticker,
        telemetry: { ledger, agent: "pulse" },
        entry: MODELS.PULSE,
      });
      const cleaned = cleanJson(extractText(response));
      const parsed = JSON.parse(cleaned) as PulseOutput;
      // Defensive: force scopeStatus and rationale to the gate's truth.
      parsed.scopeStatus = "IN_SCOPE";
      parsed.scopeRationale = gate.rationale;
      if (!Array.isArray(parsed.keyEvidence)) parsed.keyEvidence = [];
      if (!Array.isArray(parsed.sourcesConsulted)) parsed.sourcesConsulted = [];
      console.log(
        `[PULSE] ${ticker}: signal=${parsed.culturalSignal} trajectory=${parsed.trajectory} confidence=${parsed.directionalConfidence} mgmt=${parsed.managementAwareness}`,
      );
      return parsed;
    });
  } catch (err) {
    console.warn(
      `[PULSE] ${ticker}: search failed, using fallback. Error: ${err}`,
    );
    return fallbackPulse;
  }
}

export class MathematicianModelUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "MathematicianModelUnavailableError";
  }
}

async function runMathematician(
  factSheet: HistoricalFactSheet,
  deterministicData: any,
  ledger?: TokenLedger,
): Promise<MathematicalEngine> {
  const isIpoOrigin = deterministicData?.type5Origin === 'ipo';
  const prompt = `You are "The Mathematician" (Agent B). Your role has changed. 
You no longer calculate the DCF yourself. Instead, you define 
the precise valuation assumptions that will be passed into a 
deterministic calculation engine. Your output must be a JSON 
object matching the DCFAssumptions interface exactly.

GROUNDING REQUIREMENT FOR THE MATHEMATICIAN:

Every assumption you set must cite its source from the deterministic 
data provided to you or from a tool call you make. The required 
Format for assumptionRationales string values:

  "Source: [named data point]. Judgment applied: [what you adjusted and why]."

Example of a valid value for the "normalizedOwnerEarnings" rationale:
  "Source: FMP cash flow statement, 5-year average FCF adjusted for maintenance CapEx band plus D&A. Judgment applied: used midpoint of CapEx band rather than management stated figure."

Example of a valid value for the "terminalMultiple" rationale:
  "Source: T trades at 15.5x EV/FCF (FMP key metrics via Historian), CHTR at 16.2x. Judgment applied: VZ base case set at 16x — in line with sector given comparable leverage profile."

If you cannot source an assumption from deterministic data or a 
tool call, state the gap and use the most conservative defensible 
value. Do not fill assumption gaps with training knowledge alone.

Where the Historian or Intelligence Brief has identified structural factors that affect normalized earnings power or terminal multiple beyond what historical financials show — an industry inflection, a management capability, an order book signal — you may adjust your assumptions to reflect this. State the adjustment explicitly: what you changed, by how much, and what the specific evidence was.

HISTORIAN DATA: ${JSON.stringify(factSheet)}

Key fields to reference explicitly:
- momentumCharacterization: ${factSheet.momentumCharacterization || "NOT PROVIDED"}
- hasTrendWarning: ${factSheet.hasTrendWarning ?? false}
- peerBenchmarks: ${factSheet.peerBenchmarks ? `${factSheet.peerBenchmarks.length} peers fetched` : "NOT PROVIDED — treat peer comparisons as unverified"}
- segmentData: ${factSheet.segmentData ? `${factSheet.segmentData.length} segments identified` : "Single-segment business or data unavailable"}
CURRENT MARKET DATA: ${JSON.stringify(deterministicData)}

YOUR TASK:
Analyze the business and define the following assumptions. 
For each assumption, provide the value AND a one-sentence 
rationale explaining why you chose it.

─────────────────────────────────────────────────────────────
ENTITY TYPE GATE (check this before any assumption)
─────────────────────────────────────────────────────────────

Pre-classified entity type: ${deterministicData?.entityType || "Type 6"}

${
  deterministicData?.isType4Financial
    ? `
⚠️ TYPE 4 FINANCIAL ENTITY DETECTED ⚠️

Sub-type (pre-classified, use this — do not re-derive): ${deterministicData?.type4SubType || "FINANCIAL_OTHER"}

Sub-type definitions:
  INSURANCE_CONGLOMERATE — Float-driven holding company with diversified operating subsidiaries
    (BRK, MKL, FFH). Primary IV anchor: float-adjusted TBV × P/TBV multiple.
    Peer set: MKL, FFH, CB. Key metric: combined ratio.
  INSURER — Pure-play insurance carrier. Primary IV anchor: P/TBV.
    Peer set: use get_stock_peers. Key metric: combined ratio.
  BANK — Deposit-taking institution. Primary IV anchor: ROTCE × P/TBV.
    Peer set: BAC, WFC, C for G-SIBs; use get_stock_peers for regionals.
    Key metric: NIM (Net Interest Margin).
  ASSET_MANAGER — Fee-based capital allocator. Primary IV anchor: P/AUM or P/E.
    Peer set: use get_stock_peers. Key metric: fee revenue margin.
  FINANCIAL_OTHER — Apply bank logic as default fallback.

Standard owner earnings DCF is NOT the primary valuation methodology for
this entity. The core economic engine is determined by sub-type above.

YOUR PRIMARY IV ANCHOR is determined by sub-type above, not owner earnings DCF.

YOUR normalizedOwnerEarnings FIELD:
Set this to the consolidated operating earnings of the non-financial
subsidiaries only. Do NOT include investment gains, unrealized gains, or
insurance float income. This field is used for the secondary DCF cross-check only.

YOUR WACC:
For a diversified financial conglomerate: 30Y Treasury +
ERP + 25-50bps insurance tail risk premium. State the build explicitly.

YOUR TERMINAL MULTIPLE:
Use Price/Tangible Book Value (P/TBV) as the terminal anchor — NOT Price/Book.
Book equity for a financial entity often includes significant goodwill and intangibles
that are not part of the core franchise value. TBV strips these out.

REQUIRED PROCESS — Judgment Only (No Arithmetic):

The deterministic engine will handle all terminal compounding math (calculating Year 10 TBV and converting it to total aggregate value). Your ONLY task is to supply the structural assumptions: the Price-to-Tangible-Book (P/TBV) multiples.

Deterministic Inputs (Provided — do NOT recalculate):
- Current TBV Per Share: ${deterministicData?.type4Valuation?.tbvPerShare != null ? "$" + deterministicData.type4Valuation.tbvPerShare.toFixed(2) : "Unavailable"} (Source: ${deterministicData?.type4Valuation?.tbvSource || "N/A"})
- ROTCE (5-year historical average): ${deterministicData?.historicalMetrics?.avgRoic5Yr != null ? (deterministicData.historicalMetrics.avgRoic5Yr * 100).toFixed(2) + "%" : "Unavailable"}
- Retention Rate: ${(deterministicData?.retentionRate ?? 1).toFixed(4)}
- Historical P/TBV Range (10-yr):
  ${deterministicData?.type4Valuation?.insufficientHistory ? "⚠️ INSUFFICIENT HISTORY (< 5 years)" : ""}
  Trough: ${deterministicData?.type4Valuation?.ptbvTrough != null ? deterministicData.type4Valuation.ptbvTrough.toFixed(2) + "x" : "N/A"}
  Median: ${deterministicData?.type4Valuation?.ptbvMedian != null ? deterministicData.type4Valuation.ptbvMedian.toFixed(2) + "x" : "N/A"}
  Peak:   ${deterministicData?.type4Valuation?.ptbvPeak != null ? deterministicData.type4Valuation.ptbvPeak.toFixed(2) + "x" : "N/A"}

Step 1 — Select the terminal P/TBV multiple parameters:
  Base case (terminalMultiple): 10-year median P/TBV. Do not use the peak unless ROTCE is trending meaningfully above its own 10-year average. Do not use the trough unless structural decline has been flagged by the Historian.
  Bear override (bearOverrides.terminalMultiple): 10-year trough P/TBV, or 1.0x if trough is below 1.0x (floor).
  Bull override (bullOverrides.terminalMultiple): 10-year peak P/TBV, capped at 2.5x for US mega-cap banks unless a specific structural re-rating catalyst is named.
  State which multiple you chose for each scenario and why.

Step 2 — Set required output fields:
  a) Set useTerminalMultiple to true.
  b) Set terminalMultiple to your selected BASE P/TBV multiple (e.g., 1.4 for 1.4x book).
  c) DO NOT output projectedYear10TBVTotal. The deterministic engine will compute and inject this.

DISCLOSURE REQUIREMENT:
Your assumptionRationales.terminalMultiple field MUST begin with:
"TYPE 4 P/TBV ANCHOR: " followed by the trough/median/peak figures, and the
selected P/TBV multiple for each scenario.
This field must contain all four: trough, median, peak, and selected values.
A rationale that does not contain all four will be treated as non-compliant.

DISCLOSURE REQUIREMENT:
Your assumptionRationales.normalizedOwnerEarnings rationale MUST begin with
the following text. Do NOT include surrounding double quotes — this text goes
inside a JSON string field and any inner quotes must be escaped with backslash:
TYPE 4 OVERRIDE: NAV-based valuation. Owner earnings represents operating
subsidiary earnings only, used for secondary DCF cross-check.

`
    : `
Standard methodology applies. Proceed with owner earnings DCF as normal.
`
}

─────────────────────────────────────────────────────────────

1. normalizedOwnerEarnings: The steady-state owner earnings 
   in millions. Apply Rule 5a — if the 3-year average and 
   current run-rate diverge by more than 20%, reconcile them 
   and state your anchor. Must be positive for FCF-positive 
   businesses.

CYCLICAL TROUGH NORMALIZATION PROTOCOL (MANDATORY)
---------------------------------------------------
Before passing any owner earnings figure to the DCF engine, apply 
this gate sequence:

${(() => {
  // CALC-8b-r1 (attention-dilution guard, Tuning v2.59): only inject the mid-cycle anchors when a
  // DETERMINISTIC cyclical/commodity/capital-intensive signal is present — otherwise a non-cyclical
  // grower (e.g. AAPL) sees a growth-lag-biased mid-cycle number it is told to skip (Gate 0 skips
  // tech/staples/healthcare/financials). Errs toward SHOWING: any capital-heavy signal (incl. the
  // sector-independent oeAssetHeavySignature, which catches FMP-sector-liars like SUNB) keeps the
  // anchors in-frame so Gate 0 never fires without them.
  const secStr = ((deterministicData?.sector || "") + " " + (deterministicData?.industry || "")).toLowerCase();
  const cyclicalSectorHit = [
    "energy", "basic material", "materials", "semiconductor", "mining", "metals",
    "defense", "aerospace", "industrial", "oil", "gas", "chemical", "steel", "commodit"
  ].some((k) => secStr.includes(k));
  const anchorsApplicable =
    cyclicalSectorHit ||
    deterministicData?.isCapitalHeavy === true ||
    deterministicData?.ownerEarningsData?.oeAssetHeavySignature === true;
  return anchorsApplicable
    ? `DETERMINISTIC NORMALIZATION ANCHORS (engine-computed — you do NOT average or reconstruct these):
  Mid-cycle average OE (${deterministicData?.normalizationAnchors?.midCycleYearsUsed ?? 0} yrs): $${deterministicData?.normalizationAnchors?.midCycleAvgOe != null ? deterministicData.normalizationAnchors.midCycleAvgOe.toFixed(0) : "unavailable"}M
  Median operating margin: ${deterministicData?.normalizationAnchors?.medianOperatingMarginPct != null ? deterministicData.normalizationAnchors.medianOperatingMarginPct.toFixed(1) + "%" : "unavailable"}
  Method B candidate OE (median margin × trailing revenue + D&A − maint. capex): $${deterministicData?.normalizationAnchors?.methodBCandidateOe != null ? deterministicData.normalizationAnchors.methodBCandidateOe.toFixed(0) : "unavailable"}M
${deterministicData?.normalizationAnchors?.hasAnchors === false ? "  ANCHORS UNAVAILABLE — fewer than 3 valid statement years. Methods A and B are unavailable; if normalization is required, proceed to Method C (peer anchor) with its confidence penalty.\n" : ""}`
    : "";
})()}
GATE 0 — CYCLICAL DISTORTION PRE-CHECK (cyclical/commodity sectors only):
This gate fires BEFORE the sign check. It catches both trough AND peak
distortions in sectors where trailing FCF is structurally unreliable as
a normalized base — even when positive.

Apply Gate 0 only if Agent A classified this business as cyclical,
commodity, or capital-intensive (energy, basic materials, semiconductors,
mining, defense, heavy industrials). Skip directly to Gate 1 for
technology, consumer staples, healthcare, and financial services.

Use the pre-computed FCF yield below (do NOT recompute it):
  Base Case OE: $${deterministicData?.ownerEarningsData?.ownersEarnings != null ? deterministicData.ownerEarningsData.ownersEarnings.toFixed(0) : "unavailable"}M
  Market Cap:   $${deterministicData?.marketCap != null ? deterministicData.marketCap.toFixed(0) : "unavailable"}M
  FCF Yield (computed): ${deterministicData?.ownerEarningsData?.ownersEarnings != null && deterministicData?.marketCap != null && deterministicData.marketCap > 0 ? ((deterministicData.ownerEarningsData.ownersEarnings / deterministicData.marketCap) * 100).toFixed(2) : "unavailable"}%

  FCF YIELD < 1.5% (and positive):
  → FCF is positive but cyclically depressed — near-zero earnings
    in an operating business. This is a trough signal even without
    a negative sign. Using this figure as a normalized base will
    produce a severely understated IV.
  → Do NOT pass trailing TTM as normalized base.
  → Proceed to Gate 2 for trough vs. structural classification.
  → Label: "Gate 0 Trough: FCF yield below 1.5% in cyclical sector.
    Proceeding to mid-cycle normalization."

  FCF YIELD > 8%:
  → FCF is likely cyclically elevated — peak earnings in a commodity
    or capex cycle. Using peak FCF as the DCF base overstates IV and
    produces misleadingly high margin-of-safety signals.
  → Use the engine's Mid-cycle average OE above as the normalized base
    if it is materially lower than trailing TTM. Do NOT compute your
    own average.
  → Label: "Gate 0 Peak: FCF yield above 8% in cyclical sector.
    Normalizing to engine mid-cycle average (${deterministicData?.normalizationAnchors?.midCycleYearsUsed ?? 0}-yr)."
  → State explicitly: the stock may appear undervalued on trailing
    FCF but IV has been normalized to mid-cycle.

  FCF YIELD 1.5%–8%:
  → No Gate 0 distortion detected. Proceed to Gate 1.

GATE 1 — SIGN CHECK:
Is the current trailing twelve-month owner earnings figure negative?

  NO → Proceed normally per Rule 83 historical anchor.

  YES → Do NOT pass a negative figure as normalized owner earnings.
        A negative base fed into any growth-rate DCF produces a 
        sign-flipping cascade that renders all outputs meaningless.
        This is a fatal input error. Proceed to Gate 2.

GATE 2 — TROUGH vs. STRUCTURAL CLASSIFICATION:
Before selecting a normalization method, classify the cause of 
negative earnings using Agent D's Intelligence Brief:

  CYCLICAL TROUGH: Agent D confirms the negative margins are driven 
  by industry supply/demand imbalance, not permanent business 
  deterioration. Indicators: industry cycle language, peer margins 
  also compressed, historical positive earnings in prior cycles.
  → Use Method A (engine Mid-cycle average OE) as primary normalization anchor.
  → Label: "Cyclical Trough — Mid-Cycle Normalized Owner Earnings"
  → Do NOT use structural decline language in DCF assumptions.

  STRUCTURAL DECLINE: Agent D confirms permanent secular erosion —
  technology substitution, permanent market share loss, or business
  model obsolescence. Indicators: multi-year margin compression 
  independent of cycle, confirmed technology displacement.
  → Use Method B (Mid-Cycle Margin) anchored to the last positive 
    cycle peak, discounted for structural deterioration.
  → Label: "Structural Decline — Peak-Cycle Discounted Owner Earnings"

  SPINOFF TROUGH: Entity is Type 5 AND Agent D confirms growth is 
  real but current margins are suppressed by separation costs, 
  stranded overhead, or industry cycle timing. This is the most 
  common false-positive for structural decline.
  → Use Method B (Mid-Cycle Margin) anchored to parent segment 
    history adjusted for standalone cost structure.
  → Label: "Spinoff Trough — Standalone Normalized Owner Earnings"
  → CRITICAL: Do NOT classify as structural decline. The business 
    is not in secular decay — it is in early standalone maturation 
    during a cyclical trough. These are two separate phenomena.

  AMBIGUOUS: Cannot determine from available data.
  → Use Method A with a -10 confidence penalty.
  → Flag in output: "Trough classification ambiguous — normalization
    method conservative. Re-run when cycle position clarifies."

GATE 3 — NORMALIZATION METHODS:

  Method A — Engine Mid-Cycle Average: Adopt the Mid-cycle average OE
  from the anchors above. If positive, use as normalized base. You do
  not compute this figure.

  Method B — Mid-Cycle Margin: Adopt the Method B candidate OE from
  the anchors above (engine-computed from the median operating margin).
  Your judgment: whether a structural-deterioration discount applies
  per the Gate 2 classification, expressed as a stated percentage
  haircut with rationale — the engine applies the haircut you state.

  Method C — Peer Anchor (last resort): Use peer mid-cycle margins 
  as proxy if A and B cannot produce positive figure. Apply -15 
  confidence penalty. Label: "Peer-Anchored Normalized Owner Earnings"

GATE 4 — ZERO FLOOR (last resort only):
If all methods fail to produce a positive figure:
  - Floor at smallest supportable positive value (minimum $1M)
  - Apply -20 confidence penalty
  - Cap verdict at PASS or AVOID
  - Mandatory disclosure: "Owner earnings floored at minimum positive 
    value. IV is theoretical floor only. Treat with extreme caution."

LABELING REQUIREMENT (mandatory in all cases):
Mathematical Engine output MUST show both:
  (a) Raw current TTM owner earnings
  (b) Normalized figure passed to DCF engine
  (c) Method used and classification rationale

${
  deterministicData?.isType4Financial
    ? `
CAPEX BANDS — SUPPRESSED FOR TYPE 4:
The synthetic owner earnings figures are based on consolidated net income
and are NOT valid for this entity. Consolidated net income for financial
entities includes investment gains, deposit spread income, and trading
revenue that are accounted for separately in the NAV analysis above.
DO NOT use these figures as your normalizedOwnerEarnings anchor:
  - Consolidated OE (invalid for Type 4): $${deterministicData.ownerEarningsData.ownersEarnings.toFixed(0)}M
Use the operating subsidiary earnings approach defined in the Entity
Type Gate above. This figure is provided for reference only.
`
    : `
MAINTENANCE CAPEX CONFIDENCE BANDS:
The deterministic engine has pre-calculated three owner earnings figures:
  - Base Case OE: $${deterministicData.ownerEarningsData.ownersEarnings.toFixed(0)}M
    (maintenance capex at ${(deterministicData.ownerEarningsData.maintenanceCapexRatioUsed * 100).toFixed(0)}% of total CapEx)
  - Optimistic OE: $${deterministicData.ownerEarningsData.ownersEarningsLow.toFixed(0)}M
    (maintenance capex at 60% of total CapEx)
  - Stress OE: $${deterministicData.ownerEarningsData.ownersEarningsHigh.toFixed(0)}M
    (maintenance capex at ${deterministicData.ownerEarningsData.isCapitalHeavy ? "100%" : "85%"} of total CapEx)
${
  deterministicData.ownerEarningsData.hasFmpCrossCheck
    ? `  - FMP Reported Owner Earnings (deterministic cross-check): $${deterministicData.ownerEarningsData.fmpOwnerEarnings.toFixed(0)}M
    Divergence from synthetic Base Case: ${deterministicData.ownerEarningsData.fmpVsSyntheticDivergencePct.toFixed(0)}%.
    This is FMP's independently pre-calculated figure, provided as a data-quality cross-check.
    If |divergence| exceeds 15%, note it explicitly in the factSheet as an owner-earnings
    data-quality flag (positive if synthetic is conservative / lower; investigate if synthetic
    materially exceeds FMP). Do NOT substitute this figure for the locked Base Case anchor.`
    : ""
}

PRE-FCF MODEL LIMITATION CHECK (runs before the lock):
Use the pre-computed FCF yield from the figures above (do NOT recompute it).

If FCF yield < 0.5%:
  → This business is effectively pre-FCF or near-zero FCF relative
    to its market capitalization. The DCF model will produce an IV
    that is a fraction of market price, not because the stock is
    overvalued but because FCF does not yet reflect the business's
    true earnings power or optionality value.
  → You MUST include this exact text at the START of your
    assumptionRationales.normalizedOwnerEarnings field:
    "MODEL LIMITATION: FCF yield below 0.5%. DCF output is
    speculative and IV should not be taken as authoritative.
    This business is priced on optionality, narrative, or future
    earnings potential — not current cash generation. The Oracle
    must disclose this limitation prominently and should not
    anchor the verdict to the margin of safety figure."
  → Still complete the DCF with your best assumptions. But the
    warning above is mandatory and must appear first in the rationale.

normalizedOwnerEarnings: DEFAULT LOCKED to Base Case OE: $${deterministicData.ownerEarningsData.ownersEarnings.toFixed(0)}M.
You MAY deviate from this figure ONLY under one of these three conditions:
  (a) CYCLICAL TROUGH: Agent D confirms earnings are cyclically depressed.
      → Use 10-year average owner earnings if higher than current run-rate.
      → State: "Cyclical deviation: [reason]. Using [figure]M instead of locked base."
  (b) STRUCTURAL DECLINE: Agent D confirms permanent secular deterioration.
      → Use Stress OE: $${deterministicData.ownerEarningsData.ownersEarningsHigh.toFixed(0)}M.
      → State: "Structural deviation: [reason]. Using stress OE."
  (c) TYPE 5 SPINOFF: Entity is Type 5 with TTM FCF available.
      → Use TTM FCF anchor per spinoff protocol.
      → State: "Spinoff deviation: using TTM FCF anchor."
If none of (a), (b), (c) apply: use the locked base figure. No other deviations permitted.
`
}

2. year1to5GrowthRate: Durable growth rate for years 1-5. 
   Must be anchored to the trailing 3-year historical trend 
   per Rule 83. If the Historian flagged a TREND WARNING, 
   apply a conservative anchor.

   GROWTH DISCIPLINE RULE: If your base case year1to5GrowthRate
   exceeds 0.25 (25%), you MUST populate the growthCapJustification
   field in assumptionRationales with a specific structural reason.
   The justification must:
     (a) Name the specific demand driver sustaining above-25% growth
         (e.g. "AI hyperscaler capex cycle driving 40%+ data center
         revenue CAGR with 3-year order backlog visibility")
     (b) Explain why this rate is durable for the full 5-year window,
         not just the current cycle peak
     (c) Acknowledge the specific risk that would cause this rate to
         disappoint

   Generic statements ("strong growth momentum", "expanding TAM",
   "market leadership") do NOT satisfy this requirement and will be
   treated as an unfiled justification by the code enforcement layer.

   If your base case year1to5GrowthRate is ≤ 0.25, set
   growthCapJustification to an empty string "".

   GROWTH RATE ANCHOR (deterministic — from filed financials):
     Revenue 5-yr CAGR: ${deterministicData?.historicalMetrics?.revenueGrowth5Yr != null ? (deterministicData.historicalMetrics.revenueGrowth5Yr * 100).toFixed(1) + "%" : "unavailable"}
     FCF 3-yr avg growth: ${deterministicData?.historicalMetrics?.avgFcfGrowth3Yr != null ? (deterministicData.historicalMetrics.avgFcfGrowth3Yr * 100).toFixed(1) + "%" : "unavailable"}

   Your year1to5GrowthRate MUST be anchored to these figures. Specifically:
     - You may NOT set year1to5GrowthRate above the higher of these two figures
       without naming a specific forward catalyst (new product, market expansion,
       acquisition accretion) that justifies above-historical growth.
     - You MAY set it below these figures if Agent D's brief identifies structural
       headwinds that will compress future growth below the historical rate.
     - If both figures are negative, your base case growth may still be positive
       if Agent D identifies a genuine cyclical recovery — but state this explicitly.
     ${isIpoOrigin ? '- For Type 5 recent IPOs: the multi-year CAGR is genuine but short (S-1 restated); weight the TTM standalone trajectory and treat pre-IPO growth as unproven at scale.' : '- For Type 5 spinoffs: ignore the 5-yr CAGR (parent-contaminated).'}
       Use TTM standalone revenue trajectory instead.

TYPE 4 GROWTH DISCIPLINE RULE (applies only when deterministicData.isType4Financial is true):

For financial entities subject to regulatory capital requirements, durable
organic growth is structurally constrained. The following caps are mandatory
and override the standard Rule 83 anchor when they produce a lower result:

Active sub-type: ${deterministicData?.type4SubType || "FINANCIAL_OTHER"}
Apply the cap that matches the sub-type:
  BANK (G-SIB, total assets > $500B): ≤ 6.5%
  BANK (regional, total assets $50B–$500B): ≤ 7.5%
  INSURER or INSURANCE_CONGLOMERATE: ≤ 8.5%
  ASSET_MANAGER: ≤ 10% (fee revenue is less capital-constrained)
  FINANCIAL_OTHER: apply BANK G-SIB cap as conservative default

G-SIB BANKS (total assets > $500B): base case year1to5GrowthRate ≤ GDP + 200bps.
At the current 30Y Treasury yield of ${deterministicData.thirtyYearTreasuryYield?.toFixed(2) || "5.00"}%,
the implied nominal GDP growth is approximately 4.0-4.5%. The hard cap for
a G-SIB bank base case is therefore 6.5% (4.5% GDP + 200bps regulatory drag buffer).

REGIONAL BANKS (total assets $50B-$500B): base case cap is GDP + 300bps = 7.5%.

INSURANCE CONGLOMERATES (float-based, e.g. BRK, MKL): base case cap is
GDP + 400bps = 8.5%, reflecting the optionality of float deployment into
opportunistic investments during market dislocations.

RATIONALE REQUIREMENT: If your base case year1to5GrowthRate exceeds the
applicable cap, you MUST either (a) reduce it to the cap and state why the
cap applies, OR (b) name a specific non-interest income catalyst — wealth
management AUM growth, fee-based CIB revenue, insurance premium expansion —
that structurally justifies above-cap growth, size the catalyst as a
percentage of total revenue, and confirm it is already generating revenue
(not narrative). Generic statements like "digital banking expansion" or
"market share gains" do NOT satisfy this requirement.

A base case growth rate that exceeds the cap without a named, sized catalyst
will be treated as a Rule 83 violation by the code enforcement layer.

   ABSOLUTE CEILING: 0.50 (50%). No base case growth above this
   value will be accepted regardless of justification. If your
   analysis supports above-50% growth, model it in the bull case only.

3. year6to10GrowthRate: Growth rate for years 6-10. 
   Assess the business's Reinvestment Runway — how long it can sustain high ROIC on incremental capital.
   If HIGH (TAM >3x revenue, expanding/stable ROIC), keep growth durable.
   If MEDIUM (TAM 1.5-3x revenue, or growth is acquisition-dependent), classify runway as MEDIUM.
   If LOW (TAM <1.5x revenue, compressing ROIC, or structural decline), classify runway as LOW.
   Emit your assessment as mathReinvestmentRunway: "HIGH" | "MEDIUM" | "LOW".
   Emit year6to10GrowthRate UNADJUSTED for runway — the engine applies the
   deduction deterministically (MEDIUM: −50bps, LOW: −100bps, floored at the
   terminal growth rate) to all scenarios. Do NOT subtract the basis points
   yourself; doing so would double-apply the penalty.

4. terminalGrowthRate: Long-run terminal growth. Hard caps 
   apply: Mega-cap (>$100B) = 2.5%, Large = 3.0%, 
   Mid/Small = 3.5%.

─────────────────────────────────────────────────────────────
CASH FLOW METHODOLOGY — LEVERED VS. UNLEVERED SELECTION

You must select the correct cash flow basis before projecting any figures. This selection governs the entire DCF and cannot be changed mid-analysis.

RULE CF-1: ENTITY TYPE GATE
- TYPE 4 (Financial / REIT): Continue using existing Owner Earnings methodology. No change.
- TYPE 5 (Spin-off): Continue using existing normalized Owner Earnings methodology. No change.
- ALL OTHER TYPES: Apply the sector routing below.

RULE CF-2: SECTOR ROUTING — CAPITAL-INTENSIVE INCUMBENTS
For companies in the following sectors, you MUST select the FCFF basis (Unlevered Free Cash Flow to Firm); the engine computes it:
  - Telecommunications (wireless carriers, wireline, cable)
  - Utilities (electric, gas, water)
  - Pipelines and Midstream Energy
  - Railroads and Transportation Infrastructure
  - Tower/Cell Site REITs (if classified as non-Type-4)
If the deterministic FCFF is marked UNAVAILABLE below, route to Owner Earnings (CF-3) even for these sectors, and record the fallback in sectorRoutingReason.

DETERMINISTIC FCFF (engine-computed — you do NOT compute FCFF):
  FCFF Base (avg of ${deterministicData?.fcffData?.yearsUsed ?? 0} yrs): $${deterministicData?.fcffData?.fcffBase != null ? deterministicData.fcffData.fcffBase.toFixed(0) : "unavailable"}M
  FCFF Latest Year: $${deterministicData?.fcffData?.fcffLatest != null ? deterministicData.fcffData.fcffLatest.toFixed(0) : "unavailable"}M
  Effective Tax Rate used: ${deterministicData?.fcffData?.effectiveTaxRate != null ? (deterministicData.fcffData.effectiveTaxRate * 100).toFixed(1) + "%" : "unavailable"}
${deterministicData?.fcffData?.hasFcff === false ? "  FCFF UNAVAILABLE — engine could not compute FCFF from statements. Fall back to Owner Earnings basis (CF-3) and state this in sectorRoutingReason." : ""}

If you select CASH_FLOW_BASIS = FCFF, your normalizedOwnerEarnings field is LOCKED to the FCFF Base figure above. You MAY deviate ONLY under the same conditions as the owner-earnings lock — (a) CYCLICAL TROUGH confirmed by Agent D (use the more representative of FCFF Base vs FCFF Latest and state why), or (b) STRUCTURAL DECLINE confirmed by Agent D (use the lower of the two and state why). State any deviation explicitly: "FCFF deviation: [reason]. Using [figure]M instead of locked FCFF base." No other deviations permitted. Never recompute FCFF from statement line items.

You must state which basis you selected in your output under a field labeled:
  CASH_FLOW_BASIS: [FCFF | Owner Earnings]
  SECTOR_ROUTING_REASON: [one sentence]

RULE CF-3: ALL OTHER SECTORS
For all other non-financial entity types, continue projecting Owner Earnings:
  Owner Earnings = Net Income + D&A − Capex ± Change in NWC

RULE CF-4: MATHEMATICAL CONSEQUENCE — DO NOT OVERRIDE
This selection has a downstream consequence in the DCF bridge that is enforced by the engine, not by you:
- If CASH_FLOW_BASIS = FCFF → the engine will subtract Net Debt from PV total to arrive at Equity Value. This is correct.
- If CASH_FLOW_BASIS = Owner Earnings → the engine treats positive Net Cash as an add-back, but treats Net Debt as already accounted for via interest expense.
You do not control the bridge math. Your only job is to select the correct basis and project the cash flows faithfully.

RULE CF-5: DISCLOSURE REQUIREMENT
In the Mathematician's narrative output (the section the Oracle reads), include one sentence acknowledging which basis was used and why, so the Oracle can reference it in the memo.
─────────────────────────────────────────────────────────────

5. wacc: Discount rate (Weighted Average Cost of Capital).
   You MUST calculate a BLENDED WACC reflecting the company's capital structure (Debt vs Equity mix). Do NOT just use Risk-Free Rate + Equity Risk Premium unless the company has zero debt.

   COMPONENT 1 — COST OF EQUITY (Ke):
   - Risk-Free Rate: LOCKED at exactly ${deterministicData.thirtyYearTreasuryYield.toFixed(2)}%.
   - Equity Risk Premium (ERP): Baseline is 5.5%. Adjust up to 6.5% for elevated risk.
   - Company Premium: Add 0-300bps for specific risks (e.g., transition, tech threat, regulation).
   - Ke = Risk-Free Rate + ERP + optional company premium.

   COMPONENT 2 — COST OF DEBT (Kd):
   - Estimate the after-tax cost of debt (typically 4-6% for investment grade, higher for high yield).

   COMPONENT 3 — CAPITAL STRUCTURE WEIGHTING:
   - Identify or estimate the Debt Weight (Wd) and Equity Weight (We) based on Market Cap vs Net Debt.
   - Blend them: WACC = (Ke * We) + (Kd * Wd).

   For highly levered, mature utilities and telecoms (e.g., T, VZ, CHTR), the heavy debt weighting (cheap capital) pulls the blended WACC down significantly (commonly 6.0% - 8.0%). Do NOT use Cost of Equity as the final WACC for levered companies; this double-penalizes them since Net Debt is subtracted at the end.
   
   You MUST state your final WACC build explicitly in your rationale, mentioning the blending:
   "Cost of Eq [Ke]% blended with Cost of Debt [Kd]% at [We]%/[Wd]% weighting = [WACC]%."

   Hard ceiling: WACC may not exceed ${(deterministicData.thirtyYearTreasuryYield + 11.5).toFixed(2)}%
   (reserved for distressed entities).

6. terminalMultiple: Exit multiple if using multiple method. 
   Must be defended against comparable businesses. For non-pure-software businesses (consumer, industrial, healthcare, retail, financial, commodity, conglomerate, REIT), terminal multiples above 22x require explicit defense: name the specific peer or historical precedent that supports it. Terminal multiples above 27x are NOT permitted for non-pure-software businesses under any circumstances. Pure-software and SaaS businesses are exempt from this ceiling but must still defend multiples above 30x.
   **TERMINAL MULTIPLE — PEER ANCHOR REQUIREMENT (ALL BUSINESSES):**

   Every terminal multiple you select — bear, base, and bull — must be explicitly 
   anchored to observable market data. You are not permitted to state a multiple 
   without naming the evidence behind it.

   Required format for your assumptionRationales.terminalMultiple field:

     "Base: [X]x — anchored to [named peer] at [Y]x [metric] (source: [observed]). Applied [premium] of [Z]x because [reason]. Bear: [A]x — [historical trough]. Bull: [B]x — [expansion scenario anchor]."

   EXAMPLES OF ACCEPTABLE ANCHORS:
   - "T trades at 15.5x EV/FCF, CHTR at 16.2x. VZ base case: 16x — in line with sector given comparable leverage. Bear: 12x (sector trough). Bull: 18x (convergence bundle)."
   - "MSFT at 32x, GOOGL at 24x — blended SaaS/search anchor 28x. VZ is not in this category and this example does not apply."
   - "Historical 10-year EV/FCF trough for US telecom incumbents: 12-14x (2020). Base case: 16x — going-concern premium above trough."

   CRITICAL MATH WARNING: MULTIPLE TYPE VS ENGINE LOGIC
   The DCF engine calculates Total Value (PV of Cash Flows + PV of Terminal Value) and then 
   SUBTRACTS Net Debt at the very end to arrive at Equity Value.
   Because Net Debt is subtracted, the Terminal Multiple MUST be an ENTERPRISE multiple 
   (like EV/Owner Earnings), not an Equity multiple (like P/FCF or P/E).

   P/FCF is an Equity multiple. For a highly levered business like Verizon, P/FCF might be 
   9x, but EV/FCF is ~16x. If you use 9x in the engine, it produces an Equity-level 
   terminal value, and then the engine subtracts the $181B Net Debt a SECOND time, 
   producing an absurd $3 intrinsic value. 

   Always anchor to ENTERPRISE multiples (EV/FCF or EV/Operating Cash Flow) or adjust 
   P/FCF upwards to reflect the enterprise capitalization. A mature telecom base case 
   multiple for an enterprise-level DCF is 14x–18x. 9x is not a conservative base case; 
   it is a math error.

   WHAT IS NOT ACCEPTABLE:
   - Stating a multiple without naming a peer, historical precedent, or index
   - Using a round number (15x, 20x) without explaining why that specific number
   - Citing a multiple that is inconsistent with the peer benchmarks the Historian 
     already fetched (those are in your context — use them)

   NOTE ON FLOORS AND CEILINGS:
   - The hard ceiling of 27x for non-pure-software businesses still applies.
   - There is NO minimum floor. If peer data and business analysis support a 
     base case multiple of 8x or 10x, use it and defend it. A well-defended 
     low multiple is analytically correct. An unsupported high multiple is not.
   - Historical trough multiples are valid BASE CASE anchors for businesses in 
     structural decline — do not automatically treat them as bear-case-only inputs.
     If the business is in secular decline (as flagged by structuralDeclineAssessment),
     the base case should reflect the cycle the business is actually in, not an 
     optimistic midpoint.

   MOAT-QUALITY STARTING ANCHORS (calibrated against 15-year historical market data):
   These are calibrated STARTING POINTS — not ceilings. Move them based on peer
   data in your context. They exist because the market pays structurally different
   multiples for different business quality tiers, and anchoring all businesses at
   15x systematically undervalues wide-moat compounders.

   • moatAnalysis.strength = "Exceptional" OR ("Wide" + archetype.category =
     "Dominant Leader" or "Compounder") + avgRoic5Yr > 0.25 (25%):
     → Base case starting anchor: 22–28x EV/FCF
     → Rationale: persistent pricing power + high-ROIC reinvestment commands
       a durable premium. Market has consistently priced these businesses at
       this range regardless of near-term FCF fluctuations. You must still
       name a peer anchor — do not assert 25x without citing one.

   • moatAnalysis.strength = "Wide" + archetype.category = "Compounder" or
     "Dominant Leader" + avgRoic5Yr 0.15–0.25 (15–25%):
     → Base case starting anchor: 17–22x EV/FCF
     → Rationale: above-cost-of-capital reinvestment with durable competitive
       position justifies a quality premium above the commodity baseline.

   • moatAnalysis.strength = "Narrow" + stable FCF + moderate growth (5–15%):
     → Base case starting anchor: 14–17x EV/FCF
     → This is the empirically accurate zone for most businesses the Oracle
       analyzes. The 15x default performs well here.

   • moatAnalysis.strength = "Wide" or "Narrow" + archetype.category =
     "YIELD UTILITY / BOND PROXY" + low growth (0–5%) + stable/predictable FCF:
     → Base case starting anchor: 16–20x EV/FCF
     → Rationale: The market prices durable, low-growth businesses with
       predictable cash flows as bond proxies — not growth compounders.
       Stable FCF with pricing power (consumer staples, tobacco, mature
       healthcare) commands a yield premium above the commodity baseline
       even at 2–4% growth. Empirical data: KO, PEP, PM, JNJ have
       traded at 17–27x EV/FCF historically. The 14–17x Narrow+stable
       tier understates this premium. Use this tier when the archetype
       is explicitly "YIELD UTILITY / BOND PROXY" regardless of moat width.
       You must still name a peer anchor.

   • moatAnalysis.strength = "None" or "Thin" + cyclical/commodity FCF +
     capital-intensive sector:
     → Base case starting anchor: 10–13x EV/FCF
     → Rationale: no pricing power premium, multiple compresses at cycle
       troughs. Using 15x for cyclicals produces misleading undervaluation
       signals because trailing FCF is not normalized.

   CRITICAL: These anchors do NOT override the peer benchmark requirement.
   You still MUST name a specific peer or historical precedent. These
   anchors tell you WHERE TO LOOK in the range — they do not substitute
   for named evidence.

7. netCash: ${
    deterministicData?.isType4Financial
      ? `
   TYPE 4 CRITICAL OVERRIDE — NET CASH:
   For this financial entity, the deterministicData.netDebt field
   (currently ${deterministicData?.netDebt?.toFixed(0) ?? "unknown"}M) is
   NOT a valid input for this assumption. Here is why:

   For banks: FMP reports cash reserves (~$500-900B for large banks) and
   short-term securities as cash equivalents, while bonded debt is only
   ~$200-400B. This produces a large negative netDebt that has nothing to
   do with shareholder-accessible cash — it reflects the bank's balance
   sheet structure, not excess capital.

   For insurance conglomerates: FMP's longTermInvestments includes the
   full equity and bond portfolio ($300B+ for BRK), which has already
   been valued in the NAV analysis above. Adding it again as netCash
   double-counts it in the DCF equity bridge.

   INSTRUCTION: Set netCash to ZERO (0) for all Type 4 entities.
   The NAV analysis (float premium, investment portfolio valuation,
   operating business SOTP) captures asset value correctly. The
   DCF bridge's netCash component must be zero to avoid double-counting.

   If the entity holds genuinely surplus unrestricted cash at the
   holding company level that was NOT captured in the NAV steps
   (e.g., BRK's $30B T-bill reserve held explicitly as dry powder),
   you may add that specific figure instead of zero and state the source.
   Default to zero when uncertain.

   Set cashHaircutPercent to 0.0.
   `
      : `LOCKED. Use exactly ${(-(deterministicData?.netDebt ?? 0)).toFixed(0)}M.
   (Negative value = net debt position, which is correct for highly levered companies.)
   Sourced from most recent balance sheet: totalDebt − cash. Do not recalculate.
   cashHaircutPercent: 0.0 for US-domiciled entities unless you identify a specific
   repatriation barrier — state it explicitly if non-zero.`
  }

8. cashHaircutPercent: Repatriation or friction discount 
   applied to cash per Rule 98. 0.0 for US-domiciled 
   companies with no friction. Must be explicitly stated 
   even if zero.

9. sharesOutstanding: LOCKED — do not calculate or estimate.
   Use this exact figure: ${deterministicData.sharesOutstanding.toFixed(0)}M shares.
   This value was sourced from the most recent filed income statement
   (weightedAverageShsOut from FMP stable/income-statement) and is the
   authoritative filing-sourced share count. You MUST use this figure exactly
   as provided. Do not substitute a different source, do not round differently,
   do not use float-adjusted or treasury-adjusted variants.
   This is not an assumption — it is a deterministic input.

10. useTerminalMultiple: Determines terminal value method.

    MANDATORY SELECTION RULES — follow in order:

    (a) If the entity is classified as Type 4 Financial: useTerminalMultiple = true.
        Use P/TBV multiple per the Type 4 protocol above. No discretion.

    (b) If the base year1to5GrowthRate is ≤ 3.0% AND the business operates
        in a mature, capital-intensive sector (telecom, utilities, industrials,
        consumer staples, legacy media): useTerminalMultiple = true.
        Use an exit multiple anchored to the sector's historical FCF or EBITDA
        trading range. Rationale: GGM denominator instability at low growth
        rates produces terminal values that are unreliable and highly sensitive
        to small WACC assumption changes.

    (c) If the base year1to5GrowthRate is ≥ 8.0% AND the business is a
        software, SaaS, or high-margin platform, apply the following
        sub-rules in order:

        (c)(i) WIDE-MOAT COMPOUNDER EXCEPTION: If moatAnalysis.strength is
               "Wide" or "Exceptional" AND archetype.category is "Dominant
               Leader" or "Compounder": useTerminalMultiple = true.
               Select your terminal multiple from the Moat-Quality Starting
               Anchors table above (section 6). You MUST name a peer anchor
               as required by that table. Do NOT use GGM.
               Rationale: GGM with a 2.5% perpetual growth cap at a ~10%
               WACC produces a ~13x multiplier — the correct range for a
               narrow-moat business. A wide-moat compounder with enduring
               pricing power commands 17-28x in the market. The anchor
               table captures this premium; GGM does not.

        (c)(ii) All other high-growth platforms (narrow or no moat, or
                Challenger / Speculative Growth archetypes):
                useTerminalMultiple = false. Use Gordon Growth Model.
                Rationale: high-growth businesses without proven durable
                moats are better valued on normalized long-run cash flow.
                Exit multiples may inflate terminal values for businesses
                whose competitive advantages are not yet confirmed durable.

    (d) All other cases: use your judgment and state the reason explicitly.
        Default toward useTerminalMultiple = true when in doubt.

    State your selection and the rule letter that applies (a/b/c/d or
    c(i)/c(ii)).

═══════════════════════════════════════════════════════════════
SCENARIO ENGINE REQUIREMENTS (MANDATORY — NON-NEGOTIABLE)
═══════════════════════════════════════════════════════════════

You MUST produce three structurally distinct scenarios. A scenario is NOT 
distinct if it only applies a small arithmetic adjustment to the base case. 
Each scenario must represent a meaningfully different outcome for the 
business, grounded in specific real-world mechanisms.

─────────────────────────────────────────────────────────────
BEAR CASE — Required Construction Logic
─────────────────────────────────────────────────────────────

The Bear Case MUST model simultaneous multiple contraction AND growth 
disappointment. It is NOT sufficient to reduce growth alone. The market 
does not stay patient with a premium multiple while fundamentals deteriorate.

STRUCTURAL RULES FOR BEAR CASE (all three are mandatory):

Rule BEAR-1 — Double Compression:
  bearOverrides.year1to5GrowthRate must be ≤ base × 0.60 (at least 40% below base).
  This models the growth disappointment leg.
  Example: base = 12% → bear max = 7.2%

Rule BEAR-2 — Multiple Contraction to Historical Trough:
  bearOverrides.terminalMultiple must be ≤ base × 0.65 (at least 35% below base).
  This models the re-rating leg. Use the company's historical trough multiple 
  OR the sector trough multiple, whichever is lower, as your anchor. 
  State the historical trough source in your rationale.
  Example: base = 25x → bear max = 16.25x (you may go lower if history supports it)

Rule BEAR-3 — Bear Case Narrative Must Be Specific:
  The bearCaseNarrative in your assumptionRationales MUST describe:
  (a) The specific mechanism causing growth to disappoint (e.g., "App Store regulatory loss of 30% take rate")
  (b) The specific mechanism causing the multiple to contract (e.g., "Market re-rates to mature industrial multiple as Services growth normalizes")
  Generic statements like "growth slows" or "market declines" are FORBIDDEN.

Rule BEAR-4 (Type 4 only) — Bear Case TBV Must Reflect Stressed ROTCE:
  If isType4Financial is true, the engine will compute bear Year-10 TBV
  deterministically from the lower bound of the historical ROTCE range.
  Your ONLY job is to select the bear P/TBV multiple (terminalMultiple)
  and to narrate the stressed-ROTCE reasoning in bearCaseNarrative. State the bear
  ROTCE that the engine will use (the historical trough).
  Example: "Bear ROTCE of 8.5% (historical trough vs base 11.2%) applied to TBV growth..."

─────────────────────────────────────────────────────────────
BEAR CASE CALIBRATION GATE (runs after BEAR-1, BEAR-2, BEAR-3)
─────────────────────────────────────────────────────────────

After satisfying BEAR-1 and BEAR-2, apply this calibration check:

Step 1 — Identify the primary thesis killer mechanism from the
whatWouldBreakThisFast context provided. Classify it as one of:

  TYPE A — Revenue floor event: membership churn, subscriber loss,
  customer concentration departure, pricing power collapse.
  These directly compress the earnings base, not just growth.

  TYPE B — Market share structural loss: a competitor permanently
  captures a segment (e.g., Geico vs. AI-native insurers,
  BNSF vs. autonomous trucking, App Store vs. regulatory unbundling).
  These compress growth but leave the base largely intact.

  TYPE C — Multiple re-rating only: regulatory, governance, or
  macro events that compress what the market will pay but do not
  impair the underlying business cash flows.

  TYPE D — No structural thesis killer identified. Standard BEAR-1
  floor applies.

Step 2 — Apply the calibration based on type:

  TYPE A: Bear growth must model the revenue floor scenario
  directly. If a 10% churn event on a membership business reduces
  the earnings base by 15%, your bear Y1-5 growth must reflect
  that compression — typically 0% to 2%, not base × 0.60.
  The BEAR-1 floor (base × 0.60) is the MAXIMUM for TYPE A,
  not the target. State the specific churn/loss rate assumed
  and show the earnings bridge.

  TYPE B: Bear growth should reflect the structural share loss
  trajectory. Model the market share endpoint at Year 5 and
  work backward to an annual growth drag. A 20% Geico market
  share loss over 5 years implies a specific per-year revenue
  headwind that must appear in the growth rate.
  The BEAR-1 floor is still the maximum — you may go further.

  TYPE C: BEAR-1 floor applies as the target range.
  Bear growth = base × 0.55 to 0.60.
  The compression story lives in the multiple, not the growth.

  TYPE D: BEAR-1 floor applies as the target.
  Bear growth = base × 0.58 to 0.60.

Step 3 — State the TYPE classification and the specific mechanism
in your bearCaseNarrative. This is mandatory. A bear case that
does not identify its TYPE is a Rule BEAR-3 violation.

Example TYPE A output (COST membership churn):
  TYPE A identified: 10% membership churn reduces fee income
  by ~$4.5B, compressing normalized earnings from $8.09B to
  ~$6.8B. Y1-5 growth modeled at 1.5% reflecting the earnings
  base reset, not a growth rate applied to the pre-churn base.
  This produces a bear growth of 1.5% vs. a BEAR-1 floor of
  5.4% — floor is not the constraint here, scenario severity is.

─────────────────────────────────────────────────────────────
BASE CASE — Existing Rules Apply (no change)
─────────────────────────────────────────────────────────────

Base case continues to follow Rule 83 (anchor to 3-year historical trend) 
and Rule 49 (within 200-300bps of market-implied growth). No change.

─────────────────────────────────────────────────────────────
BULL CASE — Required Construction Logic
─────────────────────────────────────────────────────────────

The Bull Case MUST model a specific value-creating catalyst, not just 
"growth is better." The multiple expansion must be justified by a 
structural improvement in business quality, not just optimism.

STRUCTURAL RULES FOR BULL CASE (all three are mandatory):

Rule BULL-1 — Genuine Growth Acceleration:
  bullOverrides.year1to5GrowthRate must be ≥ base × 1.20 (at least 20% above base).
  Example: base = 12% → bull min = 14.4%

Rule BULL-2 — Multiple Premium Must Be Earned:
  bullOverrides.terminalMultiple must be ≥ base × 1.15 (at least 15% above base).
  The higher multiple must be anchored to a specific reason the market would 
  re-rate the business upward (e.g., new segment reaching 40% of revenue and 
  commanding a SaaS multiple).
  Example: base = 25x → bull min = 28.75x

Rule BULL-3 — Bull Case Narrative Must Name the Catalyst:
  The bullCaseNarrative in your assumptionRationales MUST name:
  (a) The specific catalyst driving growth acceleration (e.g., "AI hardware supercycle drives Y1-5 iPhone replacement at 18% CAGR")
  (b) Why that catalyst justifies a higher terminal multiple (e.g., "Services reaching 35% of revenue shifts blended multiple toward software comps at 30x")
  "Better execution" or "favorable macro" are NOT valid catalysts.

Rule BULL-3a — Pure-Software Multiple Anchor (mandatory for SaaS, cloud, and platform businesses):

For businesses classified as pure-software, SaaS, or cloud platform (identified by archetype or sector), the bull case terminal multiple MUST be anchored to a named comparable. Generic multiples are not permitted.

Required format for bullCaseNarrative when Rule BULL-3a applies:
  "Bull multiple of [Xx] anchored to [named peer] at [Xx] as of [period], justified by [specific structural reason — e.g., 'Services reaching 35% of revenue at 75% gross margin shifts blended multiple toward pure-SaaS comps']. Catalyst: [specific named event driving growth acceleration]."

BULL-3a applies when ALL of the following are true:
  (a) The business derives >50% of revenue from software subscriptions, cloud services, or platform fees
  (b) The base terminal multiple is above 20x
  (c) The bull terminal multiple is within 5x of the base multiple

If BULL-3a applies and no valid peer anchor can be named, the bull multiple must not exceed base × 1.20 (a more conservative ceiling than the standard base × 1.15 floor). A bull case with no named anchor is structurally weak and must be disclosed as such in the bullCaseNarrative.

Rule BULL-3b — Hardware-Software Hybrid Multiple Anchor (mandatory for mixed hardware/software ecosystems):

For businesses that derive meaningful revenue from both hardware/product sales AND a growing high-margin software or services segment, the bull terminal multiple is computed by the deterministic engine as a blended weighted average of your segment inputs. You do NOT perform the blend arithmetic. Your job is the four judgment inputs, emitted in the bullBlend object:

  1. servicesRevenueSharePct — Services/software share of revenue (0–100), sourced from segment disclosures.
  2. servicesAnchorMultiple — the Services segment multiple, anchored to a NAMED SaaS/subscription peer (e.g., "Adobe at 28x, Salesforce at 26x").
  3. hardwareRevenueSharePct — Hardware/product share of revenue (0–100). The two shares must sum to approximately 100.
  4. hardwareAnchorMultiple — the Hardware segment multiple, anchored to a NAMED hardware peer.
  Plus servicesAnchorPeer and hardwareAnchorPeer — the peer names and their multiples as evidence strings.

The engine computes: Bull multiple = (servicesShare × servicesAnchor) + (hardwareShare × hardwareAnchor), enforces the base × 1.15 floor, and writes the disclosure. Any bullOverrides.terminalMultiple you emit will be overwritten by the engine when bullBlend is present.

If BULL-3b applies but segment-level data is insufficient to source the four inputs, set bullBlend to null and state in bullCaseNarrative: "Insufficient segment-level data for blended multiple. Bull multiple capped at base × 1.15 pending disclosure of segment margins." The engine will cap the bull multiple at base × 1.15 in that case.

If BULL-3b does not apply (single-segment business), omit bullBlend or set it to null.

Rule BULL-4 (Type 4 only) — Bull Case TBV Must Reflect Improved ROTCE:
  If isType4Financial is true, the engine will compute bull Year-10 TBV
  deterministically from the upper bound of the historical ROTCE range.
  Your ONLY job is to select the bull P/TBV multiple (terminalMultiple)
  and to narrate the improved-ROTCE reasoning in bullCaseNarrative. State the bull
  ROTCE that the engine will use (the historical peak).
  Example: "Bull ROTCE of 13.5% (historical peak vs base 11.2%) driven by [catalyst]..."

─────────────────────────────────────────────────────────────
HARD TOKEN LIMITS — MANDATORY (violations will cause rejection)
─────────────────────────────────────────────────────────────

All assumptionRationales values MUST comply with these hard character limits.
Exceeding them is a structural error, not a style choice.

ASSUMPTION RATIONALES (normalizedOwnerEarnings, year1to5GrowthRate,
year6to10GrowthRate, terminalGrowthRate, wacc, terminalMultiple, netCash,
cashHaircutPercent, sharesOutstanding, useTerminalMultiple):
  → Maximum 40 words each. Strict format: "Source: [X]. Judgment applied: [Y]."
  → GOOD: "Source: FMP 3Y avg FCF $4.2B, run-rate $4.8B. Judgment applied: anchored to avg per Rule 5a."
  → BAD: "Given the company's strong performance, I have chosen to anchor to the three-year average."

NARRATIVE FIELDS (bearCaseNarrative, bullCaseNarrative):
  → Maximum 40 words each. Two sentences maximum.
  → Sentence 1: The specific mechanism causing growth to change.
  → Sentence 2: The specific mechanism causing the multiple to change.
  → Both mechanisms MUST name a concrete business event, not a market condition.

─────────────────────────────────────────────────────────────
OUTPUT FORMAT (unchanged structure, new fields added)
─────────────────────────────────────────────────────────────

Return a single JSON object with this structure. Escape all inner double quotes (\\") inside string values:

{
  "assumptionRationales": {
    "normalizedOwnerEarnings": "rationale",
    "methodBHaircutPct": null,
    "year1to5GrowthRate": "rationale",
    "growthCapJustification": "required if year1to5GrowthRate > 0.25, else empty string",
    "year6to10GrowthRate": "rationale",
    "mathReinvestmentRunway": "HIGH | MEDIUM | LOW — reinvestment runway class; engine applies bps deduction",
    "terminalGrowthRate": "rationale",
    "wacc": "rationale showing blended WACC build",
    "terminalMultiple": "rationale",
    "netCash": "rationale with source date",
    "cashHaircutPercent": "rationale",
    "sharesOutstanding": "rationale with SEC filing date",
    "useTerminalMultiple": "rationale",
    "sectorRoutingReason": "required sentence explaining cashFlowBasis choice",
    "bearCaseNarrative": "specific mechanism for growth miss + multiple contraction",
    "bullCaseNarrative": "specific catalyst for growth acceleration + multiple re-rating"
  },
  "base": { 
    ...DCFAssumptions,
    "cashFlowBasis": "[FCFF | OwnerEarnings]"
  },
  "bearOverrides": {
    ...Partial<DCFAssumptions>
  },
  "bullOverrides": {
    ...Partial<DCFAssumptions>
  },
  "bullBlend": {
    "servicesRevenueSharePct": <number 0-100>,
    "servicesAnchorMultiple": <number>,
    "servicesAnchorPeer": "string — named peer(s) with their multiple(s)",
    "hardwareRevenueSharePct": <number 0-100>,
    "hardwareAnchorMultiple": <number>,
    "hardwareAnchorPeer": "string — named peer(s) with their multiple(s)"
  }
}

SELF-CHECK BEFORE RETURNING (run this before finalizing your JSON):
□ If isType4Financial: is year1to5GrowthRate ≤ 6.5% for G-SIB banks,
  ≤ 7.5% for regional banks, or ≤ 8.5% for insurance conglomerates?
  If not, is a named, sized, revenue-generating catalyst documented?
□ Bear Y1-5 growth ≤ base × 0.60? If not, revise downward.
□ Bear terminal multiple ≤ base × 0.65? If not, revise downward.
□ Bull Y1-5 growth ≥ base × 1.20? If not, revise upward.
□ Bull terminal multiple ≥ base × 1.15? If not, revise upward.
□ Bear IRR stated? Is it below 7%? If above 7%, bear case is too mild — compress further.
□ If year1to5GrowthRate > 0.25, is growthCapJustification populated
  with a specific structural reason (not generic phrases)?
□ Is year1to5GrowthRate ≤ 0.50? If not, cap it at 0.50 and move the
  excess growth assumption to the bull case.
□ ARE ALL INNER DOUBLE QUOTES PROPERLY ESCAPED? Use \\" for any quotes inside strings.

Make sure your response contains ONLY the raw JSON object and nothing else.
□ Bear case TYPE identified (A, B, C, or D)?
□ If TYPE A or B: bear growth derived from scenario mechanism, not from base × 0.60?
□ bearCaseNarrative states the TYPE and the specific mechanism?
□ Bear growth still ≤ base × 0.60 (BEAR-1 remains the ceiling)?
□ Each narrative names a SPECIFIC mechanism, not a generic observation?
□ All assumptionRationales values within token limits?`;
  const processMathematicianOutput = (cleanedStr: string) => {
    console.log(
      `[TYPE4-CHECK] ${factSheet.ticker}: isType4Financial=${deterministicData?.isType4Financial ?? "UNDEFINED"}, ` +
        `entityType=${deterministicData?.entityType ?? "UNDEFINED"}, ` +
        // (Tuning v2.92) log hygiene: "Type 6" is the standard/default bucket
        // (financialEngine default param); spin-offs can resolve Type 6 with the flag
        // carried separately, so print it for classification auditability.
        `isRecentSpinOff=${(factSheet as any)?.isRecentSpinOff === true}`,
    );
    const parsed = JSON.parse(cleanedStr);

    if (
      deterministicData?.isType4Financial &&
      deterministicData?.type4Valuation
    ) {
      const type4 = deterministicData.type4Valuation;
      const retentionRate = deterministicData.retentionRate ?? 1;
      const shares = deterministicData.sharesOutstanding;
      const baseMultiple =
        parsed.base?.terminalMultiple || type4.ptbvMedian || 1;
      const bearMultiple =
        parsed.bearOverrides?.terminalMultiple ||
        type4.ptbvTrough ||
        baseMultiple;
      const bullMultiple =
        parsed.bullOverrides?.terminalMultiple ||
        type4.ptbvPeak ||
        baseMultiple;

      const computedBase = computeType4TerminalValue(
        type4.tbvPerShare,
        type4.rotceBase || 0,
        retentionRate,
        shares,
        baseMultiple,
      );

      const computedBear = computeType4TerminalValue(
        type4.tbvPerShare,
        type4.rotceTrough || 0,
        retentionRate,
        shares,
        bearMultiple,
      );

      const computedBull = computeType4TerminalValue(
        type4.tbvPerShare,
        type4.rotcePeak || 0,
        retentionRate,
        shares,
        bullMultiple,
      );

      if (parsed.base) {
        if (parsed.base.projectedYear10TBVTotal != null) {
          const diff =
            Math.abs(
              parsed.base.projectedYear10TBVTotal -
                computedBase.projectedYear10TBVTotal,
            ) / (computedBase.projectedYear10TBVTotal || 1);
          if (diff > 0.05) {
            console.warn(
              `[TYPE 4 MATH OVERRIDE] ${factSheet.ticker}: LLM produced base projectedYear10TBVTotal = ${parsed.base.projectedYear10TBVTotal}, replacing with deterministic engine value = ${computedBase.projectedYear10TBVTotal}.`,
            );
          }
        }
        parsed.base.projectedYear10TBVTotal =
          computedBase.projectedYear10TBVTotal;
        parsed.base.isType4Financial = true;
      }

      if (!parsed.bearOverrides) parsed.bearOverrides = {};
      if (parsed.bearOverrides.projectedYear10TBVTotal != null) {
        const diff =
          Math.abs(
            parsed.bearOverrides.projectedYear10TBVTotal -
              computedBear.projectedYear10TBVTotal,
          ) / (computedBear.projectedYear10TBVTotal || 1);
        if (diff > 0.05) {
          console.warn(
            `[TYPE 4 MATH OVERRIDE] ${factSheet.ticker}: LLM produced bear projectedYear10TBVTotal = ${parsed.bearOverrides.projectedYear10TBVTotal}, replacing with deterministic engine value = ${computedBear.projectedYear10TBVTotal}.`,
          );
        }
      }
      parsed.bearOverrides.projectedYear10TBVTotal =
        computedBear.projectedYear10TBVTotal;

      if (!parsed.bullOverrides) parsed.bullOverrides = {};
      if (parsed.bullOverrides.projectedYear10TBVTotal != null) {
        const diff =
          Math.abs(
            parsed.bullOverrides.projectedYear10TBVTotal -
              computedBull.projectedYear10TBVTotal,
          ) / (computedBull.projectedYear10TBVTotal || 1);
        if (diff > 0.05) {
          console.warn(
            `[TYPE 4 MATH OVERRIDE] ${factSheet.ticker}: LLM produced bull projectedYear10TBVTotal = ${parsed.bullOverrides.projectedYear10TBVTotal}, replacing with deterministic engine value = ${computedBull.projectedYear10TBVTotal}.`,
          );
        }
      }
      parsed.bullOverrides.projectedYear10TBVTotal =
        computedBull.projectedYear10TBVTotal;
    }

    // TERMINAL-METHOD-LOCK: Enforce useTerminalMultiple = true for low-growth,
    // capital-intensive businesses regardless of what the Mathematician returns.
    // The Mathematician prompt instructs this via rule (b), but the instruction
    // is occasionally ignored. This is the code-level backstop.
    const enforcedTerminalMultiple = (() => {
      const y15 = Number(parsed.base?.year1to5GrowthRate ?? 0);
      const sector = (deterministicData?.sector || "").toLowerCase();
      const industry = (deterministicData?.industry || "").toLowerCase();
      const isMatureCapIntensive =
        sector.includes("communication") ||
        sector.includes("utilities") ||
        sector.includes("energy") ||
        industry.includes("telecom") ||
        industry.includes("wireless") ||
        industry.includes("wireline");
      return y15 <= 0.03 && isMatureCapIntensive;
    })();

    if (
      enforcedTerminalMultiple &&
      parsed.base?.useTerminalMultiple === false
    ) {
      console.warn(
        `[TERMINAL-METHOD-LOCK] ${factSheet?.ticker || "UNKNOWN"}: ` +
          `Mathematician selected GGM (useTerminalMultiple=false) for a low-growth ` +
          `mature sector entity. Overriding to exit multiple method per rule (b). ` +
          `Terminal multiple will be derived from existing terminalMultiple field.`,
      );
      parsed.base = { ...parsed.base, useTerminalMultiple: true };
      if (parsed.bearOverrides)
        parsed.bearOverrides = {
          ...parsed.bearOverrides,
          useTerminalMultiple: true,
        };
      if (parsed.bullOverrides)
        parsed.bullOverrides = {
          ...parsed.bullOverrides,
          useTerminalMultiple: true,
        };
    }
    // ─── END TERMINAL-METHOD-LOCK ────────────────────────────────────────────────

    // WIDE-MOAT-COMPOUNDER-LOCK: Enforce useTerminalMultiple = true for wide-moat,
    // high-growth platform businesses. Counterpart to TERMINAL-METHOD-LOCK above.
    // Rule (c)(i) in the Mathematician prompt instructs this, but the model has a
    // strong prior that "high-growth platform = GGM." This is the code-level backstop.
    const enforcedWideMoatMultiple = (() => {
      const y15 = Number(parsed.base?.year1to5GrowthRate ?? 0);
      const moatStrength = ((factSheet as any)?.moatAnalysis?.strength || "").toLowerCase();
      const archetypeCategory = ((factSheet as any)?.archetype?.category || "").toLowerCase();
      const isWideMoat =
        moatStrength.includes("wide") || moatStrength.includes("exceptional");
      const isCompoundingArchetype =
        archetypeCategory.includes("dominant leader") ||
        archetypeCategory.includes("compounder");
      return y15 >= 0.08 && isWideMoat && isCompoundingArchetype;
    })();

    if (
      enforcedWideMoatMultiple &&
      parsed.base?.useTerminalMultiple === false
    ) {
      const existingMultiple = Number(parsed.base?.terminalMultiple ?? 0);
      console.warn(
        `[WIDE-MOAT-COMPOUNDER-LOCK] ${factSheet?.ticker || "UNKNOWN"}: ` +
          `Mathematician selected GGM (useTerminalMultiple=false) for a wide-moat ` +
          `compounder. Overriding to exit multiple method per rule (c)(i). ` +
          `Existing terminalMultiple field: ${existingMultiple.toFixed(1)}x. ` +
          (existingMultiple < 15
            ? `WARNING: terminalMultiple < 15x — model may have set this in GGM mode. ` +
              `Verify terminal multiple is sourced from Moat-Quality Anchor Table.`
            : `Terminal multiple appears anchor-table-sourced. Proceeding.`),
      );
      parsed.base = { ...parsed.base, useTerminalMultiple: true };
      if (parsed.bearOverrides)
        parsed.bearOverrides = {
          ...parsed.bearOverrides,
          useTerminalMultiple: true,
        };
      if (parsed.bullOverrides)
        parsed.bullOverrides = {
          ...parsed.bullOverrides,
          useTerminalMultiple: true,
        };
    }
    // ─── END WIDE-MOAT-COMPOUNDER-LOCK ──────────────────────────────────────────

    // ─── BEAR CASE FLOOR ENFORCEMENT (BEAR-1 and BEAR-2) ───────────────────
    // Rules live in the Mathematician prompt. This block is the code backstop.
    // If the model parks at or violates the floor, clamp and warn.
    const mathBase = parsed.base || {};
    const mathBear = parsed.bearOverrides || {};

    const baseGrowth = Number(mathBase.year1to5GrowthRate ?? 0);
    const bearGrowthRaw = Number(mathBear.year1to5GrowthRate ?? baseGrowth);
    const bearGrowthCeiling = baseGrowth * 0.6;
    if (bearGrowthRaw > bearGrowthCeiling) {
      console.warn(
        `[BEAR-1 Violation] ${factSheet.ticker}: bear growth ${bearGrowthRaw.toFixed(2)}% exceeds ` +
          `ceiling of base × 0.60 (${bearGrowthCeiling.toFixed(2)}%). Clamping.`,
      );
      parsed.bearOverrides.year1to5GrowthRate = bearGrowthCeiling;
    }

    const baseMultiple = Number(mathBase.terminalMultiple ?? 0);
    const bearMultipleRaw = Number(mathBear.terminalMultiple ?? baseMultiple);
    const bearMultipleCeiling = baseMultiple * 0.65;
    if (baseMultiple > 0 && bearMultipleRaw > bearMultipleCeiling) {
      console.warn(
        `[BEAR-2 Violation] ${factSheet.ticker}: bear terminal multiple ${bearMultipleRaw.toFixed(1)}x ` +
          `exceeds ceiling of base × 0.65 (${bearMultipleCeiling.toFixed(1)}x). Clamping.`,
      );
      parsed.bearOverrides.terminalMultiple = bearMultipleCeiling;
    }
    // ─── END BEAR CASE FLOOR ENFORCEMENT ─────────────────────────────────────
    // ─── BULL-3B-BLEND LOCK (CALC-4): blend arithmetic is TypeScript-owned ──
    // The Mathematician supplies segment shares + named anchor multiples only.
    // Engine computes the weighted average and enforces the base × 1.15 floor.
    {
      const bb = (parsed as any).bullBlend;
      const bullFloor = baseMultiple * 1.15;
      if (bb && typeof bb === "object") {
        const sShare = Number(bb.servicesRevenueSharePct);
        const hShare = Number(bb.hardwareRevenueSharePct);
        const sMult = Number(bb.servicesAnchorMultiple);
        const hMult = Number(bb.hardwareAnchorMultiple);
        const sharesValid =
          isFinite(sShare) && isFinite(hShare) &&
          sShare >= 0 && hShare >= 0 &&
          Math.abs(sShare + hShare - 100) <= 5;
        const multsValid = isFinite(sMult) && sMult > 0 && isFinite(hMult) && hMult > 0;
        if (sharesValid && multsValid && baseMultiple > 0) {
          const blended = (sShare / 100) * sMult + (hShare / 100) * hMult;
          const finalBull = Math.max(blended, bullFloor);
          if (!parsed.bullOverrides) parsed.bullOverrides = {};
          parsed.bullOverrides.terminalMultiple = Number(finalBull.toFixed(1));
          console.log(
            `[BULL-3B-BLEND] ${factSheet.ticker}: blended=(${sShare}%×${sMult}x)+(${hShare}%×${hMult}x)=${blended.toFixed(1)}x, ` +
              `floor=${bullFloor.toFixed(1)}x, final=${finalBull.toFixed(1)}x` +
              (finalBull > blended ? " (raised to floor)" : ""),
          );
          if (!parsed.assumptionRationales) parsed.assumptionRationales = {};
          (parsed.assumptionRationales as any).bullBlendDisclosure =
            `Bull multiple ${finalBull.toFixed(1)}x computed by engine as blended weighted average: ` +
            `Services (${sShare}% of revenue) at ${sMult}x anchored to ${bb.servicesAnchorPeer || "unnamed peer"} + ` +
            `Hardware (${hShare}% of revenue) at ${hMult}x anchored to ${bb.hardwareAnchorPeer || "unnamed peer"} = ${blended.toFixed(1)}x` +
            (finalBull > blended ? `; raised to floor of ${bullFloor.toFixed(1)}x (base × 1.15 minimum).` : ".");
        } else {
          console.warn(
            `[BULL-3B-BLEND] ${factSheet.ticker}: bullBlend present but invalid ` +
              `(sharesValid=${sharesValid}, multsValid=${multsValid}) — capping bull multiple at base × 1.15.`,
          );
          if (baseMultiple > 0) {
            if (!parsed.bullOverrides) parsed.bullOverrides = {};
            const existing = Number(parsed.bullOverrides.terminalMultiple ?? bullFloor);
            parsed.bullOverrides.terminalMultiple = Number(Math.min(existing, bullFloor).toFixed(1));
          }
        }
      }
    }
    // ─── END BULL-3B-BLEND LOCK ──────────────────────────────────────────────

    // ─── TERMINAL MULTIPLE CAP (non-pure-software) ───────────────────────────
    const isSoftwareBusiness = (() => {
      const archStr = (
        (typeof parsed.archetype === "string"
          ? parsed.archetype
          : parsed.archetype?.category) || ""
      ).toUpperCase();
      const descStr = (parsed.description || "").toLowerCase();
      return (
        archStr.includes("SAAS") ||
        archStr.includes("SOFTWARE") ||
        descStr.includes("pure-play software") ||
        descStr.includes("saas") ||
        descStr.includes("software-as-a-service")
      );
    })();

    const baseTerminalMultiple = Number(mathBase.terminalMultiple ?? 0);
    const HARD_CAP_NON_SOFTWARE = 27;

    if (
      !isSoftwareBusiness &&
      !deterministicData?.isType4Financial &&
      baseTerminalMultiple > HARD_CAP_NON_SOFTWARE
    ) {
      console.warn(
        `[TERMINAL-MULTIPLE-CAP Violation] ${factSheet.ticker}: base terminal multiple ` +
          `${baseTerminalMultiple.toFixed(1)}x exceeds hard cap of ${HARD_CAP_NON_SOFTWARE}x ` +
          `for non-pure-software businesses. Clamping to ${HARD_CAP_NON_SOFTWARE}x.`,
      );
      parsed.base.terminalMultiple = HARD_CAP_NON_SOFTWARE;
      // Recalculate bear and bull scenario multiples against the clamped base
      if (parsed.bearOverrides?.terminalMultiple) {
        parsed.bearOverrides.terminalMultiple = Math.min(
          Number(parsed.bearOverrides.terminalMultiple),
          HARD_CAP_NON_SOFTWARE * 0.65,
        );
      }
      if (parsed.bullOverrides?.terminalMultiple) {
        parsed.bullOverrides.terminalMultiple = Math.min(
          Number(parsed.bullOverrides.terminalMultiple),
          HARD_CAP_NON_SOFTWARE * 1.15,
        );
      }
    }
    // ─── END TERMINAL MULTIPLE CAP ───────────────────────────────────────────

    const scenarioViolations: string[] = [];

    // ─── BASE CASE GROWTH DISCIPLINE (RULE 83 / RULE 49) ────────────────────
    const ABSOLUTE_GROWTH_CEILING = 0.5; // 50% — catches broken outputs only
    const RULE_49_SPREAD_THRESHOLD = 0.2; // 20pp above market-implied triggers flag

    const rawBaseGrowth = Number(parsed.base?.year1to5GrowthRate ?? 0);
    const growthJustification = String(
      parsed.assumptionRationales?.growthCapJustification ?? "",
    ).trim();

    // Hard ceiling: clamp anything above 50% regardless of justification.
    if (rawBaseGrowth > ABSOLUTE_GROWTH_CEILING) {
      console.warn(
        `[GROWTH-CEILING] ${factSheet.ticker}: base Y1-5 growth ` +
          `${(rawBaseGrowth * 100).toFixed(1)}% exceeds absolute ceiling of ` +
          `${(ABSOLUTE_GROWTH_CEILING * 100).toFixed(0)}%. Clamping.`,
      );
      parsed.base.year1to5GrowthRate = ABSOLUTE_GROWTH_CEILING;
      // Recalculate bear/bull relative to clamped base
      const clampedBearCeiling = ABSOLUTE_GROWTH_CEILING * 0.6;
      if (
        Number(parsed.bearOverrides?.year1to5GrowthRate ?? 0) >
        clampedBearCeiling
      ) {
        parsed.bearOverrides.year1to5GrowthRate = clampedBearCeiling;
      }
      const clampedBullFloor = ABSOLUTE_GROWTH_CEILING * 1.2;
      if (
        Number(parsed.bullOverrides?.year1to5GrowthRate ?? 0) < clampedBullFloor
      ) {
        parsed.bullOverrides.year1to5GrowthRate = Math.min(
          clampedBullFloor,
          ABSOLUTE_GROWTH_CEILING * 1.4, // bull ceiling: 140% of clamped base
        );
      }
      scenarioViolations.push(
        `[GROWTH-CEILING] Base Y1-5 growth ${(rawBaseGrowth * 100).toFixed(1)}% ` +
          `exceeded 50% absolute ceiling. Clamped to 50%. ` +
          `Bear/bull recalculated relative to clamped base.`,
      );
    }

    // Missing justification flag: base growth > 25% with no substantive justification.
    const GENERIC_PHRASES = [
      "strong growth",
      "expanding tam",
      "market leadership",
      "strong momentum",
      "favorable macro",
      "industry tailwinds",
    ];
    const justificationIsGeneric =
      GENERIC_PHRASES.some((phrase) =>
        growthJustification.toLowerCase().includes(phrase),
      ) || growthJustification.length < 80;

    const currentBaseGrowth = Number(parsed.base?.year1to5GrowthRate ?? 0);
    if (
      currentBaseGrowth > 0.25 &&
      (growthJustification === "" || justificationIsGeneric)
    ) {
      console.warn(
        `[GROWTH-JUSTIFICATION] ${factSheet.ticker}: base Y1-5 growth ` +
          `${(currentBaseGrowth * 100).toFixed(1)}% exceeds 25% without a ` +
          `substantive growthCapJustification. Flagging for Oracle disclosure.`,
      );
      scenarioViolations.push(
        `[GROWTH-JUSTIFICATION] Base Y1-5 growth ${(currentBaseGrowth * 100).toFixed(1)}% ` +
          `exceeds 25% threshold. Justification was ${growthJustification === "" ? "absent" : "generic"}. ` +
          `Review Mathematician assumptions before relying on this IV.`,
      );
    }
    // ─── END BASE CASE GROWTH DISCIPLINE ─────────────────────────────────────

    // ─── TYPE 4 NET CASH ENFORCEMENT ────────────────────────────────────────
    // The Mathematician prompt instructs Type 4 entities to set netCash = 0.
    // The model consistently ignores this because its banking knowledge leads
    // it to set netCash to a large negative number (deposit liabilities),
    // producing a negative IV. Code enforcement is required — same pattern
    // as BEAR-1. Must run before runScenarios so the DCF engine receives 0.
    if (deterministicData?.isType4Financial) {
      const mathNetCash = Number(parsed.base?.netCash ?? 0);
      if (mathNetCash !== 0) {
        console.warn(
          `[TYPE4-NETCASH] ${factSheet.ticker}: Mathematician set netCash to ` +
            `${mathNetCash.toFixed(0)}M. Overriding to 0 for Type 4 entity — ` +
            `deposit liabilities and investment portfolios are not shareholder ` +
            `accessible cash and are accounted for separately in NAV analysis.`,
        );
        parsed.base.netCash = 0;
        // Bear and bull scenarios inherit netCash from base via spread operator
        // unless explicitly overridden. Zero out any explicit override too.
        if (parsed.bearOverrides?.netCash !== undefined) {
          parsed.bearOverrides.netCash = 0;
        }
        if (parsed.bullOverrides?.netCash !== undefined) {
          parsed.bullOverrides.netCash = 0;
        }
        // Also zero cashHaircutPercent — no haircut needed when cash is zero.
        parsed.base.cashHaircutPercent = 0;
      }
    }
    // ─── END TYPE 4 NET CASH ENFORCEMENT ─────────────────────────────────────

    // ─── TYPE 4 DCF ENGINE FLAGS ─────────────────────────────────────────────
    // Pass isType4Financial so dcfEngine.ts
    // can route to the P/TBV terminal value branch instead of earnings × multiple.
    if (deterministicData?.isType4Financial) {
      parsed.base.isType4Financial = true;

      if (!parsed.base?.projectedYear10TBVTotal) {
        // F13 measurement (Tuning v2.63 §39.2, ship-free precursor to the gated F13 fix):
        // record that the Type-4 Year-10 TBV projection nulled, PLUS which deterministic input
        // was missing (tbvPerShare source, ROTCE base, history sufficiency). This is the corpus
        // that decides whether the null is a population-wide input gap or insurer-specific, so
        // the F13 fix can target the real defect rather than guessing.
        const t4 = (deterministicData as any)?.type4Valuation || {};
        (deterministicData as any).type4TbvProjectionNull = true;
        (deterministicData as any).type4TbvSource = t4.tbvSource ?? null;
        (deterministicData as any).type4TbvPerShareUnavailable =
          t4.tbvPerShareUnavailable === true;
        (deterministicData as any).type4RotceBaseZero = !(Number(t4.rotceBase) > 0);
        console.warn(
          `[TYPE4-TBV] ${factSheet.ticker}: Deterministic engine produced 0 or null for projectedYear10TBVTotal. ` +
            `Terminal value may fall back to earnings × multiple. ` +
            `[F13-DIAG] tbvSource="${t4.tbvSource ?? "n/a"}", tbvPerShareUnavailable=${t4.tbvPerShareUnavailable === true}, ` +
            `rotceBase=${Number(t4.rotceBase) > 0 ? Number(t4.rotceBase).toFixed(4) : "0/none"}, insufficientHistory=${t4.insufficientHistory === true}.`,
        );
      }
    }
    // ─── END TYPE 4 DCF ENGINE FLAGS ─────────────────────────────────────────

    // Normalize Type 4 peer pFcf values (combined ratio / NIM scale correction)
    if (deterministicData?.isType4Financial && factSheet?.peerBenchmarks) {
      factSheet.peerBenchmarks = factSheet.peerBenchmarks.map((peer: any) => {
        if (peer.pFcf == null) return peer;
        const v = Number(peer.pFcf);
        // Combined ratio stored as whole number (e.g., 96.5) → convert to decimal
        if (v > 1.5) {
          return { ...peer, pFcf: v / 100 };
        }
        // Combined ratio stored as sub-component fraction (e.g., 0.088) → likely loss ratio only
        // A valid combined ratio decimal is 0.80–1.15. Flag values below 0.80 as suspect.
        if (v > 0 && v < 0.8 && deterministicData?.type4SubType !== "BANK") {
          console.warn(
            `[TYPE4-PEER-RATIO] ${factSheet.ticker}: peer ${peer.ticker || "unknown"} ` +
              `pFcf=${v} appears to be a sub-component fraction, not a combined ratio. ` +
              `Suppressed (renders N/A) — Historian likely stored loss ratio only.`,
          );
          // A value below the 0.80 valid-combined-ratio floor is not a usable combined ratio
          // (a 12–18% "combined ratio" is impossible). Null it so the peer card renders "—"
          // rather than a misleading sub-component fraction. BANK subtype (NIM) excluded above.
          return { ...peer, pFcf: null };
        }
        return peer;
      });
    }

    // SHARES-LOCK: The Mathematician frequently returns wrong sharesOutstanding values
    // (hallucinated, wrong units, stale filing data). deterministicData.sharesOutstanding
    // is computed from live FMP data (mktCap / price as fallback) and is always more
    // reliable. Override parsed.base before runScenarios so all three scenarios use
    // the correct share count. If deterministicData is missing, apply the same
    // implied-mktcap cross-check the BRK special case uses.
    if (
      deterministicData?.sharesOutstanding &&
      deterministicData.sharesOutstanding > 0
    ) {
      const deterministicShares = deterministicData.sharesOutstanding;
      const aiShares = parsed.base?.sharesOutstanding || 0;
      const pctDiff =
        aiShares > 0
          ? Math.abs(deterministicShares - aiShares) / deterministicShares
          : 1;
      if (pctDiff > 0.03) {
        // >3% divergence → AI is wrong, use deterministic
        console.warn(
          `[SHARES-LOCK] ${factSheet?.ticker || "UNKNOWN"}: AI returned ${aiShares.toLocaleString()}M shares, ` +
            `deterministic data shows ${deterministicShares.toLocaleString()}M. ` +
            `Overriding AI value (${(pctDiff * 100).toFixed(1)}% divergence).`,
        );
        parsed.base = {
          ...parsed.base,
          sharesOutstanding: deterministicShares,
        };
        if (parsed.bearOverrides)
          parsed.bearOverrides = {
            ...parsed.bearOverrides,
            sharesOutstanding: deterministicShares,
          };
        if (parsed.bullOverrides)
          parsed.bullOverrides = {
            ...parsed.bullOverrides,
            sharesOutstanding: deterministicShares,
          };
      }
    } else if (
      parsed.base?.sharesOutstanding &&
      deterministicData?.currentPrice &&
      deterministicData?.marketCap
    ) {
      // Fallback cross-check: if implied mktcap from AI shares diverges >20% from actual, correct it
      const impliedMktCap =
        parsed.base.sharesOutstanding * deterministicData.currentPrice; // millions
      const actualMktCap = deterministicData.marketCap; // already in millions
      const mktCapPctDiff =
        Math.abs(impliedMktCap - actualMktCap) / actualMktCap;
      if (mktCapPctDiff > 0.2) {
        const correctedShares = actualMktCap / deterministicData.currentPrice;
        console.warn(
          `[SHARES-LOCK] ${factSheet?.ticker || "UNKNOWN"}: AI shares imply $${(impliedMktCap / 1000).toFixed(0)}B mktcap ` +
            `vs actual $${(actualMktCap / 1000).toFixed(0)}B (${(mktCapPctDiff * 100).toFixed(1)}% off). ` +
            `Correcting to ${correctedShares.toFixed(0)}M shares.`,
        );
        const corrected = correctedShares;
        parsed.base = { ...parsed.base, sharesOutstanding: corrected };
        if (parsed.bearOverrides)
          parsed.bearOverrides = {
            ...parsed.bearOverrides,
            sharesOutstanding: corrected,
          };
        if (parsed.bullOverrides)
          parsed.bullOverrides = {
            ...parsed.bullOverrides,
            sharesOutstanding: corrected,
          };
      }
    }
    // ─── END SHARES-LOCK ──────────────────────────────────────────────────────

    // SHARES-LOCK SECONDARY GATE: Unconditional market cap sanity check.
    // Runs regardless of whether deterministicData.sharesOutstanding was present.
    // Catches cases where the primary lock was bypassed due to missing deterministic data.
    if (
      parsed.base?.sharesOutstanding &&
      deterministicData?.currentPrice > 0 &&
      deterministicData?.marketCap > 0
    ) {
      const impliedMktCap =
        parsed.base.sharesOutstanding * deterministicData.currentPrice;
      const actualMktCap = deterministicData.marketCap; // in millions
      const mktCapPctDiff =
        Math.abs(impliedMktCap - actualMktCap) / actualMktCap;
      if (mktCapPctDiff > 0.1) {
        // tighter than before — 10% threshold
        const correctedShares = actualMktCap / deterministicData.currentPrice;
        console.warn(
          `[SHARES-LOCK-2] ${factSheet?.ticker || "UNKNOWN"}: post-lock check failed. ` +
            `AI shares (${parsed.base.sharesOutstanding.toFixed(0)}M) imply ` +
            `$${(impliedMktCap / 1000).toFixed(0)}B mktcap vs actual ` +
            `$${(actualMktCap / 1000).toFixed(0)}B (${(mktCapPctDiff * 100).toFixed(1)}% off). ` +
            `Forcing correction to ${correctedShares.toFixed(0)}M shares.`,
        );
        parsed.base = { ...parsed.base, sharesOutstanding: correctedShares };
        if (parsed.bearOverrides)
          parsed.bearOverrides = {
            ...parsed.bearOverrides,
            sharesOutstanding: correctedShares,
          };
        if (parsed.bullOverrides)
          parsed.bullOverrides = {
            ...parsed.bullOverrides,
            sharesOutstanding: correctedShares,
          };
      }
    }

    // NETCASH-LOCK: If Mathematician deviated from deterministic netDebt without
    // a Type 4 override, correct it silently.
    if (!deterministicData?.isType4Financial) {
      const deterministicNetCash = -(deterministicData?.netDebt ?? 0);
      const aiNetCash = parsed.base?.netCash ?? 0;
      const netCashDiff = Math.abs(aiNetCash - deterministicNetCash);
      if (netCashDiff > 500) {
        // >$500M divergence
        console.warn(
          `[NETCASH-LOCK] ${factSheet?.ticker}: AI netCash=${aiNetCash.toFixed(0)}M ` +
            `vs deterministic=${deterministicNetCash.toFixed(0)}M. Overriding.`,
        );
        parsed.base = { ...parsed.base, netCash: deterministicNetCash };
        if (parsed.bearOverrides)
          parsed.bearOverrides = {
            ...parsed.bearOverrides,
            netCash: deterministicNetCash,
          };
        if (parsed.bullOverrides)
          parsed.bullOverrides = {
            ...parsed.bullOverrides,
            netCash: deterministicNetCash,
          };
      }
    }

    // TERMINAL-GROWTH-LOCK: The Mathematician is instructed to apply tier caps
    // (mega-cap 2.5%, large 3.0%, mid/small 3.5%) but freely returns sub-cap values
    // (e.g. 2.35% instead of 2.5%). The dcfEngine cap only prevents exceeding the
    // ceiling — it does not correct downward drift. A 15bps drift on a $1.5T equity
    // value produces a ~$15B error in PV(TV) and ~$1/share IV error.
    // This lock snaps any value within 20bps of the tier cap up to the exact cap.
    if (parsed.base?.terminalGrowthRate !== undefined) {
      const mktCapMillions = deterministicData?.marketCap || 0; // already in millions
      const isMegaCap = mktCapMillions > 100000; // >$100B
      const isLargeCap = mktCapMillions > 10000; // >$10B
      const tierCap = isMegaCap ? 0.025 : isLargeCap ? 0.03 : 0.035;
      const tolerance = 0.002; // 20bps snap zone
      const rawTermG = Number(parsed.base.terminalGrowthRate);
      if (
        rawTermG > 0 &&
        rawTermG < tierCap &&
        tierCap - rawTermG <= tolerance
      ) {
        console.warn(
          `[TERMINAL-GROWTH-LOCK] ${factSheet?.ticker || "UNKNOWN"}: ` +
            `terminalGrowthRate ${(rawTermG * 100).toFixed(2)}% is within 20bps of tier cap ` +
            `${(tierCap * 100).toFixed(1)}%. Snapping to cap.`,
        );
        parsed.base = { ...parsed.base, terminalGrowthRate: tierCap };
        if (parsed.bearOverrides?.terminalGrowthRate !== undefined) {
          const bearTermG = Number(parsed.bearOverrides.terminalGrowthRate);
          if (
            bearTermG > 0 &&
            bearTermG < tierCap &&
            tierCap - bearTermG <= tolerance
          ) {
            parsed.bearOverrides = {
              ...parsed.bearOverrides,
              terminalGrowthRate: tierCap,
            };
          }
        }
        if (parsed.bullOverrides?.terminalGrowthRate !== undefined) {
          const bullTermG = Number(parsed.bullOverrides.terminalGrowthRate);
          if (
            bullTermG > 0 &&
            bullTermG < tierCap &&
            tierCap - bullTermG <= tolerance
          ) {
            parsed.bullOverrides = {
              ...parsed.bullOverrides,
              terminalGrowthRate: tierCap,
            };
          }
        }
      }
    }
    // ─── END TERMINAL-GROWTH-LOCK ─────────────────────────────────────────────

    // ─── BULL MULTIPLE FLOOR ENFORCEMENT (pre-runScenarios) ──────────────────
    // Mirror of BEAR-1: auto-correct bull terminalMultiple to base × 1.15 minimum
    // BEFORE runScenarios so the DCF receives the corrected value.
    // validateScenarioConstraints runs after runScenarios and would only warn —
    // by that point the IV is already computed on the wrong multiple.
    const minBullMultiple =
      (parsed.base?.terminalMultiple ?? 0) *
      (1 + DEFAULT_SCENARIO_CONSTRAINTS.bullMultipleFloorPremiumPct);
    const rawBullMultiple = Number(
      parsed.bullOverrides?.terminalMultiple ??
        parsed.base?.terminalMultiple ??
        0,
    );
    if (rawBullMultiple > 0 && rawBullMultiple < minBullMultiple) {
      parsed.bullOverrides = {
        ...parsed.bullOverrides,
        terminalMultiple: minBullMultiple,
      };
      console.warn(
        `[BULL-MULTIPLE-FLOOR] ${factSheet.ticker}: bull terminal multiple ` +
          `${rawBullMultiple.toFixed(1)}x is below floor of base × 1.15 ` +
          `(${minBullMultiple.toFixed(1)}x). Auto-corrected before runScenarios.`,
      );
      scenarioViolations.push(
        `[BULL-MULTIPLE-FLOOR] Bull multiple was ${rawBullMultiple.toFixed(1)}x; ` +
          `raised to ${minBullMultiple.toFixed(1)}x (base × 1.15 floor).`,
      );
    }
    // ─────────────────────────────────────────────────────────────────────────

    // SHARES-UNIT-NORMALIZE: Ensure sharesOutstanding is always in MILLIONS before
    // passing to runScenarios. The DCF engine expects millions (e.g., 4226 for VZ).
    // If the Mathematician or any lock gate stored actual share count instead of millions,
    // the per-share division produces near-zero IV, triggering the IV sanity gate incorrectly.
    const normalizedShares = (() => {
      const s = parsed.base?.sharesOutstanding ?? 0;
      // Heuristic: any value > 50,000 is almost certainly an actual share count, not millions.
      // Even the largest companies (AAPL ~15,000M, Saudi Aramco ~30,000M) are under 50,000M shares.
      // A raw count of 4,226,000,000 would appear as 4,226,000,000 — far above 50,000.
      if (s > 50000) {
        const corrected = s / 1_000_000;
        console.warn(
          `[SHARES-UNIT-NORMALIZE] ${factSheet?.ticker || "UNKNOWN"}: ` +
            `sharesOutstanding=${s} appears to be actual count, not millions. ` +
            `Converting to ${corrected.toFixed(0)}M.`,
        );
        parsed.base = { ...parsed.base, sharesOutstanding: corrected };
        if (parsed.bearOverrides)
          parsed.bearOverrides = {
            ...parsed.bearOverrides,
            sharesOutstanding: corrected,
          };
        if (parsed.bullOverrides)
          parsed.bullOverrides = {
            ...parsed.bullOverrides,
            sharesOutstanding: corrected,
          };
      }
    })();

    // Pass currentPrice so it dynamically back-calculates impliedMarketGrowth
    // ─── NORM-METHOD-B LOCK (CALC-8b): Method B haircut arithmetic is TS-owned ──
    // Method-B selection signal: the normalization label string set by the
    // Mathematician (Gate 2/3 labels "Peak-Cycle Discounted" / "Spinoff Trough").
    // When Method B is selected with a stated haircut, the engine (not the LLM)
    // multiplies the engine-computed methodBCandidateOe by (1 − haircut).
    {
      const anchors = (deterministicData as any)?.normalizationAnchors;
      const labelStr = String(
        parsed.base?.normalizationLabel ||
          parsed.normalizationLabel ||
          parsed.base?.normalizedOwnerEarningsLabel ||
          "",
      );
      const methodBSelected =
        /Peak-Cycle Discounted|Spinoff Trough|Mid-Cycle Margin/i.test(labelStr);
      const haircut = Number((parsed as any).methodBHaircutPct);
      if (
        methodBSelected &&
        isFinite(haircut) &&
        haircut >= 0 &&
        haircut <= 50 &&
        anchors?.methodBCandidateOe != null &&
        anchors.methodBCandidateOe > 0
      ) {
        const normalizedBase = anchors.methodBCandidateOe * (1 - haircut / 100);
        if (parsed.base) parsed.base.normalizedOwnerEarnings = normalizedBase;
        console.log(
          `[NORM-METHOD-B] ${factSheet?.ticker || "UNKNOWN"}: candidate=${anchors.methodBCandidateOe.toFixed(0)}M, ` +
            `haircut=${haircut.toFixed(1)}%, normalizedBase=${normalizedBase.toFixed(0)}M`,
        );
      }
    }

    const baseWithPrice = {
      ...parsed.base,
      currentPrice: deterministicData?.currentPrice || 0,
      ticker: factSheet?.ticker || "UNKNOWN",
    };

    // ─── OE-DEGENERATE GUARD (F12, Tuning v2.62 §39.1): reject a normalizedOwnerEarnings
    // that is implausibly small relative to the DETERMINISTIC anchors the engine already
    // computed. The Mathematician's normalizedOwnerEarnings field is DEFAULT-LOCKED to the
    // Base-Case OE anchor (prompt O-lock ~line 3169), but under token pressure the LLM can
    // still emit a sentinel/near-zero value (e.g. TRV emitted 1M against a 6968M anchor),
    // which the DCF then faithfully turns into a $0.07-class IV. This is a deterministic
    // TypeScript sanity gate — NOT a prompt instruction — per the "never trust the LLM for
    // math / correct violations by moving logic into code" invariant. If the LLM's OE is
    // below 5% of the largest deterministic anchor, the valuation is unresolvable and the DCF
    // must NOT mint a number from the garbage input. Measurement-only ratio is captured for
    // the corpus regardless of whether the guard fires.
    {
      const llmOe = Number(baseWithPrice.normalizedOwnerEarnings);
      const anchors = (deterministicData as any)?.normalizationAnchors || {};
      const anchorCandidates = [
        Number((deterministicData as any)?.ownerEarningsData?.ownersEarnings),
        Number(anchors.midCycleAvgOe),
        Number(anchors.methodBCandidateOe),
        Number((deterministicData as any)?.fcffData?.fcffBase),
      ].filter((v) => isFinite(v) && v > 0);
      const anchorMax =
        anchorCandidates.length > 0 ? Math.max(...anchorCandidates) : 0;
      const oeVsAnchorRatio =
        anchorMax > 0 && isFinite(llmOe) ? llmOe / anchorMax : null;
      (deterministicData as any).normalizedOeVsAnchorRatio =
        oeVsAnchorRatio != null ? Number(oeVsAnchorRatio.toFixed(4)) : null;
      if (
        anchorMax > 0 &&
        (!isFinite(llmOe) || llmOe <= 0 || llmOe < 0.05 * anchorMax)
      ) {
        (deterministicData as any).type4OeDegenerateFired = true;
        (deterministicData as any).isUnresolvableValuation = true;
        console.warn(
          `[TYPE4-OE-DEGENERATE] ${factSheet?.ticker || "UNKNOWN"}: ` +
            `LLM normalizedOwnerEarnings=${isFinite(llmOe) ? llmOe.toFixed(0) : String(llmOe)}M is < 5% of ` +
            `max deterministic anchor (${anchorMax.toFixed(0)}M; ratio=${oeVsAnchorRatio != null ? oeVsAnchorRatio.toFixed(4) : "n/a"}). ` +
            `Marking valuation unresolvable BEFORE the DCF — the engine will not mint an IV from a degenerate OE base.`,
        );
      }
    }
    // ─── END OE-DEGENERATE GUARD ─────────────────────────────────────────────

    // ─── RUNWAY-ADJ LOCK (CALC-9): runway bps deduction is TypeScript-owned ──
    // The Mathematician emits mathReinvestmentRunway (HIGH/MEDIUM/LOW) and an
    // UNADJUSTED year6to10GrowthRate. The engine applies the deduction here,
    // BEFORE runScenarios, so the DCF consumes the deducted rate. Applied to the
    // base (via baseWithPrice) and to any bear/bull override that carries its own
    // year6to10GrowthRate; scenarios without an override inherit the base deduction.
    // Growth fields are decimals (dcfEngine: e.g. 0.04) → 50bps=0.005, 100bps=0.01.
    {
      const runway = String((parsed as any).mathReinvestmentRunway || "").toUpperCase();
      const deduction = runway === "LOW" ? 0.01 : runway === "MEDIUM" ? 0.005 : 0;
      if (deduction > 0) {
        // CALC-9-r1: floor inherits the base terminal rate for all scenarios,
        // since sparse bear/bull overrides omit terminalGrowthRate and inherit base.
        const termFloor =
          typeof baseWithPrice.terminalGrowthRate === "number"
            ? Math.abs(baseWithPrice.terminalGrowthRate)
            : typeof (parsed as any).base?.terminalGrowthRate === "number"
              ? Math.abs((parsed as any).base.terminalGrowthRate)
              : 0;
        const applyTo = (obj: any, label: string) => {
          if (obj && typeof obj.year6to10GrowthRate === "number") {
            const before = obj.year6to10GrowthRate;
            obj.year6to10GrowthRate = Math.max(before - deduction, termFloor);
            console.log(
              `[RUNWAY-ADJ] ${factSheet?.ticker || "UNKNOWN"} ${label}: runway=${runway}, ` +
                `y6to10 ${(before * 100).toFixed(2)}% → ${(obj.year6to10GrowthRate * 100).toFixed(2)}%` +
                (obj.year6to10GrowthRate === termFloor ? " (floored at terminal)" : ""),
            );
          }
        };
        applyTo(baseWithPrice, "base");
        applyTo(parsed.bearOverrides, "bear");
        applyTo(parsed.bullOverrides, "bull");
      } else if (runway && runway !== "HIGH") {
        console.warn(
          `[RUNWAY-ADJ] ${factSheet?.ticker || "UNKNOWN"}: unrecognized mathReinvestmentRunway "${runway}" — no deduction applied.`,
        );
      }
    }
    // ─── END RUNWAY-ADJ LOCK ─────────────────────────────────────────────────

    // DIAGNOSTIC: Log exact values entering runScenarios to trace IV discrepancy
    console.warn(
      `[TERMINAL-MULTIPLE-TRACE] ${factSheet?.ticker || "UNKNOWN"}: ` +
        `Mathematician returned terminalMultiple=${Number(parsed.base?.terminalMultiple ?? 0).toFixed(1)}x, ` +
        `useTerminalMultiple=${parsed.base?.useTerminalMultiple}, ` +
        `baseGrowth=${(Number(parsed.base?.year1to5GrowthRate ?? 0) * 100).toFixed(2)}%, ` +
        `WACC=${(Number(parsed.base?.wacc ?? 0) * 100).toFixed(2)}%, ` +
        `terminalGrowth=${(Number(parsed.base?.terminalGrowthRate ?? 0) * 100).toFixed(2)}%`,
    );
    console.warn(
      `[DCF-INPUT-TRACE] ${factSheet?.ticker || "UNKNOWN"}: ` +
        `sharesOutstanding=${baseWithPrice.sharesOutstanding?.toFixed(2)}M, ` +
        `netCash=${baseWithPrice.netCash?.toFixed(0)}M, ` +
        `wacc=${((baseWithPrice.wacc || 0) * 100).toFixed(2)}%, ` +
        `normalizedOE=${baseWithPrice.normalizedOwnerEarnings?.toFixed(0)}M, ` +
        `y15growth=${((baseWithPrice.year1to5GrowthRate || 0) * 100).toFixed(2)}%, ` +
        `terminalGrowth=${((baseWithPrice.terminalGrowthRate || 0) * 100).toFixed(2)}%, ` +
        `terminalMultiple=${baseWithPrice.terminalMultiple?.toFixed(1)}x, ` +
        `useTerminalMultiple=${baseWithPrice.useTerminalMultiple}`,
    );

    // [F1] Resolve the AUTHORITATIVE anchor basis now that the LLM-final
    // normalizedOwnerEarnings (the DCF input) is known — the pre-DCF financialEngine
    // value is a provisional "ttm". Nearest candidate by relative distance across the
    // full {ttm, midCycle, methodB} set (all $M, unit-clean); if the closest is >5%
    // off the final anchor, basis = "other". Stashes __oeAnchorBasisResolved for the
    // S2 detector/enrich via the __-prefixed idiom — does not mutate normalizationAnchors.
    {
      const __anchors = (deterministicData as any)?.normalizationAnchors || {};
      const __finalOe = Number(baseWithPrice.normalizedOwnerEarnings);
      const __cands: Array<[string, number]> = (
        [
          ["ttm", Number(__anchors.ttmOeBaseM)],
          ["midCycle", Number(__anchors.midCycleAvgOe)],
          ["methodB", Number(__anchors.methodBCandidateOe)],
        ] as Array<[string, number]>
      ).filter(([, v]) => isFinite(v) && v !== 0);
      let __basis = "other";
      if (isFinite(__finalOe) && __finalOe !== 0 && __cands.length > 0) {
        let __bestRel = Infinity;
        let __bestName = "other";
        for (const [name, val] of __cands) {
          const rel = Math.abs(__finalOe - val) / Math.abs(__finalOe);
          if (rel < __bestRel) {
            __bestRel = rel;
            __bestName = name;
          }
        }
        __basis = __bestRel <= 0.05 ? __bestName : "other";
      }
      (deterministicData as any).__oeAnchorBasisResolved = __basis;
      const __fmt = (v: any) =>
        isFinite(Number(v)) ? Number(v).toFixed(0) + "M" : "n/a";
      console.log(
        `[OE-ANCHOR-BASIS] ${factSheet?.ticker || "UNKNOWN"}: final=${__fmt(__finalOe)} → basis=${__basis} ` +
          `(ttm=${__fmt(__anchors.ttmOeBaseM)}, midCycle=${__fmt(__anchors.midCycleAvgOe)}, methodB=${__fmt(__anchors.methodBCandidateOe)})`,
      );
    }

    let scenarios: ReturnType<typeof runScenarios>;
    if (deterministicData?.isType5DataContaminated) {
      const sharesOut = baseWithPrice.sharesOutstanding || 1;
      const adjustedNetCash = baseWithPrice.netCash > 0 
        ? baseWithPrice.netCash * (1 - (baseWithPrice.cashHaircutPercent || 0))
        : baseWithPrice.netCash;
      const totalEq = Math.max(0, adjustedNetCash);
      const netCashFloor = totalEq / sharesOut;
      
      console.warn(`[TYPE5-CONTAMINATED-SKIP] ${factSheet?.ticker || "UNKNOWN"}: DCF bypassed, IV set to net-cash floor $${netCashFloor.toFixed(2)}`);
      
      const mockValuation = {
        projectedOwnerEarnings: Array(10).fill(0),
        presentValues: Array(10).fill(0),
        sumOfPVs: 0,
        terminalValue: 0,
        pvOfTerminalValue: 0,
        adjustedNetCash: adjustedNetCash,
        totalEquityValue: totalEq,
        sharesOutstanding: sharesOut,
        intrinsicValuePerShare: netCashFloor,
        impliedMarketGrowth: 0,
        crossCheckPassed: true,
        crossCheckDelta: 0,
        emergencyFloorApplied: false,
        cashFlowBasis: baseWithPrice.cashFlowBasis || 'OwnerEarnings'
      } as const;
      
      scenarios = {
        base: { ...mockValuation },
        bear: { ...mockValuation },
        bull: { ...mockValuation }
      };
    } else {
      scenarios = runScenarios(
        baseWithPrice,
        parsed.bearOverrides,
        parsed.bullOverrides,
      );
    }
    // Save terminal rescale measurements before we potentially re-run or lose them
    deterministicData.type4TerminalRescaled = scenarios.base.type4TerminalRescaled || false;
    deterministicData.terminalMultipleOriginal = scenarios.base.terminalMultipleOriginal || null;

    // ─── M42 (Tuning v2.67 §42.2): Type-4 terminal double-count MONITOR (measurement only) ──
    // Two deterministic detectors, Type-4 only. terminalToInterimPvRatio flags when the P/TBV
    // terminal dominates the interim owner-earnings PVs; effectiveBookGrowthMultiple flags when
    // rotce×retention compounds tangible book to an implausible multiple over 10yr. Both are pure
    // functions of already-computed deterministic values — the LLM does no math and no IV/scenario/
    // verdict changes. Non-Type-4 names emit nulls (no P/TBV terminal → ratios meaningless).
    if (deterministicData?.isType4Financial) {
      const _sumPv = Number(scenarios.base.sumOfPVs);
      const _pvTerm = Number(scenarios.base.pvOfTerminalValue);
      const _rotce = Number(deterministicData?.type4Valuation?.rotceBase);
      const _ret = Number(deterministicData?.retentionRate ?? 1);
      (deterministicData as any).terminalToInterimPvRatio =
        isFinite(_sumPv) && _sumPv > 0 && isFinite(_pvTerm)
          ? Number((_pvTerm / _sumPv).toFixed(4))
          : null;
      (deterministicData as any).effectiveBookGrowthMultiple =
        isFinite(_rotce) && isFinite(_ret)
          ? Number(Math.pow(1 + _rotce * _ret, 10).toFixed(4))
          : null;
      console.log(
        `[TYPE4-TERMINAL-MONITOR] ${factSheet?.ticker || "UNKNOWN"}: ` +
          `terminalToInterimPvRatio=${(deterministicData as any).terminalToInterimPvRatio ?? "n/a"}, ` +
          `effectiveBookGrowthMultiple=${(deterministicData as any).effectiveBookGrowthMultiple ?? "n/a"}x ` +
          `(rotceBase=${isFinite(_rotce) ? (_rotce * 100).toFixed(2) + "%" : "n/a"}, retention=${isFinite(_ret) ? _ret.toFixed(2) : "n/a"}).`,
      );
    }
    // ─── END M42 ────────────────────────────────────────────────────────────

    console.warn(
      `[DCF-OUTPUT-TRACE] ${factSheet?.ticker || "UNKNOWN"}: ` +
        `sumOfPVs=${scenarios.base.sumOfPVs?.toFixed(0)}M, ` +
        `pvTerminal=${scenarios.base.pvOfTerminalValue?.toFixed(0)}M, ` +
        `netCashApplied=${(scenarios.base.adjustedNetCash || 0) > 0 ? (scenarios.base.adjustedNetCash || 0).toFixed(0) + "M" : "0 (levered-FCF bridge; net debt serviced in CF)"}, ` +
        `totalEquity=${scenarios.base.totalEquityValue?.toFixed(0)}M, ` +
        `sharesOut=${scenarios.base.sharesOutstanding?.toFixed(0)}M, ` +
        `IV=$${scenarios.base.intrinsicValuePerShare?.toFixed(2)}`,
    );

    // IV SANITY GATE: Catch DCF engine output that is implausible relative to market price.
    // A valid AVOID verdict can have IV well below price, but IV should never be
    // negative or more than 95% below current price for a profitable operating company.
    // If triggered, it almost always means shares were wrong and the engine divided by too few.
    const ivps = scenarios.base.intrinsicValuePerShare;
    const currentPx = deterministicData?.currentPrice || 0;
    let isUnresolvableValuation =
      deterministicData?.type4Valuation?.tbvPerShareUnavailable === true ||
      (deterministicData as any)?.isUnresolvableValuation === true;

    if (!deterministicData?.isType5DataContaminated && !isUnresolvableValuation && currentPx > 0 && ivps > 0 && ivps < currentPx * 0.05) {
      const emergencyShares = deterministicData.marketCap / currentPx; // both in millions
      const sharesAreFine = emergencyShares > 0 && Math.abs(baseWithPrice.sharesOutstanding - emergencyShares) / emergencyShares < 0.1;

      if (sharesAreFine) {
        console.warn(`[IV-SANITY] ${factSheet?.ticker || "UNKNOWN"}: base IV ($${ivps.toFixed(2)}) < 5% of price ($${currentPx.toFixed(2)}), but shares (${baseWithPrice.sharesOutstanding.toFixed(2)}M) roughly match mktCap/price (${emergencyShares.toFixed(2)}M). Marking valuation unresolvable instead of attempting shares correction.`);
        isUnresolvableValuation = true;
      } else {
        console.error(
          `[IV-SANITY] ${factSheet?.ticker || "UNKNOWN"}: base IV ($${ivps.toFixed(2)}) is ` +
            `less than 5% of current price ($${currentPx.toFixed(2)}). ` +
            `This almost certainly indicates a shares outstanding error. ` +
            `Forcing shares to mktCap/price and re-running scenarios.`,
        );
        const correctedBase = {
          ...scenarios.base,
          sharesOutstanding: emergencyShares,
        }; // not how runDCF works — see note
        // Re-run with corrected shares:
        const correctedAssumptions = {
          ...baseWithPrice,
          sharesOutstanding: emergencyShares,
        };
        const rerun = runScenarios(
          correctedAssumptions,
          parsed.bearOverrides || {},
          parsed.bullOverrides || {},
        );
        // Replace scenarios with the corrected run
        Object.assign(scenarios, rerun);
      }
    }

    if (isUnresolvableValuation) {
      deterministicData.isUnresolvableValuation = true;
    }

    // [FLATRATE-DCF-SHADOW] R-F measurement: what is base IV at the deterministic flat rate?
    // Shadow only — never written back to scenarios / IV / verdict.
    {
      const liveBaseIv = scenarios.base.intrinsicValuePerShare;
      const flatRatePct = deterministicData.thirtyYearTreasuryYield + 5.5;
      if (
        !isUnresolvableValuation &&
        !deterministicData?.isType5DataContaminated &&
        typeof liveBaseIv === "number" && liveBaseIv > 0
      ) {
        const shadowAssumptions = { ...baseWithPrice, wacc: flatRatePct / 100 };
        const shadowScen = runScenarios(
          shadowAssumptions,
          parsed.bearOverrides || {},
          parsed.bullOverrides || {},
        );
        const shadowFlatIv = shadowScen.base.intrinsicValuePerShare;
        const shadowFlatDeltaPct = ((shadowFlatIv - liveBaseIv) / liveBaseIv) * 100;
        const liveWaccPct = (baseWithPrice.wacc || 0) * 100;
        console.warn(
          `[FLATRATE-DCF-SHADOW] ${factSheet?.ticker || "UNKNOWN"}: ` +
            `liveIv=$${liveBaseIv.toFixed(2)}, shadowFlatIv=$${shadowFlatIv.toFixed(2)}, ` +
            `deltaPct=${shadowFlatDeltaPct.toFixed(1)}%, liveWacc=${liveWaccPct.toFixed(2)}%, ` +
            `flatRate=${flatRatePct.toFixed(2)}%`,
        );
        // stash for the analysis_completed enrichment (Step 3); use outer-scope vars if the
        // enrichment is built later, otherwise attach to the object already assembled there.
        (deterministicData as any).__shadowFlatIv = shadowFlatIv;
        (deterministicData as any).__shadowFlatDeltaPct = shadowFlatDeltaPct;
        (deterministicData as any).__shadowLiveWacc = liveWaccPct;
        (deterministicData as any).__shadowFlatRate = flatRatePct;
      } else {
        console.warn(`[FLATRATE-DCF-SHADOW] ${factSheet?.ticker || "UNKNOWN"}: SKIPPED (no positive base IV)`);
      }
    }

    // [TYPE4-RI-SHADOW] §42 residual‑income shadow (RI‑1, Tuning v2.79 §47).
    // Type‑4 only. Shadow ONLY — never written back to scenarios / IV / verdict.
    // Uses the F16‑CORRECTED ROTCE (the value the coupled RI‑2 flip would use) so
    // the corpus shows the intended post‑fix number directly. Discounts at the
    // same rate the live DCF used (baseWithPrice.wacc) so the delta reflects the
    // FORMULA change only, not a rate change.
    if (deterministicData?.isType4Financial && deterministicData?.type4Valuation) {
      // ── [SPEC-1] CURRENT MARKET P/TBV (deterministic; single source of truth) ──
      // price ÷ tbvPerShare. The engine previously surfaced only the RI-IMPLIED fair
      // multiple (__type4RiImpliedPtbv), never the CURRENT market multiple, so prose
      // derived it free-hand and drifted (BRK-B stated 1.51x in three places against
      // a true 1.76x). Computed here at the OUTER Type-4 guard — deliberately NOT
      // inside either RI carrier block — because the current multiple has no
      // dependence on the RI cost-of-equity basis and must not inherit the
      // TYPE4_RI_TERMINAL_ENABLED gate. Threaded onto scenarios.base so the memo
      // renders it from this field alone; prose must never restate it.
      {
        const _tbvPS = Number(deterministicData?.type4Valuation?.tbvPerShare);
        const _px = Number(deterministicData?.currentPrice);
        const _curPtbv =
          _tbvPS > 0 && _px > 0 ? Number((_px / _tbvPS).toFixed(4)) : null;
        (deterministicData as any).__type4CurrentPtbv = _curPtbv;
        (scenarios.base as any).__type4CurrentPtbv = _curPtbv;
      }

      const t4 = deterministicData.type4Valuation;
      const liveBaseIvRi = scenarios.base.intrinsicValuePerShare;
      const tbvPs = Number(t4.tbvPerShare);
      const ret = Number(deterministicData?.retentionRate ?? 1);
      const keRi = Number(baseWithPrice.wacc);
      const sharesM = Number(baseWithPrice.sharesOutstanding);
      const ptbvMed = Number(t4.ptbvMedian);

      // F16 corrected‑ROTCE selection (SHADOW copy of the RI‑2 rule; behavior‑free here).
      const opLen = Number(t4.opIncomeSeriesLen ?? 0);
      const rotceOp = Number(t4.rotceOpIncome);
      const rotceKm = Number(t4.rotce5yrKeyMetrics);
      const RI_DIVERGENCE_THRESHOLD = 0.25; // |op−km|/km; tune from the RI‑1 corpus
      const kmUsable = isFinite(rotceKm) && rotceKm > 0;
      const opUsable = isFinite(rotceOp) && rotceOp > 0;
      const divergent =
        kmUsable && opUsable
          ? Math.abs(rotceOp - rotceKm) / rotceKm > RI_DIVERGENCE_THRESHOLD
          : false;
      const useKm = kmUsable && (opLen < 3 || !opUsable || divergent);
      const rotceCorrected = useKm ? rotceKm : (opUsable ? rotceOp : Number(t4.rotceBase));
      const rotceSourceRi = useKm ? "key-metrics-5yr" : (opUsable ? "operatingIncome" : "rotceBase");

      const ri = computeType4ResidualIncomeValue(tbvPs, rotceCorrected, ret, keRi, sharesM);
      if (ri && isFinite(ri.riValuePerShare)) {
        const deltaVsLive =
          typeof liveBaseIvRi === "number" && liveBaseIvRi > 0
            ? ((ri.riValuePerShare - liveBaseIvRi) / liveBaseIvRi) * 100
            : null;
        console.warn(
          `[TYPE4-RI-SHADOW] ${factSheet?.ticker || "UNKNOWN"}: ` +
            `riIv=$${ri.riValuePerShare.toFixed(2)}, liveIv=$${(liveBaseIvRi ?? 0).toFixed(2)}, ` +
            `deltaPct=${deltaVsLive != null ? deltaVsLive.toFixed(1) + "%" : "n/a"}, ` +
            `rotceUsed=${(rotceCorrected * 100).toFixed(2)}% (source=${rotceSourceRi}, ` +
            `op=${isFinite(rotceOp) ? (rotceOp * 100).toFixed(2) + "%" : "n/a"}, ` +
            `km=${isFinite(rotceKm) ? (rotceKm * 100).toFixed(2) + "%" : "n/a"}), ` +
            `impliedPtbvRi=${ri.impliedPtbvFromRi != null ? ri.impliedPtbvFromRi.toFixed(2) + "x" : "n/a"} ` +
            `vs histMedian=${isFinite(ptbvMed) ? ptbvMed.toFixed(2) + "x" : "n/a"}, ` +
            `omega=${ri.omegaUsed.toFixed(2)}, ke=${(keRi * 100).toFixed(2)}%, retention=${ret.toFixed(2)}`,
        );
        (deterministicData as any).__type4RiIv = Number(ri.riValuePerShare.toFixed(2));
        (deterministicData as any).__type4RiImpliedPtbv =
          ri.impliedPtbvFromRi != null ? Number(ri.impliedPtbvFromRi.toFixed(4)) : null;
        (deterministicData as any).__type4RiVsLiveDeltaPct =
          deltaVsLive != null ? Number(deltaVsLive.toFixed(2)) : null;
        (deterministicData as any).__type4RiRotceUsed = Number((rotceCorrected * 100).toFixed(2));
        (deterministicData as any).__type4RiRotceSource = rotceSourceRi;
        (deterministicData as any).__type4RiOmega = ri.omegaUsed;
      } else {
        console.warn(`[TYPE4-RI-SHADOW] ${factSheet?.ticker || "UNKNOWN"}: SKIPPED (tbvPerShare<=0 or non-finite ROTCE)`);
      }
    }

    // [TYPE4-RI-APPLIED] RI-2 (Tuning v2.81 §49): promote RI to the LIVE Type-4 IV behind a flag.
    // SELF-CONTAINED: recomputes the F16 ROTCE selection + shares locally (the shadow block's
    // rotceCorrected/rotceSourceRi/sharesM are const-scoped inside its own braces — do NOT reference
    // them here). Ke = the R-F flat rate (30yr Treasury + 5.5% ERP), the cost-of-equity basis RI needs.
    // RI is OE-independent, so it also carries the verdict past a degenerate-OE unresolvable mark.
    if (
      process.env.TYPE4_RI_TERMINAL_ENABLED === "true" &&
      deterministicData?.isType4Financial &&
      deterministicData?.type4Valuation &&
      deterministicData?.type4Valuation?.tbvPerShareUnavailable !== true
    ) {
      const t4b = deterministicData.type4Valuation;
      const tbvPs2 = Number(t4b.tbvPerShare);
      const ret2 = Number(deterministicData?.retentionRate ?? 1);
      const sharesM2 = Number(baseWithPrice.sharesOutstanding);
      const keFlat = (Number(deterministicData.thirtyYearTreasuryYield) + 5.5) / 100;

      // F16 corrected-ROTCE selection (local copy of the shadow's rule — keep in sync).
      const opLen2 = Number(t4b.opIncomeSeriesLen ?? 0);
      const rotceOp2 = Number(t4b.rotceOpIncome);
      const rotceKm2 = Number(t4b.rotce5yrKeyMetrics);
      const RI_DIVERGENCE_THRESHOLD_2 = 0.25;
      const kmUsable2 = isFinite(rotceKm2) && rotceKm2 > 0;
      const opUsable2 = isFinite(rotceOp2) && rotceOp2 > 0;
      const divergent2 =
        kmUsable2 && opUsable2
          ? Math.abs(rotceOp2 - rotceKm2) / rotceKm2 > RI_DIVERGENCE_THRESHOLD_2
          : false;
      const useKm2 = kmUsable2 && (opLen2 < 3 || !opUsable2 || divergent2);
      const rotceB = useKm2 ? rotceKm2 : (opUsable2 ? rotceOp2 : Number(t4b.rotceBase));
      const rotceSrc2 = useKm2 ? "key-metrics-5yr" : (opUsable2 ? "operatingIncome" : "rotceBase");

      // bear/bull ROTCE: deterministic band on the corrected base (rotceTrough/Peak are
      // opIncome-derived and unreliable once F16 switches to key-metrics — do NOT use them here).
      const rotceBear = Math.max(0, rotceB * 0.85);
      const rotceBull = rotceB * 1.10;

      const riBase = computeType4ResidualIncomeValue(tbvPs2, rotceB, ret2, keFlat, sharesM2);
      const riBear = computeType4ResidualIncomeValue(tbvPs2, rotceBear, ret2, keFlat, sharesM2);
      const riBull = computeType4ResidualIncomeValue(tbvPs2, rotceBull, ret2, keFlat, sharesM2);

      if (riBase && isFinite(riBase.riValuePerShare) && riBase.riValuePerShare > 0) {
        const applyOne = (scn: any, ri: any) => {
          if (!scn || !ri || !isFinite(ri.riValuePerShare)) return;
          scn.intrinsicValuePerShare = ri.riValuePerShare;
          scn.totalEquityValue = ri.riEquityValueTotal;
          scn.__ivMethod = "residual-income";
        };
        applyOne(scenarios.base, riBase);
        applyOne(scenarios.bear, riBear);
        applyOne(scenarios.bull, riBull);

        // Carry the verdict: clear an unresolvable mark set ONLY by degenerate OE (RI ignores OE).
        // A genuine tbvPerShareUnavailable is excluded by the outer guard above.
        if (
          (deterministicData as any)?.type4OeDegenerateFired === true &&
          (deterministicData as any)?.isUnresolvableValuation === true
        ) {
          (deterministicData as any).isUnresolvableValuation = false;
          isUnresolvableValuation = false;
          (deterministicData as any).type4RiClearedUnresolvable = true;
        }

        (deterministicData as any).__type4RiApplied = true;

        // ── UNGATED FIX (applied‑basis RI multiple) ──────────────────────────
        // The SHADOW block above populated __type4RiIv / __type4RiImpliedPtbv from
        // the WACC‑basis shadow RI (ke = live WACC). The APPLIED IV is computed at
        // keFlat (30yr Treasury + 5.5% ERP), so its true implied fair multiple
        // differs — e.g. BRK‑B shadow 1.54x @9.90% vs applied 1.46x @10.44%. Surface
        // the APPLIED figures so telemetry, the C‑3f attribution prompt, and the memo
        // all cite the multiple that reconciles to the DISPLAYED IV. Deterministic.
        (deterministicData as any).__type4RiIv = Number(riBase.riValuePerShare.toFixed(2));
        (deterministicData as any).__type4RiImpliedPtbv =
          riBase.impliedPtbvFromRi != null ? Number(riBase.impliedPtbvFromRi.toFixed(4)) : null;
        (deterministicData as any).__type4RiKeFlat = Number((keFlat * 100).toFixed(2));

        // ── RI‑3 display: attach the deterministic per‑year residual‑income walk ──
        // to the memo‑facing base scenario. MemoTab renders an additive panel
        // (tangible book anchor + ΣPV(RI) + PV continuing = RI IV) in place of the
        // discarded live‑DCF projection. __‑prefixed keys survive JSON round‑trip.
        (scenarios.base as any).__ivMethod = "residual-income";
        (scenarios.base as any).__riWalk = riBase.walk;
        (scenarios.base as any).__riTangibleBookAnchorPerShare = riBase.tangibleBookAnchorPerShare;
        (scenarios.base as any).__riSumPvResidualPerShare = riBase.sumPvResidualPerShare;
        (scenarios.base as any).__riContinuingResidualPerShare = riBase.continuingResidualPerShare;
        (scenarios.base as any).__riPvContinuingPerShare = riBase.pvContinuingPerShare;
        (scenarios.base as any).__riValuePerShare = riBase.riValuePerShare;
        (scenarios.base as any).__riKeUsedPct = Number((riBase.keUsed * 100).toFixed(2));
        (scenarios.base as any).__riOmega = riBase.omegaUsed;
        (scenarios.base as any).__riClearedUnresolvable =
          (deterministicData as any).type4RiClearedUnresolvable === true;

        // Measurement (rides analysis_completed): the panel is additive iff
        // anchor + ΣPV(RI) + PV(continuing) == RI IV to the cent.
        const _riIdentity =
          riBase.tangibleBookAnchorPerShare +
          riBase.sumPvResidualPerShare +
          riBase.pvContinuingPerShare;
        const _riWalkAdditive = Math.abs(_riIdentity - riBase.riValuePerShare) < 0.01;
        (deterministicData as any).__type4RiWalkEmitted =
          Array.isArray(riBase.walk) && riBase.walk.length > 0;
        (deterministicData as any).__type4RiWalkAdditive = _riWalkAdditive;

        console.warn(
          `[TYPE4-RI-APPLIED] ${factSheet?.ticker || "UNKNOWN"}: ` +
            `IV set to RI base=$${riBase.riValuePerShare.toFixed(2)} ` +
            `(bear=$${riBear ? riBear.riValuePerShare.toFixed(2) : "n/a"}, ` +
            `bull=$${riBull ? riBull.riValuePerShare.toFixed(2) : "n/a"}), ` +
            `rotceBase=${(rotceB * 100).toFixed(2)}% (source=${rotceSrc2}), keFlat=${(keFlat * 100).toFixed(2)}%, ` +
            `clearedDegenerateUnresolvable=${(deterministicData as any)?.type4RiClearedUnresolvable === true}.`,
        );
        console.warn(
          `[TYPE4-RI-WALK] ${factSheet?.ticker || "UNKNOWN"}: emitted ${riBase.walk.length}-yr walk; ` +
            `anchor $${riBase.tangibleBookAnchorPerShare.toFixed(2)} + ΣPV(RI) $${riBase.sumPvResidualPerShare.toFixed(2)} ` +
            `+ PV(CV) $${riBase.pvContinuingPerShare.toFixed(2)} = $${_riIdentity.toFixed(2)} vs riIv $${riBase.riValuePerShare.toFixed(2)} ` +
            `(additive=${_riWalkAdditive}); appliedImpliedPtbv=${riBase.impliedPtbvFromRi != null ? riBase.impliedPtbvFromRi.toFixed(4) + "x" : "n/a"} ` +
            `(supersedes shadow), keFlat=${(keFlat * 100).toFixed(2)}%.`,
        );
      }
    }

    // GROWTH-NARRATIVE-LOCK v3: Bidirectional bounds enforcement.
    // Catches both upward inflation (e.g., 9.7% stated as 1%) and
    // downward contradiction (e.g., -2.7% when 1.5% was stated in prose).
    // Bounds are anchored to deterministic historical data and terminal growth rate.
    if (!deterministicData?.isType5DataContaminated) {
      const y15 = parsed.base?.year1to5GrowthRate ?? 0;
      const y610 = parsed.base?.year6to10GrowthRate ?? y15;
      const termG = Math.abs(parsed.base?.terminalGrowthRate ?? 0.015);
      const histCagr =
        deterministicData?.historicalMetrics?.revenueGrowth5Yr ?? 0;
      const isSoftware = (() => {
        const arch = (
          (typeof parsed.archetype === "string"
            ? parsed.archetype
            : parsed.archetype?.category) || ""
        ).toUpperCase();
        return arch.includes("SAAS") || arch.includes("SOFTWARE");
      })();

      if (!isSoftware) { // Software entities are exempt — high growth is structural
        let changed = false;
        let newY15 = y15;
        let newY610 = y610;

        // UPWARD BOUND: base growth cannot exceed 3x historical CAGR or 3x terminal growth
        // (whichever is higher), with a hard floor of 8% for the ceiling itself.
        const upwardCeiling = Math.max(Math.abs(histCagr) * 3, termG * 3, 0.08);
        if (y15 > upwardCeiling) {
          newY15 = Math.max(
            termG * 2,
            Math.min(Math.abs(histCagr) * 1.5, upwardCeiling),
          );
          newY610 = Math.min(y610, newY15);
          console.error(
            `[GROWTH-NARRATIVE-LOCK] ${factSheet?.ticker}: y15=${(y15 * 100).toFixed(1)}% exceeds upward ceiling ${(upwardCeiling * 100).toFixed(1)}%. Clamping to ${(newY15 * 100).toFixed(1)}%.`,
          );
          changed = true;
        }

        // DOWNWARD BOUND: base case growth cannot be more than 3% below terminal growth.
        // Prevents the Mathematician from running the base case as a bear case.
        // Exception: structural decline entities may have base growth down to -5%.
        const isStructuralDecline =
          deterministicData?.structuralDeclineAssessment?.isStructuralDecline ===
            true ||
          factSheet?.structuralDeclineAssessment?.isStructuralDecline === true;
        const downwardFloor = isStructuralDecline ? -0.05 : termG - 0.03;
        
        let ivPreFloor: number | null = null;
        let mathRequestedGrowthPct: number | null = null;
        let growthFloorPct: number | null = null;
        let ivPostFloor: number | null = null;
        let ivFloorDeltaPct: number | null = null;
        let downwardFloorClamped = false;

        if (y15 < downwardFloor) {
          downwardFloorClamped = true;
          ivPreFloor = scenarios.base.intrinsicValuePerShare;
          mathRequestedGrowthPct = Number((y15 * 100).toFixed(1));
          growthFloorPct = Number((downwardFloor * 100).toFixed(1));
          newY15 = downwardFloor;
          console.error(
            `[GROWTH-NARRATIVE-LOCK] ${factSheet?.ticker}: y15=${(y15 * 100).toFixed(1)}% below downward floor ${(downwardFloor * 100).toFixed(1)}%. Clamping to floor.`,
          );
          changed = true;
        }

        if (changed) {
          parsed.base = {
            ...parsed.base,
            year1to5GrowthRate: newY15,
            year6to10GrowthRate: newY610,
          };
          // Rebuild baseWithPrice from the corrected parsed.base to ensure currentPrice is included
          const correctedBaseWithPrice = {
            ...parsed.base,
            currentPrice: deterministicData?.currentPrice || 0,
          };
          const rerun = runScenarios(
            correctedBaseWithPrice,
            parsed.bearOverrides || {},
            parsed.bullOverrides || {},
          );
          // Replace scenario references directly — Object.assign misses nested structure
          scenarios.base = rerun.base;
          scenarios.bear = rerun.bear;
          scenarios.bull = rerun.bull;
          console.error(
            `[GROWTH-NARRATIVE-LOCK] ${factSheet?.ticker}: scenarios re-run with corrected growth rates. New base IV: $${rerun.base.intrinsicValuePerShare.toFixed(2)}, Y1-5: ${(newY15 * 100).toFixed(1)}%`,
          );
          
          if (downwardFloorClamped) {
            ivPostFloor = rerun.base.intrinsicValuePerShare;
            ivFloorDeltaPct = ivPreFloor ? ((ivPostFloor - ivPreFloor) / ivPreFloor) * 100 : null;
            console.log(
              `[GROWTH-FLOOR-GAP] ${factSheet?.ticker}: mathGrowth=${mathRequestedGrowthPct}% floor=${growthFloorPct}% ivPreFloor=$${ivPreFloor?.toFixed(2)} ivPostFloor=$${ivPostFloor?.toFixed(2)} delta=${ivFloorDeltaPct?.toFixed(1)}% (measurement only; floor unchanged)`
            );
          }
        }
        
        if (!downwardFloorClamped) {
          console.log(`[GROWTH-FLOOR-GAP] ${factSheet?.ticker}: no clamp (growth at/above floor)`);
        }
        
        if (!parsed.assumptionRationales) parsed.assumptionRationales = {};
        (parsed.assumptionRationales as any).growthFloorGap = {
          growthFloorClampedFromPct: mathRequestedGrowthPct,
          growthFloorPct,
          ivPreFloor,
          ivPostFloor,
          ivFloorDeltaPct
        };

      }
    }
    // ─── END GROWTH-NARRATIVE-LOCK v3 ────────────────────────────────────────────

    // Build IRR bridge strings deterministically from Gemini's numeric outputs.
    // This replaces the previous pattern of asking Gemini to format these strings.
    const irrRationales = parsed.assumptionRationales as Record<
      string,
      string | number
    >;

    // ─── RULE 49: BASE GROWTH vs MARKET-IMPLIED SPREAD CHECK ─────────────────
    const impliedGrowth = scenarios.base.impliedMarketGrowth ?? -1;
    const finalBaseGrowth = Number(parsed.base?.year1to5GrowthRate ?? 0);

    if (impliedGrowth > 0 && finalBaseGrowth > 0) {
      const spreadVsImplied = finalBaseGrowth - impliedGrowth;
      if (spreadVsImplied > RULE_49_SPREAD_THRESHOLD) {
        console.warn(
          `[RULE-49] ${factSheet.ticker}: base growth ${(finalBaseGrowth * 100).toFixed(1)}% ` +
            `is ${(spreadVsImplied * 100).toFixed(1)}pp above market-implied ` +
            `${(impliedGrowth * 100).toFixed(1)}%. Flagging — not clamping.`,
        );
        scenarioViolations.push(
          `[RULE-49] Base case growth ${(finalBaseGrowth * 100).toFixed(1)}% exceeds ` +
            `market-implied growth ${(impliedGrowth * 100).toFixed(1)}% by ` +
            `${(spreadVsImplied * 100).toFixed(1)}pp (threshold: 20pp). ` +
            `This gap is the primary source of the valuation discount to market price. ` +
            `Review Mathematician assumptions if this gap is unexpected.`,
        );
      }
    }
    // ─── END RULE 49 CHECK ───────────────────────────────────────────────────

    // ─── DETERMINISTIC SCENARIO IRR COMPUTATION ─────────────────────────────
    const currentPrice = deterministicData?.currentPrice || 0;
    const baseSharesOut = parsed.base?.sharesOutstanding || 1;
    const baseEarnings = parsed.base?.normalizedOwnerEarnings || 0;
    const ownerEarningsPerShare =
      baseSharesOut > 0 ? baseEarnings / baseSharesOut : 0;

    const entryMultiple =
      ownerEarningsPerShare > 0 && currentPrice > 0
        ? currentPrice / ownerEarningsPerShare
        : Math.max(parsed.base?.terminalMultiple ?? 15, 10);

    const fcfYieldDecimal =
      currentPrice > 0 && ownerEarningsPerShare > 0
        ? ownerEarningsPerShare / currentPrice
        : 0;

    const baseGrowthDecimal = Number(parsed.base?.year1to5GrowthRate ?? 0);
    const bearGrowthDecimal = Number(
      parsed.bearOverrides?.year1to5GrowthRate ??
        parsed.base?.year1to5GrowthRate ??
        0,
    );
    const bullGrowthDecimal = Number(
      parsed.bullOverrides?.year1to5GrowthRate ??
        parsed.base?.year1to5GrowthRate ??
        0,
    );
    const engineBaseTerminalMultiple = Number(
      parsed.base?.terminalMultiple ?? 15,
    );

    // ─── BEAR-DECLINE-GAP (observability only — Tuning 5.11) ─────────────────
    // Measures the scenario-realism gap on names the market prices for decline:
    // reverse-DCF implied growth is negative, yet the bear case still models
    // non-negative growth (bear cannot express the implied/asserted downside).
    // MEASUREMENT ONLY — alters no scenario value. The gated behavioral change
    // (negative bear floor) is deferred pending cyclical-trough false-positive
    // analysis. impliedGrowth and bearGrowthDecimal are already in scope above.
    {
      if (!parsed.assumptionRationales) parsed.assumptionRationales = {};
      const bdgImplied = impliedGrowth; // -1 sentinel = not computed
      const bdgBear = bearGrowthDecimal;
      const bdgStructural =
        factSheet?.structuralDeclineAssessment?.isStructuralDecline === true;
      const bearDeclineGapFired =
        bdgImplied > -1 && bdgImplied < 0 && bdgBear >= 0;
      (parsed.assumptionRationales as any).bearDeclineGap = {
        fired: bearDeclineGapFired,
        impliedGrowth: bdgImplied,
        bearGrowth: bdgBear,
        baseGrowth: baseGrowthDecimal,
        isStructuralDecline: bdgStructural,
      };
      if (bearDeclineGapFired) {
        console.warn(
          `[BEAR-DECLINE-GAP] ${factSheet?.ticker}: market-implied growth ` +
            `${(bdgImplied * 100).toFixed(1)}% < 0 but bear growth ` +
            `${(bdgBear * 100).toFixed(1)}% >= 0 — bear cannot model the ` +
            `implied decline. isStructuralDecline=${bdgStructural} ` +
            `(measurement only; no scenario change).`,
        );
      }
    }
    // ─── END BEAR-DECLINE-GAP ────────────────────────────────────────────────

    const scenarioReturns = computeScenarioReturns(
      entryMultiple,
      fcfYieldDecimal,
      10, // horizonYears
      baseGrowthDecimal,
      engineBaseTerminalMultiple,
      bearGrowthDecimal,
      bullGrowthDecimal,
    );

    const bearIrrBridge = buildIrrBridge(
      scenarioReturns.bear.fcfYieldContribution * 100,
      scenarioReturns.bear.growthContribution * 100,
      scenarioReturns.bear.multipleChangeContribution * 100,
    );
    const baseIrrBridge = buildIrrBridge(
      scenarioReturns.base.fcfYieldContribution * 100,
      scenarioReturns.base.growthContribution * 100,
      scenarioReturns.base.multipleChangeContribution * 100,
    );
    const bullIrrBridge = buildIrrBridge(
      scenarioReturns.bull.fcfYieldContribution * 100,
      scenarioReturns.bull.growthContribution * 100,
      scenarioReturns.bull.multipleChangeContribution * 100,
    );

    // Attach back to assumptionRationales as strings for downstream consumers
    parsed.assumptionRationales.bearCaseIRR = bearIrrBridge;
    parsed.assumptionRationales.baseCaseIRR = baseIrrBridge;
    parsed.assumptionRationales.bullCaseIRR = bullIrrBridge;

    // Store exact percentages for the UI fallback
    parsed.assumptionRationales.bearGrowthPct =
      scenarioReturns.bear.growth * 100;
    parsed.assumptionRationales.baseGrowthPct =
      scenarioReturns.base.growth * 100;
    parsed.assumptionRationales.bullGrowthPct =
      scenarioReturns.bull.growth * 100;

    // ─── [SCENARIO-IRR-TRACE] (S1, measurement only) ─────────────────────────
    // Emit the additive IRR decomposition per scenario so the bear branch is
    // root-causeable from logs alone (the memo's −1.6% bear IRR was previously
    // un-diagnosable). Read-only over already-computed intermediates — no IRR
    // arithmetic is changed. Under the engine's decomposition the components
    // sum (within rounding) to scenarioIrr:
    //   cashYield + growthContribution + multipleChange === impliedIrr.
    for (const [scenarioName, r] of [
      ["bear", scenarioReturns.bear],
      ["base", scenarioReturns.base],
      ["bull", scenarioReturns.bull],
    ] as const) {
      console.log(
        `[SCENARIO-IRR-TRACE] ${factSheet?.ticker || "UNKNOWN"} ${scenarioName}: ` +
          `growthAssumption=${(r.growth * 100).toFixed(1)}%, ` +
          `cashYield=${(r.fcfYieldContribution * 100).toFixed(1)}%, ` +
          `growthContribution=${(r.growthContribution * 100).toFixed(1)}%, ` +
          `multipleChange=${(r.multipleChangeContribution * 100).toFixed(1)}%, ` +
          `scenarioIrr=${(r.impliedIrr * 100).toFixed(1)}%`,
      );
    }

    // [SCENARIO-IRR-TRACE] enrich: promote the bear decomposition to scalars so
    // the −1.6%-class outlier is queryable corpus-wide, not just in one log line.
    parsed.assumptionRationales.bearIrrCashYield =
      scenarioReturns.bear.fcfYieldContribution * 100;
    parsed.assumptionRationales.bearIrrGrowth =
      scenarioReturns.bear.growthContribution * 100;
    parsed.assumptionRationales.bearIrrMultipleChange =
      scenarioReturns.bear.multipleChangeContribution * 100;
    // ────────────────────────────────────────────────────────────────────────

    // Validate structural scenario constraints
    scenarioViolations.push(
      ...validateScenarioConstraints(
        parsed.base,
        parsed.bearOverrides,
        parsed.bullOverrides,
        DEFAULT_SCENARIO_CONSTRAINTS,
      ),
    );

    if (scenarioViolations.length > 0) {
      console.warn(
        "[Oracle] Scenario constraint violations detected:",
        scenarioViolations,
      );
    }

    if (!scenarios.bear.crossCheckPassed)
      console.warn("Bear scenario cross-check failed");
    if (!scenarios.base.crossCheckPassed)
      console.warn("Base scenario cross-check failed");
    if (!scenarios.bull.crossCheckPassed)
      console.warn("Bull scenario cross-check failed");

    const effectiveBase: any = {
      ...parsed.base,
      currentPrice: currentPrice,
      sharesOutstanding: scenarios.base.sharesOutstanding
    };
    
    let sensitivities: any = null;
    if (deterministicData?.isType5DataContaminated || deterministicData?.isUnresolvableValuation) {
      console.log(`[SENS-HARNESS] skipped: contaminated/unresolvable path — IV is the net-cash floor or N/A; sensitivity grid not meaningful. (ratified methodology)`);
    } else if ((deterministicData as any)?.__type4RiApplied === true) {
      console.log(`[SENS-HARNESS] skipped: Type-4 residual-income model supersedes the DCF spine (displayed IV = RI IV, not DCF IV). A DCF WACC/growth sensitivity grid would be computed off the discarded DCF base and would not describe the reported valuation. Suppressed (memo omits the sensitivity line). (ratified methodology)`);
    } else {
      sensitivities = runSensitivities(effectiveBase);
      
      if (Math.abs(sensitivities.baseIV - scenarios.base.intrinsicValuePerShare) > 0.05) {
        console.error(`[SENS-HARNESS] IV mismatch: sens baseIV ${sensitivities.baseIV.toFixed(2)} vs scenarios.base.IV ${scenarios.base.intrinsicValuePerShare.toFixed(2)}`);
      }
      
      if (sensitivities.ivDeltaPerWaccPlus1 > 0 || sensitivities.ivDeltaPerGrowthPlus2 < 0) {
        console.warn(`[SENS-SIGN] Unexpected sensitivity signs: WACC+1=${sensitivities.ivDeltaPerWaccPlus1}, Growth+2=${sensitivities.ivDeltaPerGrowthPlus2}`);
      }
    }

    return {
      ...parsed,
      scenarios,
      sensitivities,
      scenarioConstraintViolations: scenarioViolations,
      oracleIntrinsicValue: scenarios.base.intrinsicValuePerShare,
      valuationBridge: {
        // RI‑3 / Rule‑75 carve‑out: for an RI‑applied Type‑4 the bridge is
        // TangibleBook + PV(residual income) = Equity — there is no EV→equity
        // net‑debt walk. Surfacing the discarded DCF EV (sumPV+PVterminal) here is
        // the source of the non‑additive per‑share bridge, so mirror the RI equity
        // instead (EV ≡ Equity ≡ TBV + ΣPV(RI)). Standard entities unchanged.
        enterpriseValue:
          (scenarios.base as any).__ivMethod === "residual-income"
            ? `$${scenarios.base.totalEquityValue.toFixed(1)}`
            : `$${(scenarios.base.sumOfPVs + scenarios.base.pvOfTerminalValue).toFixed(1)}`,
        netDebt: (() => {
          // For Type 4 entities, netCash was forced to 0 before runScenarios.
          // adjustedNetCash will therefore be 0 or near-zero.
          // Display "N/A (excluded — Type 4 methodology)" rather than the raw
          // deterministicData.netDebt figure, which reflects deposit liabilities
          // and is not meaningful in the equity bridge for a bank or insurer.
          if (deterministicData?.isType4Financial) {
            return "N/A (excluded — Type 4 P/TBV methodology)";
          }
          // Standard entities: display adjusted net cash as before.
          // Negative adjustedNetCash = net debt position (subtract from equity value).
          // Positive adjustedNetCash = net cash position (add to equity value).
          const adj = Number(scenarios.base.adjustedNetCash) || 0;
          return adj >= 0
            ? `+$${adj.toFixed(1)}B (net cash)`
            : `-$${Math.abs(adj).toFixed(1)}B (net debt)`;
        })(),
        equityValue: `$${scenarios.base.totalEquityValue.toFixed(1)}`,
        sharesOutstanding: `${scenarios.base.sharesOutstanding.toLocaleString()}M`,
        intrinsicValuePerShare: `$${scenarios.base.intrinsicValuePerShare.toFixed(2)}`,
        goodwill: "N/A",
        // RI‑3: show the deterministic tangible‑book anchor (total, $MM) for the
        // RI bridge so TangibleBV + ΣPV(RI) reconciles to Equity on the page.
        tangibleBookValue:
          (scenarios.base as any).__ivMethod === "residual-income" &&
          (scenarios.base as any).__riTangibleBookAnchorPerShare != null
            ? `$${(((scenarios.base as any).__riTangibleBookAnchorPerShare * scenarios.base.sharesOutstanding)).toFixed(1)}`
            : "N/A",
        discountRate: `${(baseWithPrice.wacc * 100).toFixed(1)}%`,
        terminalGrowthRate: `${(baseWithPrice.terminalGrowthRate * 100).toFixed(1)}%`,
      },
    };
  };

  const executeWithTimeout = async (model: string, retries: number) => {
    return await withRetryJson("Mathematician", model, async () => {
      let timeoutId: NodeJS.Timeout | undefined;
      try {
        const response = await Promise.race([
          callGeminiWithTools({
            model,
            contents: [
              {
                role: "user",
                parts: [
                  {
                    text:
                      prompt +
                      "\n\nRespond only with valid JSON. Do not wrap in markdown code blocks.",
                  },
                ],
              },
            ],
            // [THINKING-EXPLICIT] Agent B thinking level now set explicitly from the
            // registry (previously implicit). See commit note — one of the B/D/E changes.
            config: { thinking_level: MODELS.MATHEMATICIAN.thinkingLevel },
            tools: [...fmpTools],
            symbol: factSheet.ticker,
            telemetry: { ledger, agent: "mathematician" },
            entry: MODELS.MATHEMATICIAN,
          }),
          new Promise<any>((_, reject) => {
            timeoutId = setTimeout(() => reject(new Error("Timeout")), 300000);
          })
        ]);
        return processMathematicianOutput(cleanJson(extractText(response)));
      } finally {
        if (timeoutId) clearTimeout(timeoutId);
      }
    }, retries);
  };

  try {
    return await executeWithTimeout(MODELS.MATHEMATICIAN.primary, 2);
  } catch (err: any) {
    const status = err?.status || err?.error?.code || err?.code || 0;
    if (status === 403 || status === 404 || status === "403" || status === "404") {
      console.warn(`[MATHEMATICIAN-MODEL-ERROR] model=${MODELS.MATHEMATICIAN.primary} status=${status}`);
      recordModelFailover({
        agent: "Mathematician",
        modelTried: MODELS.MATHEMATICIAN.primary,
        fallbackModel: MODELS.MATHEMATICIAN.fallback,
        statusCode: status,
        ticker: factSheet.ticker
      });
    } else {
      console.warn(
        `[Fallback] Mathematician primary failed (${err.message}), falling back to ${MODELS.MATHEMATICIAN.fallback}...`,
      );
      recordModelFailover({
        agent: "Mathematician",
        modelTried: MODELS.MATHEMATICIAN.primary,
        fallbackModel: MODELS.MATHEMATICIAN.fallback,
        statusCode: status,
        ticker: factSheet.ticker
      });
    }

    try {
      return await executeWithTimeout(MODELS.MATHEMATICIAN.fallback, 1);
    } catch (err2: any) {
      const status2 = err2?.status || err2?.error?.code || err2?.code || 0;
      if (status2 === 403 || status2 === 404 || status2 === "403" || status2 === "404") {
        console.warn(`[MATHEMATICIAN-MODEL-ERROR] model=${MODELS.MATHEMATICIAN.fallback} status=${status2}`);
      }
      throw new MathematicianModelUnavailableError(`Mathematician exhausted all models. Last error: ${err2?.message || err2}`);
    }
  }
}
export async function analyzeCompany(
  ticker: string,
  currentDateTime: string,
  onProgress?: (message: string) => void,
  registryType5Origin?: boolean,
  registryParentTicker?: string | null,
): Promise<CompanyAnalysis> {
  const symbol = ticker.toUpperCase();
  // MCP STYLE: Slim initial context. Only fetch identity.
  let institutionalData: any = null;
  let deterministicData: any = null;
  // F15 (Tuning v2.75 §44): hoisted out of the init-fetch try block. The F13-ROOT
  // Type-4 rebuild runs AFTER that try closes and reads all three; block-scoped
  // declarations caused `ReferenceError: aggregatedData is not defined` on every
  // Type-4 name (total analysis failure, confirmed on BRK-B). esbuild cannot catch
  // free identifiers — do not rely on the bundle gate for scope correctness.
  let aggregatedData: any = null;
  let usdConversionRate = 1;
  let reportedCurrencyForConversion: string = "USD";
  // F15b (Tuning v2.76 §44.9): same scope-escape class as F15. preClassifiedType (+ its
  // sibling preClassifiedOrigin) are read by the F13-ROOT [TYPE4-REBUILD] block after the
  // init-fetch try closes; block-scoped declarations threw `ReferenceError:
  // preClassifiedType is not defined` on every Type-4 name. Hoisted to function scope.
  let preClassifiedType = "Type 6";
  let preClassifiedOrigin: 'spinoff' | 'ipo' | 'heuristic' | null = null;

  try {
    onProgress?.("Fetching financial statements and market data...");
    // Fire AV OVERVIEW in parallel with FMP calls. fetchAvOverview has its own
    // 5s timeout and never throws — zero impact on non-spin-off tickers.
    const avOverviewPromise = fetchAvOverview(symbol).catch(() => null);
    const [
      profile,
      quote,
      ratios,
      metrics,
      keyMetricsTtm,
      ratiosTtm,
      income,
      cashFlow,
      balance,
      dcf,
      leveredDcf,
      cashFlowQuarterly,
      incomeGrowth,
      enterpriseValues,
      priceChangeData,
      spyPriceChangeData,
      treasuryData,
      ownerEarningsFmp,
      sharesFloatResult,
      analystEstimates,
    ] = await Promise.all([
      fetchFMP(`stable/profile`, { symbol }),
      fetchFMP(`stable/quote`, { symbol }),
      fetchFMP(`stable/ratios`, { symbol, limit: "1", period: "annual" }),
      fetchFMP(`stable/key-metrics`, { symbol, limit: "10", period: "annual" }),
      fetchFMP(`stable/key-metrics-ttm`, {
        symbol,
        period: "annual",
        limit: "1",
      }),
      fetchFMP(`stable/ratios-ttm`, { symbol }),
      fetchFMP(`stable/income-statement`, {
        symbol,
        limit: "10",
        period: "annual",
      }),
      fetchFMP(`stable/cash-flow-statement`, {
        symbol,
        limit: "10",
        period: "annual",
      }),
      fetchFMP(`stable/balance-sheet-statement`, {
        symbol,
        limit: "1",
        period: "annual",
      }),
      fetchFMP(`stable/discounted-cash-flow`, { symbol }),
      fetchFMP(`stable/levered-discounted-cash-flow`, { symbol }),
      fetchFMP(`stable/cash-flow-statement`, {
        symbol,
        limit: "4",
        period: "quarter",
      }),
      fetchFMP(`stable/income-statement-growth`, { symbol, limit: "1" }),
      fetchFMP(`stable/enterprise-values`, { symbol, limit: "1" }),
      fetchFMP(`stable/stock-price-change`, { symbol }),
      fetchFMP(`stable/stock-price-change`, { symbol: "SPY" }),
      (() => {
        let absoluteUrl = "/api/treasury";
        if (typeof window === "undefined") {
          const port = 3000;
          absoluteUrl = `http://127.0.0.1:${port}${absoluteUrl}`;
        }
        return getProxyAuthHeaders()
          .then((headers) => fetch(absoluteUrl, { headers }))
          .then((res) => res.json())
          .catch(() => ({ thirtyYearTreasuryYield: 4.5 }));
      })(),
      fetchFMP(`stable/owner-earnings`, { symbol, limit: "8" }),
      fetchFMP(`stable/shares-float`, { symbol }),
      fetchFMP(`stable/analyst-estimates`, {
        symbol,
        period: "annual",
        limit: "12",
      }),
    ]);

    // [QUOTE-SNAPSHOT-LOCK] Freeze this job's price snapshot for the agent tool.
    setQuoteSnapshot(symbol, quote);

    aggregatedData = {
      estimates: (() => {
        const arr = Array.isArray(analystEstimates) ? analystEstimates : [];
        const now = Date.now();
        const future = arr
          .filter((e: any) => e?.date && new Date(e.date).getTime() >= now)
          .sort(
            (a: any, b: any) =>
              new Date(a.date).getTime() - new Date(b.date).getTime(),
          );
        if (future.length > 0) return future[0];
        const past = arr
          .filter((e: any) => e?.date)
          .sort(
            (a: any, b: any) =>
              new Date(b.date).getTime() - new Date(a.date).getTime(),
          );
        return past[0] ?? null;
      })(),
      profile: Array.isArray(profile) ? profile[0] : profile,
      quote: Array.isArray(quote) ? quote[0] : quote,
      ratios: Array.isArray(ratios) ? ratios[0] : ratios,
      metrics: Array.isArray(metrics) ? metrics[0] : metrics,
      metricsHistory: Array.isArray(metrics) ? metrics : [],
      keyMetricsTtm: keyMetricsTtm?.[0] ?? null,
      ratiosTtm: Array.isArray(ratiosTtm) ? ratiosTtm[0] : (ratiosTtm ?? null),
      income: Array.isArray(income) ? income : [],
      cashFlow: Array.isArray(cashFlow) ? cashFlow : [],
      balance: Array.isArray(balance) ? balance : [],
      dcf: Array.isArray(dcf) ? dcf[0] : dcf,
      leveredDcf: Array.isArray(leveredDcf) ? leveredDcf[0] : leveredDcf,
      cashFlowQuarterly: Array.isArray(cashFlowQuarterly)
        ? cashFlowQuarterly
        : [],
      incomeGrowth: Array.isArray(incomeGrowth)
        ? incomeGrowth[0]
        : incomeGrowth,
      enterpriseValues: Array.isArray(enterpriseValues)
        ? enterpriseValues[0]
        : enterpriseValues,
      priceChange: Array.isArray(priceChangeData)
        ? priceChangeData[0]
        : priceChangeData,
      spyPriceChange: Array.isArray(spyPriceChangeData)
        ? spyPriceChangeData[0]
        : spyPriceChangeData,
      treasuryData: treasuryData,
      fmpOwnerEarnings: (() => {
        // FMP owner-earnings is QUARTERLY-only (a period=annual param is ignored).
        // Sum the trailing 4 quarters to an annual TTM figure so the OE cross-check
        // compares like-for-like against the ANNUAL synthetic Base Case. The prior
        // behavior (a single quarter) understated FMP OE ~4x and inflated the logged
        // divergence — e.g. WEN read +330% from one seasonal quarter ($64M) vs ~+50%
        // on a correct TTM basis (~$183M); MCD read +230% vs ~-17% on TTM.
        if (Array.isArray(ownerEarningsFmp)) {
          if (ownerEarningsFmp.length === 0) return null;
          const q = ownerEarningsFmp.slice(0, 4);
          const ttm = q.reduce(
            (sum, row) => sum + (Number(row?.ownersEarnings) || 0),
            0,
          );
          // If fewer than 4 quarters exist (e.g. recent IPO), scale the partial sum
          // up to an annual run-rate rather than under-reporting.
          const annualized = q.length < 4 ? ttm * (4 / q.length) : ttm;
          return { ...ownerEarningsFmp[0], ownersEarnings: annualized };
        }
        return ownerEarningsFmp && !ownerEarningsFmp._restricted
          ? ownerEarningsFmp
          : null;
      })(),
      sharesFloatResult,
    };

    console.log(
      `[INCOME-SHARES-CHECK] ${symbol}: income[0].weightedAverageShsOut=` +
        `${aggregatedData.income?.[0]?.weightedAverageShsOut}`,
    );

    institutionalData = aggregatedData;

    // Determine entity classification for Type 5 before getDeterministicData
    preClassifiedType = "Type 6";
    preClassifiedOrigin = null;
    const profileInfoForPreClass = aggregatedData?.profile || {};
    const tempRevenue = aggregatedData?.income?.[0]?.revenue || 0;
    const annualIncomeCount = (aggregatedData?.income || []).filter(
      (i: any) => i.period === "FY" || !i.period,
    ).length;

    // Gate 0: Known spin-off override — FMP backfills parent-company history for these tickers,
    // defeating both the ipoDate and sparse-history gates. Must run FIRST so getDeterministicData
    // receives the correct entityType and computes isType5DataContaminated accurately.
    // This set is the single source of truth — keep it in sync with KNOWN_TYPE5_TICKERS below.
    const KNOWN_TYPE5_FOR_PRECLASSIFICATION = new Set([
      "SNDK", // WDC NAND division spin-off, separated Feb 2024
    ]);
    if (registryType5Origin || KNOWN_TYPE5_FOR_PRECLASSIFICATION.has(symbol.toUpperCase())) {
      preClassifiedType = "Type 5";
      preClassifiedOrigin = 'spinoff';
    }

    // Gate 1: FMP ipoDate within last 2 years
    if (preClassifiedType !== "Type 5" && profileInfoForPreClass.ipoDate) {
      const ipoDateObj = new Date(profileInfoForPreClass.ipoDate);
      const twoYearsAgo = new Date();
      twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
      if (ipoDateObj > twoYearsAgo) {
        preClassifiedType = "Type 5";
      }
      if (ipoDateObj > twoYearsAgo && preClassifiedOrigin === null) {
        preClassifiedOrigin = 'ipo';
      }
    }

    // Gate 2: Revenue-significant but sparse annual history (backfilled spin-off detection)
    if (
      preClassifiedType !== "Type 5" &&
      tempRevenue >= 500000000 &&
      annualIncomeCount < 3
    ) {
      preClassifiedType = "Type 5";
    }
    if (tempRevenue >= 500000000 && annualIncomeCount < 3 && preClassifiedOrigin === null) {
      preClassifiedOrigin = 'heuristic';
    }

    // AV OVERVIEW: already in-flight since before Promise.all — just collect result.
    const avOverviewData = await avOverviewPromise;
    const avIpoDate = avOverviewData?.IPO || null;

    // Gate 3 (AV): AV reflects actual exchange listing date; FMP ipoDate often
    // reflects the parent company's original founding date for spin-offs.
    if (preClassifiedType !== "Type 5" && avIpoDate) {
      const avIpoDateObj = new Date(avIpoDate);
      const twoYearsAgoAv = new Date();
      twoYearsAgoAv.setFullYear(twoYearsAgoAv.getFullYear() - 2);
      if (avIpoDateObj > twoYearsAgoAv) {
        preClassifiedType = "Type 5";
        console.log(
          `[TYPE5] ${symbol.toUpperCase()}: Pre-classified Type 5 via AV listing date ${avIpoDate} — FMP ipoDate was stale.`,
        );
      }
      if (avIpoDateObj > twoYearsAgoAv && preClassifiedOrigin === null) {
        preClassifiedOrigin = 'ipo';
      }
    }
    
    if (preClassifiedType === "Type 5") {
      console.log(`[TYPE5-IPO-ORIGIN] ${symbol.toUpperCase()}: type5Origin=${preClassifiedOrigin} (spinoff=registry/known-set; ipo=listing-date gate; heuristic=sparse-history gate)`);
    }

    // Store on aggregatedData so the main classifier can reference it without
    // a second fetch.
    (aggregatedData as any).avIpoDate = avIpoDate;
    (aggregatedData as any).type5Origin = preClassifiedOrigin;

    // --- DETERMINISTIC CURRENCY NORMALIZATION ---
    // FMP returns financial statements in the company's reporting currency, not USD.
    // All conversion math happens here in TypeScript — no AI touches these numbers.
    // income-statement reportedCurrency is authoritative — profile.currency reflects
    // trading currency (always USD for NASDAQ/NYSE listings), not reporting currency.
    const incomeCurrency: string = (
      aggregatedData.income?.[0]?.reportedCurrency || ""
    ).toUpperCase();
    const profileCurrency: string = (
      aggregatedData.profile?.currency || "USD"
    ).toUpperCase();
    reportedCurrencyForConversion =
      incomeCurrency && incomeCurrency !== "USD"
        ? incomeCurrency
        : profileCurrency;

    usdConversionRate = 1;
    if (reportedCurrencyForConversion !== "USD") {
      usdConversionRate = await fetchUsdConversionRate(
        reportedCurrencyForConversion,
      );
      console.log(
        `[CURRENCY] ${symbol}: reports in ${reportedCurrencyForConversion}. Conversion rate applied: ${usdConversionRate}`,
      );
    }
    // --- END CURRENCY NORMALIZATION ---

    deterministicData = getDeterministicData(
      symbol,
      aggregatedData,
      preClassifiedType,
      usdConversionRate,
      "USD", // price is always quoted in USD on US exchanges
      reportedCurrencyForConversion,
      (aggregatedData as any).type5Origin ?? null
    );
    if (treasuryData?.thirtyYearTreasuryYield) {
      deterministicData.thirtyYearTreasuryYield =
        treasuryData.thirtyYearTreasuryYield;
    }
  } catch (err) {
    console.error("Initial context fetch failed:", err);
    // Surface the full stack so runtime errors inside getDeterministicData
    // or the pre-classification block are visible in logs.
    if (err instanceof Error) {
      console.error("[INIT-FETCH-STACK]", err.stack);
    }
  }

  // Determine if we are in production mode
  const isProduction = !!(
    institutionalData &&
    (institutionalData.profile || institutionalData.quote)
  );

  if (!institutionalData?.profile && !institutionalData?.quote) {
    throw new Error(
      `NOT_FOUND: The stock symbol '${symbol}' is not listed on any of the markets that Oracle Insights analyzes, or could not be found.`,
    );
  }

  // Rule 0: Entity Type Classifier [PRE-ANALYSIS · FATAL]
  const profileInfo = institutionalData?.profile || {};
  const sicCodeRaw = profileInfo.sicCode || "";
  const sicCode = parseInt(String(sicCodeRaw)) || 0;
  const currentTicker = symbol.toUpperCase().replace(/\./g, "-");
  const revenue = (deterministicData?.lastYearRevenue || 0) * 1000000;
  const last4QIncome = (institutionalData?.income || [])
    .slice(0, 4)
    .reduce((acc: number, curr: any) => acc + (curr.operatingIncome || 0), 0);
  const cumulativeFcf = (institutionalData?.cashFlow || []).reduce(
    (acc: number, curr: any) => acc + (curr.freeCashFlow || 0),
    0,
  );

  let entityType = "Type 6";
  const noRevenue =
    (institutionalData?.income || []).length > 0 &&
    (institutionalData?.income || []).every((i: any) => !i.revenue);
  const isSpacSuffix =
    currentTicker.endsWith("-U") ||
    currentTicker.endsWith("-WT") ||
    currentTicker.endsWith("-R");

  if (noRevenue || sicCode === 6770 || isSpacSuffix) {
    entityType = "Type 1";
  } else if (
    revenue < 50000000 ||
    (revenue < 500000000 && last4QIncome <= 0 && cumulativeFcf <= 0)
  ) {
    entityType = "Type 2";
  } else if ((sicCode >= 6500 && sicCode <= 6552) || sicCode === 6798) {
    entityType = "Type 3";
  } else if (
    (sicCode >= 6020 && sicCode <= 6099) ||
    (sicCode >= 6311 && sicCode <= 6399) ||
    (sicCode >= 6150 && sicCode <= 6199)
  ) {
    entityType = "Type 4";
  }

  let type4ClassificationBasis: 'sic' | 'sector-industry-fallback' | 'fallback-declined' | null = null;
  if (entityType === "Type 4") {
    type4ClassificationBasis = "sic";
  }
  let type4FallbackDeclined = false;

  // TYPE 4 TICKER/SECTOR FALLBACK
  // FMP frequently returns null sicCode for large-cap financials, causing the
  // SIC gate to miss classification. This fallback catches known financial
  // entities by ticker and by FMP sector string. Add tickers here as needed.
  const KNOWN_TYPE4_TICKERS = new Set([
    "JPM",
    "BAC",
    "WFC",
    "C",
    "GS",
    "MS",
    "USB",
    "PNC",
    "TFC",
    "COF",
    "BRK-A",
    "BRK-B",
    "MKL",
    "FFH",
    "PRG",
    "AIG",
    "MET",
    "PRU",
    "AFL",
    "ALL",
  ]);
  const fmpSector = (profileInfo.sector || "").toLowerCase();
  const isSectorFinancial =
    fmpSector.includes("financial") ||
    fmpSector.includes("insurance") ||
    fmpSector.includes("banking");

  // FINANCIAL-FINGERPRINT GUARD (operating-company false-positive suppression)
  // FMP occasionally mislabels asset-heavy operating companies (equipment rental,
  // transport, industrials) with sector="Financial Services" and a null SIC — e.g.
  // SUNB (Sunbelt Rentals): sector="Financial Services", sicCode="", force-classified
  // Type 4 and benchmarked against insurers. A genuine Type 4 (bank/insurer/lender) is
  // investment-asset-heavy and PP&E-light; an operating company mislabeled financial
  // shows the inverse. Require a financial balance-sheet fingerprint before a
  // sector-string-only signal can force Type 4. The hand-verified KNOWN_TYPE4_TICKERS
  // allowlist bypasses this guard unconditionally.
  const t4Bs = institutionalData?.balance?.[0] || {};
  const t4TotalAssets = Number(t4Bs.totalAssets) || 0;
  const t4Investments =
    (Number(t4Bs.totalInvestments) || 0) +
    (Number(t4Bs.longTermInvestments) || 0);
  const t4NetPpe = Number(t4Bs.propertyPlantEquipmentNet) || 0;
  const t4InvToAssets = t4TotalAssets > 0 ? t4Investments / t4TotalAssets : 0;
  const t4PpeToAssets = t4TotalAssets > 0 ? t4NetPpe / t4TotalAssets : 0;
  // Operating-company shape: negligible investment securities AND a material fixed-asset base.
  const looksLikeOperatingCompany =
    t4TotalAssets > 0 && t4InvToAssets < 0.1 && t4PpeToAssets > 0.1;
  const isKnownType4 = KNOWN_TYPE4_TICKERS.has(currentTicker);

  const TYPE4_FLOAT_INDUSTRY_ALLOWLIST = [
    "bank",
    "credit services",
    "mortgage finance",
    "financial conglomerates",
  ];

  if (
    entityType !== "Type 4" &&
    (isKnownType4 || (isSectorFinancial && !looksLikeOperatingCompany))
  ) {
    if (isKnownType4) {
      entityType = "Type 4";
      type4ClassificationBasis = "sector-industry-fallback";
      console.log(
        `[TYPE4-FALLBACK] ${currentTicker}: SIC-based gate missed. Classified Type 4 via ticker allowlist fallback (sicCode=${sicCodeRaw}, sector="${profileInfo.sector}").`,
      );
    } else {
      const industryNorm = (profileInfo.industry || "").toLowerCase().trim();
      const isAllowedIndustry =
        (industryNorm.includes("insurance") && !industryNorm.includes("insurance brokers")) ||
        TYPE4_FLOAT_INDUSTRY_ALLOWLIST.some((kw) => industryNorm.includes(kw));

      if (isAllowedIndustry) {
        entityType = "Type 4";
        type4ClassificationBasis = "sector-industry-fallback";
        console.log(
          `[TYPE4-FALLBACK] ${currentTicker}: SIC-based gate missed. Classified Type 4 via sector+industry fallback (sicCode=${sicCodeRaw}, sector="${profileInfo.sector}", industry="${profileInfo.industry}").`,
        );
      } else {
        entityType = "Type 6";
        type4ClassificationBasis = "fallback-declined";
        type4FallbackDeclined = true;
        console.log(
          `[TYPE4-FALLBACK-DECLINED] ${currentTicker}: sector="${profileInfo.sector}" but industry="${profileInfo.industry}" is not float-based — classified Type 6 per Rule 0 float-as-raw-material test.`,
        );
      }
    }
  } else if (
    entityType !== "Type 4" &&
    isSectorFinancial &&
    looksLikeOperatingCompany &&
    !isKnownType4
  ) {
    console.warn(
      `[TYPE4-FALLBACK-SUPPRESSED] ${currentTicker}: FMP sector="${profileInfo.sector}" but balance sheet is operating-company shaped ` +
        `(investments/assets ${(t4InvToAssets * 100).toFixed(1)}%, PP&E/assets ${(t4PpeToAssets * 100).toFixed(1)}%). Not forcing Type 4.`,
    );
  }

  // TYPE 5 KNOWN SPIN-OFF OVERRIDE
  // FMP maps new spin-off tickers to parent company history (ipoDate + backfilled financials),
  // causing both automatic Type 5 detection gates (ipoDate and sparse-history) to miss
  // genuine recent spin-offs. Add tickers to this set whenever FMP cannot be relied on
  // to flag the entity correctly via data signals alone.
  const KNOWN_TYPE5_TICKERS = new Set([
    "SNDK", // WDC NAND division spin-off, separated Feb 2024
  ]);

  let type5Origin = (institutionalData as any)?.type5Origin || null;

  if (entityType !== "Type 4" && (registryType5Origin || KNOWN_TYPE5_TICKERS.has(currentTicker))) {
    entityType = "Type 5";
    type5Origin = "spinoff"; // registry evidence is stronger, overrides any preclassified 'ipo'
    console.log(
      `[TYPE5] ${currentTicker}: Forced Type 5 via ${registryType5Origin ? 'spinoffEvents registry' : 'KNOWN_TYPE5_TICKERS'} — recent spin-off; FMP backfills parent-company history, defeating automatic detection gates.`,
    );
  }

  // Compute once at outer scope — used by Type 5 sparse-history gate AND surfaced in UI
  const annualFilingCount = (institutionalData?.income || []).filter(
    (i: any) => i.period === "FY" || !i.period,
  ).length;

  if (entityType !== "Type 4") {
    // Primary check: resolve listing date using AV as first choice (reflects actual
    // exchange listing date), falling back to FMP ipoDate (may reflect parent company).
    const avIpoDateForClassifier =
      (institutionalData as any)?.avIpoDate || null;
    const effectiveIpoDate =
      avIpoDateForClassifier || profileInfo.ipoDate || null;

    if (effectiveIpoDate) {
      const ipoDateObj = new Date(effectiveIpoDate);
      const twoYearsAgo = new Date();
      twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
      if (ipoDateObj > twoYearsAgo && entityType !== "Type 5") {
        entityType = "Type 5";
        if (!type5Origin) type5Origin = "ipo"; // ipoDate gate => 'ipo' only if origin null
        const ipoSource = avIpoDateForClassifier ? "AV" : "FMP";
        console.log(
          `[TYPE5] ${currentTicker}: Recent IPO/spin-off detected via ${ipoSource} listing date ${effectiveIpoDate}.`,
        );
      }
    }

    // Secondary check: Revenue-significant company with sparse annual history
    // Catches spin-offs where FMP ipoDate reflects the parent company's founding date
    // rather than the spin-off separation date (common FMP limitation).
    if (
      entityType !== "Type 5" &&
      entityType !== "Type 2" &&
      revenue >= 500000000 &&
      annualFilingCount < 3
    ) {
      entityType = "Type 5";
      if (!type5Origin) type5Origin = "heuristic"; // sparse gate => 'heuristic' only if origin null
      console.log(
        `[TYPE5] ${currentTicker}: Reclassified to Type 5 — revenue-significant ($${(revenue / 1000000000).toFixed(1)}B) but only ${annualFilingCount} annual filing(s) in FMP. Likely a recent spin-off or IPO with limited standalone history.`,
      );
    }
  }

  // Sync the classifier-refined origin back onto deterministicData — all downstream
  // prose, verdict branches, and observability read deterministicData.type5Origin.
  if (deterministicData && (deterministicData as any).type5Origin !== type5Origin) {
    (deterministicData as any).type5Origin = type5Origin;
    if ((deterministicData as any).isType5DataContaminated) {
      (deterministicData as any).type5UnvaluableReason =
        type5Origin === 'ipo' ? 'no-earnings-anchor' : 'parent-contamination';
    }
  }

  // Handle Type 1 & 2 Fatal Stops
  if (entityType === "Type 1" || entityType === "Type 2") {
    const disclosure =
      entityType === "Type 1"
        ? "Entity classified as SPAC, blank-check company, or shell with insufficient post-merger operating history. Oracle Insight requires a minimum of 4 quarters of post-merger operating history before analysis is possible. No analysis issued."
        : "Entity is pre-revenue or early-stage. Oracle Insight requires a minimum of 3 years of positive FCF history to anchor valuation. No valuation or verdict issued.";

    return {
      ...(deterministicData || {}),
      ticker: symbol,
      name: profileInfo.companyName || symbol,
      description: disclosure,
      summary: `[Entity Type: ${entityType}] ${disclosure}`,
      oracleJudgment: disclosure,
      oracleFramework: {
        decision: { value: "PASS", rationale: disclosure },
        moat: { value: "None", rationale: "N/A" },
        management: { value: "Average", rationale: "N/A" },
        understandable: { value: "Moderate", rationale: "N/A" },
        longTerm: { value: "Moderate", rationale: "N/A" },
        stability: { value: "Moderate", rationale: "N/A" },
        marginOfSafety: { value: "Insufficient", rationale: "N/A" },
        riskTier: "STABLE",
        riskTierRationale: "N/A",
        oracleIntrinsicValue: 0,
      },
      capitalAllocationDecision: "PASS",
      confidenceScore: { score: 0, label: "Low", rationale: disclosure },
      intrinsicValue: 0,
      buyUnder: null,
      allocation: null,
      buyZoneLow: null,
      buyZoneHigh: null,
      isProduction,
    } as any;
  }

  // Multi-Agent Orchestration
  // Inject pre-computed entity classification so Mathematician and Oracle
  // receive the same entity type the SIC-based gate determined.
  if (deterministicData) {
    // ─── F13-ROOT (classification-timing fix, Tuning v2.73 §43): getDeterministicData was called
    // above with `preClassifiedType`, which is only ever "Type 6"/"Type 5" — NEVER "Type 4". The
    // type4Valuation IIFE is guarded by `if (entityType !== "Type 4") return null`, so it returned
    // null for EVERY financial, leaving deterministicData.type4Valuation null. Downstream, the
    // computeType4TerminalValue call site is gated on `deterministicData?.type4Valuation`, so the
    // P/TBV terminal was always skipped and the DCF always fell back to earnings×multiple — the
    // observed 100% Type-4 failure rate. (This also made the F13 ROTCE-sourcing code dead, since it
    // lives inside that null IIFE.) Now that the SIC/sector/allowlist gate has resolved the REAL
    // entityType, rebuild deterministicData with it so type4Valuation is actually computed.
    // getDeterministicData is pure + synchronous (operates on already-fetched aggregatedData, ZERO
    // FMP calls), so re-invoking is safe and deterministic; we preserve the only prior mutation
    // (thirtyYearTreasuryYield). Fires once, only for Type-4 names whose first pass produced null.
    if (entityType === "Type 4" && !deterministicData.type4Valuation && aggregatedData) {
      const prevTreasury = deterministicData.thirtyYearTreasuryYield;
      const rebuilt = getDeterministicData(
        symbol,
        aggregatedData,
        entityType,
        usdConversionRate,
        "USD",
        reportedCurrencyForConversion,
        (aggregatedData as any).type5Origin ?? null,
      );
      if (rebuilt) {
        if (prevTreasury != null) rebuilt.thirtyYearTreasuryYield = prevTreasury;
        (rebuilt as any).type4ValuationRebuilt = true;
        deterministicData = rebuilt;
        console.log(
          `[TYPE4-REBUILD] ${currentTicker}: entityType resolved to Type 4 after the initial ` +
            `getDeterministicData (preClassifiedType=${preClassifiedType}); rebuilt deterministicData so ` +
            `type4Valuation is computed. Now ${deterministicData.type4Valuation ? "PRESENT" : "STILL NULL"}.`,
        );
      }
    }
    deterministicData.entityType = entityType;
    deterministicData.isType4Financial = entityType === "Type 4";
    deterministicData.type4ClassificationBasis = type4ClassificationBasis;
    deterministicData.type4FallbackDeclined = type4FallbackDeclined;
    if (entityType === "Type 4") {
      const t4SicCode = parseInt(String(profileInfo?.sicCode || "")) || 0;
      const t4Ticker = (currentTicker || "").toUpperCase();

      const INSURANCE_CONGLOMERATE_TICKERS = new Set([
        "BRK-A",
        "BRK-B",
        "MKL",
        "FFH",
      ]);
      const ASSET_MANAGER_TICKERS = new Set([
        "BLK",
        "BAM",
        "APO",
        "KKR",
        "CG",
        "ARES",
        "BX",
      ]);

      if (INSURANCE_CONGLOMERATE_TICKERS.has(t4Ticker)) {
        deterministicData.type4SubType = "INSURANCE_CONGLOMERATE";
      } else if (t4SicCode >= 6311 && t4SicCode <= 6399) {
        deterministicData.type4SubType = "INSURER";
      } else if (
        (t4SicCode >= 6020 && t4SicCode <= 6099) ||
        (t4SicCode >= 6100 && t4SicCode <= 6149)
      ) {
        deterministicData.type4SubType = "BANK";
      } else if (
        (t4SicCode >= 6150 && t4SicCode <= 6199) ||
        ASSET_MANAGER_TICKERS.has(t4Ticker)
      ) {
        deterministicData.type4SubType = "ASSET_MANAGER";
      } else {
        deterministicData.type4SubType = "FINANCIAL_OTHER";
      }
    } else {
      deterministicData.type4SubType = null;
    }
  }
  onProgress?.("Agent A: Auditing 10-year financial history...");
  const peerBenchmarks = await fetchPeerBenchmarks(
    symbol,
    entityType,
    deterministicData,
    registryParentTicker,
  );
  // [S6] Reset the per-job Gemini JSON parse-retry counters before any agent
  // runs. analyzeCompanySSE delegates to analyzeCompany, so this covers both
  // entry points.
  resetGeminiJsonRetryStats();
  // [TOKEN-LEDGER] Per-analysis accumulator for token usage + actual model
  // versions, keyed by agent. Measurement only — threaded into every agent
  // runner; read into observability at the end so the analysis_completed
  // PostHog enrichment and the verdict log can slice by model. Does not alter
  // any request behavior.
  const ledger = createTokenLedger();
  // [MODEL-1] Set true if an Oracle call finishes with MAX_TOKENS (set in
  // executeAnalysis below; closed over). Rides analysis_completed via observability.
  let oracleTruncated = false;
  const factSheet = await runHistorian(
    symbol,
    institutionalData,
    deterministicData,
    peerBenchmarks,
    ledger,
  );
  factSheet.isType5DataContaminated =
    deterministicData?.isType5DataContaminated || false;

  // ════════════════════════════════════════════════════════════════
  // STRUCTURAL-DECLINE-LOCK (Stage 1 — deterministic + Historian evidence)
  // Code backstop for factSheet.structuralDeclineAssessment.isStructuralDecline.
  // The boolean drives three downstream enforcements (Oracle C-15a PASS gate,
  // T1 verdict override, confidence cap at 55) but is otherwise LLM-set. This
  // lock coerces it true — one-directionally, NEVER true→false — when the
  // deterministic triggers PLUS the Historian's OWN replacement-threat finding
  // satisfy >=2 of the 3 structural-decline triggers. Runs BEFORE the
  // Mathematician so the -5% DCF growth floor (GROWTH-NARRATIVE-LOCK) applies.
  // The pure deterministic-only case (both triggers, no named permanence) is
  // deliberately NOT coerced here — that pattern is indistinguishable from a
  // cyclical trough and is deferred to Stage 2 where the Analyst's
  // isCyclicalTrough flag can disambiguate.
  {
    if (!factSheet.structuralDeclineAssessment) {
      factSheet.structuralDeclineAssessment = {
        isStructuralDecline: false,
        triggersConfirmed: "",
        replacementThreatDescription: "",
      };
    }
    const hm = deterministicData?.historicalMetrics;
    const isType4 = deterministicData?.entityType === "Type 4";
    const revTrig = hm?.revenueGrowth5Yr != null && hm.revenueGrowth5Yr < 0;
    const gmTrig = !isType4 &&
      hm?.grossMarginTrend5Yr != null && hm.grossMarginTrend5Yr < -300;
    const omTrig = !isType4 &&
      hm?.operatingMarginTrend5Yr != null && hm.operatingMarginTrend5Yr < -300;
    const detTriggerCount = (revTrig ? 1 : 0) + (gmTrig || omTrig ? 1 : 0);

    const threatRaw = String(
      factSheet.structuralDeclineAssessment.replacementThreatDescription || "",
    ).trim();
    const threatLower = threatRaw.toLowerCase();
    const historianNamedThreat =
      threatRaw.length >= 12 &&
      ![
        "none",
        "n/a",
        "na",
        "unknown",
        "no replacement threat",
        "no threat",
        "not applicable",
      ].some((p) => threatLower === p || threatLower.startsWith(p));

    const alreadyFlagged =
      factSheet.structuralDeclineAssessment.isStructuralDecline === true;

    // Coerce when the Historian named a substantive replacement threat AND at
    // least one deterministic trigger fired = >=2 of 3 by the model's own
    // evidence. Catches the internal-contradiction case (model named the threat
    // AND data confirms decline, yet wrote isStructuralDecline=false).
    if (!alreadyFlagged && historianNamedThreat && detTriggerCount >= 1 && deterministicData?.entityType !== "Type 5") {
      factSheet.structuralDeclineAssessment.isStructuralDecline = true;
      const firedList = [
        revTrig
          ? `revenue 5yr CAGR ${(hm.revenueGrowth5Yr * 100).toFixed(1)}% < 0`
          : null,
        gmTrig ? `gross margin ${hm.grossMarginTrend5Yr}bps < -300` : null,
        omTrig
          ? `operating margin ${hm.operatingMarginTrend5Yr}bps < -300`
          : null,
      ]
        .filter(Boolean)
        .join("; ");
      factSheet.structuralDeclineAssessment.triggersConfirmed = `${firedList}; named replacement threat: ${threatRaw}`;
      console.warn(
        `[STRUCTURAL-DECLINE-LOCK Stage 1] ${factSheet.ticker}: coerced ` +
          `isStructuralDecline=true. Historian named a replacement threat and ` +
          `${detTriggerCount} deterministic trigger(s) fired (${firedList}), ` +
          `but the Historian wrote isStructuralDecline=false. >=2 of 3 triggers ` +
          `satisfied -> flag forced true before Mathematician (-5% floor applies).`,
      );
    }
  }

  onProgress?.("Agent D: Fetching SEC filings from EDGAR...");
  const edgarContext = await fetchEdgarContext(symbol);

  onProgress?.("Agent B: Building DCF model and valuation bridge...");
  onProgress?.("Agent D: Researching current business intelligence...");
  onProgress?.("Agent F: Reading the brand pulse...");
  // Agents B, D, and F run in parallel — none depends on the others, all depend on Agent A
  const [mathEngine, intelligenceBrief, pulseOutput] = await Promise.all([
    runMathematician(factSheet, deterministicData, ledger),
    runAnalyst(
      symbol,
      institutionalData?.profile?.companyName || symbol,
      entityType,
      edgarContext,
      deterministicData?.type5Origin ?? null,
      ledger,
    ),
    runPulse(
      symbol,
      institutionalData?.profile?.companyName || symbol,
      deterministicData,
      entityType,
      undefined,
      ledger,
    ),
  ]);
  onProgress?.("Agent D: Intelligence brief complete.");
  onProgress?.("Agent F: Pulse complete.");

  const analystIntelligence = {
    keyContextSummary: intelligenceBrief?.keyContextSummary ?? "",
    upcomingCatalysts: Array.isArray(intelligenceBrief?.upcomingCatalysts) ? intelligenceBrief.upcomingCatalysts : [],
    currentMaterialRisks: Array.isArray(intelligenceBrief?.currentMaterialRisks) ? intelligenceBrief.currentMaterialRisks : [],
    sourcesConsulted: Array.isArray(intelligenceBrief?.sourcesConsulted) ? intelligenceBrief.sourcesConsulted : [],
    searchConfidenceScore: intelligenceBrief?.searchConfidenceScore ?? 0,
    edgarFilingsUsed: intelligenceBrief?.edgarFilingsUsed ?? false,
  };

  // ════════════════════════════════════════════════════════════════
  // STRUCTURAL-DECLINE-LOCK (Stage 2 — Analyst reconciliation)
  // The structural-decline determination is split across two agents: the
  // Historian (financials) and the Analyst (live search, with isCyclicalTrough
  // to separate secular decline from a cyclical bottom). Only the Historian's
  // flag fed the T1 verdict gate; the Analyst's troughClassification was
  // previously invisible to the verdict. This stage reconciles them: if the
  // Analyst confirms structural decline at MEDIUM+ confidence and did NOT flag
  // a cyclical trough, coerce the flag the downstream gates read. Runs AFTER
  // the Mathematician, so a NEW coercion here does not re-apply the -5% growth
  // floor — that residual is already covered by the C-15a(c) verbatim
  // disclosure ("do not treat Bear Case IV as a floor"). The verdict gate and
  // confidence cap (the safety-critical enforcements) fire correctly because
  // both read this flag post-Oracle.
  {
    if (!factSheet.structuralDeclineAssessment) {
      factSheet.structuralDeclineAssessment = {
        isStructuralDecline: false,
        triggersConfirmed: "",
        replacementThreatDescription: "",
      };
    }
    const tc = intelligenceBrief?.troughClassification;
    const analystStructural = tc?.isStructuralDecline === true;
    const analystCyclical = tc?.isCyclicalTrough === true;
    const conf = String(tc?.classificationConfidence || "LOW").toUpperCase();
    const analystConfident = conf === "HIGH" || conf === "MEDIUM";
    const alreadyFlagged =
      factSheet.structuralDeclineAssessment.isStructuralDecline === true;

    const hm = deterministicData?.historicalMetrics;
    const isType4 = deterministicData?.entityType === "Type 4";
    const revTrig = hm?.revenueGrowth5Yr != null && hm.revenueGrowth5Yr < 0;
    const gmTrig = !isType4 &&
      hm?.grossMarginTrend5Yr != null && hm.grossMarginTrend5Yr < -300;
    const omTrig = !isType4 &&
      hm?.operatingMarginTrend5Yr != null && hm.operatingMarginTrend5Yr < -300;
    const detTriggerCount = (revTrig ? 1 : 0) + (gmTrig || omTrig ? 1 : 0);

    if (
      !alreadyFlagged &&
      analystStructural &&
      analystConfident &&
      !analystCyclical
    ) {
      factSheet.structuralDeclineAssessment.isStructuralDecline = true;
      factSheet.structuralDeclineAssessment.triggersConfirmed =
        `Analyst (Agent D) confirmed structural decline at ${conf} confidence ` +
        `(isCyclicalTrough=false). ${tc?.classificationRationale || ""}`.trim();
      console.warn(
        `[STRUCTURAL-DECLINE-LOCK Stage 2] ${factSheet.ticker}: coerced ` +
          `isStructuralDecline=true from Analyst troughClassification ` +
          `(confidence=${conf}, isCyclicalTrough=false). Historian had not ` +
          `flagged it. Verdict gate + confidence cap will fire; bear IV floor ` +
          `unchanged (C-15a(c) disclosure covers the residual).`,
      );
    } else if (
      !alreadyFlagged &&
      detTriggerCount >= 2 &&
      !analystStructural &&
      !analystCyclical
    ) {
      // Unresolved gap: both deterministic triggers fired but NEITHER agent
      // confirmed permanence (replacement threat) NOR classified it cyclical.
      // Do NOT fire the PASS gate without permanence evidence (cyclical-trough
      // false-positive risk). Surface loudly so it is not silent.
      console.warn(
        `[STRUCTURAL-DECLINE-LOCK Stage 2] ${factSheet.ticker}: UNRESOLVED ` +
          `DECLINE GAP — both deterministic triggers fired (rev ` +
          `${hm?.revenueGrowth5Yr != null ? (hm.revenueGrowth5Yr * 100).toFixed(1) + "%" : "n/a"}, ` +
          `gm ${hm?.grossMarginTrend5Yr ?? "n/a"}bps, om ${hm?.operatingMarginTrend5Yr ?? "n/a"}bps) ` +
          `but neither the Historian nor the Analyst confirmed a replacement ` +
          `threat and neither flagged a cyclical trough. Verdict left untouched ` +
          `(permanence unproven); review whether this is early-stage decline.`,
      );
    }
  }

  // ────────────────────────────────────────────────────────────────
  // EARLY-DECLINE WATCH (non-blocking, informational)
  // Catches directional erosion BEFORE the full 2-of-3 structural-decline
  // threshold. Feeds keyRisks + a modest confidence cap (70). Does NOT touch
  // the verdict (no PASS gate) — a leading-indicator caution, not a confirmed
  // melt. Suppressed if the structural-decline flag is already set (the
  // stronger signal supersedes it).
  (factSheet as any).earlyDeclineWatch = null;
  {
    const flagged =
      factSheet.structuralDeclineAssessment?.isStructuralDecline === true;
    const hm = deterministicData?.historicalMetrics;
    const isType4 = deterministicData?.entityType === "Type 4";
    if (!flagged && hm) {
      const revTrig = hm.revenueGrowth5Yr != null && hm.revenueGrowth5Yr < 0;
      const gmTrig = !isType4 &&
        hm.grossMarginTrend5Yr != null && hm.grossMarginTrend5Yr < -300;
      const omTrig = !isType4 &&
        hm.operatingMarginTrend5Yr != null && hm.operatingMarginTrend5Yr < -300;
      const detTriggerCount = (revTrig ? 1 : 0) + (gmTrig || omTrig ? 1 : 0);

      // Soft margin compression (half the structural -300bps threshold).
      const gmSoft = !isType4 &&
        hm.grossMarginTrend5Yr != null && hm.grossMarginTrend5Yr < -150;
      const omSoft = !isType4 &&
        hm.operatingMarginTrend5Yr != null && hm.operatingMarginTrend5Yr < -150;
      // Near-flat revenue (decelerating toward zero).
      const nearFlatRev =
        hm.revenueGrowth5Yr != null &&
        hm.revenueGrowth5Yr >= 0 &&
        hm.revenueGrowth5Yr < 0.02;

      const analystThreatNamed =
        Array.isArray(intelligenceBrief?.currentMaterialRisks) &&
        intelligenceBrief.currentMaterialRisks.some((r: any) =>
          /substitut|disrupt|secular|obsolet|declin|replac|structural/i.test(
            String(r),
          ),
        );

      const watchFired =
        detTriggerCount === 1 ||
        (nearFlatRev && (gmSoft || omSoft)) ||
        ((gmSoft || omSoft) && analystThreatNamed);

      if (watchFired) {
        const parts = [
          revTrig
            ? `revenue 5yr CAGR negative (${(hm.revenueGrowth5Yr * 100).toFixed(1)}%)`
            : null,
          nearFlatRev
            ? `revenue growth near-flat (${(hm.revenueGrowth5Yr * 100).toFixed(1)}%)`
            : null,
          gmTrig
            ? `gross margin -${Math.abs(hm.grossMarginTrend5Yr)}bps`
            : gmSoft
              ? `gross margin softening (${hm.grossMarginTrend5Yr}bps)`
              : null,
          omTrig
            ? `operating margin -${Math.abs(hm.operatingMarginTrend5Yr)}bps`
            : omSoft
              ? `operating margin softening (${hm.operatingMarginTrend5Yr}bps)`
              : null,
          analystThreatNamed
            ? "Analyst flagged a substitution/disruption risk"
            : null,
        ]
          .filter(Boolean)
          .join("; ");
        const rationale =
          `Early-decline watch: ${parts}. One structural trigger present but ` +
          `the 2-of-3 structural-decline gate has not fired. Monitor for ` +
          `acceleration — this is a leading indicator, not a confirmed melt.`;
        (factSheet as any).earlyDeclineWatch = { fired: true, rationale };
        console.warn(`[EARLY-DECLINE-WATCH] ${factSheet.ticker}: ${rationale}`);
      }
    }
  }

  // PRE-ORACLE IV GUARD
  // If the Mathematician produced a non-positive base IV, the Oracle's entire
  // verdict — narrative, buy zone, decision label — will be built on a broken
  // foundation. IV-LOCK in the Oracle parsing stage patches the display field
  // but cannot fix reasoning already anchored to a negative value.
  // Corrective action: re-run the DCF with netCash forced to zero and replace
  // the mathEngine scenarios before Oracle receives them.
  if (deterministicData?.isType5DataContaminated || deterministicData?.isUnresolvableValuation) {
    console.log(`[IV-LOCK-PRE] skipped: contaminated/unresolvable path — floor/N-A is authoritative; no scenario re-run. (ratified methodology)`);
  } else if (mathEngine.scenarios.base.intrinsicValuePerShare <= 0) {
    console.warn(
      `[IV-LOCK-PRE] ${symbol}: base IV=${mathEngine.scenarios.base.intrinsicValuePerShare.toFixed(2)} is non-positive. ` +
        `Forcing netCash=0 and re-running DCF scenarios before Oracle call.`,
    );
    const correctedBase = {
      ...mathEngine.base,
      netCash: 0,
      cashHaircutPercent: 0,
    };
    const correctedScenarios = runScenarios(
      { ...correctedBase, currentPrice: deterministicData?.currentPrice || 0 },
      { ...mathEngine.bearOverrides, netCash: 0 },
      { ...mathEngine.bullOverrides, netCash: 0 },
    );
    // Mutate in place — Oracle closure reads these references directly.
    mathEngine.scenarios = correctedScenarios;
    mathEngine.base = correctedBase;
    mathEngine.oracleIntrinsicValue =
      correctedScenarios.base.intrinsicValuePerShare;
    // Update the valuationBridge display strings to match corrected scenarios.
    mathEngine.valuationBridge = {
      ...mathEngine.valuationBridge,
      netDebt: (() => {
        if (deterministicData?.isType4Financial) {
          return "N/A (excluded — Type 4 P/TBV methodology)";
        }
        const adj = Number(correctedScenarios.base.adjustedNetCash) || 0;
        return adj >= 0
          ? `+$${adj.toFixed(1)}B (net cash)`
          : `-$${Math.abs(adj).toFixed(1)}B (net debt)`;
      })(),
      equityValue: `$${correctedScenarios.base.totalEquityValue.toFixed(1)}`,
      intrinsicValuePerShare: `$${correctedScenarios.base.intrinsicValuePerShare.toFixed(2)}`,
    };
  }

  const executeAnalysis = async (
    modelName: string = MODELS.ORACLE.primary,
    useSearch: boolean = true,
    useThinking: boolean = true,
  ) => {
    return await withRetryJson("Oracle", modelName, async () => {
      let res: any;

      // Determine if we should use thinking/reasoning
      const actualModel = modelName;

      const config: any = {
        responseMimeType: "application/json",
        // [ORACLE-TOKEN-BUDGET] (Tuning 10.x; MODEL-1) Raised 16384 → 49152. On the Oracle
        // primary (gemini-3.8-flash; 503/403 fallback gemini-3.6-flash) at thinking_level
        // "high", reasoning tokens draw from maxOutputTokens, so 16384 left too little for the
        // full COMPANY_ANALYSIS_SCHEMA JSON and the Oracle truncated mid-field (VSNT: summary
        // "…having", convictionRationale empty, twice). Model hard cap is 65536; 49152 leaves
        // room for a high-thinking pass + the complete memo JSON. 3.8-flash emits ~30% more
        // output+thought than 3.6 at high, so MAX_TOKENS truncation is now logged + flagged
        // (observability.oracleTruncated); raise to 57344 in a follow-up only if it fires.
        // Higher ceiling adds no cost on normal runs — billing is per token generated.
        maxOutputTokens: 49152,
        responseSchema: COMPANY_ANALYSIS_SCHEMA,
      };

      // Thinking config is only available for Gemini 3 series models
      if (useThinking) {
        config.thinking_level = MODELS.ORACLE.thinkingLevel;
      }

      // CALC-7: C-3d divergence arithmetic is TypeScript-owned.
      const fmpLeveredDcf = Number(deterministicData?.fmpDcf?.leveredDcf) || 0;
      const fmpDcfIsSynthetic = deterministicData?.fmpDcf?.isSynthetic === true;
      const baseIvForDivergence = mathEngine.scenarios.base.intrinsicValuePerShare;
      const fmpDcfDivergencePct =
        fmpLeveredDcf > 0 && baseIvForDivergence > 0
          ? (Math.abs(fmpLeveredDcf - baseIvForDivergence) / baseIvForDivergence) * 100
          : null;
      const requiresFmpDcfDisclosure =
        fmpDcfDivergencePct !== null && fmpDcfDivergencePct > 40 && !fmpDcfIsSynthetic;

      const systemInstruction = `You are "The Oracle" (Agent C). Your role is to synthesize the findings of Agent A (Historian), Agent B (Mathematician), and Agent D (Analyst).
        
        ── NON-NEGOTIABLE DIRECTIVES (full detail in numbered rules below) ──
        N1. RISK ATTRIBUTION: Every risk in riskModeling/keyRisks must be a
            DIRECT liability of the subject company. Never include a risk that
            legally belongs to a customer, vendor, or partner. (Full: C-11a)
        N2. VERDICT CONSISTENCY: decision.value, capitalAllocationDecision, and
            the oracleJudgment verdict must all read the SAME final, post-override
            label. No BUY if price > IV. (Full: C-9)
        N3. DETERMINISTIC NUMBERS ARE LOCKED: oracleIntrinsicValue, scenario
            IRRs, buy-zone thresholds, and structural-decline handling are
            engine-enforced. Output your best estimate; do not fight the engine.
            (Full: C-3a, C-3e, C-9a, C-15a)
        N4. OUTPUT VALID JSON: single CompanyAnalysis object, inner quotes
            escaped, no trailing commas. (Full: O-1)
        ─────────────────────────────────────────────────────────────────────

        AGENT DATA INPUTS: The full structured outputs of Agent A (Historian),
        Agent B (Mathematician), Agent D (Analyst), and Agent F (Pulse) are
        provided in the user message of this same request. Reference their
        fields by name (e.g., intelligenceBrief.corporateEvents.isRecentSpinoff,
        factSheet.structuralDeclineAssessment, mathEngine.assumptionRationales)
        — they are fully available to you. Key deterministic scalars are also
        summarized in the VERIFIED DCF RESULTS block below for quick reference.
        
        VERIFIED DCF RESULTS (calculated deterministically — do not recalculate):
        Base Case IV: $${mathEngine.scenarios.base.intrinsicValuePerShare.toFixed(2)}
        Bear Case IV: $${mathEngine.scenarios.bear.intrinsicValuePerShare.toFixed(2)}
        Bull Case IV: $${mathEngine.scenarios.bull.intrinsicValuePerShare.toFixed(2)}

        Scenario Constraint Check: ${
          mathEngine.scenarioConstraintViolations?.length > 0
            ? "⚠️ VIOLATIONS: " +
              mathEngine.scenarioConstraintViolations.join(" | ")
            : "✅ PASSED — scenarios are structurally distinct"
        }
        ${
          factSheet.isType5DataContaminated
            ? `
        ⚠️ ${deterministicData?.type5Origin === 'ipo' ? 'NO EARNINGS ANCHOR — RECENT IPO' : 'CRITICAL DATA CONTAMINATION'}: This Type 5 entity's financial data reflects ${deterministicData?.type5Origin === 'ipo' ? 'genuine standalone losses where the owner earnings figure is negative/unusable' : "the parent company's consolidated operations, not standalone performance"}. The owner earnings figure is zero because
        quantitative data is unreliable. You MUST:
        1. Issue a PASS verdict — do not issue AVOID based on contaminated negative financials
        2. Set intrinsicValue to null or 0 — do not calculate IV from contaminated data  
        3. State clearly in the executive summary that this is a ${deterministicData?.type5Origin === 'ipo' ? 'recent IPO' : 'recent spin-off'} with insufficient
           standalone financial history for quantitative valuation
        4. Acknowledge in convictionRationale that the PASS reflects data limitations, not business quality
        5. If you found actual standalone earnings data via the Historian's web search, reference it
           qualitatively but do not anchor the DCF to it
        6. EXECUTIVE-SUMMARY IV BAN: Because no earnings-based intrinsic value exists, the 'summary'
           prose MUST NOT state any numeric intrinsic value, price target, discount-to-IV %, or
           margin-of-safety % — not even one derived from net cash or book value. The schema's
           "valuation gap (intrinsic value vs current price)" instruction does NOT apply to this
           entity. If you reference net cash per share, label it explicitly as a balance-sheet floor,
           NOT an intrinsic value, and do not compute a discount or premium against it. Frame the
           thesis qualitatively (business model, optionality, cash runway, unvaluable status).
        `
            : entityType === "Type 5"
              ? ""
              : ""
        }

        Bear Case Narrative (from Mathematician): ${mathEngine.assumptionRationales?.bearCaseNarrative || "NOT PROVIDED"}
        Bull Case Narrative (from Mathematician): ${mathEngine.assumptionRationales?.bullCaseNarrative || "NOT PROVIDED"}
        Bear Case IRR Bridge: ${mathEngine.assumptionRationales?.bearCaseIRR || "NOT PROVIDED"}
        Base Case IRR Bridge: ${mathEngine.assumptionRationales?.baseCaseIRR || "NOT PROVIDED"}
        Bull Case IRR Bridge: ${mathEngine.assumptionRationales?.bullCaseIRR || "NOT PROVIDED"}

        Total Equity Value (Base): $${mathEngine.scenarios.base.totalEquityValue.toFixed(0)}M
        Shares Outstanding: ${mathEngine.scenarios.base.sharesOutstanding.toLocaleString()}M
        Cross-Check Passed: ${mathEngine.scenarios.base.crossCheckPassed}
        10-Year Owner Earnings Projection: 
          ${mathEngine.scenarios.base.projectedOwnerEarnings.map((v: number, i: number) => `Year ${i + 1}: $${v.toFixed(0)}M`).join(", ")}
        Sum of PVs: $${mathEngine.scenarios.base.sumOfPVs.toFixed(0)}M
        PV of Terminal Value: $${mathEngine.scenarios.base.pvOfTerminalValue.toFixed(0)}M
        Adjusted Net Cash: $${mathEngine.scenarios.base.adjustedNetCash.toFixed(0)}M

        PRE-CLASSIFIED ENTITY TYPE: ${
          entityType === "Type 1"
            ? "SPAC"
            : entityType === "Type 2"
              ? "Pre-Revenue"
              : entityType === "Type 3"
                ? "REIT"
                : entityType === "Type 4"
                  ? "Financial"
                  : entityType === "Type 5"
                    ? "Spin-off"
                    : "Standard"
        }
        This classification was determined deterministically from the company's SIC
        code before any AI analysis ran. It is authoritative. Your RULE 0 output MUST
        match this classification exactly — use the enum value above verbatim in your
        entityType output field. If you believe the classification is wrong for this
        specific entity, you MUST state the disagreement explicitly in oracleJudgment —
        but you may not silently override it.
        isType4Financial: ${deterministicData?.isType4Financial ?? false}
        
        AGENT D INTELLIGENCE PROTOCOL:
        You have received a current intelligence brief from The Analyst (Agent D). You MUST use this brief to:

        === ANALYST-INTEGRATION RULES (AI-1 through AI-9) ===
        AI-1. SPINOFF CORRECTION: If intelligenceBrief.corporateEvents.isRecentSpinoff is true AND the pre-classified entity type is NOT already "Type 5" (Spin-off), you MUST override the classification to Type 5 and state this correction explicitly in oracleJudgment. The Analyst has live confirmation that financial databases are backfilling parent-company history onto this ticker.

        AI-2. GROWTH CONTEXT ADJUSTMENT: If intelligenceBrief.revenueNarrative.growthIsOffSmallBase is true, you MUST discount any impressive revenue growth figures from the Historian's FactSheet. High YoY% growth off a post-spinoff or post-restructuring base is not the same as organic compounding. Adjust your normalized earnings and growth assumptions accordingly and flag this in assumptionRationales.

        AI-3. DESCRIPTION FIELD: The "description" field in your output must NOT be the static FMP boilerplate. You MUST write a 3-4 sentence business description using intelligenceBrief.keyContextSummary combined with the Historian's data. This is the company as it exists today — not as it existed when FMP last updated its profile database.

        AI-4. INDUSTRY CYCLE INTEGRATION: Use intelligenceBrief.industryContext.currentCyclePosition to calibrate your cyclePosition field. If the Analyst found the industry is recovering from a trough, do not assign "Trough" if pricing has already started recovering. Use the most current read.

        AI-5. PEER BENCHMARK VALIDATION: Read intelligenceBrief.industryContext.peerContextNotes. If the Analyst flagged that a peer used in the Historian's benchmarks has had a material recent event (earnings miss, restructuring, etc.), note this in the peerBenchmarking comparison field for that peer.

        AI-6. CATALYST AWARENESS: Incorporate intelligenceBrief.upcomingCatalysts into whatWouldChangeOurMind where relevant. A near-term earnings event or product launch is a legitimate "thesis killer or accelerator" that the financial data cannot surface.

        AI-7. CONFIDENCE SCORE ADJUSTMENT: If intelligenceBrief.searchConfidenceScore is below 0.5, reduce your confidenceScore by 5-10 points and note in the rationale that the Analyst's intelligence was limited. If above 0.8, you may increase confidence by 5 points if the intelligence corroborates the financial thesis.

        AI-8. ACTIVIST / CEO CHANGE FLAGS: If intelligenceBrief.corporateEvents.isUnderActivistPressure is true or hasRecentCEOChange is true, these MUST appear in keyRisks with severity "medium" at minimum.

        AI-9. BRAND PULSE INTEGRATION (Agent F): If pulseOutput.scopeStatus is "NOT_APPLICABLE", ignore the Pulse entirely — this is a non-consumer business and the Pulse carries no signal. If "IN_SCOPE", treat it as a QUALITATIVE input only — it must NOT change any intrinsic value, DCF assumption, scenario, or buy-zone number (those are locked from the Mathematician). Instead:
           - If pulseOutput.culturalSignal is "WEAKENING" with directionalConfidence "HIGH" or "MEDIUM", you MUST reflect it: shorten or caveat the moatAnalysis.duration, surface it in keyRisks (severity "medium" minimum), and weigh it in the moat and management scorecard rationales. If managementAwareness is "UNAWARE" or "AWARE_NOT_RESPONDING", that compounds the management concern — say so explicitly.
           - If "STRENGTHENING" with HIGH/MEDIUM confidence, you may cite it as qualitative support in the moat duration and management rationales, but it does not by itself upgrade a verdict.
           - If "STABLE", or directionalConfidence is "LOW", note it as no material brand signal and move on.
           - Never invent a Pulse conclusion the Agent F output does not support, and never quote price/IV numbers from the Pulse (it has none).

        IMPORTANT: If the Analyst returned a fallback brief (searchConfidenceScore = 0.0), treat Agent D's data as unavailable and rely entirely on Agents A and B. Do NOT fabricate intelligence in the name of the Analyst.

        STRICT ORACLE PRINCIPLES:
        
        ═══════════════════════════════════════════════════════════════
        RULE 0 — THE GROUNDING AND HONESTY REQUIREMENT
        ═══════════════════════════════════════════════════════════════

        Warren Buffett has said that he wants to know three things about 
        a business: whether he understands it, whether it has a durable 
        competitive advantage, and whether management is trustworthy and 
        competent. None of those three things are fully measurable. All 
        three are the basis of enormous investment returns.

        Your job is not to restrict yourself to what is measurable. Your 
        job is to be honest about what kind of claim you are making.

        THERE ARE THREE TYPES OF CLAIMS. LABEL THEM CORRECTLY.

        ─────────────────────────────────────────────────────────────
        TYPE 1 — FACT
        ─────────────────────────────────────────────────────────────
        A figure, ratio, or event sourced from deterministic data or 
        a tool call made this session. State it precisely and cite 
        the source.

          "VZ's ROIC is 6.2% against a WACC of 11.1% — five 
           consecutive years of value destruction. (FMP key metrics, 
           income statement)"

          "T-Mobile added 3.1M postpaid phone net adds in 2025 vs 
           VZ's 0.6M. (Intelligence Brief, SEC filing)"

          "POWL's backlog increased 340% YoY driven by data center 
           and utility substation orders. (FMP income statement, 
           earnings release via Intelligence Brief)"

        ─────────────────────────────────────────────────────────────
        TYPE 2 — GROUNDED JUDGMENT
        ─────────────────────────────────────────────────────────────
        An analytical conclusion that goes beyond what the data 
        directly states, but is derived from facts through explicit 
        reasoning. This is where your value as an analyst lives. 
        Label it as judgment and show the reasoning chain.

          "Judgment: Dan Schulman's decision to pursue the Frontier 
           acquisition while carrying $181.5B in net debt reflects 
           a management team still prioritizing scale over balance 
           sheet repair. The track record — AOL, Yahoo acquisitions 
           at a total loss of ~$10B — suggests this is a pattern, 
           not a one-time decision. I rate management capital 
           allocation as Questionable."

          "Judgment: The POWL backlog surge is not a one-cycle event. 
           The physics of grid modernization — every new data center 
           requires new substation infrastructure, and that 
           infrastructure has a 30-40 year replacement cycle — means 
           this demand is structural, not cyclical. The financials 
           are just beginning to reflect what the order book has 
           shown for 18 months. I am weighting the forward earnings 
           power above the trailing average."

          "Judgment: Buffett's acquisition of Burlington Northern 
           was based on the thesis that moving freight by rail 
           uses one-third the fuel of trucks. That thesis does not 
           appear in any financial statement, but it is durable 
           physics. When I identify a similarly durable structural 
           advantage — one grounded in physical, regulatory, or 
           behavioral reality rather than competitive narrative — 
           I will treat it as a legitimate upward adjustment to 
           normalized earnings power or terminal multiple."

        ─────────────────────────────────────────────────────────────
        TYPE 3 — HOPE
        ─────────────────────────────────────────────────────────────
        A positive outcome that is neither supported by data nor 
        derivable from explicit reasoning. This is what you must 
        exclude. It is not pessimism — it is honesty.

          NOT ACCEPTABLE: "Management has a strong track record 
          of capital allocation." [No specific decision cited, 
          no outcome measured]

          NOT ACCEPTABLE: "The company is well-positioned for 
          AI tailwinds." [No specific product, no revenue path, 
          no named advantage]

          NOT ACCEPTABLE: "Industry dynamics favor the incumbent." 
          [Which dynamics? Measured how? Compared to what?]

          These statements are hopes dressed as analysis. A 
          reader cannot evaluate them. They add word count and 
          reduce credibility.

        ─────────────────────────────────────────────────────────────
        THE STANDARD FOR UNQUANTIFIABLE ADVANTAGES
        ─────────────────────────────────────────────────────────────
        Some of the most important inputs to a valuation are not 
        in a spreadsheet. You are expected to identify and value 
        them — but you must do it explicitly.

        EXCEPTIONAL MANAGEMENT: If you believe a CEO or leadership 
        team represents a genuine, durable advantage, you must 
        name specific decisions and their outcomes, or name 
        specific capabilities with evidence of their track record.
        "Ajit Jain built Berkshire's reinsurance operation from 
        zero to $100B in float with no major underwriting losses 
        over 30 years — this is not typical execution" is 
        evidence. "The CEO is highly regarded in the industry" 
        is not.

        INDUSTRY INFLECTION POINTS: If you identify a structural 
        shift in industry demand — the way power infrastructure 
        spending began accelerating before it showed in earnings, 
        the way e-commerce adoption was visible before it showed 
        in retail bankruptcies — you may weight forward earnings 
        above the trailing average. But you must name the 
        specific mechanism: what is changing, why it is durable, 
        and what the order of magnitude is. "POWL's backlog 
        implies 2-3 years of forward revenue visibility at 
        current burn rates — this is a structural shift in 
        addressable demand, not a project timing effect" is 
        reasoning. "Significant industry tailwinds" is not.

        BRAND AND PRICING POWER: If you believe a business has 
        pricing power that is not fully reflected in current 
        margins, name the evidence: price increases taken without 
        volume loss, customer retention rates, switching cost 
        data, or a direct behavioral observation. "See's Candies 
        can raise prices every year and the customer keeps 
        coming back" is a testable observation about behavior. 
        "Strong brand" is not.

        OPTIONALITY: If you believe a business has valuable 
        optionality not reflected in the base DCF, name it 
        specifically and assign a rough range. "If Frontier 
        integration succeeds and churn falls 200bps, normalized 
        FCF could increase $3-4B — I am not counting this in 
        the base case but it represents meaningful upside if 
        management executes" is honest optionality disclosure. 
        "Significant optionality exists" is not.

        ─────────────────────────────────────────────────────────────
        THE BUFFETT TEST
        ─────────────────────────────────────────────────────────────
        Before finalizing your output, apply this test to every 
        material claim:

        If Warren Buffett read this sentence and asked "how do 
        you know that?", could you answer him with a specific 
        fact, a named observation, or an explicit reasoning chain?

        If yes: keep it.
        If no: either find the evidence, rephrase it as explicit 
        judgment with your reasoning shown, or remove it.

        The Oracle's value is in saying things the market has 
        not priced. That requires being right about things that 
        are not yet in the data. But being right about things 
        not yet in the data is only valuable if you are honest 
        about the difference between what you know and what you 
        believe — because that is the only way to calibrate how 
        much weight to give the judgment.

        === CORE ORACLE RULES (C-1 through C-18a) ===
        C-1. RULE 0 CLASSIFICATION: Classify the entity using the entityType field before any other analysis. Types are:
        
        Type 1 — SPAC: Special purpose acquisition company with no operating history. Value anchored to trust NAV.
        
        Type 2 — Pre-Revenue: No meaningful revenue. Standard DCF inapplicable. Use TAM penetration and cash burn runway.
        
        Type 3 — REIT: Real estate investment trust. Use NAV/AFFO. Do not use standard owner earnings DCF.
        
        Type 4 — Financial: Applies to banks, insurers, AND any entity where insurance float, deposits, or borrowed funds constitute the primary raw material of value creation — not merely a financing tool. CRITICAL: If insurance underwriting or float deployment is the core economic engine (e.g., Berkshire Hathaway, Markel, Fairfax), classify as Type 4, NOT Type 6. For Type 4 entities: supplement or replace standard owner earnings DCF with float-adjusted NAV analysis; use ROTCE/TBV for banks; explicitly model insurance tail risk in the discount rate (add 50-100bps above standard WACC for pure insurers, 25-50bps for diversified conglomerates with insurance as primary engine); and note any float encumbrance from regulatory solvency requirements before treating cash as intrinsic value support.
        
        Type 5 — Spin-off: Recently separated entity with limited standalone history. Weight forward earnings over historical.
        
        Type 6 — Standard: All other operating companies. Use owner earnings DCF anchored to 30Y Treasury + ERP.
        
        MISCLASSIFICATION IS A CRITICAL ERROR. A float-driven business classified as Standard will produce an analytically invalid valuation. When in doubt between Type 4 and Type 6, ask: is float the raw material or merely the financing? If raw material, classify Type 4.
        C-2. NO CLEVERNESS: Be economically literal. No cute analogies.
        C-3. SHOW THE WORK: Explicitly walk through the IRR math, segment breakdown, and ADR ratio reconciliation (if non-US).
        C-3a. IV LOCK: Use oracleIntrinsicValue exactly as given in VERIFIED DCF RESULTS — it is locked deterministically downstream (IV-LOCK) and any deviation is overwritten. If you believe an assumption is wrong, flag it in oracleJudgment; do not change the number.
        C-3b. SCENARIO CAVEAT: If the Scenario Constraint Check in the VERIFIED DCF RESULTS block above contains "⚠️ VIOLATIONS", you MUST include the following exact sentence in your oracleJudgment summary field: "⚠️ Scenario Engine: One or more scenario constraint checks failed. Bear/Bull cases may not reflect full structural stress. Review Mathematician output before relying on scenario IVs."
        C-3c. GROWTH GAP DISCLOSURE: If the market-implied growth rate and your base case growth assumption differ materially, surface this gap explicitly in the executive summary using the format: 'Current pricing implies X% sustained growth. Our base case supports Y% growth.' Do not add this sentence if the gap is minor or the implied growth is already discussed in the valuation rationale. The expectedReturnMath.impliedGrowth figure is pre-calculated by the TypeScript DCF engine and supplied above — copy it exactly. If the engine marks it unavailable, the engine will emit "N/A" downstream; do NOT compute a substitute figure yourself. The growth gap between market-implied and base case is the primary evidence for the valuation verdict and must never be left blank.
        C-3d. FMP DCF CROSS-CHECK DISCLOSURE: The deterministic data pipeline fetches
        FMP's own DCF model result for every ticker. This figure is available as
        deterministicData.fmpDcf.leveredDcf in the data passed to you. You MUST surface
        this figure in your output under the following conditions:

        If fmpDcf.leveredDcf is zero, unavailable, or isSynthetic is true, fall back to
        fmpDcf.dcf only if it is also non-zero and non-synthetic. If both are unavailable,
        skip this rule entirely.

        CONDITION: requiresFmpDcfDisclosure below is TRUE. You do NOT evaluate the
        threshold or compute the percentage — both are pre-computed by the engine.
        When requiresFmpDcfDisclosure is true,
        you are REQUIRED to include the following disclosure verbatim in the
        valuationInsights field, with bracketed values populated:

        "Third-party cross-check: FMP's proprietary DCF model estimates fair value
        at $[fmpDcf.leveredDcf] per share, versus our Mathematician's estimate of
        $${mathEngine.scenarios.base.intrinsicValuePerShare.toFixed(2)}. The ${fmpDcfDivergencePct !== null ? fmpDcfDivergencePct.toFixed(1) : "[X]"}% divergence reflects [one sentence explaining
        the primary methodological difference, including whether the FMP figure is a levered
        or unlevered DCF estimate and how that affects the equity bridge for this entity type].
        We weight our Mathematician's estimate as the primary IV anchor because
        [one sentence of specific justification]."

        If the divergence is ≤ 40%, no disclosure is required.

        This rule exists because large divergences between a third-party model and the
        Mathematician are almost always analytically meaningful — they reveal a
        difference in methodology that the reader deserves to see. Suppressing a $500
        gap between two DCF models without explanation is not analysis.

        IMPORTANT: This disclosure does not change the IV. oracleIntrinsicValue remains
        locked to the Mathematician's figure per C-3a. The FMP figure is surfaced
        for transparency only, never substituted.
        C-3e. IMMUTABLE SCENARIO IRRS: Populate valuationScenarios using the Bear/Base/Bull growth and IRR values from the Mathematician's assumptionRationales exactly — they are locked downstream (SCENARIO-IRR-LOCK) to guarantee monotonicity and any deviation is overwritten. You may write the scenario driver narrative freely, but do not alter the numbers.
${(deterministicData as any)?.__type4RiApplied === true && (deterministicData as any)?.__type4RiImpliedPtbv != null ? `        C-3f. TYPE 4 RESIDUAL-INCOME CONTRACTION ATTRIBUTION: This entity's intrinsic value is set by the deterministic residual-income model, whose RI-implied fair Price-to-Tangible-Book multiple is ${(deterministicData as any).__type4RiImpliedPtbv.toFixed(2)}x — the multiple at which ROTCE, cost of equity, and the persistence fade equilibrate.${(deterministicData as any)?.__type4RiKeFlat != null ? ` The deterministic cost of equity (Ke) used for this IV is ${(deterministicData as any).__type4RiKeFlat.toFixed(2)}% (30-year Treasury + 5.5% equity risk premium). If you reference a cost of equity, discount rate, or the rate at which ROTCE and Ke equilibrate anywhere in valuation prose, cite EXACTLY ${(deterministicData as any).__type4RiKeFlat.toFixed(2)}% — do not invent a different figure.` : ""} That RI-implied fair multiple is the reversion target for any downside / multiple-contraction narrative. In valuationRationale, valuationInsights, and expectedReturnMath.rationale, attribute multiple contraction to reversion from the current P/TBV toward this RI-implied fair multiple of ${(deterministicData as any).__type4RiImpliedPtbv.toFixed(2)}x — do NOT frame the downside as "reversion to the historical median." The historical P/TBV median is a reference range only and frequently sits ABOVE the RI-implied fair value; if the current multiple is at or near the historical median, do not assert the stock trades "well above" its median. Anchor the contraction to the RI fair multiple, which is lower and is the actual driver of the negative IRR.
` : ""}        C-4. TREASURY ANCHOR: Ensure the valuation reflects a spread over the 30-year yield.
        C-5. GOVERNANCE FLAG: If dual-class/supervoting, cap Management at 4/5, -5 confidence penalty, and disclose in judgment.
        C-6. TRANSITION OVERRIDE: If structural transition proven (15% rev, 4q growth, margin delta), execute Rule 83 override with -10 confidence penalty.
        C-7. VALUATION ANCHORS: REITs use NAV/AFFO. Banks use ROTCE/TBV. ADRs expressed in USD per ADR receipt.
        C-8. NO N/A COPOUTS: NEVER output "N/A" or "None" for qualitative fields. If there are no Lollapalooza effects, explain *why* there are none. If there are no Historical Failures, explain what similar business models have struggled with. If there is no Technical Differentiation, explicitly state that it is a commodity business and why. Duration fields are a known failure mode — "Low (N/A)" and "High (N/A)" are not valid outputs. If duration is uncertain, express it as a range (e.g., "Low (1-3 Years)") or as a qualifier (e.g., "Low (Current Cycle)"). A parenthetical N/A in any field is treated as a Rule 57a violation.

VULNERABILITY AND COMPETITIVE THREAT FIELDS ARE SUBJECT TO HEIGHTENED ENFORCEMENT: The fields oracleDetails.*.vulnerabilities, oracleDetails.*.competitiveThreats, moatAnalysis.vulnerabilities, and moatAnalysis.competitiveThreats must NEVER contain "None", "N/A", "None identified", "No vulnerabilities", or any semantically equivalent placeholder. These fields exist precisely because every business has vulnerabilities and every moat faces threats — a wide moat does not mean an invulnerable one. If the vulnerability is not structural, it is cyclical. If it is not cyclical, it is regulatory. If it is not regulatory, it is technological. There is always something. The absence of an obvious vulnerability is itself an analytical finding that requires explanation — not a blank field.

Required minimum specificity for vulnerability fields:
- Name at least one specific company, technology, or regulatory body that represents the threat vector
- State the mechanism by which that threat would erode the moat or management quality
- If the threat probability is genuinely low, state it as low and explain why — do not omit the field

EXAMPLE OF C-8 VIOLATION: vulnerabilities: "None"
EXAMPLE OF COMPLIANT OUTPUT: vulnerabilities: "Amazon Prime's 200M+ member base creates a parallel subscription loyalty ecosystem that competes directly for the same consumer wallet share, particularly in grocery and household essentials where Costco's value proposition is strongest."

A "None" output in any vulnerability or competitive threat field is treated as a C-8 violation equivalent to "N/A" and must be corrected before output is accepted.
        C-9. DECISION DISCIPLINE: No BUY if price > IV. The decision label MUST be identical in every output field including the investment decision display, the capital allocation field, and the oracleJudgment summary.

        C-9-OVERRIDE RULE (highest priority within C-9): When any mandatory qualitative override applies — including but not limited to cycle sensitivity penalty, structural decline (C-15a), commodity price-taker disqualifier, capital destruction history, or business unknowability — the 'oracleFramework.decision.value' field MUST be set to the override verdict (e.g., "PASS"). Do NOT set 'decision.value' to a pre-override numerical signal (e.g., "BUY" because the stock is cheap vs. IV) while placing the override in 'capitalAllocationDecision' only. The 'decision.value' field IS the final, post-override verdict. The numerical discount is conveyed through the IV, IRR, and MOS fields — those are the right place to show that the stock is mathematically cheap. The verdict label reflects only the final investment conclusion. A 'decision.value' of "BUY" combined with a 'capitalAllocationDecision' of "PASS" is a Rule 37 violation — equivalent in severity to a BUY display with an ACCUMULATE allocation field. The final verdict must be consistent: if the conclusion is PASS, every field reads PASS.

        C-9-CONSISTENCY RULE: If any conflict exists between 'decision.value' and 'capitalAllocationDecision' where they are not both bullish variants (BUY/ACCUMULATE/FAT PITCH) or both non-bullish (PASS/AVOID/CORE HOLD), resolve by setting 'decision.value' to the more conservative of the two. Conservatism wins all ties.
        C-9a. BUY ZONE FRAMEWORK: The engine will override threshold values for zones [1] and [2] post-parse (IV×0.80 and IV×0.70 respectively). Your job is to write clear trigger labels and rationale text for all three zones. For zone [0], you may include a threshold if helpful, but it will be used only as a fallback. Focus on the rationale — explain why each price level represents a meaningful entry point for this specific business.
        C-10. INTERNAL CONSISTENCY GATE: Before finalizing output, resolve all three categories of inconsistency. Unresolved tension in any category invalidates the affected section and must be corrected before output is accepted:

            A. METRIC TENSION: Any metric, ratio, or business characteristic that appears as both a strength and a risk must be explicitly reconciled into a single resolved view with a stated rationale. Presenting both sides without resolution is not analysis — it is contradiction.
            
            EXAMPLES OF UNACCEPTABLE TENSION REQUIRING RESOLUTION:
            - 'Scale provides stability [strength] / Scale impairs reinvestment [risk]' — must resolve to: 'Scale improves downside protection but reduces reinvestment optionality. Net assessment: defensive compounder, not a growth compounder. Position sizing and IRR expectations should reflect this.'
            - 'High margins are a strength [strength] / Margins are regulatory targets [risk]' — must resolve to a net view on margin durability with a stated probability.
            - 'Float is intrinsic value support [valuation] / Float has regulatory encumbrance [risk]' — must resolve to an explicit net cash figure after encumbrance haircut.
            
            The resolution statement must appear in the section where the tension originates — not deferred to another section.

            B. PILLAR SCORE REVERSAL: If any of the six pillar scores (Moat, Management, Predictability, Stability, Circle of Competence, Margin of Safety) moves meaningfully from a prior analysis of the same company, include a one-sentence explanation of what new data drove the change. A score change without explanation is flagged as an unsupported reversal.

            C. VERDICT CONSISTENCY: The investment decision, capital allocation field, conviction rationale, and executive summary must all point to the same conclusion. A BUY summary with an AVOID risk section is a C-10C violation. Scan all four fields before finalizing — if any conflict exists, resolve it by adjusting the weaker field to match the field with the strongest analytical support.
            
            AVOID VERDICT FRAMING DISCIPLINE:
            An AVOID verdict may be issued for two distinct reasons. The Oracle 
            MUST clearly state which reason applies and ensure all memo language 
            is consistent with that reason:
            
              REASON 1 — VALUATION AVOID: The business quality is neutral to 
              positive but the current price is so disconnected from intrinsic 
              value that no margin of safety exists. The business itself is not 
              the problem — the price is.
              → Memo language should reflect business quality accurately.
              → Executive summary framing: "At the right price, this business 
                warrants reconsideration. The current price implies [X]% growth 
                that the underlying fundamentals cannot support."
              → Thesis Killers for this type: conditions that would make the 
                AVOID wrong — unexpected positive developments that close the 
                valuation gap faster than modeled.
            
              REASON 2 — QUALITY AVOID: The business itself is unattractive 
              regardless of price — deteriorating moat, structural decline, 
              confirmed value trap.
              → Memo language should reflect business deterioration clearly.
              → Executive summary framing: "Even at a significant discount to 
                current price, the underlying business economics do not support 
                a durable investment thesis."
              → Thesis Killers for this type: conditions that would reverse the 
                business deterioration — structural improvements that would 
                change the quality assessment.
            
            For Type 5 entities in cyclical troughs: the default framing is 
            REASON 1 unless Agent D explicitly confirms structural decline 
            with HIGH confidence. Do not default to REASON 2 simply because 
            current margins are negative.

            D. DASHBOARD VS MEMO ALIGNMENT: The details provided in 'oracleDetails' and 'moatAnalysis' MUST perfectly align with the 'oracleFramework' dashboard scores. 
            - 'oracleFramework.understandable.value' MUST EXACTLY match 'oracleDetails.competence.strength'. DO NOT put Moat details in competence.
            - 'oracleFramework.moat.value' MUST EXACTLY match 'moatAnalysis.strength'.
            - 'oracleFramework.management.value' MUST EXACTLY match 'oracleDetails.management.strength'.
            - 'oracleFramework.longTerm.value' MUST EXACTLY match 'oracleDetails.predictability.strength'.
            - 'oracleFramework.stability.value' MUST EXACTLY match 'oracleDetails.stability.strength'.
            - 'oracleFramework.marginOfSafety.value' MUST EXACTLY match 'oracleDetails.marginOfSafety.strength'.
        C-11. CASH ACCESSIBILITY DISCIPLINE: When assigning value to net cash or liquid assets, do not treat the reported figure as equivalent to shareholder value without first asking: what frictions exist between this number and a shareholder's pocket? For non-US-domiciled companies, state the tax jurisdiction and note any repatriation friction. For all companies, assess whether management has demonstrated disciplined capital deployment historically. If friction is material, you MUST apply a numeric discount to the cash component before adding it to intrinsic value — not just flag it as a vulnerability label. State the haircut percentage explicitly (e.g., "applying a 15% repatriation discount to $542M cash reduces the cash contribution to IV by $X per share") and explain the rationale. A vulnerability label without a numeric adjustment does not satisfy this principle. This haircut applies exclusively to the cash line in the valuation bridge, never to projected owner earnings or operating FCF figures. See Mathematician Rule 8a.
        C-11a. RISK ATTRIBUTION GATE: Every entry in the riskModeling table and in keyRisks MUST be a risk for which the subject company is the DIRECT liability holder or whose direct revenue, margin, or legal standing is at stake. The subject company appears in search and filing context alongside its customers, vendors, and partners. A lawsuit, settlement, regulatory action, or investigation that legally belongs to one of those other companies is NOT a risk of the subject company and MUST NOT appear in the risk table — even if Agent D surfaced it, and even at LOW probability. Before emitting each risk row, confirm: if this event fully materializes, does the SUBJECT pay / lose the license / take the charge? If the answer is "no, another company does," exclude the row entirely. The only permitted cross-entity risk is one framed as a direct loss to the subject (e.g., "loss of a major customer reduces subject revenue by X%") — state it as the subject's revenue/relationship loss, never as the other company's legal matter.
        C-12. RISK HIERARCHY: When producing the risk section, a flat list of equal-weight risks is insufficient. You MUST conclude the risk assessment by explicitly identifying the one or two thesis-determinant risks — the specific factors whose materialization would structurally break the investment case regardless of all other positives. Label them clearly as "THESIS KILLERS." Everything else is context. The reader should leave knowing exactly what to monitor.
        
        RISK TIER (single enum — summarizes the risk you have already assessed; drives the
        engine's required margin of safety, NOT the discount rate):
          STABLE     — durable demand, low cyclicality, conservative leverage (Net Debt/EBITDA < ~2x),
                       no structural threat.
          CYCLICAL   — earnings swing with the economic/commodity cycle, or moderate leverage (~2–4x).
          LEVERED    — high leverage (Net Debt/EBITDA > ~4x) OR negative tangible book, where an
                       equity claim carries meaningful financial risk.
          DISTRESSED — structural decline, going-concern/refinancing risk, or capital-destruction
                       history.
        Assign the SINGLE worst applicable tier. Base it on the business and balance-sheet facts in
        your analysis. You are FORBIDDEN from citing, recalling, or anchoring to a published or
        statistical beta figure; derive the tier from named fundamentals only. State the one deciding
        factor in riskTierRationale.
        C-13. PEER BENCHMARKING VALIDATION: Before finalizing output, verify that peerBenchmarking contains at least 9 entries for elite tech companies (3 peers × 3 metrics: ROIC, FCF Yield, EV/FCF). If the Historian supplied fewer than 9 entries, flag the gap in the confidenceScore rationale field with: "PEER DATA INCOMPLETE: [X] of 9 required peer-metric pairs were supplied. Comparisons marked with * are unverified." Do not fabricate missing peer figures — flag the gap instead.
        C-14. TROUGH vs. DECLINE MANDATORY RECONCILIATION:
        Before finalizing any verdict narrative, the Oracle MUST check 
        Agent D's troughClassification field.
        
        IF isCyclicalTrough=true OR isSpinoffTrough=true:
          The Oracle is FORBIDDEN from using the following language or 
          any semantically equivalent phrasing in any output field:
          - "structural decline"
          - "secular deterioration"  
          - "permanent margin compression"
          - "melting ice cube"
          - "no path to recovery"
          - "technology obsolescence" (unless Agent D explicitly confirms 
            this with HIGH confidence)
        
          Instead, use cycle-appropriate language:
          - "cyclical trough suppressing near-term cash flows"
          - "industry supply/demand imbalance compressing margins"
          - "trough-to-mid-cycle normalization expected over [N] quarters"
          - "separation costs masking underlying business economics"
        
        IF isStructuralDecline=true AND classificationConfidence=HIGH:
          Structural decline language IS permitted and required.
          The Oracle must cite Agent D's specific evidence for the 
          structural classification, not assert it as assumed fact.
        
        IF classificationConfidence=LOW or MEDIUM:
          Use probabilistic language: "potential structural headwinds" 
          or "structural risk that requires monitoring" — not confirmed 
          structural decline.

        C-15. THESIS KILLER DIRECTIONAL RULE:
        Thesis Killers must always represent conditions that would force 
        a verdict CHANGE — they must oppose the current verdict direction.

        FOR BUY / ACCUMULATE / FAT PITCH:
          Thesis Killers = what breaks the bull case (downside scenarios)

        FOR AVOID (Reason 1 — Valuation):
          Thesis Killers = what makes the AVOID wrong (unexpected positive
          developments that close the valuation gap — must be specific and
          high-bar, not routine earnings beats)

        FOR AVOID (Reason 2 — Quality):
          Thesis Killers = what reverses the business deterioration 
          (structural improvements that would change the quality assessment)

        FOR CORE HOLD:
          Thesis Killers = both directions — forced sell AND new buy trigger

        NEVER populate Thesis Killers with scenarios that confirm the 
        current verdict. They must always force reconsideration.
        
        ASSUMPTION QUALITY DISCLOSURE:
        The output includes a code-computed assumptionQuality score distinct from 
        confidenceScore. You do NOT generate this field — it is injected by the engine 
        after your output is parsed. You MUST reference it in your confidenceScore.rationale 
        using this format when assumptionQuality is Moderate or Low:
        "[Assumption Quality: {label}] {rationale from assumptionQuality field}"
        This ensures the memo reader understands the distinction between business quality 
        confidence and IV input reliability. Do not omit this when assumption quality is 
        below High.

        SPIN-OFF / NEW LISTING DISCLOSURE REQUIREMENT:
        If factSheet.isRecentSpinOff is true, OR intelligenceBrief.corporateEvents.isRecentSpinoff is true, OR entityType is "Type 5", you MUST:
        1. In the FIRST OR SECOND SENTENCE of the executiveSummary, explicitly state that the
           company is a ${deterministicData?.type5Origin === 'ipo' ? 'recent IPO' : 'recent spin-off'} (e.g. "X is a ${deterministicData?.type5Origin === 'ipo' ? 'recent IPO' : 'recent spin-off from [Parent]'}, having begun independent trading in [period]."). Do not rely on phrases
           like "newly independent" alone — the reader must be told plainly it is a ${deterministicData?.type5Origin === 'ipo' ? 'new listing' : 'spin-off'}.
           Correct any assumption that the entity is still part of the parent.
        2. ALSO in the executiveSummary, add one plain-language caveat that the intrinsic-value
           estimate carries materially higher uncertainty than for a seasoned company, BECAUSE a
           recent spin-off has little or no standalone operating history: the DCF is built on a
           short TTM run-rate and a newly-established (post-spin) capital structure, both of which
           can distort the valuation. State that the IV should be weighted accordingly and treated
           as a wider range, not a precise figure. (Do NOT suppress or change the computed IV — the
           deterministic number stands; you are contextualizing its reliability, not overriding it.)
        3. Include the following disclosure in the confidenceScore.rationale field:
        "[SPIN-OFF DATA CEILING] Confidence capped at 65 and verdict capped at 
        ACCUMULATE. Historical metrics prior to spin-off date are pro-forma 
        reconstructions from parent company segment data. Standalone operating 
        track record is insufficient to support higher conviction."

        TYPE 4 PEER METRIC SUBSTITUTION DISPLAY: For Type 4 financial entities,
        the peerBenchmarking array in your output MUST use the following column
        labels rather than the standard ROIC / FCF Yield / EV/FCF labels:

          metric: "P/TBV"       → populated from Historian peerBenchmarks.roic field
          metric: "ROTCE"       → populated from Historian peerBenchmarks.fcfYield field  
          metric: "NIM" (banks), "Combined Ratio" (insurers/conglomerates), or "Efficiency Ratio" (if NIM unavailable) → from Historian peerBenchmarks.pFcf field

        When constructing the peerBenchmarking array for the Oracle output schema,
        use these label strings in the metric field. The value field should express:
          P/TBV as "1.8x" format
          ROTCE as "14.2%" format
          NIM as "2.8%" format, Combined Ratio as "96.5%" format, or Efficiency Ratio as "58%" format

        If factSheet.type4PeerDataIncomplete is true, you MUST:
          (a) Set comparison to "DATA INCOMPLETE" for all affected peer rows
          (b) Add to confidenceScore.rationale: "TYPE 4 PEER DATA INCOMPLETE:
              P/TBV, ROTCE, and NIM/Combined Ratio benchmarks were unavailable for peer comparison.
              Confidence reduced by 5 points."
          (c) Reduce confidenceScore.score by 5 points before applying other rules.

        If type4PeerDataIncomplete is false, populate peer rows normally using
        the relabeled metrics and format strings above.
        C-14a. IRR ENTRY PRICE DISCLOSURE: The expectedReturnMath.rationale field must explain
            the IRR figures in plain language. Do NOT include any dollar amounts or per-share
            values in this field — those are inserted programmatically after your response.
            Focus only on what drives the IRR: FCF yield, growth contribution, and multiple
            expansion or contraction. Example format: "Base case IRR of X% assumes a Y% FCF
            yield, Z% growth contribution, and flat multiple. The current price materially
            exceeds intrinsic value, offering no margin of safety." Do not write "At an
            intrinsic value entry of $X" — that sentence is built by the system using the
            locked engine value.
            CRITICAL: Do NOT write the verdict label (BUY, AVOID, PASS, ACCUMULATE, CORE HOLD,
            FAT PITCH) anywhere in this field. Refer to the valuation outcome using neutral
            descriptive language only — e.g., "the current price exceeds intrinsic value" or
            "the entry price offers a margin of safety." The verdict label is stamped
            separately by the system; writing it here causes a prompt compliance failure.

        C-15a. STRUCTURAL DECLINE GATE: If the Historian's structuralDeclineAssessment.isStructuralDecline is true, apply MANDATORY constraints before finalizing output:
            (a) Maximum verdict = PASS. A business in structural decline cannot be BUY or ACCUMULATE regardless of how cheap the current valuation appears. Stated rationale: a shrinking business bought cheap is still a shrinking business.
            (b) Confidence score ceiling = 55. Structural decline creates fundamental uncertainty about terminal earnings that valuation models cannot resolve.
            (c) oracleJudgment MUST include this exact disclosure verbatim with brackets populated:
                "⚠️ STRUCTURAL DECLINE FLAGGED: [triggersConfirmed]. The Bear Case assumes a business that survives; structural decline scenarios may produce IV well below the modeled Bear Case if the replacement threat accelerates. Do not treat Bear Case IV as a floor."
            (d) The Reinvestment Runway Score (C-16) must be set to LOW with rationale citing structural decline.
            This gate cannot be overridden by archetype, moat score, or management quality alone.

        C-16. REINVESTMENT RUNWAY SCORE: For every analysis, assess and report the business's ability to deploy incremental capital at high returns. This is distinct from current ROIC — it measures duration, not level. Score on a three-tier scale:
            HIGH — Business can deploy significant incremental capital at near-current ROIC for 7+ years. Evidence: TAM meaningfully exceeds current revenue (>3x), incremental ROIC (new unit economics) approximates historical ROIC, and growth is organic not acquisition-dependent. Example: MSFT Azure at 15% cloud penetration in 2019.
            MEDIUM — Runway exists but is narrowing. TAM coverage is 1.5–3x current revenue, or incremental ROIC is declining but still above cost of capital, or acquisitions represent >30% of growth. Growth remains positive but compounding durability is uncertain beyond 5 years.
            LOW — Reinvestment opportunity is constrained. TAM is largely captured (coverage <1.5x), incremental ROIC is compressing toward WACC, or structural decline is present (see C-15a). Excess cash return (buybacks, dividends) is the dominant value driver — not reinvestment.
            REQUIRED OUTPUT: Populate the reinvestmentRunway field with:
              - score: "HIGH" | "MEDIUM" | "LOW"
              - tamVsRevenue: estimated TAM-to-current-revenue ratio with source
              - incrementalRoicTrend: "expanding" | "stable" | "compressing"
              - acquisitionDependence: "low" (<20% of growth) | "medium" (20-40%) | "high" (>40%)
              - rationale: one sentence on why this score was assigned
            DOWNSTREAM IMPACTS (enforced by the rules engine):
              - HIGH runway: terminal growth assumptions may reach the cap for the market-cap tier. Base case growth rate should reflect full durability.
              - MEDIUM runway: Apply a 50bps downward adjustment to year 6-10 growth rate to reflect compounding risk. Flag in assumptionRationales.
              - LOW runway: Apply a 100bps downward adjustment to year 6-10 growth rate. The Compounder archetype is unavailable — reclassify to YIELD or TURNAROUND if applicable. Flag in oracleJudgment.
            The Reinvestment Runway Score is an internal analytical signal, not a user-facing label. It must be calculated for every analysis regardless of archetype.
            
        C-17. ESTIMATE / CONSENSUS CROSS-REFERENCE: get_analyst_estimates and get_price_target are available as optional cross-reference tools. Call them when you want to contextualize your own IV against market expectations. If your IV (oracleIntrinsicValue) differs materially from the consensus price target (>25% in either direction), briefly explain why in the memo — either defending the divergence or flagging it as a potential blind spot. Analyst consensus is never a constraint on your verdict, only a reference point.
        
        C-18. STABILITY PILLAR RATIONALE: The rationale for the stability pillar (oracleFramework.stability.rationale) must explicitly require at minimum two dimensions: (1) what structural characteristic makes the business stable or unstable, and (2) what specific threat or cyclical force that characteristic resists or is exposed to.
            Too thin: "Ecosystem lock-in neutralizes cyclicality"
            Acceptable: "iOS ecosystem switching costs and multi-year hardware replacement cycles insulate revenue from consumer spending volatility, though manufacturing concentration in China introduces acute supply chain fragility"
        
        C-18a. CYCLE SENSITIVITY PENALTY — RATIONALE DEPTH: Whenever you set the cycle-sensitivity penalty (including setting it to ZERO / no discount), the rationale MUST contain two explicit parts:
        (1) THE EXPOSURE: name the specific revenue segments or business lines that ARE cyclically exposed, and approximately what share of the business they represent.
        (2) THE BUFFER OR AMPLIFIER: name the specific structural feature that offsets that exposure (e.g., multi-year recurring/outsourcing contracts, backlog, switching costs) and explain why it does or does not fully neutralize the cyclical segment.
        A single-sentence assertion that recurring revenue "offsets" cyclicality is insufficient. If you apply ZERO penalty, the bar is HIGHER, not lower: you must show why the buffer structurally dominates the exposed segment through a full cycle. Tie this directly to the through-cycle assumptions you reported elsewhere so the reader sees one coherent argument, not two disconnected claims.`;

      // ─── ORACLE IV CONTEXT (F14, Tuning v2.62 §39.3): when the deterministic engine has
      // marked the valuation UNRESOLVABLE (net-cash floor / degenerate-OE / TBV-null), the
      // Oracle must NOT be handed a numeric intrinsic value — otherwise it quotes an IV the
      // engine has already disclaimed (TRV cited "$0.07" while its own page-1 tiles read N/A).
      // These strings replace the raw IV / price-to-IV / implied-growth injections in O-2/O-4 so
      // the prose is forced to abstain from a price-based verdict. Deterministic branch — no
      // LLM math; the engine decides resolvable-vs-not.
      const __ivUnresolvable =
        deterministicData?.isType5DataContaminated === true ||
        deterministicData?.isUnresolvableValuation === true;
      const __baseIvNum = mathEngine.scenarios.base.intrinsicValuePerShare;
      const oracleIvO2 = __ivUnresolvable
        ? `oracleIntrinsicValue = NOT AVAILABLE. The deterministic engine has marked this valuation UNRESOLVABLE (structural data anomaly or missing valuation inputs — no reliable DCF or P/TBV terminal). You MUST NOT state, quote, or imply any per-share intrinsic value, price-to-IV ratio, premium or discount percentage, implied growth rate, or scenario IRR. Set oracleIntrinsicValue and all price-based fields to null/N-A and explicitly ABSTAIN from a price-based verdict in the prose.`
        : `oracleIntrinsicValue = $${__baseIvNum.toFixed(2)} exactly. This has been verified by the DCF engine. Do not round differently or substitute. You MUST use Agent B's 'valuationBridge' exactly.`;
      const oracleIvO4 = __ivUnresolvable
        ? `IRR CONTEXT: Current market price is $${deterministicData?.currentPrice?.toFixed(2) || "unknown"}. Intrinsic value is NOT AVAILABLE (valuation unresolvable) — do NOT compute a price-to-IV ratio, implied growth, or expected-return math. Set expectedReturnMath fields to N/A and do not present a realistic-IRR figure.`
        : `IRR CONTEXT: Current market price is $${deterministicData?.currentPrice?.toFixed(2) || "unknown"}. Intrinsic value is $${__baseIvNum.toFixed(2)}. Price-to-IV ratio is ${deterministicData?.currentPrice ? ((deterministicData.currentPrice / __baseIvNum) * 100).toFixed(1) : "unknown"}%. If this exceeds 120%, C-14a (IRR Entry Price Disclosure) applies. The market-implied growth rate has been pre-calculated by the DCF engine and is provided above — use this exact figure in expectedReturnMath.impliedGrowth. Never compute impliedGrowth yourself — the engine value is authoritative and is enforced downstream.`;

      const userPrompt = `Synthesize the final Oracle Memo for ${symbol} as of ${currentDateTime}.
        
        AGENT A (HISTORIAN): ${JSON.stringify(factSheet)}
        AGENT B (MATHEMATICIAN): ${JSON.stringify(mathEngine)}
        AGENT D (ANALYST — CURRENT INTELLIGENCE): ${JSON.stringify(intelligenceBrief)}
        AGENT F (THE PULSE — BRAND / CUSTOMER-RELATIONSHIP SIGNAL): ${JSON.stringify(pulseOutput)}
        
        DETERMINISTIC DATA: ${JSON.stringify(deterministicData)}
        
        FMP PROPRIETARY DCF (for C-3d cross-check disclosure only — do NOT substitute for Mathematician IV):
          FMP DCF per share: $${deterministicData?.fmpDcf?.dcf?.toFixed(2) || "unavailable"}
          FMP Levered DCF per share: $${deterministicData?.fmpDcf?.leveredDcf?.toFixed(2) || "unavailable"}
          Is synthetic (estimated, not fetched): ${deterministicData?.fmpDcf?.isSynthetic ?? true}
          Divergence from Mathematician IV (pre-computed): ${fmpDcfDivergencePct !== null ? fmpDcfDivergencePct.toFixed(1) + "%" : "N/A"}
          requiresFmpDcfDisclosure: ${requiresFmpDcfDisclosure}
          C-3d applies if and only if requiresFmpDcfDisclosure above is true.
        
        CRITICAL: 
        === OUTPUT-FORMAT RULES (O-1 through O-6) ===
        O-1. Your final output MUST be a single valid JSON object matching the CompanyAnalysis schema. Escape all inner double quotes (\\") inside string values to prevent JSON parsing errors. DO NOT output trailing commas.
        O-2. Use ${oracleIvO2}
        O-3. Reconcile Agent A's history with Agent B's model to form the final conviction.
        Market-implied growth rate (back-calculated by DCF engine from current price): ${
          __ivUnresolvable
            ? "N/A (valuation unresolvable — do NOT report an implied growth rate)"
            : mathEngine.scenarios.base.impliedMarketGrowth != null &&
                mathEngine.scenarios.base.impliedMarketGrowth > 0
              ? (mathEngine.scenarios.base.impliedMarketGrowth * 100).toFixed(1) +
                "%"
              : "unavailable — use base case growth gap as proxy"
        }. Base case growth assumption: ${(mathEngine.base.year1to5GrowthRate * 100).toFixed(1)}%.
        O-4. ${oracleIvO4}
        O-5. REQUIRED IRR: The required hurdle rate is ${
          deterministicData?.thirtyYearTreasuryYield
            ? (deterministicData.thirtyYearTreasuryYield + 5.5).toFixed(1) +
              "% (" +
              deterministicData.thirtyYearTreasuryYield.toFixed(2) +
              "% 30Y Treasury + 5.5% ERP)"
            : "~9.5% (30Y Treasury + 5.5% ERP)"
        }. Use this exact figure in expectedReturnMath.requiredIrr. Do not substitute a different value.
        O-6. IMMUTABLE SCENARIO IRRS: The Bear, Base, and Bull scenario growth assumptions and implied IRRs are fixed by the DCF engine to guarantee monotonicity at the current entry price. Do not recompute, modify, or round these numbers differently. Use them exactly as provided in the Mathematician's assumptionRationales (bearCaseIRR, baseCaseIRR, bullCaseIRR, etc.).`;

      console.log(`[ORACLE-PROMPT-SIZE] ${symbol}: sys=${systemInstruction.length} user=${userPrompt.length} total=${systemInstruction.length + userPrompt.length}`);
      console.log(
        `[Gemini] Executing analysis for ${symbol} using ${actualModel}. Search: ${useSearch}, Thinking: ${useThinking}`,
      );

      const tools: any[] = [
        {
          functionDeclarations: [
            ...(fmpTools[0].functionDeclarations || []),
            getAnalystEstimatesDeclaration,
            getPriceTargetDeclaration,
          ],
        },
      ];
      // Note: Search tool is often not supported when using forced thinkingLevel config in some preview models.
      if (useSearch && !useThinking) {
        tools.push({ googleSearch: {} });
      }

      try {
        res = await withRetry(async () => {
          return await callGeminiWithTools({
            model: actualModel,
            contents: [{ role: "user", parts: [{ text: userPrompt }] }] as any,
            config: {
              ...config,
              systemInstruction: { parts: [{ text: systemInstruction }] },
            },
            tools: tools.length > 0 ? tools : undefined,
            symbol,
            telemetry: { ledger, agent: "oracle" },
            entry: MODELS.ORACLE,
          });
        }, 3);
      } catch (err: any) {
        console.warn(
          `[Fallback] Analysis failed for ${actualModel}, falling back to the Oracle sub-call model...`,
          err,
        );
        recordModelFailover({
          agent: "Oracle-Sub",
          modelTried: actualModel,
          fallbackModel: MODELS.ORACLE_SUB.primary,
          statusCode: err?.status || err?.error?.code || err?.code || "unknown",
          ticker: symbol
        });
        // [MODEL-1] Oracle sub-call — its own registry entry (ORACLE_SUB).
        res = await withRetry(async () => {
          return await callGeminiWithTools({
            model: MODELS.ORACLE_SUB.primary,
            contents: [{ role: "user", parts: [{ text: userPrompt }] }] as any,
            config: {
              responseMimeType: "application/json",
              responseSchema: COMPANY_ANALYSIS_SCHEMA,
              systemInstruction: { parts: [{ text: systemInstruction }] },
              thinking_level: MODELS.ORACLE_SUB.thinkingLevel,
            },
            symbol,
            telemetry: { ledger, agent: "oracle" },
            entry: MODELS.ORACLE_SUB,
          });
        }, 3);
      }

      // [MODEL-1 / ORACLE-TRUNCATION] 3.8-flash emits ~30% more output+thought
      // tokens than 3.6 at thinking high. maxOutputTokens (49152) leaves headroom
      // under the 65536 cap, but make a MAX_TOKENS truncation observable rather
      // than silent — it rides analysis_completed via observability.oracleTruncated.
      try {
        const fin = (res as any)?.candidates?.[0]?.finishReason;
        if (fin === "MAX_TOKENS") {
          oracleTruncated = true;
          const um = (res as any)?.usageMetadata ?? {};
          console.warn(
            `[ORACLE-TRUNCATION] model=${(res as any)?.modelVersion ?? actualModel} ` +
            `thought=${um.thoughtsTokenCount ?? "?"} output=${um.candidatesTokenCount ?? "?"}`,
          );
        }
      } catch { /* non-fatal observability */ }

      try {
        const responseText = extractText(res);
        const cleaned = cleanJson(responseText);
        const parsed = JSON.parse(cleaned);

        // ─── ORACLE C STRUCTURAL VALIDITY GATE ───────────────────────────────
        // Gemini occasionally returns its own input schema instead of filled output
        // under heavy thinking load. Detect this and throw so withRetry can retry.
        //
        // Three failure signatures to catch:
        //   1. description is an object (schema annotation echoed back, not filled)
        //   2. description is a string under 20 chars (empty or stub response)
        //   3. oracleIntrinsicValue is 0 or missing AND description is thin
        //      (Oracle C failed to produce any substantive output)

        const descriptionIsObject =
          parsed.description !== null && typeof parsed.description === "object";
        const descriptionIsThin =
          (typeof parsed.description === "string" &&
            parsed.description.trim().length < 20) ||
          parsed.description === undefined ||
          parsed.description === null;
        const ivIsZero =
          (parsed.oracleFramework?.oracleIntrinsicValue ?? 0) === 0 &&
          (parsed.intrinsicValue ?? 0) === 0;

        if (descriptionIsObject || descriptionIsThin) {
          // Throw — withRetry will catch this and retry up to 4 times.
          // Do not continue with a malformed response: the result is a hollow
          // N/A memo that is worse than a clean retry failure.
          const failureReason = descriptionIsObject
            ? `Oracle C returned its own schema as output (description is ${typeof parsed.description})`
            : `Oracle C returned a thin description (${parsed.description?.trim().length ?? 0} chars)`;
          console.warn(
            `[ORACLE-C-GATE] ${symbol}: ${failureReason}. ` +
              `Throwing to trigger withRetry. Response snippet: ${responseText.substring(0, 150)}`,
          );
          throw new Error(
            `[ORACLE-C-GATE] Malformed Oracle C response: ${failureReason}`,
          );
        }

        // Soft warning for zero IV without thin description (IV-LOCK handles this downstream).
        // Enhanced: if IV=0 AND realisticIrr is also absent, treat as truncation and retry.
        // A complete Oracle response will always populate realisticIrr even when IV is legitimately 0.
        if (ivIsZero) {
          const realisticIrrAbsent =
            !parsed.expectedReturnMath?.realisticIrr ||
            parsed.expectedReturnMath.realisticIrr === "";

          if (realisticIrrAbsent) {
            // Both IV and realisticIrr missing — strong truncation signal. Retry via ORACLE-C-GATE.
            console.warn(
              `[ORACLE-C-GATE] ${symbol}: oracleIntrinsicValue=0 AND realisticIrr absent — ` +
              `likely truncated response. Throwing to trigger withRetry.`,
            );
            throw new Error(
              `[ORACLE-C-GATE] Truncated Oracle C response: IV=0 and realisticIrr absent for ${symbol}`,
            );
          } else {
            // IV=0 but expectedReturnMath is populated — Oracle made a deliberate choice.
            // IV-LOCK patches downstream; IRR-PATCH handles realisticIrr if needed.
            console.warn(
              `[ORACLE-C-GATE] ${symbol}: oracleIntrinsicValue=0 with valid description and IRR. ` +
              `IV-LOCK will patch downstream. Not retrying — response appears substantive.`,
            );
          }
        }
        // ─────────────────────────────────────────────────────────────────────

        // Sanitization: Ensure all fields that should be strings are strings
        const sanitizeString = (val: any) => {
          if (typeof val === "string") return val;
          if (Array.isArray(val)) {
            return val
              .map((item) => {
                if (typeof item === "string") return item;
                if (typeof item === "object" && item !== null) {
                  // Handle objects like {effect: "...", description: "..."} or {title: "...", description: "..."}
                  const entries = Object.entries(item)
                    .filter(([k, v]) => typeof v === "string")
                    .map(
                      ([k, v]) =>
                        `${k.charAt(0).toUpperCase() + k.slice(1)}: ${v}`,
                    );
                  return entries.join("");
                }
                return String(item);
              })
              .join("");
          }
          if (typeof val === "object" && val !== null) {
            // Handle single object
            const entries = Object.entries(val)
              .filter(([k, v]) => typeof v === "string")
              .map(
                ([k, v]) => `${k.charAt(0).toUpperCase() + k.slice(1)}: ${v}`,
              );
            return entries.join("");
          }
          return String(val || "N/A");
        };

        const sanitizeDetail = (detail: any) => {
          if (!detail || typeof detail !== "object")
            return {
              type: "N/A",
              strength: "N/A",
              duration: "N/A",
              vulnerabilities: "N/A",
              competitiveThreats: "N/A",
              description: "N/A",
            };
          return {
            type: sanitizeString(detail.type),
            strength: sanitizeString(detail.strength),
            duration: sanitizeString(detail.duration),
            vulnerabilities: sanitizeString(detail.vulnerabilities),
            competitiveThreats: sanitizeString(detail.competitiveThreats),
            description: sanitizeString(detail.description),
          };
        };

        const oracleDetails = parsed.oracleDetails
          ? {
              competence: sanitizeDetail(parsed.oracleDetails.competence),
              management: sanitizeDetail(parsed.oracleDetails.management),
              predictability: sanitizeDetail(
                parsed.oracleDetails.predictability,
              ),
              marginOfSafety: sanitizeDetail(
                parsed.oracleDetails.marginOfSafety,
              ),
              stability: sanitizeDetail(parsed.oracleDetails.stability),
            }
          : {
              competence: {
                type: "N/A",
                strength: "N/A",
                duration: "N/A",
                vulnerabilities: "N/A",
                competitiveThreats: "N/A",
                description: "N/A",
              },
              management: {
                type: "N/A",
                strength: "N/A",
                duration: "N/A",
                vulnerabilities: "N/A",
                competitiveThreats: "N/A",
                description: "N/A",
              },
              predictability: {
                type: "N/A",
                strength: "N/A",
                duration: "N/A",
                vulnerabilities: "N/A",
                competitiveThreats: "N/A",
                description: "N/A",
              },
              marginOfSafety: {
                type: "N/A",
                strength: "N/A",
                duration: "N/A",
                vulnerabilities: "N/A",
                competitiveThreats: "N/A",
                description: "N/A",
              },
              stability: {
                type: "N/A",
                strength: "N/A",
                duration: "N/A",
                vulnerabilities: "N/A",
                competitiveThreats: "N/A",
                description: "N/A",
              },
            };

        // ─── PILLAR RATIONALE COMPLETENESS GATE ──────────────────────────────
        // Each oracleFramework pillar rationale must be substantive — at least
        // 10 words explaining WHY the rating applies. Single-phrase rationales
        // like "Strong brand" or "Solid track record" are not acceptable.
        // Thin rationales are warned in Sentry and flagged in the output so
        // the UI can surface the gap rather than silently display a thin explanation.
        const PILLAR_MIN_WORDS = 10;
        const pillarsToCheck = [
          "understandable",
          "moat",
          "management",
          "marginOfSafety",
          "longTerm",
          "stability",
        ] as const;

        if (parsed.oracleFramework) {
          for (const pillar of pillarsToCheck) {
            const pillarObj = parsed.oracleFramework[pillar] as
              | { value?: string; rationale?: string }
              | undefined;
            if (!pillarObj) continue;

            const rationale = pillarObj.rationale ?? "";
            const wordCount = rationale
              .trim()
              .split(/\s+/)
              .filter(Boolean).length;

            if (wordCount < PILLAR_MIN_WORDS) {
              console.warn(
                `[PILLAR-RATIONALE] ${factSheet.ticker}: '${pillar}' rationale is thin ` +
                  `(${wordCount} words — minimum ${PILLAR_MIN_WORDS}). ` +
                  `Value: "${pillarObj.value ?? "N/A"}". Rationale: "${rationale.length > 80 ? rationale.substring(0, 80) + "..." : rationale}"`,
              );
              // Flag in output so UI can surface the gap — do not silently display thin text.
              pillarObj.rationale =
                `[Insufficient detail — ${wordCount} word${wordCount === 1 ? "" : "s"} provided. ` +
                `Rating: ${pillarObj.value ?? "N/A"}. ${rationale}`.trim() +
                "]";
            }
          }
        }
        // ─────────────────────────────────────────────────────────────────────

        const moatAnalysis = parsed.moatAnalysis
          ? {
              ...parsed.moatAnalysis,
              type: sanitizeString(parsed.moatAnalysis.type),
              strength: sanitizeString(parsed.moatAnalysis.strength),
              description: sanitizeString(parsed.moatAnalysis.description),
              duration: sanitizeString(parsed.moatAnalysis.duration),
              vulnerabilities: sanitizeString(
                parsed.moatAnalysis.vulnerabilities,
              ),
              competitiveThreats: sanitizeString(
                parsed.moatAnalysis.competitiveThreats,
              ),
            }
          : null;

        const keyRisks = Array.isArray(parsed.keyRisks)
          ? parsed.keyRisks.map((risk: any) => ({
              title: sanitizeString(risk.title),
              description: sanitizeString(risk.description),
              severity:
                typeof risk.severity === "string" ? risk.severity : "medium",
            }))
          : [];

        // EARLY-DECLINE WATCH -> guaranteed surfacing in keyRisks (code-side,
        // not reliant on the Oracle prompt). Pushed at "medium" severity.
        if ((factSheet as any).earlyDeclineWatch?.fired === true) {
          const alreadyPresent = keyRisks.some((r: any) =>
            /early-decline|decline watch|structural trigger/i.test(
              `${r.title} ${r.description}`,
            ),
          );
          if (!alreadyPresent) {
            keyRisks.push({
              title: "Early Decline Watch",
              description: sanitizeString(
                (factSheet as any).earlyDeclineWatch.rationale,
              ),
              severity: "medium",
            });
          }
        }

        // RISK-MATH LOCK (CALC-3): ALL risk-matrix arithmetic is TypeScript-owned.
        // The Oracle emits judgment only (ivChange %). The engine computes:
        //   ivImpactPerShare = IV × ivChange%   (negative dollar string)
        //   riskAdjustedIV   = max(0, IV − |ivImpactPerShare|)  (unweighted — ratified)
        const riskModeling = Array.isArray(parsed.riskModeling)
          ? parsed.riskModeling.map((item: any) => {
              const iv = mathEngine.scenarios.base.intrinsicValuePerShare;
              const ivChangeStr = sanitizeString(item.ivChange || "");
              // Parse '-12%' → -12. Strip everything except digits, dot, minus.
              let ivChangePct = parseFloat(ivChangeStr.replace(/[^0-9.\-]/g, ""));
              if (isNaN(ivChangePct)) {
                console.warn(
                  `[RISK-IMPACT-PARSE] ${symbol}: unparseable ivChange "${ivChangeStr}" — impact fields emitted as N/A.`,
                );
                ivChangePct = NaN;
              } else if (ivChangePct > 0) {
                // Schema requires negative; enforce sign deterministically.
                ivChangePct = -ivChangePct;
              }
              const hasMath = iv > 0 && !isNaN(ivChangePct);
              const impactDollars = hasMath ? iv * (ivChangePct / 100) : null;
              return {
                risk: sanitizeString(item.risk),
                probability: sanitizeString(item.probability),
                impact: sanitizeString(item.impact),
                ivChange: sanitizeString(item.ivChange),
                ivImpactPerShare:
                  impactDollars != null
                    ? `-$${Math.abs(impactDollars).toFixed(2)}`
                    : "N/A",
                riskAdjustedIV:
                  impactDollars != null
                    ? `$${Math.max(0, iv + impactDollars).toFixed(2)}`
                    : "N/A",
              };
            })
          : [];

        // --- OBSERVABILITY: RISK ATTRIBUTION FLAG ---
        const safeTokens = new Set<string>();
        safeTokens.add(ticker.toLowerCase());
        const cName = institutionalData?.profile?.companyName || symbol;
        cName.split(" ").forEach((w: string) => {
          const clean = w.toLowerCase().replace(/[^a-z0-9]/g, '');
          if (clean.length > 2) safeTokens.add(clean);
        });
        
        const compDevs = intelligenceBrief?.industryContext?.majorCompetitorDevelopments;
        if (compDevs && typeof compDevs === 'object') {
          for (const key of Object.keys(compDevs)) {
            safeTokens.add(key.toLowerCase());
            key.split(" ").forEach((w: string) => {
              const clean = w.toLowerCase().replace(/[^a-z0-9]/g, '');
              if (clean.length > 2) safeTokens.add(clean);
            });
          }
        }
        
        // [PEER-METRIC-LABEL-LOCK] The peer multiple shown on the detail cards is the
        // deterministic EV/FCF value (pFcf <- metrics.evToFreeCashFlow). The Analyst
        // sometimes free-texts the metric label as "P/FCF"/"Price/FCF" — a mislabel of an
        // EV/FCF figure. Coerce any FCF-multiple label to "EV/FCF" so the card matches the
        // deterministic table header. Slash-FCF only — never touches "FCF Yield"/"ROIC".
        if (Array.isArray(parsed.peerBenchmarking)) {
          const fcfMultipleLabel = /^\s*(ev|p|price|pr)\s*\/\s*fcf\s*$/i;
          for (const p of parsed.peerBenchmarking) {
            if (typeof p?.metric === "string" && fcfMultipleLabel.test(p.metric) && p.metric.trim().toUpperCase() !== "EV/FCF") {
              console.warn(
                `[PEER-METRIC-LABEL-LOCK] ${ticker}: peer ${p.peer || "?"} metric "${p.metric}" relabeled to "EV/FCF" (value is the deterministic EV/FCF multiple).`,
              );
              p.metric = "EV/FCF";
            }
          }
        }

        // [PEER-CARD-PROVENANCE-LOCK] (Tuning v2.92): every peer comparison card must
        // reference a ticker present in the deterministic peer set (factSheet.peerBenchmarks,
        // which the engine overwrites post-Historian). The Oracle fabricated a full extra
        // peer card (REGN: "INCY" with invented ROIC/FCF-yield/EV-FCF values) despite rule
        // C-13 — prompt compliance failure; this filter is the deterministic backstop.
        // LLM does no math; cards without deterministic provenance are dropped, never
        // corrected. Cards with NO resolvable ticker field are kept (fail-open for legacy
        // shapes).
        if (Array.isArray(parsed.peerBenchmarking)) {
          const __deterministicPeerTickers = new Set(
            (factSheet?.peerBenchmarks || [])
              .map((pb: any) =>
                String(pb?.ticker || pb?.peerTicker || pb?.symbol || "")
                  .toUpperCase()
                  .trim(),
              )
              .filter((t: string) => t.length > 0),
          );
          if (
            __deterministicPeerTickers.size === 0 &&
            (deterministicData as any)?.__peerEmptyAbstain === true &&
            parsed.peerBenchmarking.length > 0
          ) {
            // [PEER-CARD-ABSTAIN-SUPPRESS] (Tuning v2.94 F-1, ratified fail-CLOSED): the
            // peer gate DELIBERATELY abstained (empty deterministic set), yet the Oracle
            // authored peer cards anyway — values are LLM-recalled with no pipeline source
            // (observed live: CAAP rendered ASR/PAC/OMAB with asterisked metrics). The
            // v2.92 fail-open was designed for legacy/no-ticker shapes, not for deliberate
            // abstention; suppress ALL Oracle peer cards so the memo abstains honestly.
            // Fail-open is PRESERVED for the non-abstain empty-set case (fetch failures,
            // legacy factSheet shapes) — this branch requires the explicit abstain marker.
            const __suppressedTickers = new Set<string>(
              parsed.peerBenchmarking
                .map((p: any) => String(p?.peerTicker || p?.peer || "").toUpperCase().trim())
                .filter((t: string) => t.length > 0),
            );
            const __suppressedCount = __suppressedTickers.size > 0 ? __suppressedTickers.size : parsed.peerBenchmarking.length;
            parsed.peerBenchmarking = [];
            console.warn(
              `[PEER-CARD-ABSTAIN-SUPPRESS] ${ticker}: deterministic peer set is EMPTY by deliberate ` +
                `abstention ([PEER-EMPTY-ABSTAIN]) but the Oracle authored ${__suppressedCount} peer card(s) ` +
                `[${[...__suppressedTickers].join(", ") || "unlabeled"}] — LLM-recalled values with no pipeline ` +
                `source. Suppressed (fail-closed). Prompt compliance failure (C-13).`,
            );
            (deterministicData as any).__peerCardsDropped = __suppressedCount;
            (deterministicData as any).__peerAbstainSuppressed = true;
          } else if (__deterministicPeerTickers.size > 0) {
            // (Tuning v2.93) peerBenchmarking is METRIC-ROWS (~3 rows per peer card:
            // ROIC / FCF Yield / EV-FCF), so the original row-level count and log
            // TRIPLED per dropped card (SNDK: 1 card "WDC" → peerCardsDropped=3 and
            // 3 identical warn lines; NBIX: 2 cards → 6). Count and log UNIQUE
            // tickers; every offending row is still filtered out.
            const __droppedTickers = new Set<string>();
            parsed.peerBenchmarking = parsed.peerBenchmarking.filter((p: any) => {
              const cardTicker = String(p?.peerTicker || p?.peer || "")
                .toUpperCase()
                .trim();
              const ok =
                cardTicker.length === 0 ||
                __deterministicPeerTickers.has(cardTicker);
              if (!ok) __droppedTickers.add(cardTicker);
              return ok;
            });
            for (const __droppedTicker of __droppedTickers) {
              console.warn(
                `[PEER-CARD-PROVENANCE-LOCK] ${ticker}: dropped Oracle peer card "${__droppedTicker}" — ` +
                  `not in deterministic peer set [${[...__deterministicPeerTickers].join(", ")}]. ` +
                  `Values were LLM-authored with no pipeline source. Prompt compliance failure (C-13).`,
              );
            }
            (deterministicData as any).__peerCardsDropped = __droppedTickers.size;
          }
        }

        // [PEER-ROW-METRIC-GATE] (SPEC-7) The lock above drops whole cards whose TICKER
        // has no deterministic provenance, but says nothing about an individual metric
        // ROW whose VALUE the engine cannot source. The Oracle authored "Combined Ratio
        // 87.1% (PGR) / 87.3% (MKL)" for BRK-B — figures present in no deterministic
        // field and not derivable from any input we fetch (MKL's is likely wrong too;
        // Markel typically runs mid-90s). The pipeline carries exactly three per-peer
        // metrics — P/TBV, ROTCE, net margin — and a combined/loss/expense/underwriting
        // ratio is not among them: it is not a clean FMP field for arbitrary peers,
        // needing loss + expense ratios off insurance-specific statement lines FMP does
        // not reliably expose. Fail-closed — the model may not assert a peer metric the
        // engine cannot verify. Blocklist, NOT a whitelist: non-Type-4 rows (ROIC /
        // FCF Yield / EV/FCF) and the sourceable Type-4 rows are untouched, so a
        // non-insurer analysis has nothing to drop.
        if (Array.isArray(parsed.peerBenchmarking)) {
          const __unsourceablePeerMetrics = [
            "combined ratio",
            "loss ratio",
            "expense ratio",
            "underwriting ratio",
          ];
          const __normalizeMetric = (m: string) =>
            m.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
          const __droppedMetricTally = new Map<string, number>();
          parsed.peerBenchmarking = parsed.peerBenchmarking.filter((p: any) => {
            if (typeof p?.metric !== "string") return true;
            const __norm = __normalizeMetric(p.metric);
            // Whole-phrase containment rather than strict set equality: fabricated
            // labels arrive decorated ("Combined Ratio (2024)", "Underwriting Ratio %")
            // and an exact-match set would let those through. No sourceable peer metric
            // (P/TBV, ROTCE, net margin, ROIC, FCF Yield, EV/FCF) contains any of these
            // phrases, so containment cannot over-drop.
            const __hit = __unsourceablePeerMetrics.find((u) => __norm.includes(u));
            if (!__hit) return true;
            __droppedMetricTally.set(__hit, (__droppedMetricTally.get(__hit) ?? 0) + 1);
            return false;
          });
          const __rowsDropped = [...__droppedMetricTally.values()].reduce(
            (a: number, b: number) => a + b,
            0,
          );
          if (__rowsDropped > 0) {
            console.warn(
              `[PEER-ROW-METRIC-GATE] ${ticker}: dropped ${__rowsDropped} unsourced metric ` +
                `row(s) [${[...__droppedMetricTally.entries()].map(([m, n]) => `${m}×${n}`).join(", ")}] ` +
                `— LLM-authored, no pipeline source (C-13 row-level).`,
            );
          }
          (deterministicData as any).__peerRowsDroppedUnsourcedMetric = __rowsDropped;
        }

        // [S2] Normalized-prose check (flag-only — measurement, no prose scrub).
        // The normalized-earnings prose sometimes labels a TTM/current anchor as
        // mid-cycle "steady-state normalized" earnings power. For a compounder the
        // TTM anchor may be correct — this only SIZES the labeling gap; it never
        // rewrites prose or changes the anchor. Fires when a normalization claim
        // is present AND the engine anchored on TTM AND the TTM base diverges
        // >50% from the 7-yr mid-cycle average (ATAT: ttm 240M vs mid-cycle 89M).
        {
          const __anchors = (deterministicData as any)?.normalizationAnchors;
          // [F1] Prefer the basis resolved at the DCF-input site against the
          // LLM-final anchor; fall back to the provisional pre-DCF value.
          const __oeAnchorBasis =
            (deterministicData as any)?.__oeAnchorBasisResolved ??
            __anchors?.oeAnchorBasis ??
            null;
          const __oeMidCycleDivergencePct = __anchors?.oeMidCycleDivergencePct ?? null;
          const __prose =
            typeof parsed?.normalizedEarnings === "string"
              ? parsed.normalizedEarnings
              : "";
          const __claimMatch = __prose.match(
            /normalized|mid-?cycle|steady[ -]?state|filter(?:s|ing)? out|cyclical peak/i,
          );
          const __mismatch =
            !!__claimMatch &&
            __oeAnchorBasis === "ttm" &&
            typeof __oeMidCycleDivergencePct === "number" &&
            Math.abs(__oeMidCycleDivergencePct) > 50;
          (deterministicData as any).__normalizedProseMismatch = __mismatch;
          if (__mismatch) {
            console.warn(
              `[NORMALIZED-PROSE-CHECK] ${ticker}: claim="${__claimMatch![0]}", ` +
                `anchor=ttm, midCycleDivergence=${__oeMidCycleDivergencePct.toFixed(1)}%, mismatch=true`,
            );
          }
        }

        // [S3] Peer-card subject-metric check (flag-only — measurement, no scrub).
        // The peer comparison cards are Oracle-authored prose (the REGN/INCY/CAAP
        // fabrication surface). This detects the LLM minting a SUBJECT metric in a
        // card that diverges from the deterministic value feeding the peer-table
        // subject row — deterministicData.oracleMetrics.fcfYield (a percentage,
        // e.g. 7.5). Parses subject-referencing FCF-yield assertions from each
        // card's narrative (`context`) and flags a >2pp gap. Cards are never
        // rewritten; the subject-figure lock is the GATED follow-on.
        {
          const __detSubjectFcfYield =
            typeof (deterministicData as any)?.oracleMetrics?.fcfYield === "number"
              ? (deterministicData as any).oracleMetrics.fcfYield
              : null;
          // [F2] Forward baseline: a card claim explicitly labelled "forward" must be
          // measured against the deterministic FORWARD yield (same ×100 basis), not
          // the trailing one — otherwise the reported divergence uses the wrong comparand.
          const __detSubjectForwardFcfYield =
            typeof (deterministicData as any)?.oracleMetrics?.forwardFcfYield === "number"
              ? (deterministicData as any).oracleMetrics.forwardFcfYield
              : null;
          if (
            (__detSubjectFcfYield != null || __detSubjectForwardFcfYield != null) &&
            Array.isArray(parsed.peerBenchmarking)
          ) {
            const __subjectRefs: string[] = [];
            if (typeof ticker === "string" && ticker.trim().length > 0)
              __subjectRefs.push(ticker.trim());
            const __coName =
              typeof institutionalData?.profile?.companyName === "string"
                ? institutionalData.profile.companyName
                : "";
            const __coFirst = __coName.split(/[\s,]+/)[0];
            if (__coFirst && __coFirst.length > 2) __subjectRefs.push(__coFirst);
            const __esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
            const __refAlt = __subjectRefs.map(__esc).filter(Boolean).join("|");

            let __maxDivergence = 0;
            let __worstCard: string | null = null;
            let __worstBasisName: string | null = null;
            let __worstBasisVal: number | null = null;
            if (__refAlt) {
              // "<subject>('s)? … fcf yield … N%"  or  "N% … fcf yield … <subject>"
              const __patterns = [
                new RegExp(
                  `(?:${__refAlt})(?:['’]s)?[^.]{0,60}?fcf\\s*yield[^.]{0,15}?(\\d+(?:\\.\\d+)?)\\s*%`,
                  "gi",
                ),
                new RegExp(
                  `(\\d+(?:\\.\\d+)?)\\s*%[^.]{0,15}?fcf\\s*yield[^.]{0,40}?(?:${__refAlt})`,
                  "gi",
                ),
              ];
              for (const p of parsed.peerBenchmarking) {
                const __text = typeof p?.context === "string" ? p.context : "";
                if (!__text) continue;
                for (const __re of __patterns) {
                  __re.lastIndex = 0;
                  let __m: RegExpExecArray | null;
                  while ((__m = __re.exec(__text)) !== null) {
                    const __cardVal = parseFloat(__m[1]);
                    if (!isFinite(__cardVal)) continue;
                    // [F2] Pick the baseline from the matched text: a "forward" claim
                    // compares against the forward yield; otherwise trailing. Fall back
                    // to whichever baseline is available.
                    const __isForward = /forward/i.test(__m[0]);
                    const __baseVal =
                      __isForward && __detSubjectForwardFcfYield != null
                        ? __detSubjectForwardFcfYield
                        : __detSubjectFcfYield;
                    const __baseName =
                      __isForward && __detSubjectForwardFcfYield != null
                        ? "forwardFcfYield"
                        : "fcfYield";
                    if (__baseVal == null) continue;
                    const __div = Math.abs(__cardVal - __baseVal);
                    if (__div > __maxDivergence) {
                      __maxDivergence = __div;
                      __worstCard = `${p?.peer || p?.peerTicker || "?"}:${__cardVal}%`;
                      __worstBasisName = __baseName;
                      __worstBasisVal = __baseVal;
                    }
                  }
                }
              }
            }
            const __divergent = __maxDivergence > 2; // >2 percentage-point tolerance
            (deterministicData as any).__subjectCardMetricDivergence = __divergent;
            (deterministicData as any).__subjectCardMetricMaxDivergence =
              __maxDivergence > 0 ? Number(__maxDivergence.toFixed(2)) : null;
            // [F2] Name the baseline (fcfYield | forwardFcfYield) that drove the flag.
            (deterministicData as any).__subjectCardMetricName = __divergent
              ? __worstBasisName
              : null;
            if (__divergent) {
              console.warn(
                `[PEER-CARD-SUBJECT-METRIC-CHECK] ${ticker}: metric=${__worstBasisName}, ` +
                  `card=${__worstCard}, deterministic=${Number(__worstBasisVal).toFixed(1)}% ` +
                  `(basis=${__worstBasisName}), divergence=${__maxDivergence.toFixed(1)}pp`,
              );
            }
          }
        }

        // [SPEC-1] P/TBV prose-consistency tripwire (flag-only — measurement, no scrub).
        // Regression guard for the defect this spec fixes: the BRK-B memo stated
        // "1.51x P/TBV" three times against a true current multiple of 1.76x.
        // Scans the assembled memo prose for "N.NNx P/TBV" tokens and flags any that
        // reconcile to NEITHER engine-computed multiple.
        //
        // A token is consistent if within 0.02x of the CURRENT multiple OR the
        // RI-IMPLIED FAIR multiple: the C-3f attribution prompt deliberately instructs
        // the Oracle to cite the RI fair multiple, so checking against the current
        // multiple alone would false-flag every Type-4 RI memo. 1.51x matches neither
        // and would have tripped this.
        {
          const __curPtbv = (deterministicData as any)?.__type4CurrentPtbv;
          const __riPtbv = (deterministicData as any)?.__type4RiImpliedPtbv;
          let __ptbvConsistency: string | null = null;
          let __ptbvDivergentToken: number | null = null;
          if (typeof __curPtbv === "number" && __curPtbv > 0) {
            const __toText = (v: any): string => {
              if (typeof v === "string") return v;
              if (Array.isArray(v)) return v.map(__toText).join(" ");
              if (v && typeof v === "object")
                return Object.values(v).map(__toText).join(" ");
              return "";
            };
            const __prose = [
              parsed?.killerInsight,
              parsed?.summary,
              parsed?.valuationRationale,
              parsed?.valuationInsights,
              parsed?.expectedReturnMath?.rationale,
              parsed?.oracleDetails?.marginOfSafety,
            ]
              .map(__toText)
              .filter(Boolean)
              .join("\n");
            const __ptbvRes = [
              /(\d+(?:\.\d+)?)\s*x\s*(?:p\s*\/\s*tbv|price[-\s]to[-\s]tangible[-\s]book)/gi,
              /(?:p\s*\/\s*tbv|price[-\s]to[-\s]tangible[-\s]book)[^.]{0,24}?(\d+(?:\.\d+)?)\s*x/gi,
            ];
            let __tokenCount = 0;
            const __divergentTokens: number[] = [];
            for (const __re of __ptbvRes) {
              __re.lastIndex = 0;
              let __pm: RegExpExecArray | null;
              while ((__pm = __re.exec(__prose)) !== null) {
                const __tok = parseFloat(__pm[1]);
                if (!isFinite(__tok)) continue;
                __tokenCount += 1;
                const __okCur = Math.abs(__tok - __curPtbv) <= 0.02;
                const __okRi =
                  typeof __riPtbv === "number" && Math.abs(__tok - __riPtbv) <= 0.02;
                if (!__okCur && !__okRi) __divergentTokens.push(__tok);
              }
            }
            // null = check did not run (no token found); "consistent"/"divergent" = ran.
            __ptbvConsistency =
              __tokenCount === 0
                ? null
                : __divergentTokens.length > 0
                  ? "divergent"
                  : "consistent";
            __ptbvDivergentToken = __divergentTokens.length > 0 ? __divergentTokens[0] : null;
            if (__ptbvConsistency === "divergent") {
              console.warn(
                `[PTBV-PROSE-CONSISTENCY] ${ticker}: prose token(s) ` +
                  `${__divergentTokens.map((t) => t.toFixed(2) + "x").join(", ")} reconcile to neither ` +
                  `current ${__curPtbv.toFixed(2)}x nor RI-implied fair ` +
                  `${typeof __riPtbv === "number" ? __riPtbv.toFixed(2) + "x" : "n/a"} (>0.02x). ` +
                  `Prose not modified (measurement only).`,
              );
            }
          }
          (deterministicData as any).__ptbvProseConsistency = __ptbvConsistency;
          (deterministicData as any).__ptbvProseDivergentToken = __ptbvDivergentToken;
        }

        if (Array.isArray(parsed.peerBenchmarking)) {
          for (const p of parsed.peerBenchmarking) {
            if (p.peerTicker) safeTokens.add(p.peerTicker.toLowerCase());
            if (p.peerName) {
              p.peerName.split(" ").forEach((w: string) => {
                const clean = w.toLowerCase().replace(/[^a-z0-9]/g, '');
                if (clean.length > 2) safeTokens.add(clean);
              });
            }
          }
        }
        safeTokens.add("inc"); safeTokens.add("corp"); safeTokens.add("llc"); safeTokens.add("ltd");
        safeTokens.add("company"); safeTokens.add("the"); safeTokens.add("and");

        const megaCaps = ["Meta", "Facebook", "Apple", "Google", "Alphabet", "Amazon", "Microsoft", "Nvidia", "Tesla", "WhatsApp", "Instagram"];

        riskModeling.forEach((row: any) => {
          if (typeof row.risk === "string") {
            const riskText = row.risk;
            let flagged = false;
            
            for (const mc of megaCaps) {
              if (riskText.includes(mc) && !safeTokens.has(mc.toLowerCase())) {
                flagged = true;
                break;
              }
            }
            
            if (flagged) {
              console.warn(`[RISK-ATTRIBUTION-FLAG] ${ticker}: risk row may reference a non-subject entity — "${row.risk}"`);
            }
          }
        });
        // ---------------------------------------------

        const keyDrivers = Array.isArray(parsed.keyDrivers)
          ? parsed.keyDrivers.map((driver: any) => ({
              title: sanitizeString(driver.title),
              description: sanitizeString(driver.description),
            }))
          : [];

        const sanitizeFrameworkItem = (item: any, defaultValue: string) => {
          if (!item)
            return { value: defaultValue, rationale: "No rationale provided." };
          if (typeof item === "string")
            return { value: item, rationale: "No rationale provided." };
          return {
            value: sanitizeString(item.value || defaultValue),
            rationale: sanitizeString(
              item.rationale || "No rationale provided.",
            ),
          };
        };

        const oracleFramework = {
          moat: sanitizeFrameworkItem(
            parsed.oracleFramework?.moat || parsed.scorecardRationale?.moat,
            "Narrow",
          ),
          management: sanitizeFrameworkItem(
            parsed.oracleFramework?.management ||
              parsed.scorecardRationale?.management,
            "Good",
          ),
          understandable: sanitizeFrameworkItem(
            parsed.oracleFramework?.understandable ||
              parsed.scorecardRationale?.understandable,
            "Moderate",
          ),
          longTerm: sanitizeFrameworkItem(
            parsed.oracleFramework?.longTerm ||
              parsed.scorecardRationale?.longTerm,
            "Moderate",
          ),
          stability: sanitizeFrameworkItem(
            parsed.oracleFramework?.stability,
            "Moderate",
          ),
          marginOfSafety: sanitizeFrameworkItem(
            parsed.oracleFramework?.marginOfSafety ||
              parsed.scorecardRationale?.marginOfSafety,
            "Insufficient",
          ),
          decision: (() => {
            if (deterministicData?.isType5DataContaminated) {
              console.log(`[TYPE5-CONTAM-VERDICT-PASS] ${symbol}: contaminated Type-5 is unvaluable — forcing PASS (abstain); bypassing price-vs-floor matrix, §11 cap, and IV-LOCK injection. (ratified methodology) reason=${deterministicData?.type5UnvaluableReason}`);
              return {
                value: 'PASS',
                rationale: deterministicData?.type5UnvaluableReason === 'no-earnings-anchor' ? "Unvaluable: No positive standalone owner-earnings anchor yet (recent IPO with genuine losses). Abstaining from a price-based verdict." : "Unvaluable: Data reflects parent-company consolidated operations, not standalone performance. Abstaining from a price-based verdict."
              };
            }
            if (deterministicData?.isUnresolvableValuation) {
              console.log(`[UNRESOLVABLE-VERDICT-PASS] ${symbol}: valuation structurally unresolvable — forcing PASS (abstain).`);
              return {
                value: 'PASS',
                rationale: "Unvaluable: Structural data anomaly or missing valuation inputs prevents reliable intrinsic value calculation. Abstaining from a price-based verdict."
              };
            }
            // [TYPE5-VERDICT-CLAMP] Final Type-5 (recent spin-off, incl. registry-forced) verdict
            // ceiling. The inner IIFE below is the existing decision logic UNCHANGED; its result is
            // clamped here so NO path — Oracle-direct, decision-matrix promotion, legacy-map, or
            // fallthrough — can emit BUY/FAT PITCH for a Type-5 entity. Keyed on the resolved
            // entityType OR the legacy isRecentSpinOff flag (closes the registry-force gap that the
            // isRecentSpinOff-only cap below missed).
            const __decision = (() => {
            const rawValue = (
              typeof parsed.oracleFramework?.decision === "string"
                ? parsed.oracleFramework.decision
                : parsed.oracleFramework?.decision?.value || "PASS"
            ).toUpperCase();
            const rationale = sanitizeString(
              parsed.oracleFramework?.decision?.rationale ||
                parsed.valuationRationale ||
                "No rationale provided.",
            );
            const price =
              deterministicData?.currentPrice || parsed.currentPrice || 0;
            const iv = (() => {
              const dcfIV = mathEngine.scenarios.base.intrinsicValuePerShare;
              if (dcfIV > 0) return dcfIV;
              // Only fall back to Oracle's value if engine produced nothing
              const fromFramework = Number(
                parsed.oracleFramework?.oracleIntrinsicValue,
              );
              if (fromFramework > 0) return fromFramework;
              const fromRoot = Number(parsed.intrinsicValue);
              if (fromRoot > 0) return fromRoot;
              return 0;
            })();
            const equity = deterministicData?.bookEquity || 0;

            // Decision Validation Logic: Tiered Enforcement
            if (price > 0 && iv > 0) {
              const mos = ((iv - price) / iv) * 100;
              const isElite =
                (parsed.scorecard?.moat === "Wide" ||
                  parsed.scorecard?.moat === "Exceptional") &&
                (parsed.scorecard?.longTerm === "High" ||
                  parsed.scorecard?.longTerm === "Exceptional");
              const cyclePosition = parsed.cyclePosition || "Mid-Cycle";
              const confidenceScore = Number(parsed.confidenceScore) || 50;
              const isStage4 =
                parsed.preInflectionStage === 4 ||
                (parsed.summary && parsed.summary.includes("Stage 4"));

              // T1: Negative Equity Filter (Rule 60 Refined)
              if (equity < 0) {
                const combinedText =
                  `${rationale} ${sanitizeString(parsed.oracleJudgment)} ${parsed.description}`.toLowerCase();
                const hasCategoryA =
                  combinedText.includes("buyback-driven") ||
                  combinedText.includes("primarily by capital returns") ||
                  combinedText.includes("category a");
                const hasCategoryC =
                  combinedText.includes("mixed / ambiguous") ||
                  combinedText.includes("category c") ||
                  combinedText.includes("mixed analysis");

                // Gate 1 & 2 are handled by AI analysis; we check for classification compliance here.
                if (hasCategoryA) {
                  // Category A: Buyback-Driven. Allow verdicts if MOS floor is met.
                  if (rawValue === "BUY" || rawValue === "FAT PITCH") {
                    if (mos < 20) {
                      return {
                        value: "ACCUMULATE",
                        rationale: `[Rule 60: Negative Equity Category A] MOS floor 20% required for BUY. Current MOS: ${mos.toFixed(1)}%. ${rationale}`,
                      };
                    }
                  }
                } else if (hasCategoryC) {
                  // Category C: Mixed/Ambiguous. Max verdict ACCUMULATE, MOS floor 25%.
                  if (rawValue === "BUY" || rawValue === "FAT PITCH") {
                    return {
                      value: "ACCUMULATE",
                      rationale: `[Rule 60: Negative Equity Category C] Maximum verdict is ACCUMULATE. ${rationale}`,
                    };
                  }
                  if (rawValue === "ACCUMULATE" && mos < 25) {
                    return {
                      value: "PASS",
                      rationale: `[Rule 60: Negative Equity Category C] MOS floor 25% required. Current MOS: ${mos.toFixed(1)}%. ${rationale}`,
                    };
                  }
                } else {
                  // Category B or Unclassified. Hard stop.
                  return {
                    value: "AVOID",
                    rationale: `[T1 Override: Negative Equity] Classification as Category A required for further analysis. ${rationale}`,
                  };
                }
              }

              // T1: Price > IV Filter
              if (price > iv) {
                // Check for elite premium exception (5% band)
                if (price <= iv * 1.05 && isElite) {
                  return {
                    value: "ACCUMULATE",
                    rationale: `[T1 Override: Elite Premium Band] ${rationale}`,
                  };
                }
                return {
                  value: "AVOID",
                  rationale: `[T1 Override: Price > Intrinsic Value] ${rationale}`,
                };
              }

              // T1: Structural Decline Gate (Principle 15)
              const isStructuralDecline =
                factSheet?.structuralDeclineAssessment?.isStructuralDecline ===
                true;
              if (
                isStructuralDecline &&
                (rawValue === "BUY" ||
                  rawValue === "FAT PITCH" ||
                  rawValue === "ACCUMULATE")
              ) {
                return {
                  value: "PASS",
                  rationale:
                    `[T1 Override: Structural Decline] Maximum verdict is PASS per Principle 15. ` +
                    `Triggers confirmed: ${factSheet.structuralDeclineAssessment?.triggersConfirmed || "see Historian output"}. ${rationale}`,
                };
              }

              // T2: Cyclical at Peak filter (Rule CYC-1)
              if (
                cyclePosition === "Peak" &&
                (rawValue === "BUY" ||
                  rawValue === "FAT PITCH" ||
                  rawValue === "ACCUMULATE")
              ) {
                return {
                  value: "AVOID",
                  rationale: `[T2 Override: Cyclical at Peak] ${rationale}`,
                };
              }

              // T2: Stage 4 Override (Rule CYC-1)
              if (
                isStage4 &&
                (rawValue === "BUY" || rawValue === "FAT PITCH")
              ) {
                return {
                  value: "CORE HOLD",
                  rationale: `[T2 Override: Stage 4 / Fully Priced] ${rationale}`,
                };
              }

              // T2: Confidence Floor for BUY (Rule CONF-1)
              if (
                (rawValue === "BUY" || rawValue === "FAT PITCH") &&
                confidenceScore < 50
              ) {
                return {
                  value: "PASS",
                  rationale: `[T2 Override: Confidence Floor (<50) for BUY] ${rationale}`,
                };
              }

              // T2: Spin-off verdict cap (data fragility)
              if (
                factSheet?.isRecentSpinOff === true &&
                (rawValue === "BUY" || rawValue === "FAT PITCH")
              ) {
                return {
                  value: "ACCUMULATE",
                  rationale: `[T2 Override: Spin-Off Data Ceiling] Maximum verdict is ACCUMULATE for recent spin-offs. Pro-forma historical data carries reconstruction risk that cannot support a full BUY conviction. ${rationale}`,
                };
              }

              // RISK-TIER-MOS-LOCK: Oracle sets the tier; the engine sets the required margin.
              const __riskTier = parsed.oracleFramework?.riskTier;
              const __riskMosEnabled = process.env.RISK_TIER_MOS_ENABLED === "true";
              const __mosAddOn = !__riskMosEnabled ? 0 :
                  __riskTier === "CYCLICAL"   ? 5
                : __riskTier === "LEVERED"    ? 10
                : __riskTier === "DISTRESSED" ? 20
                : 0; // flag off → 0 (production behavior unchanged); STABLE/missing → 0

              (deterministicData as any).__riskTier = __riskTier;
              (deterministicData as any).__mosAddOn = __mosAddOn;
              (deterministicData as any).__publishedBeta = institutionalData?.profile?.beta;
              (deterministicData as any).__buyReqMos = 15 + __mosAddOn;

              (deterministicData as any).__riskMosEnabled = __riskMosEnabled;

              console.warn(
                `[RISK-TIER-MOS] ${factSheet?.ticker || "UNKNOWN"}: tier=${__riskTier || "none"}, ` +
                `mosAddOn=${__mosAddOn}, buyReqMos=${15 + __mosAddOn}, fatPitchReqMos=${25 + __mosAddOn}, ` +
                `mos=${mos?.toFixed(1)}, publishedBeta=${institutionalData?.profile?.beta ?? "n/a"}, enabled=${__riskMosEnabled}`
              );

              // T2: DISTRESSED verdict cap
              if (
                __riskMosEnabled &&
                __riskTier === "DISTRESSED" &&
                rawValue === "FAT PITCH"
              ) {
                return {
                  value: "BUY",
                  rationale: `[T2 Override: DISTRESSED Tier Ceiling] Maximum verdict is BUY for DISTRESSED tier businesses. ${rationale}`,
                };
              }

              // T2: Decision Matrix lookup (Refined Section 2 Price Gates)
              const fatPitchReqMos = 25 + __mosAddOn;
              const buyReqMos = 15 + __mosAddOn;

              if (price <= iv * (1 - fatPitchReqMos / 100) && isElite && mos >= fatPitchReqMos) {
                if (__riskMosEnabled && __riskTier === "DISTRESSED") {
                  return {
                    value: "BUY",
                    rationale: `[T2 Override: Matrix BUY (Price <= ${(1 - fatPitchReqMos / 100).toFixed(2)}*IV, DISTRESSED cap)] ${rationale}`,
                  };
                }
                return {
                  value: "FAT PITCH",
                  rationale: `[T2 Override: Matrix FAT PITCH (Price <= ${(1 - fatPitchReqMos / 100).toFixed(2)}*IV)] ${rationale}`,
                };
              }
              if (price <= iv * (1 - buyReqMos / 100) && mos >= buyReqMos) {
                return {
                  value: "BUY",
                  rationale: `[T2 Override: Matrix BUY (Price <= ${(1 - buyReqMos / 100).toFixed(2)}*IV)] ${rationale}`,
                };
              }
              if (price > iv && price <= iv * 1.05 && isElite) {
                return {
                  value: "ACCUMULATE",
                  rationale: `[T2 Override: Matrix ACCUMULATE (Premium Band 1.0-1.05*IV)] ${rationale}`,
                };
              }
              if (price > iv * 1.05) {
                return {
                  value: "CORE HOLD",
                  rationale: `[T2 Override: Matrix CORE HOLD (Price > 1.05*IV)] ${rationale}`,
                };
              }
            }

            // Map legacy HOLD to CORE HOLD
            const VALID_VERDICTS = new Set([
              "BUY",
              "CORE HOLD",
              "ACCUMULATE",
              "FAT PITCH",
              "PASS",
              "AVOID",
            ]);
            const legacyMap: Record<string, string> = {
              HOLD: "CORE HOLD",
              WATCH: "CORE HOLD", // WATCH = monitoring position, closest to CORE HOLD semantics
              NEUTRAL: "CORE HOLD",
              SELL: "AVOID",
              "STRONG BUY": "BUY",
              UNDERWEIGHT: "PASS",
              OVERWEIGHT: "BUY",
            };
            const remapped = legacyMap[rawValue] || rawValue;
            const finalValue = VALID_VERDICTS.has(remapped) ? remapped : "PASS";

            if (!VALID_VERDICTS.has(remapped)) {
              console.warn(
                `[Verdict] Unrecognized verdict "${rawValue}" — defaulting to PASS.`,
              );
            }

            return {
              value: finalValue as
                | "BUY"
                | "CORE HOLD"
                | "ACCUMULATE"
                | "FAT PITCH"
                | "PASS"
                | "AVOID",
              rationale,
            };
          })();
            const __isSpinoffEntity =
              (deterministicData as any)?.entityType === "Type 5" ||
              (factSheet as any)?.isRecentSpinOff === true;
            if (
              __isSpinoffEntity &&
              (__decision.value === "BUY" || __decision.value === "FAT PITCH")
            ) {
              return {
                value: "ACCUMULATE",
                rationale:
                  "[T2 Override: Spin-Off Data Ceiling — Type 5 verdict capped to ACCUMULATE] " +
                  __decision.rationale,
              };
            }
            return __decision;
          })(),
          oracleIntrinsicValue: (() => {
            // Rule 3a: IV is ALWAYS the deterministic DCF engine output.
            // The Oracle's returned oracleIntrinsicValue is checked only to detect
            // gross violations — it never takes priority over the engine.
            const dcfIV = mathEngine.scenarios.base.intrinsicValuePerShare;
            const oracleIV =
              typeof parsed.oracleFramework?.oracleIntrinsicValue === "number"
                ? parsed.oracleFramework.oracleIntrinsicValue
                : typeof parsed.intrinsicValue === "number"
                  ? parsed.intrinsicValue
                  : 0;

            // Log any Oracle deviation for monitoring — but always return dcfIV.
            if (oracleIV > 0 && dcfIV > 0) {
              const deviation = Math.abs(oracleIV - dcfIV) / dcfIV;
              if (deviation > 0.05) {
                // >5% deviation from engine value
                console.warn(
                  `[IV-LOCK] ${factSheet.ticker}: Oracle returned oracleIntrinsicValue=$${oracleIV.toFixed(2)} ` +
                    `but DCF engine computed $${dcfIV.toFixed(2)} (${(deviation * 100).toFixed(1)}% deviation). ` +
                    `Rule 3a enforced — using DCF engine value. Oracle may have recalculated independently.`,
                );
              }
            }
            if (oracleIV <= 0 && dcfIV <= 0) {
              console.error(
                `[IV-LOCK] ${factSheet.ticker}: Both Oracle and DCF engine returned non-positive IV. ` +
                  `Analysis is unreliable.`,
              );
            }
            // Always return the deterministic DCF engine value.
            return dcfIV > 0 ? dcfIV : oracleIV;
          })(),
        };

        // Ensure fmpDcf is populated with AI values if FMP failed
        const mergedFmpDcf = {
          ...(deterministicData?.fmpDcf || {}),
          dcf:
            (deterministicData?.fmpDcf?.dcf || 0) > 0
              ? deterministicData.fmpDcf.dcf
              : parsed.oracleIntrinsicValue || parsed.intrinsicValue || 0,
          leveredDcf:
            (deterministicData?.fmpDcf?.leveredDcf || 0) > 0
              ? deterministicData.fmpDcf.leveredDcf
              : parsed.oracleIntrinsicValue || parsed.intrinsicValue || 0,
          isSynthetic:
            deterministicData?.fmpDcf?.isSynthetic ||
            (deterministicData?.fmpDcf?.dcf || 0) === 0,
        };

        // ENFORCE Rule 10D: Exact synchronization between Memo Details and Dashboard Framework
        if (oracleDetails) {
          oracleDetails.competence.strength =
            oracleFramework.understandable.value ||
            oracleDetails.competence.strength;
          oracleDetails.management.strength =
            oracleFramework.management.value ||
            oracleDetails.management.strength;
          oracleDetails.predictability.strength =
            oracleFramework.longTerm.value ||
            oracleDetails.predictability.strength;
          oracleDetails.stability.strength =
            oracleFramework.stability.value || oracleDetails.stability.strength;
          oracleDetails.marginOfSafety.strength =
            oracleFramework.marginOfSafety.value ||
            oracleDetails.marginOfSafety.strength;
        }
        if (moatAnalysis) {
          moatAnalysis.strength =
            oracleFramework.moat.value || moatAnalysis.strength;
        }

        // Ensure ownerEarningsData is populated with AI values if FMP failed
        const mergedOwnerEarnings = {
          ...(deterministicData?.ownerEarningsData || {}),
          ownersEarnings:
            (deterministicData?.ownerEarningsData?.ownersEarnings || 0) > 0
              ? deterministicData.ownerEarningsData.ownersEarnings
              : parsed.oracleMetrics?.ownerEarnings || 0,
          ownersEarningsPerShare:
            (deterministicData?.ownerEarningsData?.ownersEarningsPerShare ||
              0) > 0
              ? deterministicData.ownerEarningsData.ownersEarningsPerShare
              : parsed.oracleMetrics?.ownerEarnings /
                  parsed.sharesOutstanding || 0,
          isSynthetic:
            deterministicData?.ownerEarningsData?.isSynthetic ||
            (deterministicData?.ownerEarningsData?.ownersEarningsPerShare ||
              0) === 0,
        };

        // Fallback if FMP fails completely
        const finalAnalysis = {
          ticker: ticker,
          name: ticker, // Fallback to ticker as name
          isProduction: !!institutionalData,
          sources:
            res?.candidates?.[0]?.groundingMetadata?.groundingChunks?.map(
              (chunk: any) => ({
                title: chunk.web?.title || chunk.web?.uri || "Source",
                uri: chunk.web?.uri || "#",
              }),
            ) || [],
          ...deterministicData,
          fmpDcf: mergedFmpDcf,
          ownerEarningsData: mergedOwnerEarnings,
          ...parsed,
          // Re-sanitize scalar narrative fields that MemoTab renders as raw React
          // children. The model occasionally returns these typed-as-string fields
          // as { title, description } objects, which crashes the memo render
          // (React error #31). sanitizeString flattens objects → safe strings;
          // plain strings pass through unchanged. Do NOT add the structured array
          // fields (marketBeliefVsReality, whatWouldChangeOurMind) here.
          killerInsight: sanitizeString(parsed.killerInsight),
          summary: sanitizeString(parsed.summary),
          valuationRationale: sanitizeString(parsed.valuationRationale),
          allocationGuidance: sanitizeString(parsed.allocationGuidance || ''),
          cycleSensitivityPenalty: sanitizeString(parsed.cycleSensitivityPenalty || ''),
          optionalityMultiplier: parsed.optionalityMultiplier
            ? {
                ...parsed.optionalityMultiplier,
                description: sanitizeString(
                  parsed.optionalityMultiplier.description,
                ),
              }
            : parsed.optionalityMultiplier,
          // ─── ACTION SIGNAL VERDICT GATE ──────────────────────────────────────────
          // Positive action signals must never display alongside AVOID or PASS verdicts.
          // A business can have structural tailwinds or elite quality but still be
          // uninvestable at its current price. Oracle C correctly identifies business
          // quality but doesn't always suppress these flags when valuation overrides
          // the verdict. Confirmed cases: AAPL (shouldActDecisively), MU (isGrowthStructurallyInevitable).
          // Enforce all four flags here deterministically.
          shouldActDecisively:
            parsed.shouldActDecisively === true &&
            parsed.oracleFramework?.decision?.value !== "AVOID" &&
            parsed.oracleFramework?.decision?.value !== "PASS"
              ? true
              : false,
          isObviouslyAttractive:
            parsed.isObviouslyAttractive === true &&
            parsed.oracleFramework?.decision?.value !== "AVOID" &&
            parsed.oracleFramework?.decision?.value !== "PASS"
              ? true
              : false,
          isGrowthStructurallyInevitable:
            parsed.isGrowthStructurallyInevitable === true &&
            parsed.oracleFramework?.decision?.value !== "AVOID" &&
            parsed.oracleFramework?.decision?.value !== "PASS"
              ? true
              : false,
          isInevitableCompounderOverride:
            parsed.isInevitableCompounderOverride === true &&
            parsed.oracleFramework?.decision?.value !== "AVOID" &&
            parsed.oracleFramework?.decision?.value !== "PASS"
              ? true
              : false,
          // ─────────────────────────────────────────────────────────────────────────

          ...(() => {
            const bearIrr = mathEngine.assumptionRationales?.bearCaseIRR || "";
            const baseIrr = mathEngine.assumptionRationales?.baseCaseIRR || "";
            const bullIrr = mathEngine.assumptionRationales?.bullCaseIRR || "";

            // Extract just the total IRR figure from the bridge string for display.
            const extractIrrTotal = (bridge: string | number): string => {
              if (typeof bridge === "number") return `${bridge}%`;
              const match = bridge.match(/IRR\s+([-\d.]+)%/i);
              return match ? `${match[1]}%` : bridge;
            };

            const bearGrowthPct = Number(
              mathEngine.assumptionRationales?.bearGrowthPct ?? 0,
            );
            const baseGrowthPct =
              Number(mathEngine.base?.year1to5GrowthRate ?? 0) * 100;
            const bullGrowthPct =
              Number(
                mathEngine.bullOverrides?.year1to5GrowthRate ??
                  mathEngine.base?.year1to5GrowthRate ??
                  0,
              ) * 100;

            let finalValuationScenarios;
            if (!bearIrr && !baseIrr && !bullIrr) {
              console.warn(
                `[SCENARIO-IRR-LOCK] ${symbol}: IRR bridges missing from Mathematician — using Oracle array as fallback.`,
              );
              finalValuationScenarios = parsed.valuationScenarios || [];
            } else {
              console.log(
                `[SCENARIO-IRR-LOCK] ${symbol}: Overriding Oracle valuationScenarios with floor-enforced IRR bridge values.`,
              );
              finalValuationScenarios = [
                {
                  scenario: "Bear",
                  growth: `${bearGrowthPct.toFixed(1)}%`,
                  irr: extractIrrTotal(bearIrr),
                  probability: "Low",
                },
                {
                  scenario: "Base",
                  growth: `${baseGrowthPct.toFixed(1)}%`,
                  irr: extractIrrTotal(baseIrr),
                  probability: "High",
                },
                {
                  scenario: "Bull",
                  growth: `${bullGrowthPct.toFixed(1)}%`,
                  irr: extractIrrTotal(bullIrr),
                  probability: "Medium",
                },
              ];
            }

            // EXPECTED RETURN MATH LOCK
            const oracleMath = parsed.expectedReturnMath || {};
            const impliedGrowthDecimal =
              mathEngine.scenarios.base.impliedMarketGrowth;
            const treasury = deterministicData?.thirtyYearTreasuryYield;

            // [WACC-HURDLE-GAP] OBSERVABILITY — measurement only; no scenario/IV change.
            // The DCF discounts cash flows at the Mathematician's blended WACC, which is
            // leverage-weighted (cheap debt drags it down), but the verdict judges
            // realisticIrr against a FLAT required-IRR hurdle of (30Y Treasury + 5.5% ERP).
            // When WACC < hurdle the IV is inflated relative to the bar the return is
            // measured against — most severe on high-leverage names (e.g. WEN ~8.3% WACC
            // vs ~10.4% hurdle). Baselines frequency/magnitude before any behavioral floor.
            (() => {
              const waccPct = Number(mathEngine.base?.wacc ?? 0) * 100;
              const requiredIrrPct = treasury != null ? treasury + 5.5 : null;
              if (requiredIrrPct != null && waccPct > 0 && waccPct < requiredIrrPct) {
                const gapBps = Math.round((requiredIrrPct - waccPct) * 100);
                console.warn(
                  `[WACC-HURDLE-GAP] ${symbol}: DCF WACC ${waccPct.toFixed(2)}% < ` +
                    `required IRR hurdle ${requiredIrrPct.toFixed(2)}% (gap ${gapBps}bps) — ` +
                    `IV discounted below the return hurdle (measurement only; no scenario change).`,
                );
                if (!mathEngine.assumptionRationales) mathEngine.assumptionRationales = {};
                (mathEngine.assumptionRationales as any).waccHurdleGap = {
                  fired: true,
                  waccPct: Number(waccPct.toFixed(2)),
                  requiredIrrPct: Number(requiredIrrPct.toFixed(2)),
                  gapBps,
                  debtToEbitda: Number((deterministicData?.oracleMetrics?.debtToEbitda ?? 0).toFixed(2)),
                };
              }
            })();
            const engineIV = mathEngine.scenarios.base.intrinsicValuePerShare;
            const currentPrice = deterministicData?.currentPrice || 0;

            const reconciliationSentence = (() => {
              if (deterministicData?.isType5DataContaminated || deterministicData?.isUnresolvableValuation) return ""; // DO NOT inject net-cash floor as IV
              if (engineIV <= 0 || currentPrice <= 0) return "";
              const priceToIV = currentPrice / engineIV;
              if (priceToIV <= 1.2) return "";
              return `IRR figures above are calculated from the current market price of $${currentPrice.toFixed(2)}. At an intrinsic value entry of $${engineIV.toFixed(2)}, the realistic base case IRR would be materially higher. The ${parsed.oracleFramework?.decision?.value || "AVOID"} verdict reflects the gap between entry price and intrinsic value, not the underlying business quality.`;
            })();

            const cleanedOracleRationale = (() => {
              const raw = oracleMath.rationale || "";
              if (!raw) return "";
              return raw
                .replace(/At an intrinsic value entry of \$[\d.]+[^.]*\./g, "")
                .trim();
            })();

            const finalRationale = reconciliationSentence
              ? cleanedOracleRationale
                ? `${cleanedOracleRationale} ${reconciliationSentence}`
                : reconciliationSentence
              : cleanedOracleRationale || oracleMath.rationale || "";

            if (reconciliationSentence) {
              console.log(
                `[IV-LOCK-RATIONALE] ${factSheet?.ticker}: injected engine IV $${engineIV.toFixed(2)} into rationale. Price/IV ratio: ${(currentPrice / engineIV).toFixed(2)}x`,
              );
            }

            // Consistency Check: Realistic IRR vs Base Scenario IRR
            const expectedRealisticIrr = extractIrrTotal(baseIrr);

            // Hard invariant: Ensure realisticIrr === base.impliedIrr (within float parsing limits).
            // Two distinct failure modes require different handling:
            //   A) realisticIrr is absent (undefined/null/"") → field omission or truncation.
            //      Auto-patch from deterministic engine; do NOT throw. Narrative drift is impossible
            //      when Oracle didn't provide a value at all.
            //   B) realisticIrr is present but diverges → genuine narrative drift.
            //      Throw so withRetry can attempt a clean Oracle response.
            if (
              expectedRealisticIrr !== "0%" &&
              expectedRealisticIrr !== "" &&
              expectedRealisticIrr != null &&
              !deterministicData?.isType5DataContaminated &&
              !deterministicData?.isUnresolvableValuation
            ) {
              const parsedExpected = parseFloat(
                expectedRealisticIrr.replace("%", ""),
              );
              const rawOracleIrr = oracleMath.realisticIrr;
              const oracleIrrIsAbsent =
                rawOracleIrr === undefined ||
                rawOracleIrr === null ||
                rawOracleIrr === "" ||
                isNaN(parseFloat(String(rawOracleIrr).replace("%", "")));

              if (oracleIrrIsAbsent) {
                // Field omission — auto-patch from deterministic engine (same value enforced below in return).
                console.warn(
                  `[IRR-PATCH] ${symbol}: Oracle realisticIrr absent (${rawOracleIrr}). ` +
                  `Auto-patching from deterministic engine: ${expectedRealisticIrr}. ` +
                  `Analysis will complete with deterministic IRR — no narrative drift detected.`,
                );
              } else {
                // Oracle provided a specific value — verify it matches the deterministic engine.
                let parsedOracle = parseFloat(
                  (rawOracleIrr || "").replace("%", ""),
                );
                if (isNaN(parsedOracle)) parsedOracle = 0;

                if (Math.abs(parsedExpected - parsedOracle) > 0.1) {
                  console.error(
                    `[IRR-CONSISTENCY-ERROR] Realistic IRR (${parsedOracle}%) deviates materially from Base Scenario IRR (${parsedExpected}%). Throwing error as this suggests narrative drift.`,
                  );
                  throw new Error(
                    `Invariant Failure: The Oracle-authored Realistic IRR (${rawOracleIrr}) diverges from the deterministically computed base-case Scenario IRR (${expectedRealisticIrr}). The Oracle must transcribe this verbatim. Base IRR was overridden to maintain math integrity.`,
                  );
                }
              }
            }

            return {
              valuationScenarios: finalValuationScenarios,
              expectedReturnMath: {
                ...oracleMath,
                rationale: finalRationale,
                impliedGrowth:
                  // [IMPLIED-GROWTH-RI-NA] (Tuning v2.91): when the Type-4 IV was set by the
                  // residual-income override (or the LLM owner-earnings base was degenerate),
                  // the implied-growth solver ran on the DISCARDED/floored DCF base — the
                  // solve is deterministic but meaningless (TRV: interim PVs of $8M "imply"
                  // 204.3% sustained growth against a $340 price). The DCF base is not the
                  // valuation basis, so no market-implied growth is derivable: emit N/A.
                  // The IMPLIED-GROWTH-PROSE-LOCK downstream propagates N/A into the C-3c
                  // prose automatically.
                  (deterministicData as any)?.__type4RiApplied === true ||
                  (deterministicData as any)?.type4OeDegenerateFired === true
                    ? (() => {
                        console.warn(
                          `[IMPLIED-GROWTH-RI-NA] ${symbol}: implied growth solved on the ` +
                            `discarded DCF base (riApplied=${(deterministicData as any)?.__type4RiApplied === true}, ` +
                            `oeDegenerate=${(deterministicData as any)?.type4OeDegenerateFired === true}); ` +
                            `emitting N/A (solver value ${impliedGrowthDecimal} discarded).`,
                        );
                        return "N/A";
                      })()
                    : impliedGrowthDecimal != null && impliedGrowthDecimal > 0
                    ? `implied ~${(impliedGrowthDecimal * 100).toFixed(1)}% sustained growth`
                    : (() => {
                        console.warn(
                          `[IMPLIED-GROWTH-NA] ${symbol}: engine impliedMarketGrowth unavailable (${impliedGrowthDecimal}); emitting N/A (Oracle value discarded).`,
                        );
                        return "N/A";
                      })(),
                requiredIrr:
                  treasury != null
                    ? `${(treasury + 5.5).toFixed(1)}% (${treasury.toFixed(2)}% 30Y Treasury + 5.5% ERP)`
                    : oracleMath.requiredIrr || "N/A",
                realisticIrr: expectedRealisticIrr || oracleMath.realisticIrr, // Enforce exactly
              },
            };
          })(),
          // BUYZONE-SEMANTIC-LOCK: thresholds, labels, AND rationale are all engine-set.
          // The two tiers are pure arithmetic from IV (Core = IV×0.80, Dislocation = IV×0.70),
          // so their meaning is fixed. The Oracle must NOT author labels/rationale here — doing
          // so let it attach inverted semantics to a code-locked discount tier (e.g. labeling the
          // 30%-discount tier a "Fair Value Ceiling / shift to hold", the opposite of its meaning).
          // Company-specific narrative color lives in `buyZoneJustification` (amber box), not here.
          buyZoneFramework: (() => {
            const iv = mathEngine.scenarios.base.intrinsicValuePerShare;
            if (!iv || iv <= 0) return [];
            const core = (iv * 0.8).toFixed(2);
            const dislocation = (iv * 0.7).toFixed(2);
            // Returns 2 zones only. The Dashboard card is rendered separately by MemoTab
            // from dcfModel.buyUnderPrice — including it here would create a duplicate 4th card.
            return [
              {
                trigger: "Core Buy Zone",
                threshold: `< $${core}`,
                rationale:
                  "20% discount to intrinsic value — a standard margin of safety that absorbs ordinary WACC and cyclical risk before initiating a position.",
              },
              {
                trigger: "Aggressive / Dislocation Zone",
                threshold: `< $${dislocation}`,
                rationale:
                  "30% discount to intrinsic value — a deep-dislocation / historical-trough tier for maximum-conviction accumulation when the market overshoots to the downside.",
              },
            ];
          })(),
          whatWouldBreakThisFast: (() => {
            const raw = Array.isArray(parsed.whatWouldBreakThisFast)
              ? parsed.whatWouldBreakThisFast
              : [];
            if (raw.length > 2) {
              console.warn(
                `[Rule 99 Violation] whatWouldBreakThisFast returned ${raw.length} items. ` +
                  `Maximum is 2. Trimming to first 2. Review prompt output for: ${ticker}`,
              );
              return raw.slice(0, 2);
            }
            return raw;
          })(),
          growthData: deterministicData?.growthData ||
            parsed.growthData || { revenueGrowth: 0, netIncomeGrowth: 0 },
          oracleFramework,
          // Deterministic classifier wins for Type 5, 4, 3.
          // Oracle C frequently returns "Standard" for these entities because the prompt
          // context doesn't always convey the data sparsity — the classifier does.
          // For all other entity types, Oracle C's output is preferred.
          entityType:
            deterministicData?.entityType === "Type 5"
              ? "Spin-off"
              : deterministicData?.entityType === "Type 4"
                ? "Financial"
                : deterministicData?.entityType === "Type 3"
                  ? "REIT"
                  : sanitizeString(parsed.entityType || "Standard"),
          description: sanitizeString(parsed.description),
          // [TYPE5-CONTAM-VALINSIGHTS] (Tuning 10.x) Same gate that nulls the IV box for a
          // contaminated Type-5: there is no earnings-based IV, so the LLM's FMP-vs-ours comparison
          // (RNA: "$15.56 primary IV anchor / 172.4% divergence") must not render. Replace with a
          // deterministic caveat. Untouched when not contaminated (SUNB keeps its real insights).
          valuationInsights: deterministicData?.isType5DataContaminated
            ? "No earnings-based intrinsic value is available: this recent spin-off lacks sufficient standalone operating history (financial databases backfill the former parent's results onto this ticker). Any net-cash-per-share figure is a balance-sheet floor, not an intrinsic value — a reliable DCF requires 2–3 years of standalone audited financials."
            : deterministicData?.isUnresolvableValuation
            ? "No reliable intrinsic value is available: structural data anomalies or missing valuation inputs prevent a reliable DCF or P/TBV terminal calculation. Abstaining from a price-based verdict."
            : sanitizeString(parsed.valuationInsights),
          buyZoneJustification: sanitizeString(
            parsed.buyZoneJustification || "",
          ),
          cyclePosition: (parsed.cyclePosition || "Mid-Cycle") as any,
          archetype: (() => {
            const rawCategory =
              typeof parsed.archetype === "string"
                ? parsed.archetype
                : parsed.archetype?.category || "COMPOUNDER";
            const rawRationale = sanitizeString(
              parsed.archetype?.rationale ||
                "Archetype classification based on growth and ROIC profile.",
            );
            // [ARCHETYPE-CONTAM-OVERRIDE] (Tuning 10.x) A quality archetype (Compounder /
            // Dominant Leader) asserts positive returns on capital. For a Type-5 spin-off whose
            // standalone ROIC is negative, that label is parent-backfilled history leaking through
            // (RNA: "Compounder" while TTM ROIC −23.9%). The engine's Type-5 ROIC is already
            // TTM-sourced, so a negative value is the clean standalone signal. Override to
            // "Speculative Growth". Gated on entityType === "Type 5" so positive-ROIC Type-5s
            // (SUNB) and cyclical troughs (non-Type-5, e.g. MU) are untouched. Runs after the DCF
            // + verdict → DISPLAY-ONLY, cannot change IV/scenarios/verdict.
            const roic = deterministicData?.oracleMetrics?.roic;
            const isQualityArchetype = /compounder|dominant leader/i.test(
              rawCategory,
            );
            if (
              deterministicData?.entityType === "Type 5" &&
              isQualityArchetype &&
              typeof roic === "number" &&
              roic < 0
            ) {
              console.warn(
                `[ARCHETYPE-CONTAM-OVERRIDE] ${symbol}: "${rawCategory}" with negative standalone ROIC ${roic.toFixed(2)}% → "Speculative Growth".`,
              );
              return {
                category: "Speculative Growth",
                rationale:
                  "Overridden to Speculative Growth: this Type-5 spin-off's standalone ROIC is negative, so a quality (Compounder / Dominant Leader) archetype — read from parent-backfilled or pre-revenue history — is not supported by the clean financials.",
              };
            }
            // [ARCHETYPE-DECLINE-OVERRIDE] (Tuning §14) Companion to the ROIC<0 branch for the OTHER
            // way a Type-5 spin-off inherits a false quality label: a structurally-declining legacy
            // business read as "Compounder" off the former parent's pre-separation growth history.
            // VSNT (Versant / Comcast spin-off) — linear-TV cord-cutting + the 2028 carriage cliff,
            // verdict PASS — but ROIC is POSITIVE (+8.8%), so the ROIC<0 test above cannot catch it.
            // A shrinking business is a value trap, not a compounder → "Value Trap Risk". Gated on
            // entityType === "Type 5" + the engine/Historian structural-decline flag (same path the
            // bear-floor uses), so only a recent spin-off confirmed in secular decline is touched.
            // Display-only (runs after the DCF + verdict).
            const isStructuralDecline =
              deterministicData?.structuralDeclineAssessment?.isStructuralDecline === true ||
              factSheet?.structuralDeclineAssessment?.isStructuralDecline === true;
            if (
              deterministicData?.entityType === "Type 5" &&
              isQualityArchetype &&
              isStructuralDecline
            ) {
              console.warn(
                `[ARCHETYPE-DECLINE-OVERRIDE] ${symbol}: "${rawCategory}" on a structurally-declining Type-5 spin-off → "Value Trap Risk".`,
              );
              return {
                category: "Value Trap Risk",
                rationale:
                  "Overridden to Value Trap Risk: this Type-5 spin-off is in confirmed structural decline, so a quality (Compounder / Dominant Leader) archetype — read from the former parent's pre-separation growth history — is not supported. A shrinking business bought cheap is still a shrinking business.",
              };
            }
            return { category: rawCategory, rationale: rawRationale };
          })(),
          reinvestmentRunway: parsed.reinvestmentRunway || {
            score: "MEDIUM",
            tamVsRevenue: "Unknown",
            incrementalRoicTrend: "stable",
            acquisitionDependence: "medium",
            rationale: "Default generated due to missing AI output.",
          },
          confidenceScore: (() => {
            let score =
              typeof parsed.confidenceScore === "number"
                ? parsed.confidenceScore
                : Number(parsed.confidenceScore?.score) || 50;
            const cyclePos = parsed.cyclePosition || "Mid-Cycle";
            const archetype =
              typeof parsed.archetype === "string"
                ? parsed.archetype
                : parsed.archetype?.category || "";
            const summaryLower = (parsed.summary || "").toLowerCase();
            const rationaleLower = (
              parsed.oracleFramework?.decision?.rationale || ""
            ).toLowerCase();
            const judgmentLower = sanitizeString(
              parsed.oracleJudgment || "",
            ).toLowerCase();
            const combinedForNegEq = `${summaryLower} ${rationaleLower} ${judgmentLower}`;

            const isCyclical =
              cyclePos === "Peak" ||
              cyclePos === "Trough" ||
              archetype.includes("TURNAROUND");
            const isNegativeEquityA =
              combinedForNegEq.includes("category a") ||
              combinedForNegEq.includes("buyback-driven") ||
              combinedForNegEq.includes("primarily by capital returns");
            const isNegativeEquityC =
              combinedForNegEq.includes("category c") ||
              combinedForNegEq.includes("mixed / ambiguous") ||
              combinedForNegEq.includes("mixed analysis");

            // Rules from CONF-1:
            if (isCyclical) score = Math.min(score, 70);
            if (
              summaryLower.includes("customer concentration") &&
              summaryLower.includes(">30%")
            )
              score = Math.min(score, 75);
            if (
              summaryLower.includes("geopolitical risk") ||
              summaryLower.includes("regulatory risk")
            )
              score = Math.min(score, 75);
            if (summaryLower.includes("secular decline"))
              score = Math.min(score, 60);
            if (
              factSheet?.structuralDeclineAssessment?.isStructuralDecline ===
              true
            )
              score = Math.min(score, 55);
            if ((factSheet as any).earlyDeclineWatch?.fired === true)
              score = Math.min(score, 70);
            if (summaryLower.includes("ios penalty"))
              score = Math.min(score, 70);
            if (isNegativeEquityA) score = Math.min(score, 80);
            if (isNegativeEquityC) score = Math.min(score, 65);

            // New Pass Penalties:
            if (entityType === "Type 5") score -= 10;
            // Spin-off data fragility ceiling (also key on resolved entityType so registry-forced
            // Type 5 spin-offs — where the Historian never sets isRecentSpinOff — still get capped).
            if (factSheet?.isRecentSpinOff === true || entityType === "Type 5") {
              score = Math.min(score, 65);
            }
            if (
              judgmentLower.includes("dual-class") ||
              judgmentLower.includes("supervoting") ||
              summaryLower.includes("dual-class") ||
              summaryLower.includes("supervoting")
            )
              score -= 5;
            if (
              combinedForNegEq.includes("transition override") ||
              judgmentLower.includes("transition override")
            )
              score -= 10;
            if (judgmentLower.includes("currency verification incomplete"))
              score -= 15;

            const finalScore = Math.max(0, Math.min(100, score));
            const label =
              parsed.confidenceScore?.label ||
              (finalScore >= 80
                ? "Exceptional"
                : finalScore >= 65
                  ? "High"
                  : finalScore >= 50
                    ? "Adequate"
                    : "Low");

            return {
              score: finalScore,
              label: label,
              rationale: sanitizeString(
                parsed.confidenceScore?.rationale ||
                  "Confidence level based on data source reliability and business predictability.",
              ),
            };
          })(),
          assumptionQuality: (() => {
            let aqScore = 100;
            const aqFlags: string[] = [];

            // Signal 1 — Synthetic owner earnings (OE had to be reconstructed)
            if (deterministicData?.ownerEarningsData?.isSynthetic === true) {
              aqScore -= 20;
              aqFlags.push(
                "Owner earnings are synthetic (reconstructed, not directly observed).",
              );
            }

            // Signal 2 — Wide maintenance CapEx band (high assumption sensitivity)
            const oeHigh =
              deterministicData?.ownerEarningsData?.ownersEarningsHigh || 0;
            const oeLow =
              deterministicData?.ownerEarningsData?.ownersEarningsLow || 0;
            if (oeLow > 0 && oeHigh > 0) {
              const bandSpreadPct = (oeHigh - oeLow) / oeLow;
              if (bandSpreadPct > 0.4) {
                aqScore -= 20;
                aqFlags.push(
                  `CapEx band spread is ${(bandSpreadPct * 100).toFixed(0)}% — maintenance CapEx assumption has material IV impact.`,
                );
              } else if (bandSpreadPct > 0.25) {
                aqScore -= 10;
                aqFlags.push(
                  `CapEx band spread is ${(bandSpreadPct * 100).toFixed(0)}% — moderate assumption sensitivity.`,
                );
              }
            }

            // Signal 3 — Recent spin-off (pro-forma history). Key on resolved entityType too so
            // registry-forced Type 5 names (Historian never sets isRecentSpinOff) get the penalty.
            if (factSheet?.isRecentSpinOff === true || (deterministicData as any)?.entityType === "Type 5") {
              aqScore -= 15;
              aqFlags.push(
                "Pro-forma reconstructed history. Standalone operating track record is limited.",
              );
            }

            // Signal 4 — Type 4 financial entity (OE excluded by design; IV rests on TBV and ROTCE)
            if (deterministicData?.isType4Financial === true) {
              aqScore -= 10;
              aqFlags.push(
                `Type 4 (${deterministicData?.type4SubType || "FINANCIAL_OTHER"}): IV anchored to TBV and ROTCE assumptions rather than observed owner earnings.`,
              );
            }

            // Signal 5 — avgRoic5Yr unavailable (ROTCE anchor is missing)
            if (deterministicData?.historicalMetrics?.avgRoic5Yr == null) {
              aqScore -= 15;
              aqFlags.push(
                "5-year ROIC average unavailable — ROTCE anchor is estimated rather than computed.",
              );
            }

            // Signal 6 — Insufficient FCF history
            if (factSheet?.hasHistoricalFcfGrowth === false) {
              aqScore -= 10;
              aqFlags.push(
                "Insufficient FCF growth history — growth assumption is forward-looking without historical anchor.",
              );
            }

            const finalAqScore = Math.max(0, Math.min(100, aqScore));
            const label =
              finalAqScore >= 80
                ? "High"
                : finalAqScore >= 55
                  ? "Moderate"
                  : "Low";
            const rationale =
              aqFlags.length > 0
                ? `[ASSUMPTION QUALITY: ${label}] ` + aqFlags.join(" ")
                : `[ASSUMPTION QUALITY: ${label}] All primary IV inputs are deterministically computed with no material reconstruction or estimation gaps.`;

            return { score: finalAqScore, label, rationale };
          })(),
          whyNotPerfect: sanitizeString(parsed.whyNotPerfect),
          lollapaloozaEffects: sanitizeString(parsed.lollapaloozaEffects),
          longTermOutlook: sanitizeString(parsed.longTermOutlook),
          tenYearHistoricalContext: sanitizeString(
            parsed.tenYearHistoricalContext,
          ),
          historicalFailuresContext: sanitizeString(
            parsed.historicalFailuresContext,
          ),
          technicalDifferentiation: sanitizeString(
            parsed.technicalDifferentiation,
          ),
          throughCycleAssumptions: sanitizeString(
            parsed.throughCycleAssumptions,
          ),
          normalizedEarnings: sanitizeString(parsed.normalizedEarnings),
          oracleDetails,
          moatAnalysis,
          keyRisks,
          riskModeling,
          keyDrivers,
          capitalAllocationDecision: (() => {
            const decisionValue = oracleFramework.decision.value;
            if (decisionValue === "PASS" || decisionValue === "AVOID")
              return decisionValue;
            if (decisionValue === "CORE HOLD")
              return "Existing holders: maintain current position. New capital deployment: zero. Re-evaluate when price enters Buy Zone.";
            return sanitizeString(parsed.capitalAllocationDecision);
          })(),
          // DCF engine scenarios — sourced directly from mathEngine, NOT from Oracle JSON.
          // This guarantees analysis.scenarios.base contains the full DCFValuation object
          // including presentValues[], sumOfPVs, pvOfTerminalValue, and totalEquityValue
          // that match the IV exactly. The Oracle JSON round-trip may drop these fields.
          scenarios: {
            base: mathEngine.scenarios.base,
            bear: mathEngine.scenarios.bear,
            bull: mathEngine.scenarios.bull,
          },
          sensitivities: mathEngine.sensitivities,
          dcfAssumptions: {
            wacc: mathEngine.base.wacc,
            terminalGrowthRate: mathEngine.base.terminalGrowthRate,
            terminalMultiple: mathEngine.base.terminalMultiple,
            year1to5GrowthRate: mathEngine.base.year1to5GrowthRate,
            year6to10GrowthRate: mathEngine.base.year6to10GrowthRate,
          },
        } as Partial<CompanyAnalysis>;

        // VERDICT-CONSISTENCY-LOCK: Detect and correct Oracle verdict contradictions.
        // The Oracle must never output a bullish decision.value with a PASS/AVOID capitalAllocationDecision.
        // If it does (prompt compliance failure), override decision.value to match the capital allocation verdict.
        {
          const rawCapAlloc = sanitizeString(
            parsed.capitalAllocationDecision ?? "",
          );
          const currentDecision = oracleFramework.decision.value;
          const bullishVerdicts = new Set(["BUY", "ACCUMULATE", "FAT PITCH"]);
          const overrideVerdicts = new Set(["PASS", "AVOID"]);
          if (
            overrideVerdicts.has(rawCapAlloc) &&
            bullishVerdicts.has(currentDecision)
          ) {
            console.warn(
              `[VERDICT-CONSISTENCY-LOCK] ${factSheet?.ticker || "UNKNOWN"}: Oracle returned decision.value="${currentDecision}" but capitalAllocationDecision="${rawCapAlloc}". These are contradictory. Overriding decision.value to "${rawCapAlloc}" per Rule 9-OVERRIDE. Prompt compliance failure — review Oracle output.`,
            );
            oracleFramework.decision = {
              ...oracleFramework.decision,
              value: rawCapAlloc as "PASS" | "AVOID",
              rationale:
                oracleFramework.decision.rationale +
                ` [VERDICT-CONSISTENCY-LOCK: decision.value overridden from "${currentDecision}" to "${rawCapAlloc}" — capitalAllocationDecision mandated the override verdict.]`,
            };
          }
        }

        const aiLoggedFields: string[] = [];
        const oracleMetricsProvenance: Record<
          string,
          "fmp" | "ai" | "unavailable"
        > = {};
        const headlineProvenance: Record<string, "fmp" | "ai" | "unavailable"> =
          {};

        const resolveValue = (
          field: string,
          det: number | null | undefined,
          ai: number | null | undefined,
          provenanceMap: Record<string, "fmp" | "ai" | "unavailable">,
        ) => {
          let val = 0;
          if (det && det !== 0) {
            val = det;
            provenanceMap[field] = "fmp";
          } else if (ai && ai !== 0) {
            val = ai;
            provenanceMap[field] = "ai";
            aiLoggedFields.push(field);
            console.warn(
              `[METRIC-PROVENANCE] ${factSheet?.ticker || "UNKNOWN"}: ${field} sourced from model (FMP gap)`,
            );
          } else {
            provenanceMap[field] = "unavailable";
          }
          return val;
        };

        // Ensure we don't overwrite valid deterministic data with 0s from AI
        finalAnalysis.currentPrice = resolveValue(
          "currentPrice",
          deterministicData?.currentPrice,
          parsed.currentPrice,
          headlineProvenance,
        );
        finalAnalysis.marketCap = resolveValue(
          "marketCap",
          deterministicData?.marketCap,
          parsed.marketCap,
          headlineProvenance,
        );
        finalAnalysis.sharesOutstanding = resolveValue(
          "sharesOutstanding",
          deterministicData?.sharesOutstanding,
          parsed.sharesOutstanding,
          headlineProvenance,
        );
        finalAnalysis.netDebt = resolveValue(
          "netDebt",
          deterministicData?.netDebt,
          parsed.netDebt,
          headlineProvenance,
        );
        finalAnalysis.lastYearRevenue = resolveValue(
          "lastYearRevenue",
          deterministicData?.lastYearRevenue,
          parsed.lastYearRevenue,
          headlineProvenance,
        );
        finalAnalysis.lastYearFcf = resolveValue(
          "lastYearFcf",
          deterministicData?.lastYearFcf,
          parsed.lastYearFcf,
          headlineProvenance,
        );
        finalAnalysis.dividend = resolveValue(
          "dividend",
          deterministicData?.dividend,
          parsed.dividend,
          headlineProvenance,
        );

        finalAnalysis.aftermarketPrice =
          deterministicData?.aftermarketPrice || parsed.aftermarketPrice || 0;
        finalAnalysis.aftermarketChange =
          deterministicData?.aftermarketChange || parsed.aftermarketChange || 0;
        finalAnalysis.pricePerformance =
          deterministicData?.pricePerformance ||
          parsed.pricePerformance ||
          null;
        finalAnalysis.spyPerformance =
          deterministicData?.spyPerformance || parsed.spyPerformance || null;
        finalAnalysis.buybacks = deterministicData?.buybacks || 0;
        finalAnalysis.avgBuybackYield = deterministicData?.avgBuybackYield || 0;

        finalAnalysis.oracleMetrics = {
          roe: resolveValue(
            "roe",
            deterministicData?.oracleMetrics?.roe,
            parsed.oracleMetrics?.roe,
            oracleMetricsProvenance,
          ),
          fcf: resolveValue(
            "fcf",
            deterministicData?.oracleMetrics?.fcf,
            parsed.oracleMetrics?.fcf,
            oracleMetricsProvenance,
          ),
          fcfPerShare: resolveValue(
            "fcfPerShare",
            deterministicData?.oracleMetrics?.fcfPerShare,
            parsed.oracleMetrics?.fcfPerShare,
            oracleMetricsProvenance,
          ),
          eps: resolveValue(
            "eps",
            deterministicData?.oracleMetrics?.eps,
            parsed.oracleMetrics?.eps,
            oracleMetricsProvenance,
          ),
          ownerEarnings: resolveValue(
            "ownerEarnings",
            deterministicData?.oracleMetrics?.ownerEarnings,
            parsed.oracleMetrics?.ownerEarnings,
            oracleMetricsProvenance,
          ),
          debtToEquity: resolveValue(
            "debtToEquity",
            deterministicData?.oracleMetrics?.debtToEquity,
            parsed.oracleMetrics?.debtToEquity,
            oracleMetricsProvenance,
          ),
          peRatio: resolveValue(
            "peRatio",
            deterministicData?.oracleMetrics?.peRatio,
            parsed.oracleMetrics?.peRatio,
            oracleMetricsProvenance,
          ),
          pegRatio: resolveValue(
            "pegRatio",
            deterministicData?.oracleMetrics?.pegRatio,
            parsed.oracleMetrics?.pegRatio,
            oracleMetricsProvenance,
          ),
          pbRatio: resolveValue(
            "pbRatio",
            deterministicData?.oracleMetrics?.pbRatio,
            parsed.oracleMetrics?.pbRatio,
            oracleMetricsProvenance,
          ),
          debtToEbitda: resolveValue(
            "debtToEbitda",
            deterministicData?.oracleMetrics?.debtToEbitda,
            parsed.oracleMetrics?.debtToEbitda,
            oracleMetricsProvenance,
          ),
          roic: resolveValue(
            "roic",
            deterministicData?.oracleMetrics?.roic,
            parsed.oracleMetrics?.roic,
            oracleMetricsProvenance,
          ),
          epsForward: resolveValue(
            "epsForward",
            deterministicData?.oracleMetrics?.epsForward,
            parsed.oracleMetrics?.epsForward,
            oracleMetricsProvenance,
          ),
          peForward: resolveValue(
            "peForward",
            deterministicData?.oracleMetrics?.peForward,
            parsed.oracleMetrics?.peForward,
            oracleMetricsProvenance,
          ),
          fcfYield: resolveValue(
            "fcfYield",
            deterministicData?.oracleMetrics?.fcfYield,
            parsed.oracleMetrics?.fcfYield,
            oracleMetricsProvenance,
          ),
          forwardFcfYield: resolveValue(
            "forwardFcfYield",
            deterministicData?.oracleMetrics?.forwardFcfYield,
            parsed.oracleMetrics?.forwardFcfYield,
            oracleMetricsProvenance,
          ),
        };

        finalAnalysis.headlineProvenance = headlineProvenance;
        finalAnalysis.oracleMetricsProvenance = oracleMetricsProvenance;

        // ─── [AVOID-PASS-COHERENCE] ──────────────────────────────────────────────
        let avoidPassCoherenceFired = false;
        let priceToIvRatioAtAvoid: number | null = null;
        // (Tuning v2.93, measure-first) universal price/IV at verdict time — quantifies
        // BUY/FAT PITCH issued while price sits above the client buy-zone trigger
        // (NBIX: BUY at 24.8% MOS vs the displayed 35% primary entry trigger) WITHOUT
        // replicating the client-side effectiveMarginOfSafety derivation server-side
        // (user MOS slider + Buffett-indicator macro adjustment + scorecard quality
        // reduction live in useDcfModel — duplicating them is the F-1 two-engine
        // anti-pattern). Pure telemetry; verdict and display unchanged.
        let priceToIvRatioUniversal: number | null = null;
        let avoidPassOverrideApplied = false;
        let passQualityCoherenceFired = false;
        
        try {
          const decision = finalAnalysis.oracleFramework?.decision;
          const currentPrice = typeof finalAnalysis.currentPrice === "number" ? finalAnalysis.currentPrice : 0;
          const baseIV = mathEngine?.scenarios?.base?.intrinsicValuePerShare ?? 0;
          // isEstimable is not natively on finalAnalysis in the backend, but we approximate it 
          // (positive IV and not contaminated) or read it if passed.
          const isEstimable = (finalAnalysis as any).isEstimable ?? (!finalAnalysis.isType5DataContaminated && !deterministicData?.isUnresolvableValuation && baseIV > 0);
          
          const isStructuralDecline = (finalAnalysis as any).structuralDeclineAssessment?.isStructuralDecline === true || 
                                      deterministicData?.structuralDeclineAssessment?.isStructuralDecline === true;
          const roic = finalAnalysis.oracleMetrics?.roic ?? deterministicData?.oracleMetrics?.roic ?? 0;

          if (baseIV > 0 && currentPrice > 0) {
            priceToIvRatioUniversal = parseFloat((currentPrice / baseIV).toFixed(4));
          }

          const onlyDefectIsPrice =
               decision?.value === 'AVOID'
            && currentPrice > baseIV
            && isEstimable === true
            && !isStructuralDecline
            && roic > 0;

          if (onlyDefectIsPrice) {
             const priceToIvRatio = currentPrice / baseIV;
             console.log(`[AVOID-PASS-COHERENCE] ${symbol}: decision=AVOID but quality-positive & only defect is price>IV (price/IV=${priceToIvRatio.toFixed(2)}x) — candidate PASS-vs-AVOID miscalibration (measurement only; verdict unchanged)`);
             avoidPassCoherenceFired = true; 
             priceToIvRatioAtAvoid = parseFloat(priceToIvRatio.toFixed(2));

             if (!finalAnalysis.isType5DataContaminated && !deterministicData?.isUnresolvableValuation) {
               console.log(`[AVOID-PASS-OVERRIDE] ${symbol}: quality-positive, sole defect price>IV (${priceToIvRatio.toFixed(2)}x) — AVOID rewritten to PASS per ratified verdict semantics (AVOID = quality defect; PASS = good business, wrong price). (Rule 9-OVERRIDE family)`);
               if (decision) decision.value = "PASS";
               finalAnalysis.capitalAllocationDecision = "PASS";
               avoidPassOverrideApplied = true;
             }
          }

          if (decision?.value === 'PASS' && (isStructuralDecline || roic <= 0)) {
            if (!finalAnalysis.isType5DataContaminated && !deterministicData?.isUnresolvableValuation) {
               console.log(`[PASS-QUALITY-COHERENCE] ${symbol}: decision=PASS but quality-negative — candidate AVOID-vs-PASS inverse miscalibration (measurement only; verdict unchanged)`);
               passQualityCoherenceFired = true;
            }
          }
        } catch (e) {
          console.error(`[AVOID-PASS-COHERENCE] Failed:`, e);
        }

        // ─── [BUYZONE-CANONICAL] (Tuning v2.93 §61.11, ratified R-3 Stage 1) ─────
        // SINGLE SOURCE OF TRUTH for the primary entry trigger. Previously
        // buyUnderPrice was derived CLIENT-side in useDcfModel from view-time state
        // (MOS slider base 25 + Buffett-indicator macro adjustment + scorecard
        // quality reduction) while the server matrix issued verdicts blind to the
        // trigger — NBIX shipped a BUY at $179.01 above its own displayed $154.82
        // trigger. Ratified: the server stamps the canonical zone at ANALYSIS TIME
        // (snapshot-consistent, like the price and the F2 fix); the client renders
        // the stamp, demotes its local formula to legacy-fallback + divergence
        // validator, and the MOS slider stays a what-if overlay
        // (userScenarioBuyUnder) that never mutates the Truth. The formula below is
        // the client formula MOVED, not duplicated. Verdict-matrix consultation of
        // the trigger is Stage 2 — verdict-moving, backtest-gated, NOT shipped.
        let buyZoneCanonicalStamped = false;
        let buyZoneEffectiveMosPct: number | null = null;
        let buyZoneVerdictAboveTrigger: boolean | null = null;
        try {
          const __bzBaseIV = mathEngine?.scenarios?.base?.intrinsicValuePerShare ?? 0;
          const __bzEstimable =
            __bzBaseIV > 0 &&
            !finalAnalysis.isType5DataContaminated &&
            !deterministicData?.isUnresolvableValuation;
          if (__bzEstimable) {
            // Buffett indicator via the server's own cached endpoint (internal
            // fetch, same localhost pattern as the treasury call at init-fetch;
            // cache-hit in the common case — zero marginal FMP load). Failure
            // degrades to macro=0 and is stamped for audit.
            let __buffettValue: number | null = null;
            try {
              let __bzUrl = "/api/buffett-indicator";
              if (typeof window === "undefined") {
                __bzUrl = `http://127.0.0.1:3000${__bzUrl}`;
              }
              const __bzHeaders = await getProxyAuthHeaders();
              const __bzCtl = new AbortController();
              const __bzTimer = setTimeout(() => __bzCtl.abort(), 3000);
              const __bzRes = await fetch(__bzUrl, {
                headers: __bzHeaders,
                signal: __bzCtl.signal,
              });
              clearTimeout(__bzTimer);
              if (__bzRes.ok) {
                const __bzJson: any = await __bzRes.json();
                const __bzVal = Number(__bzJson?.buffettIndicator?.value);
                if (Number.isFinite(__bzVal) && __bzVal > 0) __buffettValue = __bzVal;
              }
            } catch {
              /* macro degrades to 0; buffettUnavailable stamped below */
            }

            // Client formula, moved verbatim (useDcfModel semantics):
            //   quality: (moat>=5 && pred>=5) → 25 | (>=4/>=4 && stab>=4) → 15
            //            | (>=4/>=4) → 10 | else 0
            //   macro:   buffett > 150 → +10
            //   effective = max(5, 25 + macro − quality); buyUnder = IV × (1 − eff/100)
            const __bzMap = (key: string, value: any): number => {
              if (typeof value === "number") return value;
              const m: Record<string, Record<string, number>> = {
                moat: { Wide: 5, Narrow: 3, None: 1, Eroding: 2 },
                longTerm: { High: 5, Medium: 3, Low: 2, None: 1 },
                stability: {
                  "Highly Stable": 5,
                  Stable: 4,
                  Moderate: 3,
                  "High-Margin Compounder": 3,
                  Cyclical: 2,
                  Volatile: 1,
                  Low: 5,
                  High: 1,
                },
              };
              return m[key]?.[value] || 3;
            };
            const __bzMoat = __bzMap("moat", (finalAnalysis as any)?.scorecard?.moat);
            const __bzPred = __bzMap("longTerm", (finalAnalysis as any)?.scorecard?.longTerm);
            const __bzStab = __bzMap("stability", (finalAnalysis as any)?.scorecard?.stability);
            const __bzQualityReduction =
              __bzMoat >= 5 && __bzPred >= 5
                ? 25
                : __bzMoat >= 4 && __bzPred >= 4 && __bzStab >= 4
                  ? 15
                  : __bzMoat >= 4 && __bzPred >= 4
                    ? 10
                    : 0;
            const __bzMacro = (__buffettValue ?? 100) > 150 ? 10 : 0;
            const __bzBaseMos = 25;
            const __bzEffectiveMos = Math.max(5, __bzBaseMos + __bzMacro - __bzQualityReduction);
            const __bzBuyUnder = Math.max(0, __bzBaseIV * (1 - __bzEffectiveMos / 100));

            (finalAnalysis as any).canonicalBuyZone = {
              buyUnderPrice: parseFloat(__bzBuyUnder.toFixed(2)),
              effectiveMosPct: __bzEffectiveMos,
              baseMosPct: __bzBaseMos,
              macroAdjustmentPct: __bzMacro,
              qualityMosReductionPct: __bzQualityReduction,
              buffettIndicatorValue: __buffettValue,
              buffettUnavailable: __buffettValue == null,
              computedAt: new Date().toISOString(),
              source: "server-canonical",
            };
            buyZoneCanonicalStamped = true;
            buyZoneEffectiveMosPct = __bzEffectiveMos;
            console.log(
              `[BUYZONE-CANONICAL] ${symbol}: buyUnder=$${__bzBuyUnder.toFixed(2)} ` +
                `(IV=$${__bzBaseIV.toFixed(2)}, effMOS=${__bzEffectiveMos}% = base ${__bzBaseMos} ` +
                `+ macro ${__bzMacro} − quality ${__bzQualityReduction}; buffett=${__buffettValue != null ? __buffettValue.toFixed(1) + "%" : "unavailable→macro 0"})`,
            );

            // Exact, server-side completion of the finding-5 check: bullish verdict
            // issued while price sits above the canonical trigger. Measurement only;
            // verdict unchanged (Stage 2 is backtest-gated).
            const __bzVerdict = String(
              finalAnalysis.oracleFramework?.decision?.value || "",
            ).toUpperCase();
            const __bzPrice =
              typeof finalAnalysis.currentPrice === "number" ? finalAnalysis.currentPrice : 0;
            if ((__bzVerdict === "BUY" || __bzVerdict === "FAT PITCH") && __bzPrice > 0) {
              buyZoneVerdictAboveTrigger = __bzPrice > __bzBuyUnder;
              if (buyZoneVerdictAboveTrigger) {
                console.warn(
                  `[BUYZONE-VERDICT-ABOVE-TRIGGER] ${symbol}: verdict=${__bzVerdict} at ` +
                    `$${__bzPrice.toFixed(2)} > canonical trigger $${__bzBuyUnder.toFixed(2)} ` +
                    `(effMOS ${__bzEffectiveMos}%) — measurement only; verdict unchanged.`,
                );
              }
            }
          }
        } catch (e) {
          console.error(`[BUYZONE-CANONICAL] ${symbol}: failed (non-fatal)`, e);
        }

        // ─── [ARCHETYPE-ROIC-HURDLE-GAP] ─────────────────────────────────────────
        let archetypeRoicHurdleGapFired = false;
        let archetypeRoicWaccGapBps: number | null = null;
        try {
          if (deterministicData?.isType5DataContaminated || deterministicData?.isUnresolvableValuation) {
            console.log(`[ARCHETYPE-ROIC-HURDLE-GAP] skipped: contaminated/unresolvable — ROIC input not standalone. (measurement hygiene)`);
          } else {
            const finalArch = (((finalAnalysis as any).archetype?.category
                ?? (finalAnalysis as any).factSheet?.archetype?.category) || "").toString();
            const isQualityArch = /compounder|dominant leader/i.test(finalArch);
            // ROIC and WACC must be compared in the SAME units. The existing
            // [ARCHETYPE-CONTAM-OVERRIDE] branch treats oracleMetrics.roic as a PERCENT
            // (it prints `${roic.toFixed(2)}%`), so read both as percent here:
            const roicPct = finalAnalysis.oracleMetrics?.roic
                ?? deterministicData?.oracleMetrics?.roic ?? 0;
            const waccPctForArch = Number(mathEngine?.base?.wacc ?? 0) * 100;
            if (
              deterministicData?.entityType === "Type 5" &&
              isQualityArch &&
              roicPct >= 0 &&
              waccPctForArch > 0 &&
              roicPct < waccPctForArch
            ) {
              archetypeRoicWaccGapBps = Math.round((waccPctForArch - roicPct) * 100);
              archetypeRoicHurdleGapFired = true;
              console.log(`[ARCHETYPE-ROIC-HURDLE-GAP] ${symbol}: archetype="${finalArch}" roic=${roicPct.toFixed(2)}% wacc=${waccPctForArch.toFixed(2)}% gap=${archetypeRoicWaccGapBps}bps — quality label on a sub-hurdle Type-5; survived both override branches (measurement only; label unchanged)`);
            }
          }
        } catch (e) {
          console.error(`[ARCHETYPE-ROIC-HURDLE-GAP] Failed:`, e);
        }

        // [OBSERVABILITY-SURFACE] Expose the two gap markers (Tuning 5.15) so the
        // analysis_completed PostHog enrichment can read them. Both already live on
        // mathEngine.assumptionRationales by this point (bearDeclineGap written in
        // runMathematician via the {...parsed} spread; waccHurdleGap written in the
        // Oracle stage). bearDeclineGap is always present; waccHurdleGap only when fired.
        (finalAnalysis as any).observability = {
          executiveSummaryBackfilled: false,
          isUnresolvableValuation: deterministicData?.isUnresolvableValuation ?? false,
          // [TOKEN-LEDGER] Per-agent token usage + the actual modelVersion strings
          // Google returned this run. Measurement only — read by the
          // analysis_completed PostHog enrichment and the verdict log.
          tokens: ledger.tokens,
          modelVersions: ledger.modelVersions,
          // [MODEL-1] true if any Oracle call hit MAX_TOKENS this run.
          oracleTruncated,
          // [WACC-GAP-AXES] (Tuning 10.x) Capture the two drivers of (hurdle − WACC) on EVERY
          // analysis so the Fix-C threshold can be set from the live distribution: wacc (Agent B's
          // base WACC, %) and beta (FMP profile beta — fetched but otherwise unused). With these +
          // the existing waccHurdleGap.debtToEbitda, a fired gap can be classified as low-β-driven
          // (Ke = rf + β·ERP already below hurdle) vs leverage-driven (Ke above hurdle but WACC
          // pulled below it by debt weighting).
          waccPctAll: Number(((Number(mathEngine?.base?.wacc ?? 0)) * 100).toFixed(2)),
          beta:
            typeof institutionalData?.profile?.beta === "number"
              ? institutionalData.profile.beta
              : null,
          bearDeclineGap: (mathEngine as any)?.assumptionRationales?.bearDeclineGap ?? null,
          // [SCENARIO-IRR-TRACE] (S1) bear IRR decomposition scalars — cashYield +
          // growth + multipleChange sum to the bear scenario IRR. Makes the
          // negative-bear-IRR outlier class queryable across the corpus.
          bearIrrCashYield: (mathEngine as any)?.assumptionRationales?.bearIrrCashYield ?? null,
          bearIrrGrowth: (mathEngine as any)?.assumptionRationales?.bearIrrGrowth ?? null,
          bearIrrMultipleChange: (mathEngine as any)?.assumptionRationales?.bearIrrMultipleChange ?? null,
          // [SPEC-4 step 1] Measurement only — ships freely, no behavior change.
          // The RI walk compounds tangible book at (rotce × retention) — ~13%/yr for
          // BRK-B — while the scenario table advertises the base growth INPUT (~6.5%).
          // These are genuinely different quantities that prose wears the same word
          // "growth" for, so they read as contradictory. Size the gap before deciding
          // whether step 2's relabel is needed. Do NOT reconcile them: they are
          // supposed to differ (rotce × retention is not the scenario growth input).
          ...(() => {
            const __walk = (mathEngine as any)?.scenarios?.base?.__riWalk;
            const __books = Array.isArray(__walk)
              ? __walk
                  .map((w: any) => Number(w?.beginningBook))
                  .filter((b: number) => isFinite(b) && b > 0)
              : [];
            const riImpliedBookGrowthPct =
              __books.length >= 2
                ? Number(
                    (
                      (Math.pow(
                        __books[__books.length - 1] / __books[0],
                        1 / (__books.length - 1),
                      ) -
                        1) *
                      100
                    ).toFixed(2),
                  )
                : null;
            const __rawBase = Number(
              (mathEngine as any)?.assumptionRationales?.baseGrowthPct,
            );
            const scenarioBaseGrowthPct = isFinite(__rawBase)
              ? Number(__rawBase.toFixed(2))
              : null;
            const growthLabelDelta =
              riImpliedBookGrowthPct != null && scenarioBaseGrowthPct != null
                ? Number((riImpliedBookGrowthPct - scenarioBaseGrowthPct).toFixed(2))
                : null;
            if (riImpliedBookGrowthPct != null || scenarioBaseGrowthPct != null) {
              console.log(
                `[GROWTH-LABEL-GAP] ${factSheet?.ticker || "UNKNOWN"}: ` +
                  `riImpliedBookGrowth=${riImpliedBookGrowthPct != null ? riImpliedBookGrowthPct.toFixed(2) + "%" : "n/a"} ` +
                  `(tangible book compounding = rotce x retention), ` +
                  `scenarioBaseGrowth=${scenarioBaseGrowthPct != null ? scenarioBaseGrowthPct.toFixed(2) + "%" : "n/a"} ` +
                  `(scenario input), delta=${growthLabelDelta != null ? growthLabelDelta.toFixed(2) + "pp" : "n/a"} ` +
                  `(measurement only; the two are different quantities and are not reconciled).`,
              );
            }
            return { riImpliedBookGrowthPct, scenarioBaseGrowthPct, growthLabelDelta };
          })(),
          waccHurdleGap: (mathEngine as any)?.assumptionRationales?.waccHurdleGap ?? null,
          growthFloorGap: (mathEngine as any)?.assumptionRationales?.growthFloorGap ?? null,
          daToBookedCapexRatio: deterministicData?.ownerEarningsData?.daToBookedCapexRatio ?? null,
          oeMaintCapexFloored: deterministicData?.ownerEarningsData?.oeMaintCapexFloored ?? null,
          oeOverstatementRatio: deterministicData?.ownerEarningsData?.oeOverstatementRatio ?? null,
          oeAssetHeavySignature: deterministicData?.ownerEarningsData?.oeAssetHeavySignature ?? null,
          nciRatioPct: deterministicData?.ownerEarningsData?.nciRatioPct ?? null,
          type4ClassificationBasis: deterministicData?.type4ClassificationBasis ?? null,
          type4FallbackDeclined: deterministicData?.type4FallbackDeclined ?? false,
          type4TerminalRescaled: deterministicData?.type4TerminalRescaled ?? false,
          terminalMultipleOriginal: deterministicData?.terminalMultipleOriginal ?? null,
          // [PEER-CARD-PROVENANCE-LOCK] (Tuning v2.92) measurement: count of Oracle peer
          // cards dropped for lacking a ticker in the deterministic peer set.
          peerCardsDropped: (deterministicData as any)?.__peerCardsDropped ?? 0,
          // [SPEC-7] Row-level sibling of the card-level lock: count of LLM-authored
          // metric rows dropped for asserting a peer metric with no pipeline source.
          // Tripwire for future LLM-fabricated peer metrics.
          type4PeerRowsDroppedUnsourcedMetric:
            (deterministicData as any)?.__peerRowsDroppedUnsourcedMetric ?? 0,
          // (Tuning v2.94 F-1/F-3) measurement: fail-closed abstention suppression fired;
          // counts of peers dropped by the active-listing and duplicate-listing gates.
          peerAbstainSuppressed: (deterministicData as any)?.__peerAbstainSuppressed ?? false,
          peerInactiveDropped: (deterministicData as any)?.__peerInactiveDropped ?? 0,
          peerDuplicateDropped: (deterministicData as any)?.__peerDuplicateDropped ?? 0,
          // [SPEC-3] Type-4 peer ROTCE guard — tripwire for field-overloading
          // regressions. null = gate did not run (non-Type-4).
          type4PeerRotceUnverified: (deterministicData as any)?.__type4PeerRotceUnverified ?? null,
          type4PeerDuplicateFieldSuppressed:
            (deterministicData as any)?.__type4PeerDuplicateFieldSuppressed ?? null,
          // [S3] Peer-card subject-metric divergence (flag-only). Set by the
          // post-Oracle subject-metric detector; sizes the LLM-minted-subject-
          // metric surface before any GATED figure-lock.
          subjectCardMetricDivergence: (deterministicData as any)?.__subjectCardMetricDivergence ?? false,
          subjectCardMetricMaxDivergence: (deterministicData as any)?.__subjectCardMetricMaxDivergence ?? null,
          subjectCardMetricName: (deterministicData as any)?.__subjectCardMetricName ?? null,
          // [OE-DEGENERATE] (F12, Tuning v2.62 §39.1) measurement: did the LLM's normalizedOwnerEarnings
          // come in implausibly small vs the deterministic anchors (→ valuation forced unresolvable),
          // and the raw ratio (LLM OE ÷ max deterministic anchor) to size the failure class corpus-wide.
          type4OeDegenerateFired: (deterministicData as any)?.type4OeDegenerateFired ?? false,
          normalizedOeVsAnchorRatio: (deterministicData as any)?.normalizedOeVsAnchorRatio ?? null,
          // [F13 precursor] (Tuning v2.63 §39.2) Type-4 Year-10 TBV projection null diagnostics:
          // sizes the failure class + isolates the missing input (TBV source / ROTCE base / history)
          // so the gated F13 fix targets the real defect.
          type4TbvProjectionNull: (deterministicData as any)?.type4TbvProjectionNull ?? false,
          type4TbvSource: (deterministicData as any)?.type4TbvSource ?? null,
          type4TbvPerShareUnavailable: (deterministicData as any)?.type4TbvPerShareUnavailable ?? false,
          type4RotceBaseZero: (deterministicData as any)?.type4RotceBaseZero ?? false,
          // [F13] (Tuning v2.66 §41) which source produced the Type-4 ROTCE base + its value —
          // sizes how often the operatingIncome-derived ROTCE fails (insurers) and the ROE
          // fallback carries the P/TBV terminal, across the Type-4 corpus.
          // [M42] (Tuning v2.67 §42.2) Type-4 terminal double-count monitor — sizes how often the
          // P/TBV terminal dominates interim PVs and how aggressively book compounds, so the outlier
          // band is known before any terminal-formula change. Deterministic; null for non-Type-4.
          terminalToInterimPvRatio: (deterministicData as any)?.terminalToInterimPvRatio ?? null,
          effectiveBookGrowthMultiple: (deterministicData as any)?.effectiveBookGrowthMultiple ?? null,
          type4RotceSource: (deterministicData as any)?.type4Valuation?.rotceSource ?? null,
          type4PtbvMedianSource: (deterministicData as any)?.type4Valuation?.ptbvMedianSource ?? null,
          type4PtbvMedianResolved: (deterministicData as any)?.type4Valuation?.ptbvMedianResolved ?? false,
          // [F16] (Tuning v2.78 §45) Type-4 terminal ROTCE sourcing — both candidates + which the
          // switch would/did choose. Sizes how many Type-4 names compound book at a degenerate
          // short-series operatingIncome ROTCE vs the deterministic 5-yr key-metrics ROE, and (with
          // rotceSwitchApplied) whether the flag-gated switch is live. rotceSwitchWouldApply is the
          // measurement-mode signal (what the switch WOULD do while TYPE4_ROTCE_5YR_ENABLED is off).
          type4RotceOpIncome: (deterministicData as any)?.type4Valuation?.rotceOpIncome ?? null,
          type4Rotce5yrKeyMetrics: (deterministicData as any)?.type4Valuation?.rotce5yrKeyMetrics ?? null,
          type4RotceDivergencePct: (deterministicData as any)?.type4Valuation?.rotceDivergencePct ?? null,
          type4RotceSourceChosen: (deterministicData as any)?.type4Valuation?.rotceSourceChosen ?? null,
          type4RotceSwitchWouldApply: (deterministicData as any)?.type4Valuation?.rotceSwitchWouldApply ?? false,
          type4RotceSwitchApplied: (deterministicData as any)?.type4Valuation?.rotceSwitchApplied ?? false,
          // [F13-ROOT] (Tuning v2.73 §43) confirms the classification-timing fix worked: for a
          // Type-4 name, type4Valuation must now be PRESENT (was null 100% of the time before), and
          // whether it required the post-classification rebuild. Sizes the fix + the still-open root
          // ordering issue (preClassifiedType never emits "Type 4") across the corpus.
          type4ValuationPresent: !!(deterministicData as any)?.type4Valuation,
          type4ValuationRebuilt: (deterministicData as any)?.type4ValuationRebuilt === true,
          type4RotceBasePct: typeof (deterministicData as any)?.type4Valuation?.rotceBase === "number"
            ? Number(((deterministicData as any).type4Valuation.rotceBase * 100).toFixed(2))
            : null,
          nciGapFired: deterministicData?.ownerEarningsData?.nciGapFired ?? null,
          // [SPEC-2] Deterministic investment composition + which source the memo can
          // cite. null source = not a Type-4 entity (the check does not apply).
          // "llm_fallback" means the balance sheet carried no investment fields and the
          // only figure available is the Historian's descriptive (non-authoritative) string.
          type4InvestmentComposition:
            (deterministicData as any)?.type4InvestmentComposition ?? null,
          investment_portfolio_source:
            deterministicData?.entityType !== "Type 4"
              ? null
              : (deterministicData as any)?.type4InvestmentComposition
                ? "deterministic"
                : (finalAnalysis as any)?.factSheet?.type4Data?.investmentPortfolioSize
                  ? "llm_fallback"
                  : "unavailable",
          // [NORM-ANCHOR-DIVERGENCE] (Tuning v2.59 §36.7) measurement: how far each normalization
          // anchor sits from the TTM base, + the deterministic sector, so Method-A-misleads-on-growers
          // can be sized across the corpus before any Gate-0 threshold tuning.
          midCycleVsTtmDivergencePct: deterministicData?.normalizationAnchors?.midCycleVsTtmDivergencePct ?? null,
          methodBVsTtmDivergencePct: deterministicData?.normalizationAnchors?.methodBVsTtmDivergencePct ?? null,
          // [S2] OE anchor-basis + compounder-labeling gap. normalizedProseMismatch
          // is set flag-only by the post-Oracle normalized-prose detector above.
          // [F1] Prefer the DCF-input-resolved basis over the provisional pre-DCF value.
          oeAnchorBasis:
            (deterministicData as any)?.__oeAnchorBasisResolved ??
            deterministicData?.normalizationAnchors?.oeAnchorBasis ??
            null,
          oeMidCycleDivergencePct: deterministicData?.normalizationAnchors?.oeMidCycleDivergencePct ?? null,
          oeFmpDivergencePct: deterministicData?.normalizationAnchors?.oeFmpDivergencePct ?? null,
          normalizedProseMismatch: (deterministicData as any)?.__normalizedProseMismatch ?? false,
          normAnchorSector: deterministicData?.sector ?? null,
          normAnchorIndustry: deterministicData?.industry ?? null,
          qualityMetricBasis:
            deterministicData?.entityType === "Type 5" &&
            institutionalData?.ratiosTtm?.operatingProfitMarginTTM != null
              ? "TTM"
              : "annual",
          // Spin-off disclosure compliance (measurement-only). "Expected" = any spin-off signal;
          // "namedInSummary" = the executive summary actually says "spin-off" for a Type 5 entity.
          spinoffDisclosureExpected:
            deterministicData?.entityType === "Type 5" ||
            (finalAnalysis as any)?.factSheet?.isRecentSpinOff === true ||
            (intelligenceBrief as any)?.corporateEvents?.isRecentSpinoff === true,
          spinoffNamedInSummary:
            // (Tuning v2.92) FIXED false negative: CON resolved Type 6 with the spin-off
            // flag carried on factSheet, so the old entityType-only conjunct reported
            // False while the thesis literally named the spin-off. Mirror the
            // spinoffDisclosureExpected OR-condition so the two props share one gate.
            (deterministicData?.entityType === "Type 5" ||
              (finalAnalysis as any)?.factSheet?.isRecentSpinOff === true ||
              (intelligenceBrief as any)?.corporateEvents?.isRecentSpinoff === true) &&
            /spin[- ]?off/i.test(((finalAnalysis as any)?.executiveSummary) || (finalAnalysis as any)?.summary || ""),
          avoidPassCoherenceFired,
          priceToIvRatioAtAvoid,
          // (Tuning v2.93) universal price/IV — null = valuation not estimable
          // (mirrors the floorSourceParseOk null-means-not-run semantics).
          priceToIvRatio: priceToIvRatioUniversal,
          // (Tuning v2.93 §61.11, R-3 Stage 1) canonical buy-zone telemetry.
          // buyZoneVerdictAboveTrigger: null = verdict not bullish or zone not stamped.
          buyZoneCanonicalStamped,
          buyZoneEffectiveMosPct,
          buyZoneVerdictAboveTrigger,
          avoidPassOverrideApplied,
          passQualityCoherenceFired,
          archetypeRoicHurdleGapFired,
          archetypeRoicWaccGapBps,
          contaminatedVerdictForcedPass: !!deterministicData?.isType5DataContaminated,
          type5Origin: deterministicData?.type5Origin ?? null,
          type5UnvaluableReason: deterministicData?.type5UnvaluableReason ?? null,
          // [TYPE5-VERDICT-CAP] measurement-only: ceilingActive = a Type-5/spin-off cap is in force;
          // finalVerdict = post-override decision; capFired = the ceiling actually clamped a BUY/FAT
          // PITCH down (reads the rationale marker the clamp writes); capViolation MUST stay false —
          // if it ever goes true, a Type-5 leaked a BUY/FAT PITCH and the cap regressed.
          type5VerdictCeilingActive:
            deterministicData?.entityType === "Type 5" ||
            (finalAnalysis as any)?.factSheet?.isRecentSpinOff === true,
          type5FinalVerdict: (finalAnalysis as any)?.oracleFramework?.decision?.value ?? null,
          type5VerdictCapFired:
            (deterministicData?.entityType === "Type 5" ||
              (finalAnalysis as any)?.factSheet?.isRecentSpinOff === true) &&
            /Spin-Off Data Ceiling/i.test(
              (finalAnalysis as any)?.oracleFramework?.decision?.rationale || "",
            ),
          type5VerdictCapViolation:
            (deterministicData?.entityType === "Type 5" ||
              (finalAnalysis as any)?.factSheet?.isRecentSpinOff === true) &&
            ((finalAnalysis as any)?.oracleFramework?.decision?.value === "BUY" ||
              (finalAnalysis as any)?.oracleFramework?.decision?.value === "FAT PITCH"),
          // [TYPE5-OE-MARGIN] (Tuning 10.3) measurement-only: a Type 5 entity anchors its IV on
          // TTM FCF; if that anchor implies an implausibly high FCF/revenue margin the TTM base is
          // likely parent-contaminated (isType5DataContaminated only catches the TTM<=0 case).
          // Denominator is annual revenue (slight period mismatch is acceptable for a watch flag).
          type5OeMargin: (() => {
            const fcf = deterministicData?.oracleMetrics?.fcf;   // TTM-anchored FCF, $MM
            const rev = deterministicData?.lastYearRevenue;       // annual revenue, $MM
            if (deterministicData?.entityType !== "Type 5" || !(rev > 0) || typeof fcf !== "number") return null;
            const marginPct = Number(((fcf / rev) * 100).toFixed(1));
            const implausible = marginPct > 20; // watch ceiling only; NOT a gate
            if (implausible) {
              console.warn(
                `[TYPE5-OE-MARGIN] ${factSheet?.ticker || "UNKNOWN"}: TTM owner-earnings anchor implies ${marginPct}% FCF/revenue margin (>20% watch ceiling) — verify the TTM FCF base is not parent-contaminated. (measurement only; no IV change.)`,
              );
            }

            const implausiblyLow = !deterministicData?.isType5DataContaminated && marginPct > 0 && marginPct < 5;
            if (implausiblyLow) {
              console.warn(
                `[TYPE5-OE-MARGIN] ${factSheet?.ticker || "UNKNOWN"}: TTM owner-earnings anchor implies ${marginPct}% FCF/revenue margin (<5% trough watch floor) — verify the TTM base is not a post-spin trough (TSA/interest/separation drag). (measurement only; no IV change.)`
              );
            }

            return { marginPct, implausible, implausiblyLow, anchorFcfMM: Math.round(fcf), revenueMM: Math.round(rev) };
          })(),
          shadowFlatIv: (deterministicData as any)?.__shadowFlatIv ?? null,
          shadowFlatDeltaPct: (deterministicData as any)?.__shadowFlatDeltaPct ?? null,
          type4RiIv: (deterministicData as any)?.__type4RiIv ?? null,
          type4RiImpliedPtbv: (deterministicData as any)?.__type4RiImpliedPtbv ?? null,
          type4RiVsLiveDeltaPct: (deterministicData as any)?.__type4RiVsLiveDeltaPct ?? null,
          type4RiRotceUsed: (deterministicData as any)?.__type4RiRotceUsed ?? null,
          type4RiRotceSource: (deterministicData as any)?.__type4RiRotceSource ?? null,
          type4RiOmega: (deterministicData as any)?.__type4RiOmega ?? null,
          // [RI-2 EDIT B] measurement: did RI drive the live IV, and did it rescue a
          // degenerate-OE unresolvable? Booleans set in the [TYPE4-RI-APPLIED] block above.
          type4RiApplied: (deterministicData as any)?.__type4RiApplied === true,
          type4RiClearedUnresolvable: (deterministicData as any)?.type4RiClearedUnresolvable === true,
          // RI‑3 measurement: did the deterministic RI walk emit, and does it sum
          // to the RI IV (panel additivity)? plus the applied‑basis flat Ke.
          type4RiWalkEmitted: (deterministicData as any)?.__type4RiWalkEmitted === true,
          type4RiWalkAdditive: (deterministicData as any)?.__type4RiWalkAdditive === true,
          type4RiKeFlat: (deterministicData as any)?.__type4RiKeFlat ?? null,
          // [SPEC-1] Deterministic current market multiple + the prose-consistency
          // tripwire. null consistency = no P/TBV token in prose (check did not run).
          type4CurrentPtbv: (deterministicData as any)?.__type4CurrentPtbv ?? null,
          ptbv_prose_consistency: (deterministicData as any)?.__ptbvProseConsistency ?? null,
          ptbvProseDivergentToken: (deterministicData as any)?.__ptbvProseDivergentToken ?? null,
          liveWacc: (deterministicData as any)?.__shadowLiveWacc ?? null,
          flatRate: (deterministicData as any)?.__shadowFlatRate ?? null,
          riskTier: (deterministicData as any)?.__riskTier ?? null,
          mosAddOn: (deterministicData as any)?.__mosAddOn ?? null,
          publishedBeta: (deterministicData as any)?.__publishedBeta ?? null,
          buyReqMos: (deterministicData as any)?.__buyReqMos ?? null,
          riskMosEnabled: (deterministicData as any)?.__riskMosEnabled ?? false,
          // [S6] Per-job Gemini JSON parse-retry accounting: how many silently
          // retried JSON parse failures this run, and the last agent to trip it.
          // Model-reliability signal feeding the held Oracle model-tier decision.
          geminiJsonRetryCount: __geminiJsonRetryCount,
          geminiJsonRetryAgent: __geminiJsonRetryAgent,
        };

        if (aiLoggedFields.length > 0) {
          console.log(
            `[METRIC-PROVENANCE] ${factSheet?.ticker || "UNKNOWN"}: ${aiLoggedFields.join(", ")} sourced from model (FMP gap)`,
          );
        }

        // Final safety check: if currentPrice is still 0 but deterministicData had it, use it.
        // Or if AI found a price but deterministic was 0, use AI.
        if (!finalAnalysis.currentPrice && deterministicData?.currentPrice) {
          finalAnalysis.currentPrice = deterministicData.currentPrice;
        }

        // RULE 38 ENFORCEMENT: PASS/AVOID kills all allocation fields
        const avoidPassVerdicts = ["PASS", "AVOID"];
        if (
          finalAnalysis.oracleFramework?.decision?.value &&
          avoidPassVerdicts.includes(
            finalAnalysis.oracleFramework.decision.value,
          )
        ) {
          const fieldsNulled: string[] = [];
          const buyFields = [
            "capitalAllocationDecision",
            "allocationGuidance",
            "buyZoneJustification",
          ];

          for (const field of buyFields) {
            if (
              finalAnalysis[field] !== null &&
              finalAnalysis[field] !== undefined
            ) {
              finalAnalysis[field] = null;
              fieldsNulled.push(field);
            }
          }

          if (
            Array.isArray(finalAnalysis.buyZoneFramework) &&
            finalAnalysis.buyZoneFramework.length > 0
          ) {
            finalAnalysis.buyZoneFramework = [];
            fieldsNulled.push("buyZoneFramework");
          }

          if (fieldsNulled.length > 0) {
            console.warn(
              `[RULE 38] ${symbol}: Decision is ${finalAnalysis.oracleFramework.decision.value}. ` +
                `Cleared fields per Rule 38: ${fieldsNulled.join(", ")}`,
            );
          }
        }

        // ─── EXEC-SUMMARY BACKFILL ──────────────────────────────────────────────
        // Guarantee every memo carries an Executive Thesis. When the Oracle (Agent C)
        // omits `summary`, sanitizeString() upstream coerces the empty value to the
        // literal "N/A", which renders in the memo's Executive Thesis card. Rather than
        // ship "N/A", synthesize a verdict-label-free summary from the engine's own
        // deterministic outputs (price, base-case IV, archetype, killer insight).
        // Measurement-only marker + observability flag; no behavioral/valuation change.
        try {
          const rawSummary = (finalAnalysis.summary || "").trim();
          const summaryMissing = rawSummary === "" || /^n\/?a\.?$/i.test(rawSummary);
          // [EXEC-SUMMARY-TRUNCATION] (Tuning 10.x) A non-empty summary that does not end in
          // terminal punctuation is a truncated Oracle response (VSNT: "…Comcast Corporation,
          // having" — Oracle hit its output-token budget mid-field). The ORACLE-C-GATE truncation
          // test (IV=0 AND realisticIrr absent) missed it because the recovered partial still
          // carried IRR. Treat it as missing so the deterministic backfill fires instead of
          // shipping half a sentence.
          const summaryTruncated =
            !summaryMissing && !/[.!?"'\u201D\u2019]$/.test(rawSummary);
          if (summaryMissing || summaryTruncated) {
            const px =
              typeof finalAnalysis.currentPrice === "number"
                ? finalAnalysis.currentPrice
                : 0;
            const iv = mathEngine?.scenarios?.base?.intrinsicValuePerShare ?? 0;
            const name = finalAnalysis.name || symbol;
            const archetype =
              finalAnalysis.archetype &&
              typeof finalAnalysis.archetype.category === "string"
                ? finalAnalysis.archetype.category
                : "";
            const ki =
              finalAnalysis.killerInsight &&
              !/^n\/?a\.?$/i.test(String(finalAnalysis.killerInsight).trim())
                ? String(finalAnalysis.killerInsight).trim()
                : "";

            const parts: string[] = [];
            parts.push(
              archetype
                ? `${name} is assessed as a ${archetype.toLowerCase()} business.`
                : `${name} has been evaluated against the Oracle Insight framework.`,
            );
            if (px > 0 && iv > 0) {
              const ratio = px / iv;
              if (ratio > 1) {
                parts.push(
                  `At a recent price of $${px.toFixed(2)}, the stock trades at a premium of approximately ${((ratio - 1) * 100).toFixed(1)}% to our estimated base-case intrinsic value of $${iv.toFixed(2)}; with the price above intrinsic value, capital discipline is warranted.`,
                );
              } else {
                parts.push(
                  `At a recent price of $${px.toFixed(2)}, the stock trades at a discount of approximately ${((1 - ratio) * 100).toFixed(1)}% to our estimated base-case intrinsic value of $${iv.toFixed(2)}, indicating a margin of safety.`,
                );
              }
            } else if (iv > 0) {
              parts.push(
                `Our estimated base-case intrinsic value is $${iv.toFixed(2)} per share.`,
              );
            }
            if (ki) parts.push(ki);

            finalAnalysis.summary = parts.join(" ");
            (finalAnalysis as any).observability =
              (finalAnalysis as any).observability || {};
            (finalAnalysis as any).observability.executiveSummaryBackfilled = true;
            console.warn(
              `[EXEC-SUMMARY-BACKFILL] ${symbol}: Oracle ${summaryTruncated ? "returned a truncated executive summary" : "returned no executive summary"}; ` +
                `synthesized deterministically from engine outputs (price $${px.toFixed(2)}, IV $${iv.toFixed(2)}). Prompt compliance failure.`,
            );
          }
        } catch (e) {
          console.error(`[EXEC-SUMMARY-BACKFILL] ${symbol}: backfill failed`, e);
        }

        // VERDICT-PROSE-LOCK (detect + sanitize): the Oracle must not name a hard verdict
        // label in summary / valuation-rationale prose — the displayed verdict is computed
        // deterministically by the engine. When a stale label is found, we replace it with
        // neutral language to prevent the memo PDF from showing a verdict that contradicts
        // the engine-computed verdict stamp.
        try {
          // ─── BUG-5 FIX: fail-closed verdict prose-lock ──────────────────────────
          // The prior regex only matched "<LABEL> <verdict|directive|rating|call|signal>",
          // so verb-led ("we assign a BUY"), noun-trailing ("a PASS recommendation"),
          // and bare-clause ("Verdict: AVOID") phrasings slipped through. We now match
          // all three shapes, still replace with neutral language (NEVER the real
          // verdict label), and set a residual flag if any label token survives.
          const LABELS = "BUY|ACCUMULATE|FAT PITCH|CORE HOLD|HOLD|WATCH|PASS|AVOID";
          const trailingNoun =
            "verdict|directive|rating|call|signal|recommendation|stance|thesis|posture|conclusion|opinion";
          const leadingVerb =
            "assign|assigned|assigning|rate|rated|rating|issue|issued|reiterate|reiterated|maintain|maintained|call it|rate it|we rate|we call";
          const verdictWordRegex = new RegExp(
            // shape A: [article] LABEL <noun>
            `(\\b(?:the|an|a)\\s+)?(?:${LABELS})\\b\\s+(?:${trailingNoun})` +
            // shape B: <verb> [article] LABEL
            `|(?:${leadingVerb})\\s+(?:the|an|a)?\\s*(?:${LABELS})\\b` +
            // shape C: (verdict|call|recommendation|rating)[:—-] LABEL
            `|(?:verdict|call|recommendation|rating)\\s*[:—-]\\s*(?:${LABELS})\\b`,
            "gi",
          );

          const sanitize = (text: string): { sanitized: string; found: string | null } => {
            const match = text.match(verdictWordRegex);
            if (!match) return { sanitized: text, found: null };
            // Replace stale label phrase with neutral prose that preserves sentence flow.
            // Optionally consume a leading article ("The "/"the "/"a "/"an ") so we never emit a
            // doubled "The the valuation conclusion"; regenerate "The"/"the" with original casing.
            const sanitized = text.replace(
              verdictWordRegex,
              (_m: string, article?: string) => {
                if (article) {
                  const capitalized = /^[A-Z]/.test(article);
                  return `${capitalized ? "The" : "the"} valuation conclusion`;
                }
                return "the valuation conclusion";
              },
            );
            return { sanitized, found: match[0] };
          };

          // Sanitize expectedReturnMath.rationale
          if (finalAnalysis.expectedReturnMath?.rationale) {
            const { sanitized, found } = sanitize(finalAnalysis.expectedReturnMath.rationale);
            if (found) {
              finalAnalysis.expectedReturnMath.rationale = sanitized;
              console.warn(
                `[VERDICT-PROSE-LOCK] ${symbol}: expectedReturnMath.rationale named verdict label in prose ("${found}"). ` +
                  `Replaced with "the valuation conclusion". Prompt compliance failure.`,
              );
            }
          }

          // Sanitize summary
          if (finalAnalysis.summary) {
            const { sanitized, found } = sanitize(finalAnalysis.summary);
            if (found) {
              finalAnalysis.summary = sanitized;
              console.warn(
                `[VERDICT-PROSE-LOCK] ${symbol}: summary named verdict label in prose ("${found}"). ` +
                  `Replaced with "the valuation conclusion". Prompt compliance failure.`,
              );
            }
          }

          // BUG-5 FIX (fail-closed residual): after sanitizing, verify no standalone
          // verdict-label token survives as its own clause (e.g. a lone "AVOID." or
          // "PASS" on a line). We do NOT blindly nuke bare words (false-positive risk
          // on verbs like "hold"/"buy"), but we DO surface a residual flag so a leaked
          // label is observable rather than silently shipped.
          // Case-SENSITIVE uppercase whole-word: a lone upper-case label in a memo is
          // almost certainly a leaked verdict, while lower-case "hold/buy/pass" in normal
          // prose is left alone. Flag-only (no scrub) — the point is observability.
          const residualRegex =
            /\b(BUY|ACCUMULATE|FAT PITCH|CORE HOLD|HOLD|WATCH|PASS|AVOID)\b/;
          const residualIn = [
            finalAnalysis.expectedReturnMath?.rationale || "",
            finalAnalysis.summary || "",
          ].join("\n");
          const residualHit = residualRegex.test(residualIn);
          (finalAnalysis as any).verdictProseLockResidual = residualHit;
          if (residualHit) {
            console.error(
              `[VERDICT-PROSE-LOCK] ${symbol}: RESIDUAL verdict-label token survived sanitize ` +
                `(fail-closed flag set). Prose fields need manual review.`,
            );
          }

          // Sentry breadcrumb (non-blocking)
          try {
            // @ts-ignore
            Sentry.addBreadcrumb({
              category: 'verdict-prose-lock',
              level: residualHit ? 'error' : 'warning',
              message: `${symbol}: verdict label ${residualHit ? 'RESIDUAL after' : 'detected and'} sanitized in prose`,
            });
          } catch { /* Sentry optional */ }

        } catch {
          /* non-fatal */
        }

        // ─── IMPLIED-GROWTH-PROSE-LOCK (Tuning v2.89) ──────────────────────────
        // expectedReturnMath.impliedGrowth (the IMPLIED GROWTH box) is already locked
        // to the engine value — "N/A" when the DCF solver clamps at its -0.99 (−99%)
        // floor, else "implied ~X% sustained growth". But the C-3c growth-gap sentence
        // in summary / rationale prose is free Oracle text and can leak the clamp
        // sentinel as a bogus literal (e.g. BRK-B: "implies -0.99% sustained growth"
        // while ALL, from the IDENTICAL engine value, correctly rendered "N/A"). Force
        // the prose figure to agree with the locked field so a single run can never
        // display two different market-implied-growth numbers.
        try {
          const lockedIg = String(
            (finalAnalysis as any).expectedReturnMath?.impliedGrowth || "",
          ).trim();
          let canonicalIg: string | null = null;
          if (/^n\/a$/i.test(lockedIg)) {
            canonicalIg = "N/A";
          } else {
            const m = lockedIg.match(/(-?\d+(?:\.\d+)?)\s*%/);
            if (m) canonicalIg = `${m[1]}%`;
          }
          if (canonicalIg) {
            // (Tuning v2.94 F-9) "sustained" made OPTIONAL — CAAP's Oracle wrote
            // "implies N/A growth" (no "sustained"), which the old pattern never matched,
            // leaking the ungrammatical raw form past the lock.
            const igRegex =
              /(implies\s+(?:approximately\s+|about\s+|roughly\s+|~\s*)?)(-?\d+(?:\.\d+)?\s*%|n\/a)(\s+(?:sustained\s+)?growth)/gi;
            const fixIg = (
              text: string,
            ): { fixed: string; changed: string | null } => {
              let changed: string | null = null;
              const fixed = text.replace(
                igRegex,
                (_full: string, pre: string, tok: string, post: string) => {
                  // (Tuning v2.94 F-9) When the canonical value is N/A, ALWAYS normalize to
                  // the grammatical template — even when the token already reads "N/A"
                  // (CAAP rendered raw "implies N/A growth"; the bare form is never correct).
                  if (canonicalIg === "N/A") {
                    changed = tok.trim();
                    return "implies no reliably solvable sustained growth (N/A)";
                  }
                  if (
                    tok.replace(/\s+/g, "").toLowerCase() !==
                    canonicalIg!.toLowerCase()
                  ) {
                    changed = tok.trim();
                    return `${pre}${canonicalIg}${post}`;
                  }
                  return `${pre}${tok}${post}`;
                },
              );
              return { fixed, changed };
            };
            const igFields: string[] = ["summary", "executiveSummary"];
            for (const f of igFields) {
              const cur = (finalAnalysis as any)[f];
              if (typeof cur === "string" && cur) {
                const { fixed, changed } = fixIg(cur);
                if (changed !== null) {
                  (finalAnalysis as any)[f] = fixed;
                  console.warn(
                    `[IMPLIED-GROWTH-PROSE-LOCK] ${symbol}: ${f} implied-growth prose ("${changed}") ` +
                      `disagreed with locked expectedReturnMath.impliedGrowth ("${canonicalIg}"). ` +
                      `Rewrote to match. Prompt compliance failure.`,
                  );
                }
              }
            }
            if ((finalAnalysis as any).expectedReturnMath?.rationale) {
              const { fixed, changed } = fixIg(
                (finalAnalysis as any).expectedReturnMath.rationale,
              );
              if (changed !== null) {
                (finalAnalysis as any).expectedReturnMath.rationale = fixed;
                console.warn(
                  `[IMPLIED-GROWTH-PROSE-LOCK] ${symbol}: rationale implied-growth prose ("${changed}") ` +
                    `disagreed with locked value ("${canonicalIg}"). Rewrote to match.`,
                );
              }
            }
          }
        } catch (e) {
          console.error(`[IMPLIED-GROWTH-PROSE-LOCK] ${symbol}: lock failed`, e);
        }

        // ─── TERMINAL-ATTRIBUTION-CHECK (Tuning v2.92, measurement-only) ────────
        // The Oracle's valuation prose sometimes attributes the terminal multiple to a
        // peer anchor whose figure contradicts the Mathematician's actual multiple
        // (observed live, GILD: prose "anchored to ... 11.4x EV/FCF" while the engine
        // terminalMultiple was 15.0x). Same defect class as C-3f contraction attribution.
        // Measure-first discipline: flag + count only, prose UNCHANGED; a rewrite lock
        // ships only if the corpus shows frequency.
        try {
          const __engTm = Number(mathEngine?.base?.terminalMultiple);
          if (
            Number.isFinite(__engTm) &&
            __engTm > 0 &&
            mathEngine?.base?.useTerminalMultiple === true
          ) {
            const __attrFields = [
              "valuationInsights",
              "valuationRationale",
              "deepValuationInsights",
              "valuationConclusion",
            ];
            let __attrText = "";
            for (const f of __attrFields) {
              const v = (finalAnalysis as any)[f];
              if (typeof v === "string" && v) __attrText += " " + v;
            }
            let __mismatch: boolean | null = null;
            const __attrMatch = __attrText.match(
              /anchored to[^.]{0,120}?(\d+(?:\.\d+)?)\s*x/i,
            );
            if (__attrMatch) {
              const __claimed = Number(__attrMatch[1]);
              __mismatch =
                Number.isFinite(__claimed) &&
                Math.abs(__claimed - __engTm) > 0.05;
              if (__mismatch) {
                console.warn(
                  `[TERMINAL-ATTRIBUTION-CHECK] ${symbol}: prose claims terminal multiple ` +
                    `anchored at ${__claimed}x but engine terminalMultiple=${__engTm.toFixed(1)}x ` +
                    `(measurement only; prose unchanged).`,
                );
              }
            }
            (finalAnalysis as any).observability =
              (finalAnalysis as any).observability || {};
            (finalAnalysis as any).observability.terminalAttributionMismatch =
              __mismatch;
          }
        } catch (e) {
          console.error(`[TERMINAL-ATTRIBUTION-CHECK] ${symbol}: failed`, e);
        }

        // ─── BUYZONE-ACTION-COHERENCE (Tuning v2.92, measurement-only) ──────────
        // buyZoneJustification is free Oracle prose; under a non-bullish final verdict it
        // has used bullish action language (observed live, GILD: "justifying immediate
        // measured accumulation" under a WATCH-family verdict). VERDICT-PROSE-LOCK covers
        // label leakage, not action-verb coherence. Flag + count only, prose UNCHANGED.
        try {
          const __finalVerdict = String(
            (finalAnalysis as any)?.oracleFramework?.decision?.value || "",
          ).toUpperCase();
          const __nonBullish = ["PASS", "AVOID", "CORE HOLD", "WATCH"].includes(
            __finalVerdict,
          );
          const __bzText = String(
            (finalAnalysis as any)?.buyZoneJustification || "",
          );
          let __incoherent: boolean | null = null;
          if (__nonBullish && __bzText) {
            __incoherent =
              /\b(accumulat\w*|buy(?:ing)?\b|add(?:ing)?\s+(?:capital|exposure|shares)|initiat\w*\s+a\s+position|swing\s+hard)\b/i.test(
                __bzText,
              );
            if (__incoherent) {
              console.warn(
                `[BUYZONE-ACTION-COHERENCE] ${symbol}: bullish action language in ` +
                  `buyZoneJustification under a ${__finalVerdict} verdict ` +
                  `(measurement only; prose unchanged).`,
              );
            }
          } else if (__bzText) {
            __incoherent = false;
          }
          (finalAnalysis as any).observability =
            (finalAnalysis as any).observability || {};
          (finalAnalysis as any).observability.buyZoneActionIncoherent =
            __incoherent;
        } catch (e) {
          console.error(`[BUYZONE-ACTION-COHERENCE] ${symbol}: failed`, e);
        }

        // ─── TYPE4-PTBV-DETERMINISM-LOCK (Tuning v2.89 / extended v2.90) ────────
        // The current Price/Tangible-Book multiple AND the historical P/TBV median are
        // pure deterministic values (currentPrice ÷ engine tbvPerShare; type4Valuation
        // .ptbvMedian). The Oracle authors unitEconomics cards / valuation prose as free
        // text and has (a) minted a subject P/TBV value that does not reconcile to its own
        // price and TBV/share (e.g. JPM: card 2.48x vs 339.22 ÷ 106.85 = 3.17x), and
        // (b) stated a "historical median" P/TBV that disagrees with the engine's computed
        // median (e.g. BRK-B: benchmark/prose "1.51x" vs engine ptbvMedian 1.81x), which
        // then drives a false "multiple contraction back to the historical median"
        // conclusion. Per the deterministic-math invariant the LLM must never mint either
        // figure. We overwrite the subject P/TBV card value + its historical-median
        // benchmark with engine values, and rewrite any "historical median of N.NNx" prose
        // to the engine median — correcting the adjacent above/below/in-line comparator
        // too, since current-vs-median direction is itself deterministic and a bare number
        // swap would otherwise fabricate a contradiction (e.g. "well above 1.81x" when the
        // current multiple is 1.80x). Peer aggregates remain the Oracle's to author.
        try {
          const t4 = deterministicData?.type4Valuation;
          const t4tbv = Number(t4?.tbvPerShare);
          const px = Number((finalAnalysis as any).currentPrice);
          const cards = (finalAnalysis as any).unitEconomics;
          const medianVal =
            t4?.ptbvMedianResolved === true && Number(t4?.ptbvMedian) > 0
              ? Number(t4.ptbvMedian)
              : null;
          const medianStr = medianVal != null ? `${medianVal.toFixed(2)}x` : null;

          if (t4tbv > 0 && px > 0 && Array.isArray(cards)) {
            const ptbvDet = px / t4tbv; // deterministic current P/TBV
            const detStr = `${ptbvDet.toFixed(2)}x`;
            const isPtbvLabel = (label: string): boolean => {
              const l = (label || "").toLowerCase();
              return (
                l.includes("p/tbv") ||
                (l.includes("tangible book") && l.includes("price"))
              );
            };
            for (const card of cards) {
              if (
                card &&
                typeof card.label === "string" &&
                isPtbvLabel(card.label)
              ) {
                const prev = String(card.value ?? "");
                const prevNum = parseFloat(prev.replace(/[^0-9.\-]/g, ""));
                // Rewrite only when the Oracle value diverges from the engine ratio.
                if (!(Math.abs(prevNum - ptbvDet) <= 0.02)) {
                  card.value = detStr;
                  console.warn(
                    `[TYPE4-PTBV-DETERMINISM-LOCK] ${symbol}: unitEconomics P/TBV card ("${prev}") ` +
                      `did not reconcile to deterministic price/tbv ` +
                      `(px=$${px.toFixed(2)} ÷ tbv=$${t4tbv.toFixed(2)} = ${detStr}). ` +
                      `Overwrote with engine value (LLM authored a deterministic ratio).`,
                  );
                }
                // Benchmark = deterministic historical median (never LLM-authored).
                if (medianStr && medianVal != null) {
                  const prevBench = String(card.benchmark ?? "");
                  const benchNum = parseFloat(
                    prevBench.replace(/[^0-9.\-]/g, ""),
                  );
                  if (!(Math.abs(benchNum - medianVal) <= 0.02)) {
                    card.benchmark = `Historical median: ${medianStr}`;
                    console.warn(
                      `[TYPE4-PTBV-DETERMINISM-LOCK] ${symbol}: P/TBV card benchmark ("${prevBench}") ` +
                        `did not match engine ptbvMedian (${medianStr}). Overwrote with engine median.`,
                    );
                  }
                }
              }
            }

            // Prose lock: rewrite "historical median [of] N.NNx" to the engine median,
            // and correct the adjacent above/below/in-line comparator deterministically
            // from the current-P/TBV-vs-median relationship (direction is a fact, not an
            // Oracle judgment). Only the P/TBV-median context is touched — e.g. a current
            // "1.51x book value" (P/B) reference has no "historical median" anchor and is
            // left alone.
            if (medianStr && medianVal != null) {
              const ratio = ptbvDet / medianVal;
              const dirPhrase =
                ratio >= 1.15
                  ? "well above"
                  : ratio >= 1.04
                    ? "above"
                    : ratio > 0.96
                      ? "roughly in line with"
                      : ratio > 0.85
                        ? "below"
                        : "well below";
              // (a) comparator + median: "...well above its historical median of 1.51x"
              const cmpRegex =
                /((?:well\s+|significantly\s+|materially\s+|far\s+)?(?:above|below)|(?:roughly\s+)?in\s+line\s+with)(\s+(?:its|the)\s+historical\s+median\s+(?:of\s+)?)\$?(\d+(?:\.\d+)?)x/gi;
              // (b) bare median: "...historical median of 1.51x" (no comparator)
              const bareRegex =
                /(historical\s+median\s+(?:of\s+)?)\$?(\d+(?:\.\d+)?)x/gi;

              const scrub = (
                text: string,
              ): { out: string; changed: string[] } => {
                const changed: string[] = [];
                let out = text.replace(
                  cmpRegex,
                  (_m: string, _cmp: string, mid: string, num: string) => {
                    changed.push(`${num}x→${medianStr}(${dirPhrase})`);
                    return `${dirPhrase}${mid}${medianStr}`;
                  },
                );
                out = out.replace(
                  bareRegex,
                  (full: string, pre: string, num: string) => {
                    if (Math.abs(parseFloat(num) - medianVal) <= 0.02)
                      return full; // already correct (or just corrected above)
                    changed.push(`${num}x→${medianStr}`);
                    return `${pre}${medianStr}`;
                  },
                );
                return { out, changed };
              };

              const proseFields = [
                "valuationInsights",
                "valuationRationale",
                "summary",
                "executiveSummary",
              ];
              for (const f of proseFields) {
                const cur = (finalAnalysis as any)[f];
                if (typeof cur === "string" && cur) {
                  const { out, changed } = scrub(cur);
                  if (changed.length) {
                    (finalAnalysis as any)[f] = out;
                    console.warn(
                      `[TYPE4-PTBV-DETERMINISM-LOCK] ${symbol}: ${f} historical-median prose ` +
                        `[${changed.join(", ")}] disagreed with engine ptbvMedian (${medianStr}); ` +
                        `rewrote number + direction to match. Prompt compliance failure.`,
                    );
                  }
                }
              }
              if ((finalAnalysis as any).expectedReturnMath?.rationale) {
                const { out, changed } = scrub(
                  (finalAnalysis as any).expectedReturnMath.rationale,
                );
                if (changed.length) {
                  (finalAnalysis as any).expectedReturnMath.rationale = out;
                  console.warn(
                    `[TYPE4-PTBV-DETERMINISM-LOCK] ${symbol}: rationale historical-median prose ` +
                      `[${changed.join(", ")}] rewritten to engine ptbvMedian (${medianStr}).`,
                  );
                }
              }
            }

            // ─── Current-multiple prose scrub (Tuning v2.91) ────────────────────
            // The card lock closed the unitEconomics surface, and the Oracle promptly
            // minted the same class in free prose: JPM "P/TBV multiple of 2.55x"
            // (engine 3.12x), TRV "2.29x price-to-tangible book" (3.09x), WFC
            // "P/TBV of 1.64x" (1.51x). The CURRENT multiple is pure deterministic
            // arithmetic (px ÷ tbv). Rewrite prose claims of the current multiple to
            // the engine ratio, while leaving terminal / exit / median / historical /
            // trough / target multiples strictly alone — those are scenario
            // assumptions or reference stats, not the current ratio.
            {
              const detCur = `${ptbvDet.toFixed(2)}x`;
              const EXCL =
                /(terminal|exit|median|historical|trough|target|fair|assum|implied|bear|bull|base[- ]case)/i;
              // P2 (phrase-first): "... P/TBV [multiple|ratio] of N.NNx"
              const p2 =
                /((?:P\/TBV|price[- ]to[- ]tangible[- ]book(?:\s+value)?(?:\s+ratio)?)(?:\s+(?:multiple|ratio))?\s+of\s+)(\d+(?:\.\d+)?)x/gi;
              // P1 (number-first): "<trade/current verb> ... N.NNx <tangible-book phrase>"
              const p1 =
                /(\b(?:trades?|trading|buying|priced|represents?|sits?|stands?)\b[^.;]{0,60}?)(\d+(?:\.\d+)?)x(\s*(?:price[- ]to[- ]tangible[- ]book(?:\s+value)?(?:\s+multiple| ratio)?|tangible\s+book\s+value|P\/TBV))/gi;
              const scrubCurrent = (
                text: string,
              ): { out: string; changed: string[] } => {
                const changed: string[] = [];
                const fixNum = (
                  full: string,
                  offset: number,
                  src: string,
                  num: string,
                  rebuild: (v: string) => string,
                ): string => {
                  // Exclusion: reference-multiple qualifier in the 28 chars
                  // immediately preceding the match (e.g. "terminal P/TBV of",
                  // "historical median 1.90x", "exit P/TBV multiple of").
                  const pre = src.slice(Math.max(0, offset - 28), offset);
                  if (EXCL.test(pre) || EXCL.test(full)) return full;
                  const claimed = parseFloat(num);
                  if (!isFinite(claimed) || Math.abs(claimed - ptbvDet) <= 0.05)
                    return full;
                  changed.push(`${num}x→${detCur}`);
                  return rebuild(detCur);
                };
                let out = text.replace(
                  p2,
                  (full: string, pre: string, num: string, offset: number, src: string) =>
                    fixNum(full, offset, src, num, (v) => `${pre}${v}`),
                );
                out = out.replace(
                  p1,
                  (
                    full: string,
                    lead: string,
                    num: string,
                    tail: string,
                    offset: number,
                    src: string,
                  ) => fixNum(full, offset, src, num, (v) => `${lead}${v}${tail}`),
                );
                return { out, changed };
              };
              const curFields = [
                "valuationInsights",
                "valuationRationale",
                "summary",
                "executiveSummary",
                "killerInsight",
              ];
              for (const f of curFields) {
                const cur = (finalAnalysis as any)[f];
                if (typeof cur === "string" && cur) {
                  const { out, changed } = scrubCurrent(cur);
                  if (changed.length) {
                    (finalAnalysis as any)[f] = out;
                    console.warn(
                      `[TYPE4-PTBV-DETERMINISM-LOCK] ${symbol}: ${f} CURRENT P/TBV prose ` +
                        `[${changed.join(", ")}] did not reconcile to deterministic px/tbv ` +
                        `(${px.toFixed(2)} ÷ ${t4tbv.toFixed(2)} = ${detCur}); rewrote. ` +
                        `LLM minted a deterministic ratio in prose.`,
                    );
                  }
                }
              }
              if ((finalAnalysis as any).expectedReturnMath?.rationale) {
                const { out, changed } = scrubCurrent(
                  (finalAnalysis as any).expectedReturnMath.rationale,
                );
                if (changed.length) {
                  (finalAnalysis as any).expectedReturnMath.rationale = out;
                  console.warn(
                    `[TYPE4-PTBV-DETERMINISM-LOCK] ${symbol}: rationale CURRENT P/TBV prose ` +
                      `[${changed.join(", ")}] rewrote to ${detCur}.`,
                  );
                }
              }
            }
          }
        } catch (e) {
          console.error(`[TYPE4-PTBV-DETERMINISM-LOCK] ${symbol}: lock failed`, e);
        }

        if (finalAnalysis.isType5DataContaminated) {
          try {
            const floorPattern1 = /(calculated\s+)?intrinsic\s+value\s+(estimate\s+)?of\s+\$([\d,]+\.?\d*)/gi;
            const floorPattern2 = /\bintrinsic\s+value\s+is\s+\$([\d,]+\.?\d*)/gi;

            const sanitizeFloor = (text: string, fieldName: string): string => {
              const original = text;
              let sanitized = text.replace(floorPattern1, "net-cash floor of $$$3 — a balance-sheet floor, not an intrinsic value");
              sanitized = sanitized.replace(floorPattern2, "net-cash floor is $$$1 — a balance-sheet floor, not an intrinsic value");
              if (original !== sanitized) {
                console.warn(`[CONTAM-FLOOR-PROSE-LOCK] ${symbol}: Oracle prose called the net-cash floor an "intrinsic value" — rewritten. Prompt compliance failure.`);
              }
              return sanitized;
            };

            if (finalAnalysis.expectedReturnMath?.rationale) {
              finalAnalysis.expectedReturnMath.rationale = sanitizeFloor(finalAnalysis.expectedReturnMath.rationale, "expectedReturnMath.rationale");
            }
            if (finalAnalysis.summary) {
              finalAnalysis.summary = sanitizeFloor(finalAnalysis.summary, "summary");
            }
            if (finalAnalysis.valuationInsights) {
              finalAnalysis.valuationInsights = sanitizeFloor(finalAnalysis.valuationInsights, "valuationInsights");
            }
          } catch (e) {
            console.error(`[CONTAM-FLOOR-PROSE-LOCK] ${symbol}: failed`, e);
          }
        }

        // ─── [BUYZONE-PROSE-COHERENCE] (measurement-only) ───────────────────────
        // The dashboard primary entry trigger is dcfModel.buyUnderPrice (a per-name,
        // quality-scaled MoS). buyZoneFramework[0]/[1] are the GENERIC reference tiers
        // (Core = IV×0.80 / Dislocation = IV×0.70). If the Oracle's justification names a
        // tier price as "the buy-under", the prose and the dashboard disagree (ACN: prose
        // $251.62/20% vs dashboard $283.06/10%). Flag only — no mutation, no verdict change.
        try {
          const just = finalAnalysis.buyZoneJustification;
          const fw = finalAnalysis.buyZoneFramework;
          if (typeof just === "string" && just.length > 0 && Array.isArray(fw) && fw.length > 0) {
            const tierPrices = fw
              .map((z: any) => {
                const m = typeof z?.threshold === "string" ? z.threshold.match(/\$([\d,]+(?:\.\d+)?)/) : null;
                return m ? parseFloat(m[1].replace(/,/g, "")) : NaN;
              })
              .filter((n: number) => Number.isFinite(n) && n > 0);
            const buyUnderFraming = /(buy[\s-]?under|entry trigger|primary (?:entry|trigger)|accumulat\w*\s+(?:up to|below)|begin accumulating|conviction entry)/i.test(just);
            const citedPrices = (just.match(/\$([\d,]+(?:\.\d+)?)/g) || [])
              .map((s: string) => parseFloat(s.replace(/[$,]/g, "")))
              .filter((n: number) => Number.isFinite(n) && n > 0);
            const matchedTier = citedPrices.find((cp: number) =>
              tierPrices.some((tp: number) => Math.abs(cp - tp) / tp <= 0.015),
            );
            if (buyUnderFraming && matchedTier !== undefined) {
              console.warn(
                `[BUYZONE-PROSE-COHERENCE] ${symbol}: justification frames $${matchedTier.toFixed(2)} ` +
                  `(≈ a generic IV×0.80/0.70 reference tier) as the buy-under; the dashboard primary ` +
                  `trigger is dcfModel.buyUnderPrice (rendered separately). Oracle anchored on a reference ` +
                  `tier, not the primary trigger (measurement only; prose unchanged).`,
              );
            }
          }
        } catch {
          /* non-fatal */
        }

        // [TYPE5-FLOOR-CROSS-CHECK] (measurement-only): a contaminated Type-5 IV is a net-cash
        // floor (DCF bypassed). That floor rests on FMP balance-sheet data -- the same source as
        // the income we reject as parent-backfilled -- with no cross-check analog to
        // [OE-CROSS-CHECK]. Surface the floor inputs (always) and, best-effort, any DIFFERENT
        // cash figure the web-grounded prose cites (TRAX: FMP ~$298M vs prose ~$180M). Flag only.
        try {
          if (finalAnalysis.isType5DataContaminated) {
            const fmpNetCashM = -(Number(finalAnalysis.netDebt) || 0);
            const sharesM = Number(finalAnalysis.sharesOutstanding) || 0;
            const floorPerShare = sharesM > 0 ? fmpNetCashM / sharesM : null;
            console.warn(
              `[TYPE5-FLOOR-CROSS-CHECK] ${symbol}: net-cash floor rests on FMP netCash=${fmpNetCashM.toFixed(0)}M / ` +
                `shares=${sharesM.toFixed(1)}M => ~$${floorPerShare != null ? floorPerShare.toFixed(2) : "n/a"}/sh. ` +
                `Balance sheet shares the contaminated FMP source as the rejected income -- reconcile vs the standalone 10-Q.`,
            );
            // Best-effort: scan web-grounded prose for a DIFFERENT cash figure. Regex LITERALS
            // (single backslash) — directional + cash-bound, so market-cap / EV dollar figures
            // and per-share amounts (no M/B magnitude) are ignored. group1 = number, group2 = magnitude.
            const proseFields = [
              finalAnalysis.killerInsight,
              finalAnalysis.summary,
              finalAnalysis.valuationInsights,
              finalAnalysis.valuationRationale,
            ].filter((s: any): s is string => typeof s === "string" && s.length > 0);
            const prose = proseFields.join(" | ");
            const reFigCash = /\$\s?(\d+(?:\.\d+)?)\s?(b(?:n|illion)?|m(?:m|illion)?)\s+(?:in|of)?\s*(?:net\s+)?cash\b/gi;
            const reCashFig = /(?:net\s+)?cash(?:\s+(?:position|balance|reserves?))?\s*(?:of|totaling|near(?:ing|ly)?|around|approximately|:|is|was|=|stands at)?\s*\$\s?(\d+(?:\.\d+)?)\s?(b(?:n|illion)?|m(?:m|illion)?)\b/gi;
            const cands = new Set<number>();
            for (const re of [reFigCash, reCashFig]) {
              let m: RegExpExecArray | null;
              while ((m = re.exec(prose))) {
                let v = parseFloat(m[1]);
                if (/^b/i.test(m[2])) v *= 1000; // billions -> $M
                if (Number.isFinite(v) && v > 0) cands.add(Math.round(v));
              }
            }
            const diverging = [...cands].filter(
              (c) => fmpNetCashM > 0 && Math.abs(c - fmpNetCashM) / fmpNetCashM > 0.20,
            );
            if (diverging.length > 0) {
              const c = diverging[0];
              const pct = ((Math.abs(c - fmpNetCashM) / fmpNetCashM) * 100).toFixed(0);
              console.warn(
                `[TYPE5-FLOOR-CROSS-CHECK] ${symbol}: web-grounded prose cites cash ~$${c}M ` +
                  `(${pct}% vs FMP $${fmpNetCashM.toFixed(0)}M) -- possible balance-sheet contamination; ` +
                  `the floor (~$${floorPerShare != null ? floorPerShare.toFixed(2) : "n/a"}/sh) may be over/understated. Measurement only.`,
              );
            }
          }
        } catch {
          /* non-fatal */
        }

        // [FLOOR-SOURCE-GAP] (measurement-only): compare FMP net-cash vs Pulse-extracted cash on contaminated names
        try {
          if (finalAnalysis.isType5DataContaminated && pulseOutput?.currentIntelligence) {
            const fmpNetCashM = -(Number(finalAnalysis.netDebt) || 0);
            const rePulse1 = /\$([\d,]+(?:\.\d+)?)\s*(million|billion|M|B)\b[^.]{0,60}?\b(cash|runway|cushion)\b/gi;
            const rePulse2 = /\b(cash|runway|cushion)\b[^$]{0,60}?\$([\d,]+(?:\.\d+)?)\s*(million|billion|M|B)\b/gi;
            
            const pulseCands: number[] = [];
            const extract = (text: string, re: RegExp, numIdx: number, magIdx: number) => {
              let m: RegExpExecArray | null;
              while ((m = re.exec(text))) {
                let v = parseFloat(m[numIdx].replace(/,/g, ""));
                const mag = m[magIdx].toLowerCase();
                if (mag.startsWith("b")) v *= 1000;
                if (Number.isFinite(v) && v > 0) pulseCands.push(v);
              }
            };
            
            extract(pulseOutput.currentIntelligence, rePulse1, 1, 2);
            extract(pulseOutput.currentIntelligence, rePulse2, 2, 3);
            
            let floorSourceGapPct: number | null = null;
            let parseOk = false;
            let firstPulse = null;
            
            if (pulseCands.length > 0) {
              parseOk = true;
              firstPulse = pulseCands[0];
              if (fmpNetCashM > 0) {
                floorSourceGapPct = (Math.abs(fmpNetCashM - firstPulse) / fmpNetCashM) * 100;
              }
            }
            
            (finalAnalysis as any).observability = (finalAnalysis as any).observability || {};
            (finalAnalysis as any).observability.floorSourceGapPct = floorSourceGapPct;
            (finalAnalysis as any).observability.floorSourceParseOk = parseOk;
            
            console.log(`[FLOOR-SOURCE-GAP] ${symbol}: fmpNetCash=$${fmpNetCashM.toFixed(0)}M pulseCandidates=[${pulseCands.join(", ")}] firstPulse=${firstPulse != null ? "$" + firstPulse + "M" : "null"} gapPct=${floorSourceGapPct != null ? floorSourceGapPct.toFixed(1) + "%" : "null"} parseOk=${parseOk} (measurement only; floor unchanged)`);
          }
        } catch (e) {
          console.error(`[FLOOR-SOURCE-GAP] ${symbol}: failed`, e);
        }

        // ─── [PRICE-PROSE-LOCK] Current Price Normalization ──────────────────────
        try {
          if (finalAnalysis.currentPrice && finalAnalysis.currentPrice > 0) {
            // (Tuning v2.93) extended: GNTX rendered the raw analysis price "24.045" —
            // dollar-less, three decimals, behind anchors the old pattern lacked
            // ("current market price of", "trading at"). Dollar-less numbers are matched
            // ONLY when they contain a decimal point (integers like "trades at 12 times"
            // stay untouched), decimals widened 2→4 places, and any match immediately
            // followed by x/%/× is rejected (multiples and percentages are not prices).
            // Group 2 = dollar-prefixed amount, group 3 = bare decimal amount. The
            // (?!\.\d) guard blocks backtracking from defeating the x/% rejection
            // ("$15.5x" must not shrink to a "$15" match that dodges the lookahead).
            const anchorRegex = /(current market price of|market price of|current price of|currently trades at|currently trading at|trades at|trading at)\s*(?:\$([\d,]+(?:\.\d{1,4})?)|([\d,]+\.\d{1,4}))(?!\d)(?!\.\d)(?!\s*[x%×])/gi;
            const normalizeProsePrice = (obj: any, path: string = "") => {
              if (!obj || typeof obj !== 'object') return;
              for (const [k, v] of Object.entries(obj)) {
                if (typeof v === 'string') {
                  let changedValues: Array<{old: string, new: string}> = [];
                  const newVal = v.replace(anchorRegex, (match, anchor, amountDollar, amountBare, offset) => {
                    const amount = amountDollar ?? amountBare;
                    const anchorNorm = anchor.toLowerCase().trim();
                    const isBareTradesAt = anchorNorm === "trades at" || anchorNorm === "trading at";
                    if (isBareTradesAt) {
                      const startIndex = Math.max(0, offset - 30);
                      const precedingWindow = v.substring(startIndex, offset);
                      const peerTickerRegex = /\b[A-Z]{1,5}\b/g;
                      let peerMatch;
                      let skip = false;
                      while ((peerMatch = peerTickerRegex.exec(precedingWindow)) !== null) {
                        if (peerMatch[0] !== symbol && peerMatch[0] !== 'I' && peerMatch[0] !== 'A') {
                          skip = true;
                          break;
                        }
                      }
                      
                      // Peer names if available
                      const anyAnalysis = finalAnalysis as any;
                      const peers = anyAnalysis.factSheet?.peerBenchmarks || anyAnalysis.competitors;
                      if (!skip && peers) {
                        if (Array.isArray(peers)) {
                           for (const peer of peers) {
                              const pName = peer.name || peer.companyName;
                              if (pName && precedingWindow.toLowerCase().includes(pName.toLowerCase())) {
                                 skip = true;
                                 break;
                              }
                           }
                        }
                      }

                      if (skip) return match;
                    }

                    const properPrice = finalAnalysis.currentPrice.toFixed(2);
                    if (amount !== properPrice) {
                      changedValues.push({old: amount, new: properPrice});
                    }
                    return `${anchor} $${properPrice}`;
                  });
                  if (changedValues.length > 0) {
                    for (const change of changedValues) {
                      console.info(`[PRICE-PROSE-LOCK] ${symbol}: normalized narrative price $${change.old} → $${change.new} in <${path ? path + '.' + k : k}>`);
                    }
                    obj[k as keyof typeof obj] = newVal;
                  }
                } else if (typeof v === 'object') {
                  normalizeProsePrice(v, path ? `${path}.${k}` : k);
                }
              }
            };
            normalizeProsePrice(finalAnalysis);
          }
        } catch { /* non-fatal */ }

        // ─── [IV-PROSE-CHECK] Prose IV Consistency ─────────────────────────────
        try {
          const baseIV = finalAnalysis.scenarios?.base?.intrinsicValuePerShare;
          // IVPC-1: scenario-IV whitelist — prose legitimately cites bull/bear IVs
          // (e.g., upsideCase.impact citing the bull IV); those are NOT base-IV divergences.
          const scenarioIVs: Array<[string, number]> = [
            ['bull', finalAnalysis.scenarios?.bull?.intrinsicValuePerShare],
            ['bear', finalAnalysis.scenarios?.bear?.intrinsicValuePerShare],
          ].filter(([, iv]) => typeof iv === 'number' && isFinite(iv as number) && (iv as number) > 0) as Array<[string, number]>;
          if (baseIV && baseIV > 0) {
            const ivRegex = /(intrinsic value of|fair value of|base case intrinsic value of|IV of)\s*\$([\d,]+(?:\.\d{1,2})?)/gi;
            const exclusionRegex = /(bull|bear|upside|downside|strategic)/i;
            
            const checkProseIV = (obj: any, path: string = "") => {
              if (!obj || typeof obj !== 'object') return;
              for (const [k, v] of Object.entries(obj)) {
                if (typeof v === 'string') {
                  let match;
                  while ((match = ivRegex.exec(v)) !== null) {
                    const statedValue = parseFloat(match[2].replace(/,/g, ''));
                    if (!isNaN(statedValue)) {
                      const diffPct = Math.abs(statedValue - baseIV) / baseIV;
                      if (diffPct > 0.02) {
                        // IVPC-1: whitelist check — a prose IV matching the engine bull/bear IV
                        // (within display-rounding tolerance) is a scenario cite, not a divergence.
                        const scenarioMatch = scenarioIVs.find(([, iv]) =>
                          Math.abs(statedValue - iv) <= Math.max(0.5, iv * 0.005)
                        );
                        if (scenarioMatch) {
                          console.info(`[IV-PROSE-CHECK] ${symbol}: prose IV $${statedValue} in <${path ? path + '.' + k : k}> matched scenario IV (matchedScenario=${scenarioMatch[0]}) — not a divergence.`);
                        } else {
                          const startIndex = Math.max(0, match.index - 40);
                          const precedingWindow = v.substring(startIndex, match.index);
                          if (!exclusionRegex.test(precedingWindow)) {
                            console.info(`[IV-PROSE-CHECK] ${symbol}: prose base-IV $${statedValue} diverges from engine base $${baseIV.toFixed(2)} in <${path ? path + '.' + k : k}>`);
                          }
                        }
                      }
                    }
                  }
                } else if (typeof v === 'object') {
                  checkProseIV(v, path ? `${path}.${k}` : k);
                }
              }
            };
            checkProseIV(finalAnalysis);
          }
        } catch { /* non-fatal */ }

        // ─── F14b (Tuning v2.63 §39.3): propagate isUnresolvableValuation to the TOP-LEVEL
        // analysis object the client consumes. It is written to deterministicData + observability
        // upstream, but the MemoTab display gates read `analysis?.isUnresolvableValuation` at the
        // top level — without this line those gates silently read undefined and only degrade via
        // dcfModel.isEstimable. This closes the F14 leak on the non-Oracle display surfaces
        // (10-yr projection table, expected-return math, scenario cards, risk table).
        (finalAnalysis as any).isUnresolvableValuation =
          deterministicData?.isUnresolvableValuation ?? false;

        return { analysis: finalAnalysis as CompanyAnalysis, response: res };
      } catch (e) {
        const responseText = extractText(res);
        console.error(
          `JSON Parse Error.\nRaw text snippet (first 1000 chars):\n${responseText.substring(0, 1000)}\n\nEnd of snippet`,
        );
        throw e; // Trigger retry
      }
    }, 5);
  };

  // Cloud Run timeout set to 3600s.
  // AGENT C (THE ORACLE): MODELS.ORACLE.primary + thinking_level: "high" (Primary)
  // Switched from the 3.1 Pro preview model (Preview) on June 18, 2026.
  // Reason: Preview model carries restrictive rate limits causing 503s at scale.
  // Flash (Stable) + high thinking produces equivalent output with better
  // availability and 25% lower token cost.
  // AGENT E (THE KEEPER): MODELS.KEEPER.primary — see keeperService.ts.
  const primaryModel = MODELS.ORACLE.primary;

  try {
    onProgress?.("Agent C: Synthesizing Oracle verdict...");
    const result = await executeAnalysis(primaryModel, false, true);
    const sources =
      result?.response?.candidates?.[0]?.groundingMetadata?.groundingChunks?.map(
        (chunk: any) => ({
          title: chunk.web?.title || "Source",
          uri: chunk.web?.uri || "#",
        }),
      ) || [];
    return {
      ...result.analysis,
      sources,
      isProduction,
      annualFilingCount,
      analystSearchConfidenceScore: intelligenceBrief.searchConfidenceScore,
      pulse: pulseOutput,
      analystIntelligence,
      peerBenchmarks: factSheet.peerBenchmarks,
    };
  } catch (err: any) {
    // Log the full error for debugging
    console.error("Gemini Analysis Error:", err);

    const errorStr = typeof err === "string" ? err : JSON.stringify(err);
    const errorMsg = err?.message || err?.error?.message || "";
    const combinedMsg = `${errorStr} ${errorMsg}`.toLowerCase();
    const statusCode = err?.status || err?.error?.code || err?.code || 0;

    const isForbidden =
      combinedMsg.includes("403") ||
      combinedMsg.includes("forbidden") ||
      statusCode === 403 ||
      statusCode === "403";

    const isServiceUnavailable =
      combinedMsg.includes("503") ||
      statusCode === 503 ||
      statusCode === "503" ||
      combinedMsg.includes("unavailable") ||
      combinedMsg.includes("high demand") ||
      combinedMsg.includes("overloaded") ||
      combinedMsg.includes("unexpected content-type") ||
      combinedMsg.includes("starting server");

    if (isForbidden || isServiceUnavailable) {
      if (isServiceUnavailable) {
        console.warn(
          `Gemini model ${primaryModel} is experiencing high demand (503). Falling back to ${MODELS.ORACLE.fallback}...`,
        );
        recordModelFailover({
          agent: "Oracle",
          modelTried: primaryModel,
          fallbackModel: MODELS.ORACLE.fallback,
          statusCode: statusCode || 503,
          ticker
        });
        try {
          const result = await executeAnalysis(MODELS.ORACLE.fallback, false, false);
          const degradedAnalysis = {
            ...result.analysis,
            oracleJudgment:
              `⚠️ DEGRADED OUTPUT — Primary model (${primaryModel}) was unavailable (503 high demand). This memo was generated by ${MODELS.ORACLE.fallback} with reduced qualitative depth. Retry for full analysis. ${result.analysis.oracleJudgment || ""}`.trim(),
          };
          return {
            ...degradedAnalysis,
            sources: [],
            isProduction,
            annualFilingCount,
            analystSearchConfidenceScore:
              intelligenceBrief.searchConfidenceScore,
            pulse: pulseOutput,
            analystIntelligence,
            peerBenchmarks: factSheet.peerBenchmarks,
          };
        } catch (flash503Err: any) {
          console.error(
            "Gemini Fallback also failed on 503:",
            flash503Err,
          );
          throw new Error(
            "Service Unavailable (503): The Gemini model is currently experiencing high demand. Please try again later.",
          );
        }
      } else {
        // If it's a 403 Forbidden we try progressively simpler configurations

        // 1. Try without Search first (common cause for 403 on some keys/models)
        console.warn("Attempting 403 fallback without Google Search...");
        try {
          const result = await executeAnalysis(primaryModel, false, true);
          return {
            ...result.analysis,
            sources: [],
            isProduction,
            annualFilingCount,
            analystSearchConfidenceScore:
              intelligenceBrief.searchConfidenceScore,
            pulse: pulseOutput,
            analystIntelligence,
            peerBenchmarks: factSheet.peerBenchmarks,
          };
        } catch (searchErr: any) {
          console.error("Gemini without Search also failed:", searchErr);

          // 2. Try without Thinking (another potential cause for 403 on some keys/regions)
          console.warn(
            "Gemini without Search also failed. Trying without Thinking Config...",
          );
          try {
            const result = await executeAnalysis(primaryModel, false, false);
            return {
              ...result.analysis,
              sources: [],
              isProduction,
              annualFilingCount,
              analystSearchConfidenceScore:
                intelligenceBrief.searchConfidenceScore,
              pulse: pulseOutput,
              analystIntelligence,
              peerBenchmarks: factSheet.peerBenchmarks,
            };
          } catch (thinkingErr: any) {
            console.error("Gemini without Thinking also failed:", thinkingErr);

            // 3. If we tried primary, fallback to 2.5-flash as a last resort
            console.warn(
              `Primary AI model completely failed with 403. Falling back to ${MODELS.ORACLE.fallback} (no search, no thinking)...`,
            );
            recordModelFailover({
              agent: "Oracle",
              modelTried: primaryModel,
              fallbackModel: MODELS.ORACLE.fallback,
              statusCode: statusCode || 403,
              ticker
            });
            try {
              const result = await executeAnalysis(
                MODELS.ORACLE.fallback,
                false,
                false,
              );
              const degradedAnalysis = {
                ...result.analysis,
                oracleJudgment:
                  `⚠️ DEGRADED OUTPUT — Primary model (${primaryModel}) was unavailable (403 access error). This memo was generated by ${MODELS.ORACLE.fallback} with reduced qualitative depth. Check API key permissions. ${result.analysis.oracleJudgment || ""}`.trim(),
              };
              return {
                ...degradedAnalysis,
                sources: [],
                isProduction,
                annualFilingCount,
                analystSearchConfidenceScore:
                  intelligenceBrief.searchConfidenceScore,
                pulse: pulseOutput,
                analystIntelligence,
                peerBenchmarks: factSheet.peerBenchmarks,
              };
            } catch (flashErr: any) {
              console.error("Gemini Flash Fallback also failed:", flashErr);
              throw new Error(
                "Access Forbidden (403): The AI service (Gemini) rejected the request. This usually happens when the platform-provided API key is restricted or the Google Search tool is not enabled. Please check your API key in the settings.",
              );
            }
          }
        }
      }
    }

    throw err;
  }
}
export async function analyzeCompanySSE(
  ticker: string,
  currentDateTime: string,
  thirtyYearTreasury: number,
  geminiKey: string,
  callbacks: AnalysisStageCallbacks,
  registryType5Origin?: boolean,
  registryParentTicker?: string | null,
): Promise<CompanyAnalysis> {
  if (callbacks.onModelFailover) {
    setFailoverCallback(callbacks.onModelFailover);
  }
  
  // We use the existing orchestration logic in analyzeCompany and map its exact progress
  // markers to the SSE stage callbacks. This avoids restructuring the intricate
  // fallback/retry tree or data-fetching hooks internally.
  let mathStarted = false;
  let oracleStarted = false;
  let analystStarted = false;
  let pulseStarted = false;

  const analysis = await analyzeCompany(ticker, currentDateTime, (message) => {
    if (message.includes("Agent B")) {
      callbacks.onHistorianComplete();
      callbacks.onMathematicianStart();
      callbacks.onAnalystStart();
      callbacks.onPulseStart();
      mathStarted = true;
      analystStarted = true;
      pulseStarted = true;
    } else if (message.includes("Agent F: Pulse complete")) {
      callbacks.onPulseComplete();
    } else if (message.includes("Agent D: Intelligence brief complete")) {
      callbacks.onAnalystComplete();
    } else if (message.includes("Agent C")) {
      if (!mathStarted) callbacks.onMathematicianStart();
      if (!analystStarted) callbacks.onAnalystStart();
      if (!pulseStarted) callbacks.onPulseStart();
      callbacks.onMathematicianComplete();
      callbacks.onAnalystComplete();
      callbacks.onPulseComplete();
      callbacks.onOracleStart();
      oracleStarted = true;
    }
  }, registryType5Origin, registryParentTicker);

  // Ensure all callbacks fire in sequence if not explicitly triggered
  if (!oracleStarted) {
    if (!mathStarted) callbacks.onMathematicianStart();
    if (!analystStarted) callbacks.onAnalystStart();
    if (!pulseStarted) callbacks.onPulseStart();
    callbacks.onMathematicianComplete();
    callbacks.onAnalystComplete();
    callbacks.onPulseComplete();
    callbacks.onOracleStart();
  }
  callbacks.onOracleComplete();

  return analysis;
}
